export const en = {
  // ── Common ──────────────────────────────────────────────
  common: {
    optivo: "Optivo",
    save: "Save",
    saving: "Saving...",
    cancel: "Cancel",
    test: "Test",
    active: "Active",
    loading: "Loading...",
    dismiss: "Dismiss",
    // The name of the X a dialog and a sheet draw themselves (`ui/dialog`,
    // `ui/sheet`), said to a screen reader alone. Read by the primitive
    // through `closeLabel`, which keeps this English word for a page that
    // hands it a table without one.
    close: "Close",
    delete: "Delete",
    confirm: "Confirm",
    error: "Something went wrong",
  },

  // ── Pager ───────────────────────────────────────────────
  // The words the pagination primitive (`ui/pagination`) draws itself: no
  // caller hands them in, so they belong to the control, as the strength
  // presets below do. Read by the primitive through `paginationLabels`, which
  // keeps these English words for a page that hands it a table without them.
  // `previous` / `next` are what a step shows from `sm` up; `previousPage` /
  // `nextPage` are what it is named to a screen reader at every width, and
  // each holds the word it stands for (WCAG 2.5.3). They say nothing of what
  // is paged: a caller that needs to names its own pager. `page` names a
  // numbered step, which shows its number alone: "10" says ten of what? The
  // open page is the same name, `aria-current` saying that it is open.
  pagination: {
    label: "Pagination",
    previous: "Previous",
    next: "Next",
    previousPage: "Go to previous page",
    nextPage: "Go to next page",
    morePages: "More pages",
    page: "Page {page}",
  },

  // ── Calendar ────────────────────────────────────────────
  // The names the calendar primitive (`ui/calendar`) gives its own controls,
  // said to a screen reader alone. react-day-picker writes them in English
  // whatever locale it is handed; `calendarWords` reads these instead and
  // keeps the English ones for a page that hands it a table without them.
  // `{date}` is the full date as the picker writes it in the reader's locale.
  calendar: {
    navigation: "Month navigation",
    previousMonth: "Go to previous month",
    nextMonth: "Go to next month",
    today: "Today, {date}",
    selected: "{date}, selected",
    monthDropdown: "Choose a month",
    yearDropdown: "Choose a year",
    weekNumber: "Week {week}",
    weekNumberHeader: "Week number",
  },

  // ── Strength presets ────────────────────────────────────
  // One vocabulary for every "how much" dial in the product. Lives at the top
  // level rather than inside a feature group because the words belong to the
  // control, not to the setting it happens to be configuring.
  strengthPreset: {
    off: "Off",
    mild: "Mild",
    medium: "Medium",
    high: "High",
    custom: "Custom",
  },

  // ── Applying a property-wide setting to every room type ─────────────────
  // One vocabulary for every section that owns a setting a room type can take
  // over. Top level rather than inside a feature group for the same reason the
  // strength presets are: the words belong to the control, not to the setting
  // it happens to be reaching into — and five label bags is five chances for
  // one to drift. Deliberately says nothing about *which* setting: the section
  // it opens from has already named that, and the room types listed carry the
  // part that is worth reading.
  applyToRoomKinds: {
    action: "Apply to all room types",
    title: "Overwrite what these room types set?",
    body: "These room types set this themselves, so a change here never reaches them. Applying drops what they set and they follow the property-wide setting again — now and every time you change it.",
    confirm: "Apply to all room types",
  },

  // ── Which of an account's properties an access covers ───────────────────
  // The second question the two access panels ask (the owner's members panel
  // on the Connections page and the admin console's access dialog), beside
  // what the access is. Top level for the reason the strength presets are:
  // the words belong to the control (`access/AccessScopeField`), and two
  // panels' label bags are two chances for one to drift. Full access to every
  // property follows a property the account adds later; read-only access is
  // one property's, so its "every property" is never promised to.
  accessScope: {
    legend: "Which properties",
    legendRemove: "Take them off",
    onlyThis: "Only {name}",
    allFull: "Every property of the account ({count}), including ones it adds later",
    allReadOnly: "Every property the account has today ({count})",
    allRemove: "Every property of the account they reach ({count})",
    some: "Selected properties",
    chooseOne: "Choose at least one property.",
    alwaysIncluded: "{name} is always included.",
    propertyById: "Property #{id}",
    // What a row says the person reaches across the account.
    reachWhole: "Every property of the account",
    reachOnlyThis: "This property only",
    reachSome: "{count} of {total} properties: {names}",
    // A person who holds both kinds of access in the account, named by kind
    // and joined by " · ".
    reachFull: "Full access: {names}",
    reachFullWhole: "Full access: every property of the account",
    reachReadOnly: "Read-only: {names}",
    // What a change leaves the person on the account's other properties. A
    // sentence that names a list has a form for one property (`…One`), which
    // Czech needs for the noun's number; English says both alike.
    keeps: "They keep their access to {names}.",
    keepsOne: "They keep their access to {names}.",
    keepsFull: "They keep full access to {names}.",
    keepsFullOne: "They keep full access to {names}.",
    keepsReadOnly: "They keep read-only access to {names}.",
    keepsReadOnlyOne: "They keep read-only access to {names}.",
    addedLater: "Properties the account adds later will no longer be included.",
    leaves: "They will no longer reach any property of this account.",
    noOther: "They reach no other property of this account.",
    legacyLoses: "They lose access to every other property this account owns.",
    // A server that does not tell properties apart gave the whole account.
    notHonoured: "Full access was given to every property of the account rather than to the properties chosen. Remove it, or change it on their row, if that is not wanted.",
    // A server that knows scopes answers "every property" to a scoped grant
    // only for somebody who already had it: nothing was given or is to undo.
    alreadyWholeAccount: "This person already has full access to every property of the account, so nothing changed.",
    alreadyFullAccess: "This person already has full access to {names}.",
    alreadyFullAccessOne: "This person already has full access to {names}.",
    alreadyReadOnlyElsewhere: "This person already has read-only access ({kind}) to {names}. Change it on their row in that property's access list.",
    alreadyReadOnlyElsewhereOne: "This person already has read-only access ({kind}) to {names}. Change it on their row in that property's access list.",
    removeTitle: "Take {user} off which properties?",
    removeDescription: "{user} reaches {count} properties of this account. Choose which to take them off.",
    removeAction: "Remove access",
    fullAccessTitle: "Give {user} full access?",
    fullAccessDescription: "They will be able to change the prices, strategies and settings of the properties you choose. Read-only access they hold to those properties is replaced.",
    fullAccessAction: "Give full access",
    promoteTitle: "Make {user} an owner?",
    promoteDescription: "Owners reach every property of the account, including ones it adds later. Today {user} reaches {names}.",
    promoteDescriptionOne: "Owners reach every property of the account, including ones it adds later. Today {user} reaches {names}.",
    promoteAction: "Make owner",
    cancel: "Cancel",
  },

  // ── Where a demand level starts ─────────────────────────
  // A linked plan's demand level is the night's overall demand against an
  // ordinary night of its room type, while the Prices calendar tints the same
  // words by booking pace alone — so a level is shown with where it starts.
  // `{pct}` arrives signed ("−15", "+10"). Read by the control itself, like the
  // vocabularies above: the band belongs to the level, not to the channel it
  // is configured for.
  demandLevelBand: {
    below: "under {pct}%",
    from: "from {pct}%",
  },

  // ── Analysis-derived values ─────────────────────────────
  // Two records are re-measured from the property's own booking history every
  // day and follow that measurement on their own. Said by the control rather
  // than by each screen, so the wizard and the strategy editor cannot describe
  // the same mechanism differently.
  followsAnalysis: {
    bookingWindow:
      "A room type you have not changed follows a measurement from your booking history, re-checked daily: the days before arrival by which half of its booked nights are usually on the books (every night of a stay counts). That says when your guests book, not how long to hold your price, so a room type that books early is held for a long time. Type a number and that room type keeps it until you clear it.",
    basePrice:
      "Measured from your booking history and re-checked daily. Leave a field empty and it follows that measurement; type a price and it is pinned until you clear it.",
    caution:
      "Only override a value you have a reason for: it stops tracking your own trading, and it is what the rest of the pricing is measured against.",
  },

  // ── Prices from the PMS price list ──────────────────────
  // A room type no stay has priced — every room type of a property that has
  // not opened yet — starts from the weekday price its PMS price list states.
  // Said by the control, like `followsAnalysis`, so the wizard and the strategy
  // editor cannot describe it two ways.
  priceListSource: {
    effective: "Optivo will use {value} — the weekday price in your PMS price list.",
    reference: "From your PMS price list",
    basePrice:
      "This room type has no booking history yet, so Optivo starts from the weekday price your PMS price list states for it. Once its own bookings are enough to measure, it is priced from those instead. Type a price to use your own.",
  },

  // ── Weekend and holiday booking window ──────────────────
  // The one box under the Booking Window table for the nights the calendar
  // already prices as peaks. Said by the control, like the two groups around it,
  // so the wizard and the strategy editor cannot describe it two ways.
  peakNightBookingWindow: {
    label: "Weekend and holiday nights",
    hint: "Friday and Saturday nights, nights before a holiday or special date with a premium, and bridge nights between them cannot drop below their adjusted base price until this many days before arrival. Use it when these nights still sell at full price close to arrival. It applies to every room type and season, in place of the windows above.",
    follows: "Empty: these nights follow each room type's window in the table.",
    stated: "Clear the box to hand these nights back to the table.",
  },

  // ── Shoulder days ───────────────────────────────────────
  // The one control both surfaces render for the nights either side of a
  // holiday break. Its words live here rather than in either surface's label
  // bag, so the wizard and the strategy editor cannot describe the setting two
  // ways — the reason `followsAnalysis` sits at the top level too.
  shoulderDay: {
    enable: "Adjust shoulder days",
    hint: "A shoulder day is the ordinary night right next to a holiday break: the night before it begins and the night of its last day. Demand gathers on the break and usually thins out either side of it. An ordinary weekend has none — a holiday has to add a night to it, or fall midweek.",
    adjustment: "Shoulder-day adjustment",
    adjustmentHint: "Measured against the base price and added to the night's weekday adjustment. Usually a discount of up to about 15% — set a plus if your guests stretch the break into these nights instead.",
  },

  // ── A minimum price that follows the season ─────────────
  // The one control both minimums share — the usual one in Prices by Room
  // Type, the last-minute one in Last-Minute Pricing — and every line that
  // states what the choice does: the range under a room type's minimum, the
  // band summary, the last-minute column and the Seasonality status. Said by
  // the control, like the groups above. `{setIn}` and `{lastMinuteSetIn}` are
  // the editor's own section titles; `{label}` and `{lastMinuteLabel}` the
  // controls' own labels below.
  minimumSeasonality: {
    label: "Minimum price across the year",
    lastMinuteLabel: "Last-minute minimum across the year",
    fixed: "Fixed all year",
    follows: "Follows Seasonality",
    // What a fixed minimum means for a low month is said once, under the band,
    // with the figure: `fixedLowMonth` below.
    fixedHint: "The minimum is the same in every month.",
    followsHint: "The minimum moves by the same percentage as the night's Seasonality month: a minimum of 120 is 96 in a −20% month and 150 in a +25% one.",
    lastMinuteFixedHint: "The last-minute minimum is the same in every month.",
    lastMinuteFollowsHint: "The last-minute minimum moves by the night's Seasonality month, never by its weekday or holiday premium.",
    followsUsual: "Until you pick one here, it follows “{label}” in {setIn} — now {choice}.",
    departsFromUsual: "Chosen here: “{label}” in {setIn} is {choice}. Pick that to follow it again.",
    // A proposal row's value where the last-minute minimum stores no choice.
    lastMinuteFollowsUsual: "Same as the minimum price",
    range: "Follows Seasonality: from {low} in {lowMonth} to {high} in {highMonth}.",
    ordinaryMonth: "an ordinary month",
    bandSummary: "Optivo prices {roomKind} from {base}: the nightly rate stays below {max} and above a minimum that moves with Seasonality, {minLow}–{minHigh}.",
    fixedLowMonth: "The minimum is fixed all year, so a low Seasonality month cannot take a night below {min}.",
    // Where the last-minute minimum follows the season, the lower of the two
    // floors moves with the month inside the window.
    fixedLowMonthLastMinuteFollows: "The minimum is fixed all year, so a low Seasonality month cannot take a night below {min} except inside the last-minute window, where the last-minute minimum follows Seasonality.",
    // The month stands in brackets, where Czech can take it as it is.
    aboveMaximum: "The minimum moves to {value} ({month}), above the maximum of {max} — the maximum wins there.",
    lastMinuteColumn: "Every figure in this column moves with the night's Seasonality month, from {low} to {high}.",
    seasonStatus: "Minimum prices: {label} — {choice} ({setIn}); {lastMinuteLabel} — {lastMinuteChoice} ({lastMinuteSetIn}).",
  },

  // ── Non-refundable ceiling, in rooms ────────────────────
  // The line under each room type in the Non-Refundable Pricing table. The
  // ceiling reads the room type's own occupancy, which on a small room type
  // moves a whole room at a time, so the line says what the percentage means
  // in that room type's rooms. Read by the table rather than its label bag.
  // `{count}` in `offeredUpTo` is the most rooms sold on a night that still
  // gets the rate; in `neverWithheld` it is the room type's rooms.
  nonRefundableCeilingRooms: {
    offeredUpTo: {
      one: "Offered while no more than {count} of this type's {rooms} rooms is sold.",
      few: "Offered while no more than {count} of this type's {rooms} rooms are sold.",
      other: "Offered while no more than {count} of this type's {rooms} rooms are sold.",
    },
    offeredWhileNoneSold: "Offered only while none of this type's {rooms} rooms is sold.",
    neverWithheld: {
      one: "This type has {count} room, which is either free or sold, so this ceiling never switches the rate off.",
      few: "This type has {count} rooms, and this ceiling never switches the rate off while one of them is free.",
      other: "This type has {count} rooms, and this ceiling never switches the rate off while one of them is free.",
    },
  },

  // ── Minimum stay, per room type ─────────────────────────
  // The table under Rate Restrictions where a room type states its own
  // minimum stay — its floor, the weekend and holiday minimum and its two
  // releases. Read by the table rather than either surface's label bag, like
  // the groups above, so the wizard and the strategy editor cannot describe it
  // two ways. The column words also name each room type's setting in the
  // confirmation that hands them back and on a proposal's review page.
  losRoomKinds: {
    title: "Per room type",
    hint: "Set any of these for a single room type — an apartment not worth turning over for one night, say. An empty box, or a choice left on “Same as property”, follows the settings above, including every later change to them.",
    roomKindColumn: "Room type",
    minLosColumn: "Minimum stay",
    weekendColumn: "Weekends and holidays",
    releaseColumn: "Release within",
    weakWeekColumn: "Weak demand",
    nights: "nights",
    days: "days",
    follows: "Same as property",
    on: "On",
    off: "Off",
    release: "Release",
    hold: "Hold",
    reset: "Reset to the property's settings",
    noRoomKinds: "No room types available yet.",
    floorNote: "A room type's minimum stay is its floor: a weekend or holiday break can still ask for more nights, and every release hands back to the floor, never below it.",
    releaseNote: "The two release columns apply only where the weekend and holiday minimum is on.",
    releaseForRoomKinds: "Off for the property. The release settings below apply to the room types that switch the minimum on for themselves in the table.",
  },

  // Not every narrowed dial is a "how much" dial. One that decides WHEN
  // something starts reads back to front in the strength words — the mildest
  // setting is the one that waits longest — so it speaks these three instead.
  timingPreset: {
    early: "Early",
    regular: "Regular",
    late: "Late",
  },

  // A third question again: not how hard a dial pushes or when it starts, but
  // how steeply a movement runs across its window — the shape of a ramp. The
  // last-minute discount curve is the dial these exist for.
  pacePreset: {
    faster: "Faster",
    linear: "Linear",
    slower: "Slower",
  },

  // How a PMS sync cadence is said, once, for every screen that says it. The
  // store is minutes; anything from an hour up reads in hours. A whole phrase
  // per cadence rather than a count in a frame, because Czech declines the
  // noun with the number ("každou hodinu" / "každé 2 hodiny" / "každých 6
  // hodin") and English wants "every hour" over "every 1 hours".
  // `components/pms/syncCadence.ts` capitalises the first letter where the
  // phrase stands alone.
  syncCadence: {
    30: "every 30 minutes",
    60: "every hour",
    120: "every 2 hours",
    360: "every 6 hours",
    720: "every 12 hours",
    1440: "every 24 hours",
  },

  // The five groups the strategy editor and the setup wizard file their
  // sections under (`components/pricing/strategy-section-groups.ts`). Top
  // level because both surfaces print the same five, the editor's navigator
  // and the wizard's step list in the app menu.
  strategySectionGroups: {
    foundation: "Foundation",
    prices: "Prices",
    sellingRules: "Selling rules",
    calendar: "Calendar",
    distribution: "Distribution",
  },

  // ── Strategy help ───────────────────────────────────────
  // Asking Optivo from the rail, on every page
  // (`components/support/AskOptivoRail.tsx` and its dialog). A "revenue
  // study" is the analysis the `/revenue-study` method produces; its Czech name
  // is "analýza strategie revenue managementu", never "studie tržeb". No line
  // promises a turnaround the support mailbox does not already promise.
  strategyHelp: {
    askButton: "Ask Optivo",
    askHint: "Send us a question, or ask for a revenue study with the settings we recommend for your property.",
    // Where the live assistant is offered (`assistantAvailable` on the status).
    askHintChat: "Get an answer right away from the Optivo assistant (AI), send us a question, or ask for a revenue study with the settings we recommend for your property.",
    dialogTitle: "Ask Optivo",
    dialogDescription: "Your message reaches Optivo support together with the property and the strategy it is about.",
    // The window's description while the live assistant is chosen — one of the
    // places that say it is an AI assistant (EU AI Act Art. 50).
    dialogDescriptionChat: "You are writing to an AI assistant. It reads your property's settings and prices.",
    // The name of the switch between the ways to ask, which stands under the
    // window's title bar in every state. Each way goes by one word on it
    // (`kind…Short`): the three whole names need some 830px in a row, and a
    // row of the window has 466px, or 286px at its 18rem minimum. A way's
    // whole name and its hint stand at the head of its form, and are read to
    // a screen reader as the way's description. The assistant's word says it
    // is an AI (EU AI Act Art. 50; `assistant-chat-copy.test.ts`).
    kindLegend: "What do you need?",
    // The live assistant (`components/support/AssistantChatPanel.tsx`),
    // offered first where the server offers it.
    kindChat: "Chat with the Optivo assistant (AI)",
    kindChatShort: "Chat (AI)",
    kindChatHint: "Answers right away. It reads your property's settings and prices. It is an AI assistant: it can be wrong, and it changes nothing.",
    kindQuestion: "A question about my settings or prices",
    kindQuestionShort: "Question",
    kindQuestionHint: "We answer by e-mail within {hours} hours.",
    kindStudy: "A revenue study with recommended settings",
    kindStudyShort: "Study",
    // The allowance is the price list's (5 Oct 2026): one study a quarter is
    // in the price, and a further one is a price agreed first.
    // StrategyHelpRequestDialog.test.tsx holds the figure to the price list.
    kindStudyHint: "We analyse your bookings and prices, then e-mail you the study and a button that activates the settings we recommend. Nothing changes until you activate them. One study a quarter is included in your subscription; more are agreed individually. Within {hours} hours we let you know when to expect it.",
    messageQuestion: "Your question",
    messageStudy: "Anything we should know? (optional)",
    placeholderQuestion: "For example: why is Saturday priced below Friday?",
    placeholderStudy: "For example: our weekends sell out months ahead, but weekdays stay empty.",
    replyTo: "We reply to {email}.",
    replyToAccount: "We reply to your account's e-mail address.",
    // Colleagues the answer is copied to (`components/support/help-copies.ts`):
    // typed, pasted, or picked from the people who run the property.
    copyAdd: "Copy a colleague",
    copyLabel: "Copy to",
    copyPlaceholder: "Colleague's e-mail address",
    copyHint: "They get our answer too. Up to {count} addresses; press Enter after each.",
    copySuggestions: "Your colleagues in Optivo:",
    copySuggestionAdd: "Copy {email}",
    copyRemove: "Remove {email} from the copy",
    errorCopyInvalid: "\"{email}\" is not an e-mail address.",
    errorCopyOwn: "That is your own address — the answer comes to you anyway.",
    errorCopyTooMany: "At most {count} colleagues can be copied.",
    sentCopies: "Copied to {emails}.",
    send: "Send",
    sending: "Sending…",
    sentTitle: "Sent to Optivo support",
    sentQuestion: "We reply to {email} within {hours} hours.",
    sentStudy: "We'll e-mail the study to {email}.",
    failedTitle: "Your request was not sent",
    errorLimit: "This property has sent enough requests for today. For anything urgent, write to {email}.",
    errorNoEmail: "Your account has no e-mail address we could answer. Write to {email} instead.",
    errorMessageRequired: "Write your question first.",
    errorGeneric: "Try again in a moment, or write to {email}.",
    openStudy: "Revenue study requested on {date} — Optivo is preparing it.",
    openQuestion: "Question sent on {date} — Optivo replies by e-mail.",
    proposalTitle: "Optivo has proposed new settings for your strategy",
    reviewProposal: "Review the proposal",
    // Files sent with a request (`components/support/help-attachments.ts`).
    // The hint is the keyboard's own: ⌘⇧4 saves a file and shows a preview to
    // drag, Win+Shift+S puts the picture on the clipboard to paste.
    attachLabel: "Screenshots and files",
    attachHintMac: "A screenshot shows support exactly what you see: move this window aside, press ⌘⇧4, then drag the preview from the corner of the screen here.",
    attachHintOther: "A screenshot shows support exactly what you see: move this window aside, press Win+Shift+S, then paste the picture here with Ctrl+V.",
    attachHintTouch: "A screenshot shows support exactly what you see: minimise this window, take one, then attach it below.",
    attachLimits: "You can also attach a PDF, a video, a spreadsheet or a text file — up to {count} files, {size} in total.",
    attachButton: "Choose a file",
    removeAttachment: "Remove {name}",
    errorAttachTooMany: "At most {count} files can go with one request.",
    errorAttachTooLarge: "The files together are larger than {size}. Leave one out, or attach a smaller one.",
    errorAttachType: "{name} cannot be sent — attach a picture, a PDF, a video, a spreadsheet or a text file.",
    errorAttachEmpty: "{name} is empty.",
    errorAttachUnreadable: "{name} could not be read. Attach it again.",
    sentFilesUndelivered: "The attached files did not reach us, though — please send them to {email}.",
    // The window floats over the page instead of covering it (Hotel Zámek
    // Svijany, Oct 2026): it moves by its title bar, resizes by its edges and
    // minimises into the rail's Ask Optivo, so the Prices grid stays in reach
    // while a question is written and its screenshot taken.
    windowMove: "Move the window",
    windowMoveHint: "Drag the window by its title bar, or move it with the arrow keys.",
    windowMinimize: "Minimise",
    windowMinimizeHint: "Hide the window and keep what you have written — \"Ask Optivo\" brings it back.",
    windowResize: "Resize the window",
    windowResizeHint: "Drag any edge or corner of the window, or resize it with the arrow keys.",
    windowClose: "Close",
    windowMinimized: "Your request window is minimised.",
    windowRestoreHint: "Click to bring it back on screen.",
    windowMinimizedElsewhere: "Your request window is minimised. You will find it in the menu, under \"Ask Optivo\".",
    // The window itself could not be drawn (`AskOptivoProvider`). A request
    // cannot be sent from a window that is not there, which is where the
    // address is named.
    windowFailed: "The request window could not be shown",
    windowFailedHint: "Reload the page and try again, or write to {email}.",
  },

  // ── Ask Optivo: the live assistant ──────────────────────
  // The chat in the "Ask Optivo" window (`components/support/AssistantChatPanel.tsx`,
  // read through `assistant-chat-copy.ts`). Every turn the assistant writes is
  // labelled as written by an AI (EU AI Act Art. 50), and no string names a
  // number, a price or a property: those come from the answer itself.
  assistantChat: {
    assistantLabel: "Optivo assistant (AI)",
    youLabel: "You",
    transcriptLabel: "Conversation with the Optivo assistant (AI)",
    intro: "Hello, I am the Optivo assistant, an AI. I read your property's settings, prices and bookings (in totals, never a single guest) and explain them right away. I can be wrong, and I change nothing: where something should change, I tell you where to do it.",
    aiNotice: "You are writing to an AI assistant. It can be wrong, and it changes nothing.",
    // The same notice where the window is too narrow for a line of its own.
    aiNoticeShort: "AI assistant: it can be wrong.",
    composerLabel: "Your question to the assistant",
    composerPlaceholder: "For example: why is Saturday priced below Friday?",
    composerHint: "Do not write guests' names or contact details. The conversation is processed by Anthropic.",
    privacyLink: "Privacy",
    retention: "Conversations are kept for {days} days.",
    questionsLeft: "Questions left today: {count}.",
    send: "Send",
    stop: "Stop",
    newConversation: "New conversation",
    startNewConversation: "Start a new conversation",
    handOver: "Hand over to support",
    askSupportInstead: "Send it to support instead",
    suggestionsTitle: "You can ask, for example:",
    loading: "Loading the conversation…",
    loadFailed: "The conversation could not be loaded.",
    retry: "Try again",
    jumpToLatest: "Jump to latest",
    filesNotRead: "The assistant reads no files. To send a screenshot, choose the question to support above, or hand this conversation over to support.",
    // The one status line under an answer being written: what the assistant
    // is reading right now ({date} is a night, written out).
    statusThinking: "Working on the answer…",
    toolPropertyOverview: "Reading your property…",
    toolStrategySettings: "Reading your strategy…",
    toolStrategyChanges: "Comparing your strategy's versions…",
    toolPrices: "Reading the prices…",
    toolPricesFrom: "Reading the prices from {date}…",
    toolExplainNight: "Looking at the price of {date}…",
    toolCompareNights: "Comparing two nights…",
    toolNightHistory: "Reading the price history of {date}…",
    toolOccupancy: "Reading occupancy and booking pace…",
    toolOccupancyFrom: "Reading occupancy and booking pace from {date}…",
    toolOutlook: "Reading the outlook…",
    toolSync: "Checking the sync and the uploads to your PMS…",
    toolAnalysis: "Reading the property analysis…",
    toolMarket: "Reading the market…",
    toolMarketFrom: "Reading the market from {date}…",
    toolBookings: "Reading recent bookings…",
    toolDistribution: "Reading the linked rate plans…",
    toolLinkedPlansNight: "Reading the linked rate plans on {date}…",
    toolWhatIf: "Trying the change out…",
    toolDocs: "Looking it up in Optivo's documentation…",
    toolFindSetting: "Finding where that is set…",
    // How an answer ended, under it.
    stopped: "Stopped.",
    outcomeRefusal: "The assistant cannot help with this question. You can hand the conversation over to support.",
    outcomeMaxTokens: "The answer was cut short because it grew too long. Ask about the part you need.",
    outcomeIterationLimit: "The assistant ran out of steps before it finished. Ask a narrower question, or hand the conversation over to support.",
    outcomeTimeout: "The answer was cut short because it took too long.",
    outcomeInterrupted: "The answer was interrupted.",
    outcomeFailed: "The answer could not be written. Ask again, or hand the conversation over to support.",
    outcomeStillWriting: "This answer is still being written…",
    // An answer that broke off with an error.
    errorFailed: "The assistant could not finish the answer. Ask again, or hand the conversation over to support.",
    errorOverloaded: "The assistant is busy right now. Try again in a minute.",
    errorRateLimited: "Too many questions are being answered right now. Try again in a minute.",
    errorTimeout: "The answer took too long. Ask again, perhaps a narrower question.",
    errorRestarting: "Optivo is restarting. Ask again in a moment.",
    // A question refused before it was answered.
    errorUnavailable: "The assistant is not available right now. Send your question to support instead.",
    errorInvalid: "The question could not be sent. Keep it to {max} characters.",
    errorPersonalData: "Please leave out e-mail addresses, phone numbers and card or account numbers: the assistant does not take guests' personal data.",
    errorConversationGone: "This conversation is no longer available. Start a new one.",
    errorConversationClosed: "This conversation has ended. Start a new one to ask more.",
    errorConversationFull: "This conversation has reached its limit of questions. Start a new one to ask more.",
    errorBusy: "An answer is still being written. Ask again when it is done.",
    errorLimit: "You have used today's limit of questions to the assistant ({limit}). Ask again tomorrow, or send your question to support.",
    errorGeneric: "The question could not be sent. Try again in a moment, or write to {email}.",
    // Rating an answer up or down, as two icon buttons.
    feedbackUp: "Helpful answer",
    feedbackDown: "Not a helpful answer",
    feedbackFailed: "Your rating was not saved.",
    // Handing the conversation to support, confirmed inside the window.
    handOverTitle: "Hand this conversation to Optivo support?",
    handOverBody: "Support receives the whole conversation, the assistant's answers included, and replies by e-mail to {email} within {hours} hours.",
    handOverBodyAccount: "Support receives the whole conversation, the assistant's answers included, and replies to your account's e-mail address within {hours} hours.",
    handOverNoteLabel: "Anything to add? (optional)",
    handOverConfirm: "Hand over",
    handingOver: "Handing over…",
    handOverCancel: "Cancel",
    handOverFailedTitle: "The conversation was not handed over",
    handedOver: "Handed to support as {case}. We reply by e-mail to {email} within {hours} hours.",
    handedOverAccount: "Handed to support as {case}. We reply to your account's e-mail address within {hours} hours.",
    handedOverEarlier: "This conversation is with Optivo support as {case}. We reply by e-mail.",
    errorHandOverEmpty: "There is no answered question to hand over yet.",
    errorHandOverAlready: "This conversation is already with support.",
    errorHandOverBusy: "Wait until the answer is finished, then hand the conversation over.",
    // The rail entry, when an answer finished while the window was not open.
    answered: "The Optivo assistant (AI) answered",
    answeredHint: "Click to read the answer.",
    // Questions offered on an empty conversation, by the page it is opened on.
    suggestPrices1: "Why is next Saturday priced the way it is?",
    suggestPrices2: "Which prices have not been sent to my PMS yet?",
    suggestPrices3: "Why did a price change since yesterday?",
    suggestStrategy1: "What does this section change in my prices?",
    suggestStrategy2: "What did my last strategy change do?",
    suggestStrategy3: "Which settings did I set myself, and which come from the analysis?",
    suggestDashboard1: "How is this month going compared with last year?",
    suggestDashboard2: "Why is tonight priced the way it is?",
    suggestDashboard3: "Is the sync with my PMS working?",
    suggestAnalytics1: "What do these numbers mean for my prices?",
    suggestAnalytics2: "How are bookings for next month coming in?",
    suggestAnalytics3: "How does the market affect my prices?",
    suggestConnections1: "Which prices does the auto-sync send, and how often?",
    suggestConnections2: "When were prices last sent to my PMS?",
    suggestConnections3: "Why was an upload refused?",
    suggestGeneral1: "How does Optivo set my prices?",
    suggestGeneral2: "Why is tonight priced the way it is?",
    suggestGeneral3: "Which settings matter most for my prices?",
  },

  // ── Strategy proposal ───────────────────────────────────
  // The page a proposal's e-mail links (`/strategies/proposals/:id`,
  // `components/settings/StrategyProposalReview.tsx`): every setting it would
  // move, from what to what, and the one click that activates it as a new
  // strategy version. Section names are the editor's own (`strategyParams`).
  strategyProposal: {
    title: "Proposed strategy settings",
    subtitle: "From Optivo support, {date}",
    back: "Back to strategies",
    loading: "Loading the proposal…",
    notFound: "There is no proposal at this address. It may belong to an account you are not a member of.",
    loadFailed: "The proposal could not be loaded.",
    retry: "Try again",
    otherProperty: "This proposal is for {property}, not for the property you have open.",
    switchProperty: "Switch to {property}",
    summaryTitle: "In brief",
    downloadStudy: "The full study, with all its detail (PDF)",
    changesTitle: "What changes",
    changesAgainst: "Compared with {strategy} (version {version}), which prices your property now.",
    changeCount: { one: "{count} setting changes", few: "{count} settings change", other: "{count} settings change" },
    columnSetting: "Setting",
    columnNow: "Now",
    columnProposed: "Proposed",
    otherSettings: "Other settings",
    valueOn: "On",
    valueOff: "Off",
    valueUnset: "not set",
    valueFollowsProperty: "Same as the property",
    // A room type that follows the property, where the page knows the property's own value.
    valueFollowsPropertyAs: "Same as the property ({value})",
    // A special date only one side of the proposal holds.
    specialDateNotListed: "not in the list",
    specialDateRemoved: "removed from the list",
    // A setting's name where the strategy editor's own label is abbreviated
    // for a dense grid or names no setting at all; every other row reads the
    // editor's label (`proposal-changes.ts`), so the operator finds it there.
    fields: {
      occupancyScaleMax: "Maximum occupancy premium",
      paceLower: "Pace floor",
      paceUpper: "Pace ceiling",
      occupancyDamping: "How steadily the price reacts",
      propertyOccupancyWeight: "Property occupancy weight",
      minPricePct: "Default minimum price",
      maxPricePct: "Default maximum price",
      bookingWindowTypical: "Typical booking window",
      bookingWindowAverage: "Average booking window",
      bookingWindowHigh: "Booking window in high season",
      bookingWindowLow: "Booking window in low season",
      peakNightBookingWindow: "Booking window for weekend and holiday nights",
      leadTimeCurveHoldDays: "Hold around the demand peak",
      bridgeDayAdjustment: "Bridge-day adjustment",
      seasonOfMonth: "Season in {month}",
      specialDate: "Special date {n}",
      maxGuests: "Maximum guests",
      lastMinuteStep: "Discount at step {n}",
    },
    days: { one: "{count} day", few: "{count} days", other: "{count} days" },
    nights: { one: "{count} night", few: "{count} nights", other: "{count} nights" },
    rooms: { one: "{count} room", few: "{count} rooms", other: "{count} rooms" },
    guests: { one: "{count} guest", few: "{count} guests", other: "{count} guests" },
    truncated: {
      one: "{count} more change is not listed.",
      few: "{count} more changes are not listed.",
      other: "{count} more changes are not listed.",
    },
    noChanges: "Your strategy already has every one of these settings, so there is nothing to activate.",
    noActiveStrategy: "Your property has no active strategy to apply these settings to. Write to {email}.",
    activate: "Activate these settings",
    activating: "Activating…",
    decline: "Decline",
    declining: "Declining…",
    activateTitle: "Activate the proposed settings?",
    activateBody: "They are saved as a new version of {strategy} and your prices are recalculated with them. Version {version} is kept, so you can switch back to it from the strategy list at any time.",
    declineTitle: "Decline this proposal?",
    declineBody: "Your strategy stays as it is. Optivo support sees that you declined the proposal.",
    activatedToast: "Proposed settings activated",
    activatedToastDesc: "Version {version} is pricing your property now. Version {previous} is kept.",
    declinedToast: "Proposal declined",
    statusAccepted: "You activated this proposal on {date}.",
    statusDeclined: "This proposal was declined on {date}.",
    statusSuperseded: "A newer proposal replaced this one.",
    openStrategy: "Open the strategy",
    errorDecided: "This proposal was decided in the meantime.",
    errorOtherProperty: "Switch to the proposal's property first.",
    errorSpecialDate: "A special date in the proposal cannot be read. Write to {email}.",
    errorGeneric: "The proposal could not be activated. Try again, or write to {email}.",
    // A proposal's steps: why each is taken and what it aims for, with the
    // settings it moves or where the operator does it by hand.
    stepsTitle: "Recommended steps",
    stepWhy: "Why",
    stepAim: "What it aims for",
    stepByHand: "You do this yourself",
    stepWhere: "Where to find it",
    stepSection: "In the settings: {section}",
    stepAlreadySet: "Your strategy already has this setting.",
    otherChangesTitle: "Other changes",
    activateStepsTitle: "What you activate, and why",
    activateStepByHand: "You do this yourself after activating: {where}",
    remainingTitle: "Now left for you to do:",
    // The follow-up study offered with a proposal, and where it stands.
    followUpTitle: "Follow-up study",
    followUpOffered: "About {days} after you activate them, we come back to these settings in a follow-up study and check they do what they should. You only confirm it when activating.",
    followUpScheduled: "We will prepare the follow-up study around {date}.",
    followUpOpened: "We are preparing the follow-up study now.",
    followUpDelivered: "We sent you the follow-up study.",
    followUpCancelled: "The follow-up study will no longer be prepared.",
    followUpCancelledByNewer: "Activating a newer proposal cancelled the follow-up study.",
    followUpDeclined: "You did not want a follow-up study when activating.",
    followUpChoice: "Yes, I want a follow-up study in about {days} (around {date}).",
    followUpChoiceHint: "In it we check that the settings do what they should.",
  },

  // ── Study archive ───────────────────────────────────────
  // What Optivo sent the open property — revenue studies, recommended settings
  // and answers — kept in the analytics section: the list and one answer open
  // for reading. The letter itself is shown in the language it was written in.
  studyArchive: {
    title: "Study Archive",
    subtitle: "Studies, recommended settings and answers from the Optivo team",
    refresh: "Refresh",
    loading: "Loading the archive…",
    loadFailed: "The archive could not be loaded.",
    retry: "Try again",
    empty: "Optivo has not sent you a study or an answer yet.",
    emptyHint: "Ask for a revenue study, or ask a question, with “Ask Optivo” in the menu on the left. The answer will be kept here.",
    waitingQuestion: "We are preparing the answer to your question {case} of {date}.",
    waitingStudy: "We are preparing the revenue study you asked for on {date} ({case}).",
    waitingFollowUp: "We are preparing the follow-up study you confirmed when activating on {date} ({case}).",
    kind: {
      study: "Revenue study",
      settings: "Recommended settings",
      answer: "Answer",
    },
    status: {
      pending: "Waiting for your decision",
      accepted: "Activated",
      declined: "Declined",
      superseded: "Replaced by a newer proposal",
    },
    untitled: {
      study: "Revenue study",
      settings: "Recommended settings",
      answer: "Answer from Optivo",
    },
    asked: "You asked: {text}",
    studyFile: "PDF",
    back: "Back to the archive",
    entryLoading: "Loading…",
    entryNotFound: "There is no study or answer at this address. It may belong to an account you are not a member of.",
    entryLoadFailed: "This could not be loaded.",
    otherProperty: "This is for {property}, not for the property you have open.",
    sentOn: "Sent on {date}",
    preparedOn: "Prepared on {date}",
    recipient: "To {email}",
    recipientWithCopies: "To {email}, copy to {copies}",
    summaryTitle: "In short",
    letterTitle: "The letter",
    figure: "Figure {n}",
    figureUnnumbered: "Figure",
    downloadStudy: "Download the study (PDF)",
    pendingTitle: "The recommended settings are ready in Optivo",
    pendingBody: "One page shows every setting that would change, and one click activates them. Your current strategy version is kept, so you can switch back at any time.",
    review: "Review and activate the settings",
    accepted: "Activated on {date}.",
    acceptedBy: "Activated on {date} ({email}).",
    declined: "Declined on {date}.",
    declinedBy: "Declined on {date} ({email}).",
    superseded: "A newer proposal replaced this one.",
    viewProposal: "Open the proposal",
  },

  // ── The question an answer answers ──────────────────────
  // What the operator asked, said back beside Optivo's answer: on the review
  // page of a proposal and in the study archive (`components/support/
  // AskedQuestion.tsx`, read by the component itself, so the two pages say one
  // thing). It is the page's side of the question the answer's e-mail quotes
  // under its signature. A study or a setup can be asked for without a word,
  // and then a sentence says what was asked for. So can a conversation with
  // the assistant be handed over without a note: `handedOver` says that, in
  // the words the e-mail credits it with, and never the line the case stores
  // in a note's place, which is Optivo's own.
  askedQuestion: {
    title: {
      question: "What you asked",
      study: "What you asked for",
      setup: "What you asked for",
    },
    study: "You asked for a revenue study.",
    setup: "You asked Optivo to set the property up.",
    handedOver: "You handed a conversation with the Optivo assistant (AI) over to support.",
    followUp: "You confirmed a follow-up study when activating the recommendations.",
    followUpCase: "You confirmed a follow-up study when activating the recommendations ({case}).",
    by: "{email}, {date}",
  },

  // ── Price list currency ─────────────────────────────────
  // Which currency the PMS keeps the property's price list in, said where the
  // property currency is chosen: the Settings card, the new-connection form,
  // the wizard's PMS step and the admin console. Top level, read by the note
  // and the dialog themselves, so the four surfaces say one thing. No sentence
  // names a mechanism: Previo keeps a price per currency and Better Hotel
  // takes a bare number, and either way Optivo stops the send.
  priceListCurrency: {
    reading: "Checking which currency {pms} keeps this property's price list in.",
    keptIn: "{pms} keeps this property's price list in {listCurrency}.",
    keptInSeveral: "{pms} keeps this property's price list in more than one currency: {groups}.",
    group: "{roomTypes} in {listCurrency}",
    groupSeparator: "; ",
    differs:
      "Prices calculated in {pricedIn} cannot go into a price list kept in {listCurrency}, so Optivo would stop every send until the two currencies match.",
    differsSeveral:
      "Whichever currency is chosen here, Optivo stops every send until {pms} keeps the price list of every room type in the property currency.",
    unsupported:
      "Optivo cannot calculate prices in {listCurrency}. To send prices, change the price list currency in {pms} to CZK, EUR or USD.",
    unsaid: "{pms} did not say which currency it keeps this property's price list in.",
    unreadable:
      "Optivo could not ask {pms} which currency it keeps this property's price list in. Saving is not affected, and every send checks again.",
    use: "Use {listCurrency}",
    dialogTitle: "{pms} keeps this price list in {listCurrency}",
    dialogTitleSeveral: "{pms} keeps this price list in more than one currency",
    dialogDescription:
      "This property is about to be saved in {pricedIn}. Prices calculated in {pricedIn} cannot go into a price list kept in {listCurrency}, so Optivo would stop every send until the two currencies match.",
    dialogUse: "Save in {listCurrency}",
    dialogKeep: "Save in {pricedIn}",
    dialogCancel: "Cancel",
    adopted: "The property currency is {listCurrency}, the currency {pms} keeps the price list in.",
    confirmDiffers:
      "{pms} keeps this property's price list in {listCurrency}, so Optivo would stop every send while the property is priced in {to}.",
    confirmSeveral:
      "{pms} keeps this property's price list in more than one currency, so Optivo stops every send whichever currency is chosen.",
  },

  // ── Competitor table ────────────────────────────────────
  // The comp set table's own words, read inside the table through
  // `compsetTableCopy` rather than from each screen's label bag. The strategy
  // editor, the wizard and Market Conditions draw one table, and the two copies
  // its words used to live in had already given one column two Czech names.
  compsetTable: {
    inSet: "In the set",
    property: "Property",
    trend: "Price trend",
    rating: "Rating",
    reason: "Why",
    rate: "Rate",
    change: "Since last capture",
    typicalRate: "Typical rate",
    reviews: "Reviews",
    groupIncluded: "In the set ({count})",
    groupExcluded: "Left out ({count})",
    checkboxLabel: "Count {name} in the set",
    // The set kept in the operator's own Lighthouse account: membership is decided there.
    lighthouseSet: "This set is kept in your Lighthouse account.",
    // A competitor whose source states it sold out that night; others read as unpriced.
    soldOut: "Sold out",
    trendRising: "Rising",
    trendFalling: "Falling",
    trendMixed: "Up and down",
    trendStable: "Stable",
    trendUnknown: "Too little data",
    trendNights: "{moved} of {paired} nights",
    trendHint:
      "A trend compares each night's newest capture with one at least 3 days older. A competitor is rising or falling once at least a quarter of its nights moved by 2% or more; smaller moves are rounding, not a price decision.",
    reasonOwn: "Your property",
    reasonManual: "Left out by you",
    reasonPinOff: "Pinned, not counted",
    reasonType: "Property type",
    reasonBand: "Outside the price band",
    reasonRating: "Rating too low",
    reasonReviews: "Too few reviews",
    keptIncluded: "Added by you",
    keptPin: "Pinned competitor",
    counter: "{selected} of {total} properties in the set, {priced} with a price",
    coverage:
      "The set has at least {floor} prices on {covered} of {captured}. Market influence and following the market act only on those nights.",
    coverageNone:
      "The set has fewer than {floor} prices on every one of {captured}, so competitors do not move your prices. Tick more properties or widen the rules.",
    capturedNights: { one: "{count} captured night", few: "{count} captured nights", other: "{count} captured nights" },
    coverageNoCaptures: "No nights captured yet, so competitors do not move your prices.",
    coverageRecalculating: "Recalculating…",
    reset: "Use the whole market",
    saving: "Saving…",
    saved: "Saved — used from the next price calculation.",
    saveFailed: "Could not save — your last change was undone.",
    retry: "Try again",
    propertyTypes: "Property types",
    band: "Typical price band",
    bandFrom: "From",
    bandTo: "To",
    bandHint:
      "Compares each property's typical nightly price — the median of its captured nights. A property inside the band counts on every night, including one it prices outside it.",
    spread:
      "Properties that meet the other rules typically sell at {p25}–{p75} {currency} a night for two adults, median {median} {currency}.",
    spreadTooFew: "Too few priced properties yet to describe a typical price.",
    minRating: "Minimum rating",
    minReviews: "Minimum reviews",
    pinned: "Pinned",
    unpin: "Remove the pin: {name}",
    unpinHint: "Remove the pin",
    pinNotCaptured: "Not captured yet.",
    pinNotFound:
      "Not found in its own search results — searches are being spent on it with nothing to show. Remove it and pin it again with a more specific name.",
    pinNoUpcoming: "No upcoming night captured (last {date}).",
    pinNoPrice: "This hotel is listed without a price, so it adds nothing to the median.",
    unpriced: "No price shown",
    noPrevious: "First capture",
    ownRow: "Your property — never counted in the market, shown for comparison.",
    ownUnmark: "Not mine",
    ownSuggestion: "“{name}” looks like your own property. Your own listing should not count as your market.",
    ownMark: "Mark as mine",
    markOwn: "Mark as your property: {name}",
    markOwnHint: "This is my property",
    addOpen: "Add a competitor outside the market",
    addTitle: "Competitors outside your market",
    // What a pinned competitor takes, said before any search is spent. It was
    // "extra" while competitor tracking was a paid add-on; it comes with the
    // subscription since the price list of 5 Oct 2026, so what a pin spends
    // is the property's monthly searches. {monthly} and {limit} are the
    // server's own figures.
    addAllowanceNote:
      "A competitor you name is read on its own: one search covers one night of that hotel alone, while a search of your market returns every nearby property. Each one takes about {monthly} searches a month out of the same monthly allowance. Pinned competitors included in your subscription: {limit}. More are agreed individually.",
    addSearchPlaceholder: "Hotel name…",
    addSearch: "Search",
    addSearching: "Searching…",
    addSearchCost: "Each search spends one of your monthly searches, so this runs when you press the button — not as you type.",
    addNoResults: "Nothing matched that name.",
    addPin: "Pin",
    addLimitReached: "You have reached the number of pinned competitors included in your subscription. Remove one to add another. More are agreed individually: write to us through Ask Optivo.",
    addClose: "Close",
    addPinnedHint:
      "A pinned hotel joins this table switched off: its trend shows at once, and it enters the median only once you tick it.",
    empty: "Nothing captured yet.",
    pageInfo: "Showing {from}–{to} of {total}",
    sortAscending: "Sort ascending",
    sortDescending: "Sort descending",
    pagination: "Competitor pages",
    previousPage: "Previous page",
    nextPage: "Next page",
    showGroup: "Show",
    hideGroup: "Hide",
  },

  // ── Welcome / Landing Page ──────────────────────────────
  // The public page speaks for the product to somebody who has never used it,
  // so it claims only what the product does (WelcomePage.test.tsx → "claims"):
  // no AI in the calendar, no second sign-in factor, no round-the-clock
  // support, no social sign-in, and never where the market's prices are read.
  welcome: {
    logIn: "Log in",
    signUpFree: "Sign up free",
    signUp: "Sign up",
    badge: "Built by hoteliers, for hoteliers",
    // The headline is a question in two halves; the second is the emphasis and
    // is short enough to stay on one line of the hero column.
    heroTitle1: "Running a hotel,",
    heroTitle2: "or retyping rates?",
    heroTagline: "Optivo. Smarter pricing for your property.",
    heroDescription:
      "We have been there. We run a hotel, apartments and a campsite ourselves, and adjusted our rates by hand for years. Optivo does it for you: it watches demand, competitors and the weather, and explains every price it sets.",
    ctaCreate: "Create free account",
    ctaLogIn: "Log in to your account",
    resetPassword: "Reset password",
    ctaSubtext: "No credit card required. Connect your property management system after signing up.",
    valuePropTitle: "Too cheap, or too expensive? You no longer have to guess.",
    valuePropDescription:
      "Optivo helps you set prices from data and current demand. For hotels, guesthouses, apartments and campsites that want to be sure of their prices.",
    // The features with a sketch of their own (welcome/FeatureBento.tsx).
    featureDynamicTitle: "Dynamic rate adjustment",
    featureDynamicDesc:
      "We adjust your rates automatically to demand, booking pace, past performance and market pressure, to maximise your revenue.",
    featurePmsTitle: "Integration with your property management system",
    featurePmsDesc:
      "Connect the PMS you already use in a few steps. We pull your room types, booking history and rate plans ourselves.",
    featureMonitorTitle: "Rates adjusted as often as every 30 minutes",
    // The cadence is the price list's ceiling, the same for both products
    // since 5 Oct 2026: nobody chooses a plan, so nothing depends on one.
    // WelcomePage.test.tsx holds the figure to the price list.
    featureMonitorDesc:
      "We watch new bookings, your demand and the market, and adjust your prices to match: up to 24 times a day.",
    featureCompetitorsTitle: "We see what your competitors are doing",
    featureCompetitorsDesc:
      "We follow accommodation prices around you night by night. When the market moves, your prices respond, within the limits you set.",
    featureWeatherTitle: "We react to the weather",
    featureWeatherDesc:
      "A sunny weekend fills rooms differently from a wet one. We read the forecast for you and let it move your prices.",
    featureHolidaysTitle: "We track holidays for you",
    featureHolidaysDesc:
      "When is the next public holiday in Germany? We find the public holidays where you are and where your guests come from, and put them in your pricing calendar ourselves.",
    featureRatePlansTitle: "Dynamic where you don't expect it",
    featureRatePlansDesc:
      "Demand drives your linked rate plans too, each on its own: the surcharge on a sales channel and the discount on a non-refundable rate, wherever your PMS has rate plans.",
    featureMinStayTitle: "Minimum stay for each room type",
    featureMinStayDesc:
      "We manage the minimum stay as dynamically as the price, for each room type and each night. When the following night sells out, the minimum relaxes on its own.",
    // The rest of what Optivo does, as a ruled list under the sketches.
    featureMoreLabel: "And also",
    featureHorizonTitle: "Prices up to 18 months ahead",
    featureHorizonDesc:
      "We price up to {nights} nights ahead and send your PMS only the prices that changed, so it is never flooded.",
    featureHistoryTitle: "Price and strategy history",
    featureHistoryDesc:
      "See how each night's price developed, and go back to an earlier version of your strategy or to the prices before an upload.",
    featureControlTitle: "Full control",
    featureControlDesc:
      "Every price explains itself. You set the lowest and the highest price, and you can lock any night at a price of your own.",
    featureSupportTitle: "Support in Czech and English",
    // {hours} is the reply promise the app itself makes (support-contact.ts).
    featureSupportDesc:
      "Optivo and the people behind it speak Czech and English. Ask straight from the app and we answer within {hours} hours.",
    featureSecurityTitle: "Your data is safe",
    featureSecurityDesc:
      "Your PMS credentials are stored encrypted. We never store your guests' names or contact details.",
    featureSmallTitle: "Built for small properties too",
    featureSmallDesc:
      "Whether you manage a boutique hotel, an apartment or a whole portfolio, Optivo fits your scale. Built by hoteliers, for hoteliers.",
    howItWorksTitle: "Enough guessing. Time to optivise.",
    step1Title: "Sign up",
    step1Desc:
      "Create your account with your e-mail. New accounts can get 30 days free, and no credit card is needed.",
    step2Title: "Connect your PMS",
    step2Desc:
      "Enter the credentials of your property management system and Optivo reads your rooms, rates and reservations.",
    step3Title: "Optivise your prices",
    step3Desc:
      "Check the prices in the pricing calendar, switch on automatic sending and let Optivo help you earn more from every booking.",
    // Who we are: the founders' story, told by one of them.
    aboutTitle: "“About two thousand, I guess” is not a pricing strategy.",
    aboutStory: [
      "We run a hotel, apartments and a campsite. We have used dynamic pricing for more than ten years and we know it is a competitive advantage. The right price, at the right time, for the right guest: a strategy that delivers results and sustains long-term growth.",
      "We used to set prices by a mix of estimates, experience, past data and the market's reaction. A routine every owner and revenue manager knows. The nervous look at the hotel system on a phone: has the last room sold, or does it need a discount?",
      "Optivo began with a simple question from our son: why do I, the owner, adjust the prices, and not a computer? I trusted no software as much as my own intuition, my experience and an owner's sense of responsibility. So we built Optivo. It was meant for our own group alone, for a few friends at most. Later we understood that we were not the only ones asking this much of pricing, and Optivo became a real revenue management system.",
      "That is why we know how much the right pricing and a long-term strategy matter. Guessing prices is not a strategy. Setting a greedy price and then discounting dramatically is not a strategy. Holding the same prices for years, afraid to raise them while every cost has gone up, is not one either.",
      "We lived through all of it in our own properties. You are not alone in this.",
    ],
    aboutClosing: "Built by hoteliers, for hoteliers.",
    aboutRole: "co-founder of Optivo and hotelier",
    // The one thing the page says about who uses Optivo (welcome/
    // PricedRoomsSection.tsx): a count read from the database, drawn beside
    // this label. The label reads the same after any number, and nothing here
    // names a property, a partner or a property management system.
    pricedRoomsEyebrow: "Optivo at work",
    pricedRoomsTitle: "Rooms Optivo is pricing right now",
    pricedRoomsDescription:
      "A live figure from our own system: the rooms and apartments of the hotels, guesthouses and rentals Optivo calculates prices for every day.",
    // What it costs (welcome/PricingSection.tsx): the section's own four
    // sentences. Every price, and every line about what a price includes, is
    // the price list's (`legal/priceListCopy.ts`), so none of these states an
    // amount. The trial's length is the one figure, held to the price list's
    // document by WelcomePage.test.tsx.
    pricingTitle: "One price for a hotel, one for a guesthouse or a rental. Every feature is in both.",
    pricingDescription:
      "You pay for the property and a little for each room, and there is no plan to choose: the price follows what the property is.",
    pricingTrial:
      "New accounts can get 30 days free, no card needed. Nothing is charged when the trial ends: the subscription starts when you pay for it.",
    pricingFullList: "Full price list and subscription terms",
    // The closing band: the way in and the price list, side by side.
    closingTitle: "How many more times will you open your price list today?",
    closingDescription: "New accounts can try Optivo free for 30 days. No credit card required.",
    footerCopyright: "\u00a9 {year} Optivo",
    footerPricing: "Pricing",
    footerPayment: "Payment and delivery",
    footerTerms: "Terms",
    footerContact: "Contact",
    footerPrivacy: "Privacy",
    footerComplaints: "Complaints",
    footerCompanyId: "Company ID",
    footerPaymentsLabel: "We accept",
    // Header navigation: the sections in the order the page runs, the prices
    // last. The footer keeps the documents' own names (`footerPricing`).
    navLabel: "Main navigation",
    // The button the section links fold into where the bar has no room for them.
    navMenu: "Menu",
    navAbout: "Who we are",
    navFeatures: "What we do",
    navHowItWorks: "How it works",
    navPricing: "Pricing",
    // The hero's product preview: an example week, labelled as one, whose
    // explanation adds up to the price it explains (welcome/pricePreviewModel.ts).
    previewTitle: "Prices for the week ahead",
    previewExample: "Example",
    previewGridLabel: "Example prices by room type and night, in {currency}",
    previewRoomColumn: "Room type",
    previewRooms: {
      double: "Double",
      family: "Family",
      suite: "Suite",
    },
    previewHint: "Select a night to see why it costs what it does.",
    previewWhy: "Why {price}?",
    previewBase: "Base price",
    previewDemand: "Demand: {level}",
    // What the forecast and the competitors' prices say about a night; the
    // plain form names the line on a night they leave alone.
    previewWeather: {
      sunny: "Weather: sunny",
      neutral: "Weather",
      rainy: "Weather: rain",
    },
    previewCompetitors: {
      cheaper: "Competitors: cheaper",
      level: "Competitors",
      pricier: "Competitors: pricier",
    },
    // The price Optivo arrives at. It names no PMS: a visitor may not know the
    // abbreviation, and the line is about the price, not where it is sent.
    previewFinal: "Optivo price",
    // The dynamic-pricing tile: one night's price over the weeks it sold.
    priceChartCaption: "One night's price as it sold",
    priceChartPrice: "Price",
    priceChartBooking: "Booking",
    priceChartStart: "{days} days before arrival",
    priceChartArrival: "Arrival",
    // The other tiles' sketches. Each is a worked example in the product's own
    // words; these name what the product has no key for.
    marketSketchMedian: "Market median",
    marketSketchYours: "Your price",
    weatherSketchLabel: "Example forecast for the week ahead",
    holidaySketchLabel: "The next public holidays",
    ratePlanSketchLabel: "Example prices of linked rate plans",
    ratePlanBase: "Base plan",
    ratePlanChannel: "Sales channel",
    ratePlanNonRefundable: "Non-refundable rate",
    minStaySketchLabel: "Minimum stay, nights",
  },

  passwordReset: {
    requestTitle: "Reset password",
    requestDescription: "Enter the email address you use for Optivo and we will send you a password reset link.",
    emailLabel: "Email address",
    emailPlaceholder: "name@example.com",
    sendLink: "Send reset link",
    requestSuccessTitle: "Check your email",
    requestSuccessMessage: "If that account exists, a password reset link is on its way.",
    newPasswordTitle: "Choose a new password",
    newPasswordDescription: "Set a new password for your Optivo account.",
    newPasswordLabel: "New password",
    confirmPasswordLabel: "Confirm new password",
    passwordPlaceholder: "Enter a new password",
    confirmPasswordPlaceholder: "Repeat the new password",
    savePassword: "Save new password",
    successTitle: "Password updated",
    successMessage: "Your password has been updated. You can now sign in with the new password.",
    signInCta: "Back to sign in",
    mismatch: "Passwords do not match.",
    invalidToken: "This password reset link is invalid or has expired.",
    genericError: "Unable to reset password right now.",
  },

  // ── Sidebar ─────────────────────────────────────────────
  sidebar: {
    title: "Optivo",
    titlePrevioLine1: "Previo",
    titlePrevioLine2: "Optivo RMS",
    titleBetterHotelLine1: "Better Hotel",
    titleBetterHotelLine2: "Optivo RMS",
    dashboard: "Dashboard",
    performance: "Performance",
    strategies: "Strategies",
    drafts: "Prices",
    settings: "Property Manager Settings",
    administration: "Administration",
    userManagement: "Users",
    priceDraftsAdmin: "Price Plans",
    wirePaymentsAdmin: "Bank Transfers",
    connectionsAdmin: "PMS Connections",
    activityAdmin: "Activity Log",
    strategyHelpAdmin: "Strategy Help",
    supportRemindersAdmin: "Support Reminders",
    featuresAdmin: "Features",
    resetPassword: "Reset password",
    signOut: "Sign out",
    analysis: "Analysis",
    recentBookings: "Recent Bookings",
    forecast: "Revenue Forecast",
    demandForecast: "Demand Forecast",
    segmentChannel: "Channels & Segments",
    marketConditions: "Market Conditions",
    studyArchive: "Study Archive",
    setup: "Setup Wizard",
    reviewSetup: "Review Setup",
    sectionAnalytics: "Analytics",
    sectionPricing: "Pricing",
    expand: "Expand sidebar",
    collapse: "Collapse sidebar",
    // The X in the header of the phone's menu, where the rail is closed and
    // not folded. The rail's own word and not `common.dismiss`: "Dismiss" is
    // said of a notice, which states beside it what goes, and a control's name
    // is also read out with nothing around it. Read by the rail and by what
    // stands in for it (`RailUnavailable`), so the control goes by one name
    // in both.
    closeMenu: "Close menu",
    // The button in the page's top bar that opens that menu (`App`), below
    // `md`, where the rail is not beside the page. The pair of `closeMenu`.
    openMenu: "Open menu",
    // What the menu is called to a screen reader, which reads it out with the
    // word "dialog" as the menu opens (`RailFrame`). Not shown on screen. The
    // word its two controls already use for it.
    menu: "Menu",
    // Read by a screen reader alone with the folded rail's Prices icon, whose
    // flyout the right arrow opens.
    pricesFlyoutHint: "Press the right arrow key to open its tabs and calendar views.",
    // The folded rail's one button for the footer's controls.
    moreControls: "More options",
    // What stands in the rail's place when the rail itself cannot be drawn
    // (`RailUnavailable`). The page beside it stays, so this says what is
    // missing and what brings it back; the button under it is the page's own
    // "Reload" (`app.pageLoadFailedReload`). The title is read in a 16rem rail.
    unavailableTitle: "The menu could not be shown",
    unavailableHint: "Reload the page to bring it back.",
  },

  // ── Command palette ─────────────────────────────────────
  // The quick jump to a page, a Prices tab or a calendar view (⌘K / Ctrl+K).
  commandPalette: {
    title: "Go to",
    description: "Find a page, a tab or a calendar view and open it.",
    placeholder: "Search pages, tabs and views…",
    empty: "Nothing matches.",
    open: "Go to…",
    groupPages: "Pages",
    // The name of the list of matches, for a screen reader.
    results: "Results",
  },

  // ── Theme (light / dark) ────────────────────────────────
  theme: {
    switchToDark: "Switch to dark mode",
    switchToSystem: "Match my device",
    pinLight: "Always use light mode",
    matchingSystem: "Matching your device — currently {mode}.",
    light: "Light",
    dark: "Dark",
  },

  // ── Dashboard ───────────────────────────────────────────
  dashboard: {
    title: "Dashboard",
    subtitle: "Jump straight into the area you need.",
    sectionAnalytics: "Analytics",
    sectionPricing: "Pricing",
    sectionSetup: "Setup",
    performanceTitle: "Performance",
    performanceDesc: "Review revenue, occupancy, and average price trends across past stays.",
    forecastTitle: "Forecast",
    forecastDesc: "See booking pace and projected occupancy for upcoming dates.",
    demandForecastTitle: "Demand Forecast",
    demandForecastDesc: "Understand future demand pressure across your calendar.",
    segmentChannelTitle: "Channels & Segments",
    segmentChannelDesc: "Compare bookings by distribution channel and guest segment.",
    recentBookingsTitle: "Recent Bookings",
    recentBookingsDesc: "Inspect the latest reservations flowing in from your PMS.",
    marketConditionsTitle: "Market Conditions",
    marketConditionsDesc:
      "See how nearby competitors are positioned and what is driving demand for each night.",
    studyArchiveTitle: "Study Archive",
    studyArchiveDesc: "Read the revenue studies, recommended settings and answers the Optivo team has sent you.",
    pricingCalendarTitle: "Reservations",
    pricingCalendarDesc: "Inspect current and suggested nightly rates by room and date.",
    strategiesTitle: "Strategies",
    strategiesDesc: "Tune the rules and posture that drive Optivo's pricing.",
    draftsTitle: "Prices",
    draftsDesc: "Review pending price drafts before they are sent to your PMS.",
    connectionsTitle: "Manage Connections",
    connectionsDesc: "Add, switch, or remove the PMS connections feeding Optivo.",
    open: "Open",
    snapshotSection: "In numbers",
    performanceSnapshotTitle: "30-day performance",
    performanceSnapshotHint: "On-the-books vs average nightly price across the next four weeks.",
    performanceSnapshotEmpty: "No bookings or rates in the selected window yet.",
    performanceSnapshotLegendOccupancy: "Occupancy (OTB)",
    performanceSnapshotLegendAdr: "Avg nightly price",
    performanceSnapshotWeekend: "Weekend",
    performanceSnapshotToday: "Today",
    stlySnapshotTitle: "Same time last year",
    stlySnapshotHint: "How this week compares to the matching week one year ago.",
    stlySnapshotEmpty: "Not enough prior-year data to compare.",
    stlyMetricAdr: "Avg. Price",
    stlyMetricOcc: "Occupancy",
    stlyMetricPickup: "Bookings created (this week)",
    stlyMetricRevenue: "Revenue (this week)",
    stlyVsLastYear: "vs last year",
    stlyLastYearLabel: "LY",
    // The outlook: three nights and two months said as moods rather than as
    // numbers; the rooms a night still has free are its one count. Every word
    // here answers one of the server's enums (`services/outlookApi.ts`), so a
    // new mood needs its word in both files.
    outlook: {
      statusTitle: "Status",
      nightsTitle: "Next three nights",
      nightsHint: "What is left to sell, how the market looks, and what Optivo did with the prices of the free rooms over the last 24 hours.",
      monthsTitle: "This month and next",
      monthsHint: "The nights and revenue already on your books, against the same day last year.",
      loadError: "The outlook could not be loaded.",
      manualSending: "Prices are not sent to your PMS automatically. The outlook describes Optivo's plan; it reaches your guests once you send it.",
      staleCalculation: "These prices have not been recalculated for more than a day.",
      openPrices: "Open prices",
      openForecast: "Open forecast",
      columns: {
        night: "Night",
        sales: "Sales",
        market: "Market",
        prices: "Optivo",
        lastMinute: "Last minute",
      },
      nightNames: {
        today: "Today",
        tomorrow: "Tomorrow",
        dayAfter: "Day after tomorrow",
      },
      occupancy: {
        soldOut: "Sold out",
        // `lastRooms` over exactly one free room: the singular is the interface's own.
        lastRoom: "Last room left",
        lastRooms: "Last rooms left",
        busy: "Well booked",
        partlyFree: "Partly free",
        mostlyFree: "Mostly free",
        unknown: "Occupancy not read yet",
      },
      // The rooms still free, printed beside the occupancy: free over all.
      rooms: {
        figure: "{free}/{total}",
        // Beside a mood that names the rooms taken, the figure says which way it counts.
        figureFree: "{free}/{total} free",
        // The same count in words, for a reader who cannot see the figure.
        label: {
          one: "{count} of {total} rooms free",
          few: "{count} of {total} rooms free",
          other: "{count} of {total} rooms free",
        },
      },
      // {level} is the calendar's own demand word, in lower case.
      demand: "Demand: {level}",
      pricing: {
        discounting: "Discounting",
        holding: "Holding prices",
        raising: "Raising prices",
        adjusting: "Adjusting both ways",
        locked: "Held by your locks",
        nothingToSell: "Nothing left to sell",
        unknown: "No comparison yet",
      },
      pricingStatus: {
        discounting: "Optivo is discounting today",
        holding: "Optivo is holding prices today",
        raising: "Optivo is raising prices today",
        adjusting: "Optivo is fine-tuning prices today",
        locked: "Today's prices are held by your locks",
        nothingToSell: "Tonight is sold out",
        unknown: "Today's prices cannot be compared yet",
      },
      lastMinute: {
        off: "Switched off",
        notOpen: "Window not open yet",
        waiting: "Window open, no discount yet",
        discounting: "Discounting the free rooms",
        holdingDiscount: "Holding the discount it reached",
        atFloor: "At the last-minute minimum",
      },
      market: {
        strong: "Strong",
        favourable: "Favourable",
        calm: "Calm",
        soft: "Soft",
        weak: "Weak",
        unknown: "No data",
      },
      marketStatus: {
        strong: "The market is strong",
        favourable: "The market is favourable",
        calm: "The market is calm",
        soft: "The market is soft",
        weak: "The market is weak",
        unknown: "No market data",
      },
      drivers: {
        schoolHoliday: "School holidays",
        specialDate: "Special date",
        weatherUp: "Good weather",
        weatherDown: "Poor weather",
        weatherExtreme: "Extreme weather",
        competitorRatesUp: "Nearby rates are high",
        competitorRatesDown: "Nearby rates are low",
      },
      yearOverYear: {
        wellAhead: "well ahead of last year",
        ahead: "ahead of last year",
        level: "level with last year",
        behind: "behind last year",
        wellBehind: "well behind last year",
      },
      monthColumns: {
        month: "Month",
        overall: "Overall",
        revenue: "Revenue",
        occupancy: "Occupancy",
      },
      // A month's verdict against last year. No opening word in common: two
      // months are told apart by the first word read.
      monthHeadline: {
        wellAhead: "Well ahead of last year",
        ahead: "Ahead of last year",
        level: "Level with last year",
        behind: "Behind last year",
        wellBehind: "Well behind last year",
      },
      // Revenue and occupancy against last year, each in a column of its own.
      // One set of words serves both columns, and none repeats "than last
      // year": the verdict beside them says it once for the row.
      monthPart: {
        wellAhead: "Much higher",
        ahead: "Higher",
        level: "About the same",
        behind: "Lower",
        wellBehind: "Much lower",
      },
      monthPartUnknown: "Cannot be compared",
      monthPace: {
        faster: "Filling faster than usual",
        usual: "Filling as usual",
        slower: "Filling slower than usual",
      },
      monthNoComparison: "Nothing to compare with yet",
      // The status bar's month: "{month}: {mood}", the mood in lower case.
      monthStatus: "{month}: {mood}",
      monthDetailNoLastYear: "No comparison with last year yet: last year's bookings are missing or too few.",
    },
  },

  // ── Header ──────────────────────────────────────────────
  header: {
    monthly: "Monthly",
    weekly: "Weekly",
    weekAbbr: "Wk",
    selectWeek: "Select week",
    selectDatePrompt: "Select a date range to get started",
    selectDateHint: "Open the calendar and pick a month or week to load pricing data.",
    currentYearPrices: "Current Prices",
    loadingCurrentPrices: "Loading current prices...",
    clearCurrentPricesCache: "Clear cache",
    fromDate: "From date",
    toDate: "To date",
    quickRanges: "Quick Ranges",
    today: "Today",
    thisWeek: "This Week",
    nextWeek: "Next Week",
    thisMonth: "This Month",
    next14Days: "Next 14 Days",
    next30Days: "Next 30 Days",
    changeRange: "Change dates",
    previousRange: "Previous period",
    nextRange: "Next period",
  },

  // ── Pricing Calendar ────────────────────────────────────
  pricingCalendar: {
    weeklyView: "7-day",
    monthlyView: "Monthly",
    aiPricing: "Optivo Pricing",
    stopAiPricing: "Stop Optivo Pricing ({seconds}s)",
    room: "Room",
    loadingData: "Loading pricing data...",
    fetchError: "Failed to load pricing data",
    noData: "No rooms or pricing data available",
    noDataHint: "Make sure your Property Management is connected and has rooms configured for this date range.",
    filters: "Filters",
    filterActive: "active",
    filterTitle: "Filter Rooms & Days",
    filterDesc: "Select specific rooms and/or days to narrow the calendar view. Bulk operations will only apply to the visible subset.",
    filterSummary: "{rooms}/{totalRooms} rooms, {days}/{totalDays} days",
    filterActiveLabel: "Showing {rooms} rooms, {days} days",
    filterClearAll: "Clear All Filters",
    filterRoomsLabel: "Rooms",
    filterDaysLabel: "Days of Week",
    filterSelectAll: "All",
    filterClear: "None",
    filterDaysShowing: "Showing {count} of {total} dates",
    roomId: "Room ID",
    roomKind: "Room kind",
    allRoomTypes: "All room types",
    clearFilters: "Clear filters",
    noFilterResults: "No prices match the selected filters.",
    occupancy: "Occupancy",
    pricingTier: "Pricing tier",
    draftOverlay: "Showing {count} draft price(s) on the calendar",
    bookingHighlight: {
      title: "Booking · {roomKind}",
      cellsAffected: "cell(s) affected",
      nightsAffected: "night(s) affected",
      nightsHighlighted: "night(s) highlighted",
      priceBefore: "Before",
      priceAfter: "After",
      price: "Price",
      occupancy: "Occupancy",
      explanationLoading: "Working out what this booking changed…",
      demandShift: "Demand moved from {demandBefore} to {demandAfter}.",
      demandSteady: "Demand stayed {demandAfter}.",
      explanationHighlight: "The {nights} night(s) of this stay are highlighted below — each cell shows the current suggested price for that night.",
      explanationUnchanged: "Occupancy for these nights held at {occ}%, so the suggested price is unchanged.",
      explanationUp: "Occupancy moved from {occBefore}% to {occAfter}% after this booking, and the pricing model raised the suggested nightly rate.",
      explanationDown: "Occupancy moved from {occBefore}% to {occAfter}% after this booking, and the pricing model lowered the suggested nightly rate.",
      explanationFlat: "Occupancy moved from {occBefore}% to {occAfter}% after this booking, but the suggested nightly rate came out the same.",
    },
    showPastDays: "{count} past day(s)",
    hidePastDays: "Hide past",
    todayLabel: "Today",
    tierBudget: "Budget",
    tierEconomy: "Economy",
    tierStandard: "Standard",
    tierHigh: "High",
    tierLuxury: "Luxury",
    demand: {
      legendLabel: "Demand",
      minimal: "Minimal",
      low: "Low",
      standard: "Standard",
      elevated: "Elevated",
      high: "High",
      minimalTitle: "Minimal demand — this room type has far fewer bookings for this date right now than it usually has this far before arrival.",
      lowTitle: "Low demand — this room type has fewer bookings for this date right now than it usually has this far before arrival.",
      standardTitle: "Standard demand — this room type has about as many bookings for this date right now as it usually has this far before arrival.",
      elevatedTitle: "Elevated demand — this room type has more bookings for this date right now than it usually has this far before arrival.",
      highTitle: "High demand — this room type has far more bookings for this date right now than it usually has this far before arrival.",
    },
  },

  // ── Price Cell ──────────────────────────────────────────
  priceCell: {
    unitsAvailable: "{available} of {total} units left",
    optivoSuggestion: "Optivo suggestion",
    reserved: "Reserved",
    blocked: "Blocked",
    option: "Option",
    closed: "Closed",
    notPricedYet: "Not priced yet",
    noPriceCalculated: "No price calculated",
    noPriceReasons: {
      missingBasePrice: "No base price is set for this room type yet, so Optivo can't calculate a price. Set one in Strategy → Per-Room-Kind Overrides.",
    },
    aiPriceSuggestion: "Price Suggestion Detail",
    reservationDetails: "Reservation Details",
    price: "Price:",
    guests: "Guests:",
    created: "Created:",
    partner: "Partner:",
    rejectAi: "Dismiss",
    regenerateAi: "Regenerate",
    generateAi: "Generate AI Price",
    sendToPms: "Send to PMS",
    pushSuccess: "Price sent to PMS for {date}",
    pushError: "Failed to send price to PMS",
    // A sold night: what the room itself sold for, read off the booking's
    // account in the PMS rather than its whole total.
    sale: {
      heading: "What this night sold for",
      kinds: {
        accommodation: "Accommodation",
        accommodation_services: "Accommodation + services",
        package: "Package",
        package_unseparated: "Package (room not priced separately)",
        no_room_line: "No accommodation item",
      },
      perNight: "{price} per night",
      stayAverage: "Average over {nights}: the PMS records a stay's accommodation as one item",
      unseparated: "The package is a single item, so this is the whole package per night",
      zeroAmount: "No amount was charged for the room (a voucher or a free stay)",
      groupSpread: "Group booking, spread over {rooms}",
      pendingValue: "Not read yet",
      pending: "This booking's breakdown has not been read from the PMS yet",
      unavailableValue: "No breakdown",
      unavailable: "The PMS keeps no breakdown of this booking",
      accommodation: "Accommodation {amount}",
      services: "Services {amount}",
      packageAmount: "Package {amount}",
      other: "Spent during the stay {amount}",
      fees: "Fees {amount}",
      stayTotal: "Whole stay {total} · {nights}",
      notTwoGuests: "The grid's Optivo prices are for two guests",
      ratePlan: "Rate plan: {name}",
      packageName: "Package: {name}",
      bookedOn: "Booked {date}",
      cellLabel: "Sold for {price} per night · {kind}",
      nights: { one: "{count} night", few: "{count} nights", other: "{count} nights" },
      rooms: { one: "{count} room", few: "{count} rooms", other: "{count} rooms" },
      guests: { one: "{count} guest", few: "{count} guests", other: "{count} guests" },
      legendLabel: "Sold nights",
      legendPrice: "Room price per night",
    },
  },

  // ── Suggestion Detail Panel ──────────────────────────────
  suggestionDetail: {
    title: "Price Suggestion Detail",
    basePrice: "base price (2 guests)",
    occupancyPrices: "Prices by Occupancy",
    occupancy: "Occupancy",
    price: "Price",
    guest: "guest",
    guests: "guests",
    // `{nights}` is a `bulkEdit.nightCount` form.
    losRelaxed: "Relaxed from {from} to {nights} because demand for this date is soft.",
    losRelaxedWeekendFill: "Relaxed from {from} to {nights}: at this time of year last year, weekends were only {fill}% full.",
    losRelaxedSoldOut: "Relaxed from {from} to {to}: this room type is sold out on {night}, so a longer stay cannot start tonight. It goes back up if a room frees up that night.",
    losRelaxedSoldOutBefore: "Relaxed from {from} to {to}: this room type is sold out on {night}, the night before, so no earlier arrival can reach tonight and the minimum asks only for the rest of the weekend or holiday. It goes back up if a room frees up that night.",
    minLosHint: "1 night means no minimum stay. A minimum stay you save here is locked on its own: Optivo keeps it and goes on pricing the night. Uploaded only when you turn on \"Upload minimum stay\" when sending. This applies to this room type only — other room types keep their own minimum for the night. Linked rate plans receive the same minimum on the nights they are priced.",
    losTitle: "Length of Stay",
    minLos: "Min LOS",
    nights: "nights",
    justificationTitle: "Pricing Explanation",
    justificationStaleWarning: "This explanation is of an earlier calculation and may not match the price above. Optivo replaces it the next time it recalculates this night.",
    priceSourceCalculated: "Calculated by pricing engine",
    priceSourceManual: "Manually set",
    priceSourceUnknown: "Source unknown",
    savePrices: "Save & Lock prices",
    bookingImpactTitle: "Booking Impact",
    bookingImpactDesc: "Price changed after a booking for {roomKind}.",
    priceBefore: "Before",
    priceAfter: "After",
    occupancyBefore: "Occupancy before",
    occupancyAfter: "Occupancy after",
    // ── Linked rate plans, under the minimum stay ─────────────────────────
    // What each channel plan and the non-refundable plan get on this night,
    // worked out by the server with the send's own rule. The room and day
    // counts are declined forms: the rooms sit after "of", the days after "in".
    // `{demand}` arrives signed ("+32%"): the night's demand against an
    // ordinary night of the room type, which is what picks the level.
    linkedPlansTitle: "Linked rate plans",
    linkedPlansDemand: "Demand for this night: {demand} against an ordinary night — demand level: {level}.",
    linkedPlansDemandOrdinary: "Demand for this night: the same as an ordinary night — demand level: {level}.",
    linkedPlansDemandNotRecorded: "This night was last calculated before Optivo recorded its demand, so the level follows the room type's occupancy: {level}.",
    linkedPlansOccupancyNow: "Now {booked} of {rooms} sold ({percent}).",
    linkedPlansOccupancyUnknown: "This night's demand is not recorded yet and the room type's occupancy could not be read from the PMS, so the channel plans are shown at the lowest demand level — as the next send would price them.",
    linkedPlansRooms: { one: "{count} room", few: "{count} rooms", other: "{count} rooms" },
    linkedPlansDays: { one: "{count} day", few: "{count} days", other: "{count} days" },
    linkedPlansGuests: { one: "{count} guest", few: "{count} guests", other: "{count} guests" },
    linkedPlansPlanColumn: "Rate plan",
    linkedPlansAdjustmentColumn: "Adjustment",
    linkedPlansPriceColumn: "{occupancy} ({currency})",
    linkedPlansNonRefundable: "Non-refundable",
    linkedPlansPercent: "{value}%",
    linkedPlansAmount: "{value} {currency}",
    linkedPlansNotOffered: "Not offered",
    linkedPlansWithheldOccupancyUnknown: "Non-refundable is not offered for this night: this room type's occupancy could not be read, and the send does not offer it without one.",
    linkedPlansWithheldAboveCeiling: "Non-refundable is not offered for this night: {booked} of {rooms} are sold, above its {ceiling} ceiling.",
    linkedPlansWithheldInsideLeadTime: "Non-refundable is not offered for this night: arrival is in {days}, and it is only offered at least {minDays} ahead.",
    linkedPlansWithheldArrivalToday: "Non-refundable is not offered for this night: arrival is today, and it is only offered at least {minDays} ahead.",
    linkedPlansWithheldExcludedNight: "Non-refundable is not offered for this night: it is never offered on {nightTypes}.",
    linkedPlansWithheldNightTypesUnknown: "Non-refundable is not offered for this night: the nights it is never offered on could not be identified, so the send withholds it.",
    linkedPlansNightTypeWeekend: "Friday and Saturday nights",
    linkedPlansNightTypeHoliday: "holidays and special dates",
    linkedPlansNightTypeBridgeDay: "bridge days",
    linkedPlansHint: "Worked out from the saved price, the demand Optivo read when it last calculated this night and this room type's occupancy now, the way the next send prices them.",
    linkedPlansEditedNote: "Worked out from the saved price, not the one being edited.",
    linkedPlansLoadError: "Linked rate plans could not be loaded.",
  },

  // ── A hand-typed price that looks like a slip ───────────────────────────
  // The question the price detail and "Edit several nights" put before they
  // save a price that stands out (`drafts/ImplausiblePricesConfirm.tsx`, by
  // the rule in `drafts/implausibleTypedPrices.ts`). A question, never a
  // refusal: a deep discount is the operator's call, so each price is named
  // beside what it is compared with. `{price}` arrives with its currency
  // ("2810 CZK"), `{guests}` is a `forGuests` form, and every `{count}` is the
  // number of prices.
  implausiblePrices: {
    title: { one: "Is this price right?", few: "Are these prices right?", other: "Are these prices right?" },
    leadNight: {
      one: "One price you are about to save looks unusual.",
      few: "{count} prices you are about to save look unusual.",
      other: "{count} prices you are about to save look unusual.",
    },
    leadBulk: {
      one: "This edit would write one price that looks unusual.",
      few: "This edit would write {count} prices that look unusual.",
      other: "This edit would write {count} prices that look unusual.",
    },
    belowReplaced: "Under half of the price it replaces ({price}).",
    aboveReplaced: "More than three times the price it replaces ({price}).",
    belowNextGuestCount: "Under half of the price {guests} ({price}).",
    aboveNextGuestCount: "Above the price {guests} ({price}).",
    // A reason this version of the page has no sentence for.
    otherReason: "Looks unusual beside {price}.",
    forGuests: { one: "for {count} guest", few: "for {count} guests", other: "for {count} guests" },
    more: { one: "and {count} more", few: "and {count} more", other: "and {count} more" },
    consequenceNight: "Saving locks these prices as typed: Optivo stops recalculating them, and the next send takes them to your Property Management System.",
    consequenceBulk: "Applying the edit locks these prices as shown: Optivo stops recalculating them, and the next send takes them to your Property Management System.",
    back: "Back to the prices",
    saveAnyway: "Save anyway",
    applyAnyway: "Apply anyway",
    // Under the bulk edit's preview, before the question is put.
    previewNote: {
      one: "One of these prices looks unusual. Optivo asks before it applies the edit.",
      few: "{count} of these prices look unusual. Optivo asks before it applies the edit.",
      other: "{count} of these prices look unusual. Optivo asks before it applies the edit.",
    },
  },

  // ── Pricing Explanation (structured decision table) ─────
  // A locked price beside the price Optivo would set (`drafts/lockedPrice.ts`).
  // A lock stops Optivo's price going out, never Optivo calculating it, so
  // every screen that shows a locked night says both, the locked one first —
  // and every way of locking asks why, optionally (`drafts/priceLockReasons.ts`).
  lockedPrice: {
    lockedLabel: "Your locked price",
    viewerLockedLabel: "Locked price",
    optivoLabel: "Optivo's price now",
    optivoShort: "Optivo",
    gapHigher: "{difference} ({percent}) above your locked price",
    gapLower: "{difference} ({percent}) below your locked price",
    gapSame: "The same as your locked price",
    calculatedAt: "calculated {time}",
    explainer: "Your locked price is the one that goes to your PMS. Optivo keeps calculating its own price for this night beside it, and puts that in force if you unlock.",
    notCalculatedYet: "Optivo hasn't recalculated this night since it was locked. Its own price shows here after the next recalculation.",
    explanationTitle: "How Optivo calculates its own price",
    explanationFinalPrice: "Optivo's price",
    explanationNote: "This explains Optivo's own price for the night, not your locked price.",
    compactMovesTitle: "What moves Optivo's price",
    optivoColumn: "Optivo",
    unlockWillSet: "Unlocking puts Optivo's price in force: {price}, as of its last calculation ({time}).",
    markHigher: "Locked. Optivo would price it higher: {price}",
    markLower: "Locked. Optivo would price it lower: {price}",
    markSame: "Locked. Optivo would price it the same",
    legendLabel: "Locked prices",
    legendHigher: "Optivo would price higher",
    legendLower: "Optivo would price lower",
    reasonQuestion: "Why are you locking this price?",
    reasonQuestionMany: "Why are you locking these prices?",
    reasonQuestionTyped: "Why are you changing Optivo's price?",
    reasonOptional: "Optional. It shows us where Optivo's prices don't fit.",
    reasonLabel: "Why it is locked",
    reasonNone: "No reason given",
    reasonAdd: "Add a reason",
    reasonChange: "Change the reason",
    reasonSave: "Save the reason",
    reasonSavedToast: "Reason saved",
    reasons: {
      tooHigh: "Optivo's price is too high",
      tooLow: "Optivo's price is too low",
      event: "An event or demand Optivo can't see",
      agreedPrice: "A price agreed with a guest, group or partner",
      competitors: "Matching the competitors",
      keepStable: "I don't want this price to move",
      other: "Something else",
    },
    lockSelectionTitle: "Lock these prices?",
    lockSelectionWithMinStayTitle: "Lock these prices and minimum stays?",
    lockSelectionDesc: "{selection}. Optivo won't overwrite locked prices; it keeps calculating its own price for each night and shows it in the night's detail.",
    lockAction: "Lock",
  },

  pricingExplanation: {
    title: "Pricing Explanation",
    summary: {
      finalPrice: "Final price",
      strategyGoal: "Strategy goal",
      goalRevenue: "Maximize revenue",
      goalOccupancy: "Maximize occupancy",
      goalHybrid: "Optivo recommended",
      daysFromNow: "{count} day from now",
      daysFromNowPlural: "{count} days from now",
      today: "today",
    },
    market: {
      title: "Market context",
      occupancy: "Occupancy",
      basePrice: "Base price",
      minAcceptable: "Min acceptable",
      minAcceptableLastMinute: "Min acceptable (last minute)",
      // A minimum that follows the night's Seasonality month: the figure
      // under it is the stated one moved by the month.
      minAcceptableSeason: "Min acceptable (Seasonality {seasonSigned}%)",
      minAcceptableLastMinuteSeason: "Min acceptable (last minute, Seasonality {seasonSigned}%)",
      pace: "Booking pace",
      paceStrong: "strong",
      paceNormal: "normal",
      paceWeak: "weak",
      // While the pace row prices a night from how its type of night usually
      // sells, the night's own pace alone is the half that misleads: an empty
      // Saturday months out read "0.00 · weak" under a premium for pace. So the
      // two readings stand side by side, in one unit — what an average night
      // holds at this lead time — and the word is left out.
      paceWithType: "today {pace} · {nightType} usually {typical}",
      paceWithHoliday: "today {pace} · before this holiday last year {typical}",
      // The same where the pace row priced the night on the whole property: a
      // room kind of a few rooms reads the property's pace, and "0.00 · weak"
      // over a row at 157 % was one label over two numbers. The word is left
      // out here too.
      paceWithProperty: "this type {pace} · whole property {property}",
      paceWithPropertyBlend: "this type {pace} · whole property {property} · together {blend}",
      paceWithPropertyOnly: "this type nothing expected yet · whole property {property}",
      currentVsTarget: "{current}% current",
      roomKindVsProperty: "{roomKind}% this room type · {property}% property",
    },
    table: {
      title: "How this price was built",
      colDecision: "Decision",
      colWhy: "Why",
      colHow: "How",
      // The two money columns name their currency once, so the amounts under
      // them print without it: "−391 CZK" is two words, and a column at its
      // narrowest broke it over two lines.
      colImpact: "Impact ({currency})",
      colRunning: "Running price ({currency})",
      noEffect: "no effect",
      // Amount of a nested breakdown row: a share of the row above it, not a
      // further deduction of its own.
      ofWhich: "of which {amount}",
    },
    // ── Compact read-only card the calendar cells hover ──────
    // The same explanation as the table above, cut down to what fits beside a
    // grid cell. Its own keys rather than reused table headers: a hover card
    // has room for a label, not for a column heading and a sentence.
    compact: {
      biggestMoves: "Biggest moves",
      moreMoves: "+{count} more",
      viewDetail: "{view} view",
      occupancyPrices: "Prices by occupancy",
      noExplanation: "Optivo recorded no explanation for this night.",
      openDetail: "Click the cell for the full breakdown",
      // Where a click selects the cell for the action menu (`drafts.selection`).
      selectThenOpenDetail: "Click to select the cell, click again for the full breakdown",
    },
    notApplied: {
      toggleShow: "Show factors that weren't applied",
      toggleHide: "Hide factors that weren't applied",
      colFactor: "Factor",
      colReason: "Why not",
      reasons: {
        disabled: "Turned off in your pricing strategy.",
        neutral: "Conditions were within the normal range, so no adjustment was needed.",
        notApplicable: "This date didn't qualify for this adjustment.",
        outsideWindow: "The date is still outside the last-minute window.",
        noData: "Not enough booking history to apply this factor yet.",
        lockedByLastMinute: "Last-minute pricing set the final price, so this step was skipped.",
        overriddenBySpecialDay: "This night's special-date premium is at least as high as its ordinary adjustment — its day of the week, and the month where you set one — so it replaces it; the two are never added together.",
        // The mirror: the night's own premium — the room type's, where it
        // states a week of its own — is the higher, so the special date stood
        // down. `holidayOutranked` below names the day and both premiums.
        outrankedByDayAdjustment: "This night's own day-of-week premium is higher than the special date's, so it applies instead — the two are never added together.",
        nearTerm: "This date is close enough that live signals — occupancy, booking pace, competitor rates — set the price. A month from the analysis only shapes dates further out; a month you set yourself applies to every night.",
        atLastMinuteFloor: "The price had already fallen to your last-minute minimum, so there was nothing left to discount — but that minimum is what this night was held to.",
        belowLastMinuteCurve: "Last-minute pricing is running for this night, but this night's other signals already price it below where the curve stands — so the curve left it alone rather than pricing it back up. It takes over as soon as it goes deeper.",
        specialDatePriced: "You priced this date yourself, as a holiday or special date, so this automatic adjustment stood down rather than overriding your decision.",
        lastMinuteDiscounting: "Last-minute pricing is walking this night down to your minimum from its undiscounted rate, and it is the only discount that applies once it does — a second one underneath would discount the same night twice.",
        strongDemand: "This night is already selling — it is fuller than this night usually gets, or it is booking ahead of your usual curve — so it does not need an orphan-night discount.",
        followingMarket: "This night's base price was set from the local market, which already prices the weekend, the holiday, the season and the lead time — so this adjustment stood down rather than counting the same night twice.",
        // Only for a row that carries no bound to quote; a pace or pickup row
        // that does reads its own sentence below.
        heldByPaceBound: "The pace bounds set in your strategy did not allow this adjustment.",
        // A night discounting by demand whose demand colour allows none of
        // the curve: the mode runs, and the night's own demand holds the
        // price. `lastMinuteDemandHeld*` below names the colour and the reading.
        demandAboveLastMinuteStart: "This room type discounts by demand, and last-minute goes only as deep as the night's demand allows — this night's demand is stronger than the strongest level it discounts, so the price holds.",
        // A night discounting by demand whose free rooms the bookings it still
        // usually takes would fill. `lastMinuteRemainingHeld` below says the numbers.
        remainingDemandCoversRooms: "This room type discounts by demand, and the bookings it usually still takes before the cutoff would fill the rooms it has free — so the price holds for now.",
        soldOut: "Every room of this type is sold for this night, so there is nothing left for last-minute pricing to discount.",
        // The weekly demand row is never listed here — it says this in its own
        // sentence — but the reason table is complete.
        contradictedByBookings: "This night's bookings contradict the week the history expected, so the weekly demand signal stood down.",
      },
      // Nights priced before the demand colour decided carry only the market
      // pressure the old gate read, and are explained in those terms.
      lastMinuteMarketCalm: "Demand is on track (occupancy and pace are healthy), so no last-minute discount would apply even inside the window.",
      lastMinuteMarketElevated: "Demand is running soft ({pct}% market pressure), so a last-minute discount can begin once the date is within {hours} hours of arrival.",
      lastMinuteOverrideOutsideWindow: "This room kind is set to always discount, so a last-minute reduction will begin once the date is within {hours} hours of arrival.",
      // Discount by demand goes as deep as the night's demand colour allows.
      // {level} and {start} are the calendar legend's own words, lowercased;
      // {start} is the strongest demand the room type still discounts.
      lastMinuteDemandHeldPace: "Demand for this night is {level} — bookings at {demandRatioPct}% of the usual pace — but last-minute discounts only nights where demand is {start} or weaker.",
      lastMinuteDemandHeldOccupancy: "Demand for this night is {level} — occupancy at {demandRatioPct}% of your target — but last-minute discounts only nights where demand is {start} or weaker.",
      lastMinuteOutsideWindowGraded: "Once the date is within {hours} hours of arrival, the price steps toward your minimum as far as the night's demand allows — at today's {level} demand, {demandDepthPct}% of the way.",
      lastMinuteOutsideWindowHeld: "The last-minute window opens within {hours} hours of arrival, but at today's {level} demand the night would keep its price — only nights where demand is {start} or weaker are discounted.",
      // The night's demand holds the price for now, but the clock releases
      // part of that hold by the cutoff — so "only nights where demand is X or
      // weaker" would be a promise the night breaks a few hours later.
      lastMinuteDemandReleasedPace: "Demand for this night is {level} — bookings at {demandRatioPct}% of the usual pace — so it keeps its price for now; closer to arrival the discount is released anyway, reaching {demandDepthPct}% of the way to your minimum by the cutoff.",
      lastMinuteDemandReleasedOccupancy: "Demand for this night is {level} — occupancy at {demandRatioPct}% of your target — so it keeps its price for now; closer to arrival the discount is released anyway, reaching {demandDepthPct}% of the way to your minimum by the cutoff.",
      lastMinuteOutsideWindowReleased: "Once the date is within {hours} hours of arrival, the price steps toward your minimum: at today's {level} demand it holds at first, then the discount is released as arrival nears, reaching {demandDepthPct}% of the way by the cutoff.",
      lastMinuteOutsideWindowNoReading: "There is no demand reading for this night yet, so no last-minute discount would apply even inside the window.",
      // Discount by demand reads the bookings the room type still usually
      // takes before the cutoff against its free rooms. {lateBookings},
      // {freeRooms} and {momentumClause} are whole phrases, declined with their
      // numbers (`decisions.lastMinute.lateBookings` / `freeRooms`).
      lastMinuteRemainingHeld: "It usually takes {lateBookings} for this night{momentumClause}, and {freeRooms}; as fewer bookings are still expected the discount starts, and a room still free at the cutoff reaches your minimum.",
      lastMinuteOutsideWindowRemaining: "Once the date is within {hours} hours of arrival, the price steps toward your minimum as far as the bookings this room type usually still takes fall short of its free rooms — and a room still free at the cutoff reaches your minimum.",
      lastMinuteOutsideWindowSoldOut: "Every room of this type is sold for this night, so there would be nothing to discount.",
      // A special date the night's own day premium outranked, with the day
      // (`days`) and both premiums as signed whole numbers.
      holidayOutranked: "{day}'s own premium, {dayPremium}%, is higher than this date's {specialPremium}%, so it applies instead — the two are never added together.",
      // The same where a month the operator set was part of the night's own
      // calendar: the day and the month together outranked the date.
      holidayOutrankedWithMonth: "{day}'s own premium, {dayPremium}%, with the {monthPremium}% you set for the month makes {ownPremium}%, higher than this date's {specialPremium}%, so they apply instead — never added together.",
      // The strategy's pace floor (or ceiling) held a night pace would have
      // moved at no change. Said in place of "within the normal range", which
      // is what the list printed under a header reading weak pace. Only a
      // bound the operator set can hold a night whole — every reactivity
      // preset's floor sits below 0% and its ceiling above it — so this names
      // the field in their strategy. {paceRatioPct} is the blended pace the
      // step priced with, as in the pace row.
      paceFloorHeld: "Booking pace is at {paceRatioPct}% of expected, which calls for a {unboundedDeltaPct}% adjustment, but the pace floor in your strategy is {paceBoundPct}%, so booking pace cannot lower the price.",
      paceCeilingHeld: "Booking pace is at {paceRatioPct}% of expected, which calls for a {unboundedDeltaPct}% adjustment, but the pace ceiling in your strategy is {paceBoundPct}%, so booking pace cannot raise the price.",
      // Booking pickup clamps pace × pickup into the same band, so a bound
      // holds it for one of two reasons. Where the bound sits at 0% it is the
      // operator's own setting, as for pace. Where it does not, the bound only
      // held pickup because booking pace had already taken the price to it.
      pickupFloorHeld: "This date is filling slower than expected, which calls for a {unboundedDeltaPct}% adjustment, but the pace floor in your strategy is {paceBoundPct}%, and the adjustment for bookings gained shares it with booking pace, so it cannot lower the price.",
      pickupCeilingHeld: "This date is filling faster than expected, which calls for a {unboundedDeltaPct}% adjustment, but the pace ceiling in your strategy is {paceBoundPct}%, and the adjustment for bookings gained shares it with booking pace, so it cannot raise the price.",
      pickupFloorReachedByPace: "This date is filling slower than expected, which calls for a {unboundedDeltaPct}% adjustment, but booking pace has already taken the price to the pace floor in effect for this night ({paceBoundPct}%). The adjustment for bookings gained shares that floor, so it cannot lower the price any further.",
      pickupCeilingReachedByPace: "This date is filling faster than expected, which calls for a {unboundedDeltaPct}% adjustment, but booking pace has already taken the price to the pace ceiling in effect for this night ({paceBoundPct}%). The adjustment for bookings gained shares that ceiling, so it cannot raise the price any further.",
    },
    decisions: {
      baseRate: {
        what: "Base rate",
        why: "Anchor price derived from historical performance.",
        // A room type no stay has priced starts from its PMS price list.
        whyPriceList: "Anchor price from your PMS price list — this room type has no booking history to measure yet.",
        how: "Starting at {basePrice} {currency} per night.",
        whyMarket: "On nights the comp set covers, the market median is the anchor rather than the stored base price.",
        howMarket: "Set from the local market: the median of {pricedCount} priced competitors is {marketMedian} {currency}, and your position is {marketPositionSigned}% → {basePrice} {currency} per night.",
        howMarketLadder: "Set from the local market: the median of {pricedCount} priced competitors is {marketMedian} {currency}, your position is {marketPositionSigned}%, and this room type sits at {ladderPct}% of your cheapest one → {basePrice} {currency} per night.",
      },
      occupancyAdjustment: {
        what: "Occupancy adjustment",
        why: "Booking pressure, measured against the occupancy Optivo aims for on this night, adjusts the nightly price.",
        how: "{currentOccupancyPct}% booked → {priceAfter} {currency} (floor {floor}).",
        // The base-price hold kept the discount this reading asks for off the
        // night: until the booking window opens it keeps its base price, in the
        // floor row's words. {bookingWindow} is declined by that row's
        // `bookingWindowDays` forms, so the two rows quote one window.
        howBaseHold: "{currentOccupancyPct}% booked would take {heldDiscount} {currency} off, but the night is held at the base price until the booking window opens, {bookingWindow} before arrival.",
        // The same, on a weekend or holiday night held for the window the
        // property states for those nights rather than the room type's own.
        howBaseHoldPeakNight: "{currentOccupancyPct}% booked would take {heldDiscount} {currency} off, but the night is held at the base price until your weekend and holiday booking window opens, {bookingWindow} before arrival.",
        // Last-minute pricing took the night over and withdrew this discount
        // with the night's others. Said in the words the withdrawn rows'
        // `lastMinuteDiscounting` reason and the simulator's
        // `baseLastMinuteHold` already use, so three places describe one
        // takeover alike. No floor: the blend's is not where the night is headed.
        howLastMinute: "{currentOccupancyPct}% booked would take {withdrawnDiscount} {currency} off, but last-minute pricing is walking this night down to your minimum from its undiscounted rate, so its occupancy discount stood down.",
        // Historical drafts only. The blend briefly measured against a
        // booking-curve expectation rather than the target; drafts saved in
        // that window still carry `expectedOccupancyPct` and still render this
        // wording. Nothing writes it any more.
        howPaced: "{currentOccupancyPct}% booked vs {expectedOccupancyPct}% expected by now → {priceAfter} {currency} (floor {floor}).",
      },
      dynamicDemand: {
        // It reads the analysis's weekly demand index — the weeks around the
        // stay date over the past year of stays — and never a booking made
        // for this date. "Recent demand signal" sent an operator looking for
        // today's demand here, beside a demand tint that is exactly that.
        what: "Weekly demand from history",
        why: "How busy the weeks around this date were over the past year, compared with an average week — from history, not from today's bookings.",
        howPremium: "{sourceLabel} is {demandDeltaPct}% above normal demand, so the price moves up {priceDeltaPct}%.",
        howDiscount: "{sourceLabel} is {demandDeltaPct}% below normal demand, so the price moves down {priceDeltaPct}%.",
        howNeutral: "{sourceLabel} is close to normal demand, so there is no price change.",
        // No weekly reading to act on — no analysis yet, or no room-nights
        // booked in this week's history — so the row names no source, and the
        // sentence cannot take one as its subject.
        howNoSignal: "No weekly demand signal is available for this night, so there is no price change.",
        // The step stood down: this row is always drawn in the main table and
        // never listed under "factors that weren't applied", so it is the only
        // place that can say why. A week that read above or below normal is
        // still named, being what the stand-down kept out of the price; an
        // ordinary or unread one leaves only the reason. Worded as
        // `notApplied.reasons.followingMarket`, with demand in place of the
        // calendar it lists.
        howFollowingMarketPremium: "{sourceLabel} is {demandDeltaPct}% above normal demand, but this night's base price was set from the local market, which already prices demand for this night — so the signal stood down rather than counting the same demand twice.",
        howFollowingMarketDiscount: "{sourceLabel} is {demandDeltaPct}% below normal demand, but this night's base price was set from the local market, which already prices demand for this night — so the signal stood down rather than counting the same demand twice.",
        howFollowingMarket: "This night's base price was set from the local market, which already prices demand for this night — so the weekly demand signal stood down rather than counting the same demand twice.",
        // Last-minute pricing took the night over and withdrew this discount
        // with the night's others, in the words the occupancy row's
        // `howLastMinute` and `notApplied.reasons.lastMinuteDiscounting` use.
        // Only a discount is ever withdrawn, so the sentence names one.
        howLastMinute: "{sourceLabel} is {demandDeltaPct}% below normal demand, but last-minute pricing is walking this night down to your minimum from its undiscounted rate, so its demand discount stood down.",
        // A room type discounting by demand: the week yields to the night's own
        // bookings. {historyWeightPct} is the share of the week that stayed in
        // the price, {bookingsPacePct} the pace that decided it.
        howPremiumBookings: "{sourceLabel} is {demandDeltaPct}% above normal demand, but bookings for this night are at {bookingsPacePct}% of their usual pace, so only {historyWeightPct}% of it applies — the price moves up {priceDeltaPct}%.",
        howDiscountBookings: "{sourceLabel} is {demandDeltaPct}% below normal demand, but bookings for this night are at {bookingsPacePct}% of their usual pace, so only {historyWeightPct}% of it applies — the price moves down {priceDeltaPct}%.",
        howContradictedPremium: "{sourceLabel} is {demandDeltaPct}% above normal demand, but bookings for this night are at only {bookingsPacePct}% of their usual pace — the busy week the history expected is not showing — so the signal stood down.",
        howContradictedDiscount: "{sourceLabel} is {demandDeltaPct}% below normal demand, but bookings for this night are at {bookingsPacePct}% of their usual pace — ahead of it — so the signal stood down.",
        // A row that did not move the price and has no sentence above — nothing
        // writes one — still never says it moved.
        howNotApplied: "The weekly demand signal was not applied to this night's price, so there is no price change.",
        sourceProperty: "The whole property",
        sourceRoomKind: "This room type",
        sourceFallback: "The property fallback",
        sourceBlended: "This room type blended with the whole property",
        sourceNone: "No demand signal",
      },
      marketInfluence: {
        what: "Local market",
        whatWeather: "Local conditions",
        why: "The competitor set nearby shows how the local market is pricing this night.",
        whyWeather: "Conditions around the property — school holidays and the weather forecast — show how much demand this night carries.",
        howDemand: "The local market is pricing this night {marketStanceLabel} its own range, so the price moves {priceDeltaPct}%.",
        howNeutral: "The local market is close to its own average for this night, so there is no price change.",
        howWeather: "Conditions read {marketStanceLabel} usual for this night, so the price moves {priceDeltaPct}%.",
        howExtreme: "Extreme weather is forecast for this night, which is read as lowered demand, so the price moves down {priceDeltaPct}%.",
        howExtremeOutweighed: "Extreme weather is forecast for this night, but stronger demand from the other conditions outweighs it, so the price still moves up {priceDeltaPct}%.",
        stanceHigh: "high in",
        stanceLow: "low in",
        stanceHighWeather: "stronger than",
        stanceLowWeather: "weaker than",
        sample: "Based on {pricedCount} priced competitors of {supplyCount} listed.",
        sampleWeather: "Based on holidays and weather only — competitor tracking is off for this property.",
      },
      seasonality: {
        what: "Seasonality",
        why: "Demand varies by season.",
        // Read off explanations stored while only a month the operator set
        // priced every night of it; every month does now, and the row says so.
        whyStated: "You set this month's adjustment in Seasonality; it applies to every night of the month.",
        howHigh: "High season: {deltaPct}%.",
        howLow: "Low season: {deltaPct}%.",
        howNeutral: "Neutral season — no adjustment.",
      },
      holiday: {
        what: "Holiday / special date",
        why: "Demand shifts on holidays and special dates.",
        how: "\"{name}\": {deltaPct}%.",
      },
      bridgeDay: {
        what: "Bridge-day premium",
        why: "A single free night between two busy nights — nudge the price up to match demand.",
        how: "{deltaPct}% applied.",
      },
      shoulderDay: {
        what: "Shoulder day",
        why: "The ordinary night right next to a holiday break — demand gathers on the break and usually thins out either side of it.",
        howBefore: "Night before a holiday break: {deltaPct}%.",
        howAfter: "Night after a holiday break: {deltaPct}%.",
        howBeforeNamed: "Night before the holiday break ({name}): {deltaPct}%.",
        howAfterNamed: "Night after the holiday break ({name}): {deltaPct}%.",
        how: "{deltaPct}% applied.",
      },
      leadTime: {
        what: "Lead-time curve",
        why: "Price reacts to how far out the booking is.",
        howPremium: "{daysOut} days out — premium phase ({deltaPct}%).",
        howDiscount: "{daysOut} days out — discount phase ({deltaPct}%).",
        howFarOut: "{daysOut} days out — beyond the booking window, far-out uplift ({deltaPct}%).",
        howBuildup: "{daysOut} days out — demand building, price rising ({deltaPct}%).",
        howHold: "{daysOut} days out — demand peak, holding price ({deltaPct}%).",
        howDecline: "{daysOut} days out — approaching arrival, price easing ({deltaPct}%).",
        howGeneric: "{daysOut} days out ({deltaPct}%).",
      },
      lastMinute: {
        what: "Last-minute pricing",
        why: "Drop price as the arrival window closes to capture late demand.",
        how: "{hoursRemaining}h remaining, step {step}/{totalSteps} ({deltaPct}%).",
        // Demand recovered and the discount was kept rather than withdrawn.
        // The step count is the clock, not the depth held, so it is not quoted.
        howHolding: "Bookings picked up, so the discount stays where it had reached — {depthPct}% of the way to your minimum — instead of being withdrawn; {hoursRemaining}h remaining ({deltaPct}%).",
        // Discount by demand goes as deep as the night's demand colour allows:
        // {demandDepthPct} by the cutoff, {depthPct} so far. {level} is the
        // calendar legend's own word, lowercased.
        howGradedPace: "Demand is {level} — bookings at {demandRatioPct}% of the usual pace — so last-minute goes {demandDepthPct}% of the way to your minimum by the cutoff: {depthPct}% so far, {hoursRemaining}h remaining ({deltaPct}%).",
        howGradedOccupancy: "Demand is {level} — occupancy at {demandRatioPct}% of your target — so last-minute goes {demandDepthPct}% of the way to your minimum by the cutoff: {depthPct}% so far, {hoursRemaining}h remaining ({deltaPct}%).",
        // Demand alone would hold this night (or hold most of it), and the
        // clock releases the rest as the window runs out — so the row names
        // both: what demand allowed, and where it ends up by the cutoff.
        howGradedPaceReleased: "Demand is {level} — bookings at {demandRatioPct}% of the usual pace — so demand alone would allow {demandAllowedDepthPct}%; with arrival this close the discount is released to {demandDepthPct}% of the way to your minimum by the cutoff: {depthPct}% so far, {hoursRemaining}h remaining ({deltaPct}%).",
        howGradedOccupancyReleased: "Demand is {level} — occupancy at {demandRatioPct}% of your target — so demand alone would allow {demandAllowedDepthPct}%; with arrival this close the discount is released to {demandDepthPct}% of the way to your minimum by the cutoff: {depthPct}% so far, {hoursRemaining}h remaining ({deltaPct}%).",
        // Discount by demand reads the bookings the room type still usually
        // takes before the cutoff against its free rooms, and every free room
        // reaches the minimum by the cutoff. {lateBookings}, {freeRooms} and
        // {momentumClause} are whole phrases, declined with their numbers.
        howRemaining: "This room type usually takes {lateBookings} for this night before the cutoff{momentumClause}, and {freeRooms} — so last-minute follows {demandAllowedDepthPct}% of your discount curve for now and reaches your minimum by the cutoff: {depthPct}% of the way so far, {hoursRemaining}h remaining ({deltaPct}%).",
        // Only where recent bookings scaled the expectation.
        remainingMomentumClause: " ({lateBookingsAdjusted} at the pace bookings have been arriving lately, {momentumPct}% of usual)",
        lateBookings: {
            none: "almost no more bookings",
            one: "{count} more booking",
            few: "{count} more bookings",
            many: "{count} more bookings",
            other: "{count} more bookings",
        },
        freeRooms: {
            one: "{count} room is still free",
            few: "{count} rooms are still free",
            other: "{count} rooms are still free",
        },
      },
      gapFill: {
        what: "Gap fill discount",
        why: "Orphan night between two reservations — discount to fill the gap.",
        how: "{deltaPct}% applied.",
      },
      pace: {
        what: "Booking pace",
        // The level, not the change: the row under it (bookings gained) counts
        // what came in over the last days, and both "why" sentences say so,
        // or the two read as one reading twice (Hotel Ostrov, Oct 2026: "pace
        // 157 %" over "filling slower" read as a contradiction).
        why: "How many rooms this night already has booked against how many it usually has at this lead time — the level of bookings, not their recent change.",
        // Whose rooms the row read, how many are booked and how many such a
        // night usually has by now. A room kind of a few rooms reads the whole
        // property, and without this sentence "157 %" over a room type with
        // no booking at all could not be understood. It leads the pace
        // sentence, and the bound's sentence too.
        howSubjectRoomKind: "This room type ({roomKindRooms}) has {roomKindBooked} booked for this night and usually has about {roomKindExpected} at this lead time.",
        howSubjectProperty: "The whole property ({propertyRooms}) has {propertyBooked} booked for this night and usually has about {propertyExpected} at this lead time.",
        howSubjectBlend: "This room type ({roomKindRooms}) has {roomKindBooked} booked for this night (usually about {roomKindExpected}), the whole property ({propertyRooms}) {propertyBooked} (usually about {propertyExpected}); the pace counts the property at {propertyWeightPct}% and this type at {roomKindWeightPct}%.",
        rooms: { one: "{count} room", few: "{count} rooms", other: "{count} rooms" },
        // Two readings, and rarely the same number: the pace measured, and
        // what that pace was worth on this night. Both slots used to take
        // {deltaPct}, so a +2% nudge read "Pace at 2% of expected". The pace
        // named here is the room kind's blended with the property's — the one
        // the step priced with — which is why it can differ from the figure
        // in the panel header.
        how: "Pace at {paceRatioPct}% of expected → {deltaPct}% adjustment.",
        // Drafts stored before the ratio rode along carry the adjustment
        // alone, and a row is better silent about the pace than wrong about it.
        howGeneric: "{deltaPct}% adjustment from booking pace.",
        // A pace bound trimmed the move, so the adjustment is the bound rather
        // than what pace asked for. "In effect for this night", not "in your
        // strategy": under the hybrid posture a reactivity preset swaps in the
        // revenue or occupancy posture's bounds by lead time, so the figure can
        // differ from the one the strategy editor shows.
        howPaceFloor: "Pace at {paceRatioPct}% of expected calls for {unboundedDeltaPct}%, but the pace floor in effect for this night holds the adjustment at {paceBoundPct}%.",
        howPaceCeiling: "Pace at {paceRatioPct}% of expected calls for {unboundedDeltaPct}%, but the pace ceiling in effect for this night holds the adjustment at {paceBoundPct}%.",
        // A night with few bookings of its own starts from how its type of
        // night usually sells — a weekday in a season, or the night before a
        // holiday — so an empty night can carry a premium. Said only while the
        // type still has a real say in the reading, or the premium on an empty
        // night has no reason beside it.
        howType: "Until the night has enough bookings of its own, the reading starts from how {nightType} usually sell: {typicalPacePct}% of {averageNight}. Its own bookings weigh {bookingsWeightPct}% so far.",
        howTypeHoliday: "Until the night has enough bookings of its own, the reading starts from how the night before this holiday sold last year: {typicalPacePct}% of {averageNight}. Its own bookings weigh {bookingsWeightPct}% so far.",
        // By the night's own weekday, as the engine reads it: Sunday first.
        typeWeekdays: ["Sundays", "Mondays", "Tuesdays", "Wednesdays", "Thursdays", "Fridays", "Saturdays"],
        typeSeasonHigh: "{weekdays} in the high season",
        typeSeasonLow: "{weekdays} outside the high season",
        averageNightSeason: "the season's average night",
        averageNightAll: "an average night",
      },
      pickup: {
        // "Booking pickup" under "Booking pace" was two paces one above the
        // other, one the level and one the change. The row is named by its
        // window (whatWindow, from the stored {windowDays}); a draft stored
        // without one carries the general name.
        what: "Bookings gained in recent days",
        whatWindow: { one: "Bookings gained in the last day", few: "Bookings gained in the last {count} days", other: "Bookings gained in the last {count} days" },
        why: "How many bookings came in or fell away over the last few days against how many usually arrive by now — the change, not the level.",
        // Whose rooms the row counted: {change} is a counted phrase from
        // gained / lost / unchanged, {window} from window. It leads the
        // direction sentence, and the bound's sentence too.
        howSubjectRoomKind: "This room type {roomKindChange} in the last {window}; it usually gains about {roomKindExpectedPickup} by now.",
        howSubjectProperty: "The whole property {propertyChange} in the last {window}; it usually gains about {propertyExpectedPickup} by now.",
        howSubjectBlend: "This room type {roomKindChange} in the last {window} (usually about {roomKindExpectedPickup}), the whole property {propertyChange} (usually about {propertyExpectedPickup}); the reading counts the property at {propertyWeightPct}% and this type at {roomKindWeightPct}%.",
        window: { one: "day", few: "{count} days", other: "{count} days" },
        gained: { one: "gained {count} room", few: "gained {count} rooms", other: "gained {count} rooms" },
        lost: { one: "lost {count} room", few: "lost {count} rooms", other: "lost {count} rooms" },
        unchanged: "gained no rooms",
        // The template carries the direction, so {deltaPct} is interpolated
        // as an absolute value — "moves down -3%" would read as an increase.
        howFaster: "This date is filling faster than expected, so the price moves up {deltaPct}%.",
        howSlower: "This date is filling slower than expected, so the price moves down {deltaPct}%.",
        howNeutral: "Bookings are arriving at the expected speed, so there is no price change.",
        // The pace band pace and pickup share left pickup part of its move.
        // Signed here, unlike the two above: the sentence states what pickup
        // asked for and what it got, and only the sign tells them apart.
        howPaceFloor: "This date is filling slower than expected, which calls for {unboundedDeltaPct}%, but booking pace and the adjustment for bookings gained together may go no lower than the pace floor in effect for this night ({paceBoundPct}%), so this adjustment moves the price by only {deltaPct}%.",
        howPaceCeiling: "This date is filling faster than expected, which calls for {unboundedDeltaPct}%, but booking pace and the adjustment for bookings gained together may go no higher than the pace ceiling in effect for this night ({paceBoundPct}%), so this adjustment moves the price by only {deltaPct}%.",
      },
      propertyDemand: {
        what: "Property-wide demand",
        why: "Occupancy across the whole property pulls this room type's base price with it, in either direction.",
        how: "Property-wide occupancy moved this room type's base by {deltaPct}%.",
        // When this room type has no bookings of its own — the norm months out
        // — the property signal is the entire occupancy adjustment.
        howWhole: "All of this adjustment came from property-wide occupancy: this room type has no bookings of its own yet.",
      },
      dayAdjustment: {
        what: "Day-of-week adjustment",
        why: "{day} prices follow your weekly rules.",
        how: "{deltaPct}% applied.",
      },
      // No `how` that names the floor: the impact column states what the clamp
      // moved and the running-price column states where it landed, so a
      // sentence naming the floor again is the row saying one number three
      // times. What the row does name is WHICH floor — three different numbers
      // can stand under it, and the panel header states only the minimum price.
      // `what` / `why` are also what a draft priced before the floors were
      // named still reads.
      floorClamp: {
        what: "Min-price floor",
        why: "Final price was below the configured floor for this date.",
        // The minimum rises with the night's weekend or holiday premium, which
        // is why the row can land above the minimum the header states.
        whyPremium: "The minimum price for this room type, {minPrice} {currency}, rises with this night's {premiumSigned}% premium.",
        // A minimum that follows Seasonality moves with the night's month, on
        // its own or beside the night's premium.
        whySeason: "The minimum price for this room type, {minPrice} {currency}, follows this month's Seasonality of {seasonSigned}%.",
        whySeasonPremium: "The minimum price for this room type, {minPrice} {currency}, follows this month's Seasonality of {seasonSigned}% and rises with this night's {premiumSigned}% premium.",
        // The base-price hold: until the booking window opens, the night is
        // held at its base price whatever demand, occupancy or pace say.
        whatBaseHold: "Base-price floor",
        whyBaseHold: "While arrival is more than {bookingWindow} away — this room type's booking window — the price cannot drop below its base price, not even when demand, occupancy or booking pace are low.",
        whyBaseHoldAdjusted: "While arrival is more than {bookingWindow} away — this room type's booking window — the price cannot drop below its base price as adjusted for this night, not even when demand, occupancy or booking pace are low.",
        // A weekend or holiday night held for the window the property states
        // for those nights — the days alone cannot say why a Saturday holds
        // for 6 while the Tuesday beside it holds for 18.
        whyBaseHoldPeakNight: "While arrival is more than {bookingWindow} away — your booking window for weekend and holiday nights — the price cannot drop below this room type's base price, not even when demand, occupancy or booking pace are low.",
        whyBaseHoldAdjustedPeakNight: "While arrival is more than {bookingWindow} away — your booking window for weekend and holiday nights — the price cannot drop below this room type's base price as adjusted for this night, not even when demand, occupancy or booking pace are low.",
        // The derivation, not the result: the running-price column has that.
        howBaseHoldAdjusted: "Base price {basePrice} {currency}, {nightAdjustmentSigned}% for this night.",
        bookingWindowDays: { one: "{count} day", few: "{count} days", other: "{count} days" },
        whatLastMinute: "Last-minute minimum",
        whyLastMinute: "Last-minute pricing may take this night down to your last-minute minimum, and no further.",
        // A last-minute minimum that follows Seasonality: the derivation, as
        // the hold's.
        howLastMinuteSeason: "Your last-minute minimum {minPriceStated} {currency}, {seasonSigned}% for this month's Seasonality.",
      },
      ceilingClamp: {
        what: "Max-price ceiling",
        why: "Final price exceeded the configured ceiling.",
      },
      rounding: {
        what: "Rounding",
        why: "Round to the configured price granularity.",
        how: "Final price rounded.",
        howCharm: "Rounded to the nearest {granularity} {currency}, then trimmed by {charmEnding} so it ends in {ending}.",
      },
      // An operator's standing adjustment, laid over Optivo's price last
      // (`drafts.adjustment`). `what` and `why` render from empty params,
      // which is all a read-only viewer's row carries, and name the thing,
      // never the reader. No sentence names a cause the row does not carry:
      // the last-minute window is mentioned only where the row states a share
      // of the raise or that the fade is what left nothing.
      operatorAdjustment: {
        what: "Adjustment without locking",
        why: "Set on the Prices page for this night and room type. It is not a lock: Optivo keeps recalculating the price and applies the adjustment last.",
        howAmount: "{valueSigned} {currency} on Optivo's price of {priceBefore} {currency}.",
        howPercent: "{valueSigned}% of Optivo's price of {priceBefore} {currency}.",
        howShownRung: "For this room type the price shown differs from the two-guest rate the rows above work out by {shownOffsetSigned} {currency}; the adjustment is stated on the price shown.",
        howAfterFloors: "It comes after Optivo's own floors and stops only at the room type's minimum price for this night.",
        heldMinimum: "It stops at the room type's minimum price for this night.",
        atMinimum: "Optivo's price of {priceBefore} {currency} is already at the room type's minimum price for this night or under it, so the adjustment lowers nothing.",
        minimumIs: "The minimum price for this night is {bound} {currency}.",
        // Where the room type's minimum follows the night's Seasonality month.
        minimumIsSeason: "The minimum price for this night is {bound} {currency}: the room type's minimum moved by this month's Seasonality of {seasonSigned}%.",
        heldCeiling: "It stops at this night's maximum price.",
        atCeiling: "Optivo's price of {priceBefore} {currency} is already at this night's maximum price or above it, so the adjustment raises nothing.",
        ceilingIs: "This night's maximum price is {bound} {currency}.",
        boundAsShown: "For the price shown here that comes to {boundPrice} {currency}.",
        heldRounding: "It is smaller than the price rounding of {granularity} {currency}, so the price stays {priceBefore} {currency}.",
        lastMinuteShare: "Inside the last-minute window {lastMinutePct}% of the raise is in force: it shrinks as far as the last-minute curve has reached for the night, so a free room still falls to its last-minute minimum.",
        lastMinuteLittle: "So little of the raise is left in the last-minute window that the price does not move.",
        tooLow: "It would leave no price, so it was not applied.",
      },
    },
    days: {
      monday: "Monday",
      tuesday: "Tuesday",
      wednesday: "Wednesday",
      thursday: "Thursday",
      friday: "Friday",
      saturday: "Saturday",
      sunday: "Sunday",
    },
  },

  // ── Live Price Example (price simulator) ────────────────
  //
  // `step*` keys mirror the backend `SimulationStep.labelKey`, and `base*`
  // keys mirror `basePriceExplanationKey`. The backend still ships an English
  // `label` / `basePriceExplanation` as a fallback, but everything the panel
  // renders is resolved here so the breakdown never mixes languages.
  priceSimulator: {
    stepRoomKindOccupancy: "Room-kind occupancy",
    stepPropertyDemand: "Property-wide demand",
    stepDynamicDemand: "Dynamic demand",
    stepDynamicDemandPremium: "Dynamic demand premium",
    stepDynamicDemandDiscount: "Dynamic demand discount",
    stepMarketInfluence: "Market influence",
    stepSeasonality: "Seasonality",
    stepHoliday: "Holiday",
    stepHolidayNamed: "Holiday ({name})",
    stepHolidaySimulated: "Holiday (simulated)",
    stepBridgeDay: "Bridge-day premium",
    stepShoulderDay: "Shoulder day",
    stepLeadTime: "Lead-time curve ({days}d)",
    stepLeadTimeDetailed: "Lead-time curve ({days}d, {phase}, {goal})",
    stepLastMinute: "Last-minute adjustment",
    stepLastMinuteAlwaysWindow: "Last-minute (always, starts {hours}h before arrival)",
    stepLastMinuteMarket: "Last-minute (demand: {level}, {demandDepthPct}% of the curve)",
    stepLastMinuteAlwaysActive: "Last-minute (always, {hoursLeft}h left, step {step}/{totalSteps})",
    stepLastMinuteActive: "Last-minute ({hoursLeft}h left, step {step}/{totalSteps}, demand: {level}, {demandDepthPct}% of the curve)",
    stepLastMinuteHolding: "Last-minute (holding {depthPct}% of the way down, {hoursLeft}h left, demand: {level})",
    stepLastMinuteRemaining: "Last-minute (by remaining demand)",
    stepLastMinuteRemainingActive: "Last-minute ({hoursLeft}h left, step {step}/{totalSteps}, late bookings cover {coveragePct}% of free rooms)",
    stepLastMinuteRemainingHolding: "Last-minute (holding {depthPct}% of the way down, {hoursLeft}h left)",
    lastMinuteNoDemandReading: "no reading",
    stepGapFill: "Gap fill discount",
    stepPace: "Pace adjustment",
    stepPickup: "Bookings gained in recent days",
    stepDayAdjustment: "Day adjustment",
    stepDayAdjustmentNamed: "{day} adjustment",
    stepRounding: "Rounded to nearest {granularity}",
    // Vocabulary interpolated into the step labels above.
    phaseFarOut: "far out",
    phaseBuildup: "buildup",
    phaseHold: "hold",
    phaseDecline: "decline",
    goalRevenue: "revenue",
    goalOccupancy: "occupancy",
    goalHybrid: "Optivo recommended",
    dayFriday: "Friday",
    daySaturday: "Saturday",
    daySunday: "Sunday",
    // Base price explanation shown under the base rate row.
    baseRate: "Base rate: {basePrice}",
    baseNoHistory: "No historical base price available.",
    baseOccupancyBlend:
      "Base price {basePrice} adjusted by occupancy blend: {signals} → {floor} + ({basePrice} − {floor}) × {ratio} = {result}  ·  floor: {floor}",
    // Historical drafts only — see `howPaced` above. Kept so a draft saved
    // while the blend measured against a booking-curve expectation still
    // renders the sentence it was priced with.
    baseOccupancyBlendPaced:
      "Base price {basePrice} adjusted by occupancy blend: {signals} → {floor} + ({basePrice} − {floor}) × {ratio} = {result}  ·  floor: {floor}  ·  expected {expectedOccupancyPct}% by now (target {targetOccupancyPct}%)",
    baseHold:
      "Base price {basePrice} held (revenue/hybrid before the season booking window; occupancy discount suppressed)",
    baseHoldPeakNight:
      "Base price {basePrice} held (revenue/hybrid before the weekend and holiday booking window; occupancy discount suppressed)",
    baseLastMinuteHold:
      "Base price {basePrice} held (last-minute pricing is what discounts this night, so its occupancy discount stood down)",
    // The simulator has no capture to follow, so it stands in a median equal
    // to the cheapest room type's base price and says so.
    baseMarketFollow:
      "Base price {basePrice} set from the market: median {marketMedian} {position}%, ×{ladder} for this room type. The simulator assumes a market median equal to your cheapest room type's base price; live pricing uses the captured one.",
    baseSignalRoomKind: "room kind {occupancyPct}% ({weightPct}%w, ratio {ratio})",
    baseSignalProperty: "property {occupancyPct}% ({weightPct}%w, ratio {ratio})",
    // Guardrail rows under the final price.
    minPriceFloor: "Min price floor",
    minPriceHighDemand: "Min price (high demand)",
    // The floor row names the bound it shows, as the explanation's floor row
    // does — a held base price is not the minimum.
    basePriceFloor: "Base price floor",
    lastMinuteMinimum: "Last-minute minimum",
    // A minimum that follows Seasonality, moved by the simulated month.
    minPriceFloorSeason: "Min price floor (Seasonality {seasonSigned}%)",
    minPriceHighDemandSeason: "Min price (high demand, Seasonality {seasonSigned}%)",
    lastMinuteMinimumSeason: "Last-minute minimum (Seasonality {seasonSigned}%)",
    maxPriceCeiling: "Max price ceiling",
    // Panel chrome.
    months: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
    demandCalm: "Calm",
    demandNormal: "Normal",
    demandHot: "Hot",
    // The forecast moves a price ten days ahead and pricing runs a year and a half ahead,
    // so most simulated dates have no weather of their own to read.
    weatherLabel: "Weather",
    weatherNone: "As captured",
    weatherPleasant: "Pleasant",
    weatherExtreme: "Extreme",
    propertyProfile: "Property profile",
    calculating: "Calculating…",
    empty: "Change strategy parameters to preview pricing.",
    decreaseDaysUntilArrival: "Decrease days until arrival",
    increaseDaysUntilArrival: "Increase days until arrival",
  },

  // ── Settings ────────────────────────────────────────────
  settings: {
    // Competitor tracking card (included in the subscription, opt-in)
    compsetCardTitle: "Competitor tracking",
    marketLocationLabel: "Property location",
    marketLocationHint:
      "Sets the timezone pricing evaluates your day in, and ties competitor and demand signals to where the property actually is.",
    marketLocationSaved: "Property location saved",
    marketLocationSaveFailed: "Could not save property location",
    compsetSaved: "Competitor tracking updated",
    compsetSaveFailed: "Could not update competitor tracking",
    // Where competitor prices come from: Optivo's market data or the
    // property's own Lighthouse account. Lighthouse is named only here and in
    // its dialog, being the operator's own account; where Optivo's data comes
    // from is never named.
    lighthouse: {
      sourceTitle: "Where competitor prices come from",
      optivoOption: "Optivo's market data",
      optivoHint: "Properties around your location, read by Optivo. Included in your subscription.",
      lighthouseOption: "Your Lighthouse account",
      lighthouseHint: "The competitor set you keep in Lighthouse, read once a day. Needs your Lighthouse API token or client credentials.",
      sourceSaved: "Competitor price source updated",
      sourceSaveFailed: "Could not change where competitor prices come from",
      linkedSet: "set “{name}” ({hotels})",
      linkedSetUnnamed: "set {id} ({hotels})",
      setHotels: { one: "{count} hotel", few: "{count} hotels", other: "{count} hotels" },
      linkedToken: "token …{hint}",
      linkedClient: "client ID …{hint}",
      // Where the backfill of past prices stands, under the linked block.
      history: {
        pending: "Past prices will be read over the coming days.",
        running: "Reading past prices, a week at a time.",
        done: "A year of past prices has been read.",
        unavailable: "Your Lighthouse plan does not include past prices.",
        error: "Reading past prices stopped. Save the link again to start it over.",
      },
      // Where the daily read of the submarket's forecast demand stands, under
      // the history line. It names the set, never the source.
      demand: {
        none: "Market demand not read yet.",
        read: "Market demand read {date}.",
        unavailable: "Market demand is not available on this subscription.",
        error: "Market demand read failed; it is tried again tomorrow.",
      },
      pricesFrom: "prices from {when}",
      pricesNone: "no prices read yet",
      change: "Change",
      unlink: "Unlink",
      unlinkTitle: "Unlink your Lighthouse account?",
      unlinkDescription: "Competitor prices from Lighthouse are deleted from Optivo. Optivo's market data is used again.",
      unlinkConfirm: "Unlink",
      unlinkKeep: "Keep the link",
      unlinked: "Lighthouse account unlinked",
      unlinkFailed: "Could not unlink the Lighthouse account",
      dialogTitle: "Link your Lighthouse account",
      dialogDescription: "Optivo reads the competitor set you keep in Lighthouse once a day and prices with it.",
      authKindLabel: "How Optivo signs in to your account",
      authToken: "API token",
      authClient: "Client credentials",
      tokenLabel: "Lighthouse API token",
      tokenHint: "Ask Lighthouse support for an API token for your account. It is stored encrypted and never shown again.",
      clientIdLabel: "Client ID",
      clientSecretLabel: "Client secret",
      clientHint: "Lighthouse issues some accounts a client ID and secret instead of a token. Both are stored encrypted; the secret is never shown again.",
      checkToken: "Check token",
      checkCredentials: "Check credentials",
      back: "Back",
      checking: "Checking…",
      hotelLabel: "Which of your Lighthouse hotels is this property?",
      hotelNoRates: "No rate data in Lighthouse for this hotel.",
      compsetLabel: "Competitor set",
      compsetOption: "{name} ({hotels})",
      noHotels: "This token reads no hotel.",
      linkAndSwitch: "Link and switch",
      linking: "Linking…",
      linked: "Lighthouse account linked. Competitor prices are read from it.",
      errors: {
        token_rejected: "Lighthouse did not accept these credentials. Check them and try again.",
        token_unreadable: "The stored credentials cannot be read on this server. Enter them again under Change.",
        rate_limited: "Lighthouse is refusing more requests for now. Try again in a while.",
        no_rates_subscription: "This hotel has no rate data in Lighthouse, so no competitor prices can be read for it.",
        history_unavailable: "Your Lighthouse plan does not include past prices.",
        demand_unavailable: "Your Lighthouse plan does not include market demand.",
        unavailable: "Lighthouse could not be reached. Try again later.",
        not_linked: "No Lighthouse account is linked to this property.",
      },
    },
    title: "Property Manager Settings",
    description: "Configure your Property Management integration and application settings",
    pmsTitle: "Property Management Integration",
    connectionName: "Connection Name",
    connectionNamePlaceholder: "e.g. My Hotel, Beach Resort",
    setupRequired: "Setup required",
    pmsDescription:
      "Choose your Property Management System and enter the credentials Optivo will use to read hotel data.",
    // In place of the description, for anybody but an owner of the property's
    // account: the server shows the credentials to owners only.
    pmsOwnerOnly:
      "Only an owner of this property can see or change the credentials Optivo uses to read its hotel data. If the connection needs attention, ask an owner.",
    connected: "Connected",
    disconnected: "Disconnected",
    testing: "Testing...",
    notTested: "Not tested",
    previoApiBase: "Previo API Base URL",
    login: "Login (Email)",
    password: "Password",
    hotelId: "Hotel ID",
    languageId: "Language ID",
    placeholderEmail: "your-email@example.com",
    placeholderPassword: "Enter your Previo password",
    placeholderHotelId: "e.g. 731",
    lastHealthCheck: "Last health check:",
    testAndSave: "Save and Test",
    notesTitle: "Configuration Notes",
    notesDescription: "Important information about API integration",
    notesSecurity:
      "Your Property Management credentials are stored per-user and are never shared between accounts. Passwords are masked when displayed.",
    notesBackend:
      "All Previo API requests are proxied through the backend service for security.",
    notesHealth:
      "The connection to Previo is verified on every login. If the connection is unhealthy you will be prompted to re-enter your credentials.",
    securityLabel: "Security:",
    backendLabel: "Backend:",
    healthLabel: "Health checks:",
    toastConnectionSuccess: "Connection successful",
    toastConnectionSuccessDesc: "Successfully connected to Previo API",
    toastConnectionFailed: "Connection failed",
    toastConnectionFailedDesc:
      "Please check your credentials and try again",
    toastPmsSaved: "Property Management credentials saved",
    toastPmsSavedDesc:
      "Your Previo API credentials have been saved. Testing connection...",
    toastPmsSaveFailed: "Failed to save Property Management credentials",
    toastConnectedAndSaved: "Connected and saved",
    toastConnectedAndSavedDesc: "Credentials verified and saved.",
    toastConnectionFailedNotSaved:
      "Connection failed \u2014 credentials not saved",
    currency: "Property currency",
    currencyHelper:
      "Prices generated for this property are stored and displayed in this currency.",
    currencyNames: {
      CZK: "Czech koruna",
      EUR: "Euro",
      USD: "US dollar",
    },
    exchangeRatesTitle: "Exchange rates",
    ratesBase: "Base",
    ratesFetchedAt: "Updated:",
    ratesNeverFetched: "never",
    ratesRefresh: "Refresh",
    ratesSource:
      "Rates refreshed once a day from exchangerate-api.com. Used to convert revenue and pricing across CZK, EUR and USD.",
    ratesEditLabel: "Edit rates manually",
    ratesManualBadge: "manual",
    ratesPerUnitLabel: "1 {code} =",
    ratesSaveManual: "Save rates",
    ratesResetAuto: "Reset to auto",
    ratesManualHelper:
      "Enter how many CZK equal 1 unit of the target currency. Manual rates are not overwritten by the daily refresh.",
    toastRatesRefreshed: "Exchange rates refreshed",
    toastRatesRefreshFailed: "Failed to refresh exchange rates",
    toastRatesSaved: "Manual exchange rates saved",
    toastRatesSaveFailed: "Failed to save exchange rates",
    toastRatesReset: "Manual overrides cleared",
    toastRatesResetFailed: "Failed to clear manual overrides",
    currencySwitchWarning:
      "Changing to {to} converts the pricing settings stored for this property \u2014 base, minimum and maximum prices, occupancy steps and last-minute floors \u2014 and its price history from {from} at the current exchange rate, and discards the price drafts already generated in {from}.",
    currencySwitchTitle: "Change property currency to {to}?",
    currencySwitchDescription:
      "Optivo converts the pricing settings stored for this property from {from} to {to} at the current exchange rate, and restates its price history at the same rate so that it reads in {to} too. Price drafts already generated are discarded rather than converted \u2014 a draft carries no currency of its own, so it can only be read in the one it was priced in. New drafts are generated in {to} on the next pricing run. Switching back later does not bring the discarded drafts back, and converts the numbers a second time.",
    currencySwitchConfirm: "Change currency and discard drafts",
    currencySwitchCancel: "Keep {from}",
    currencyChangedTitle: "Currency changed",
    currencyChangedDescription:
      "The property currency was changed. Your stored pricing settings and price history were converted at the current exchange rate, and the price drafts generated in the old currency were discarded. Re-running the onboarding wizard derives the numbers from your booking history in the new currency instead of converting them.",
    currencyChangedConfirm: "Re-run wizard",
    currencyChangedCancel: "Not now",
    appearance: {
      title: "Appearance",
      description: "Choose how Optivo looks. \"Auto\" matches your connected PMS so the dashboard feels like an extension of it.",
      auto: "Auto (match my PMS)",
      autoHint: "Currently showing: {skin}",
      optivo: "Optivo",
      previo: "Previo",
      betterHotel: "Better Hotel",
      accessibilityGroup: "Accessibility",
      highContrast: "High contrast",
      highContrastHint: "For projectors and low-quality screens. Larger text, maximum contrast.",
      saved: "Appearance saved.",
      saveFailed: "Could not save appearance.",
    },
  },

  // ── Strategies ──────────────────────────────────────────
  strategies: {
    title: "Pricing Strategies",
    description: "Define and manage your pricing strategies",
    activeStrategy: "Active Strategy",
    activeLabel: "Active:",
    addNew: "Add New Strategy",
    editStrategy: "Edit Strategy",
    createDesc: "Create a new pricing strategy",
    updateDesc: "Update the strategy details",
    nameLabel: "Strategy Name",
    namePlaceholder: "e.g., Summer Peak Season",
    descriptionLabel: "Description",
    descriptionPlaceholder: "e.g., Apply higher rates during summer months",
    // Shown for a strategy that carries no description of its own. Onboarding
    // seeds one without wording so this renders in the operator's language.
    pacingDefaultDescription: "Booking pacing strategy: tracks how occupancy, base price and minimum acceptable price are pacing against your own history, and moves nightly rates according to whether bookings are ahead, on track, or behind.",
    noDescription: "No description yet.",
    updateButton: "Update Strategy",
    saveAsNew: "Save as New Strategy",
    // Saving an existing strategy mints a version rather than rewriting the
    // one that is pricing the property, so the label says what it does.
    saveAndActivate: "Save and Activate",
    saveAndActivateHint: "Saves your changes as a new version, activates it, and keeps the previous version so you can switch back.",
    versionLabel: "Version {version}",
    versionHistoryNote: "Every save keeps the version it replaced. The last {limit} versions of a strategy are kept — activate one to put it back in charge of your prices.",
    switchBackAction: "Switch back",
    addButton: "Add Strategy",
    activate: "Activate",
    deactivate: "Deactivate",
    noStrategies: "No strategies defined yet.",
    editButton: "Edit",
    backToStrategies: "Back to strategies",
    cancelEdit: "Cancel Edit",
    discardChangesTitle: "Discard unsaved changes?",
    discardChangesDesc: "You have unsaved changes in the strategy form. Are you sure you want to close it? All changes will be lost.",
    discardChangesAction: "Discard",
    // The screen is otherwise unchanged by a save, so this is where the
    // operator is told a version they can return to now exists — and the one
    // place they can act on it without hunting for the list.
    toastVersionSaved: "Saved and activated",
    toastVersionSavedDesc: "Version {version} is pricing your property now. Version {previous} is kept — switch back to it if these prices are not right.",
    toastVersionActivated: "Switched to version {version}",
    toastStrategyActivated: "Strategy activated",
    toastStrategyActivatedDesc: "Prices are being recalculated with this strategy.",
    rulesLabel: "Rules:",
    // Polling / Pacing monitor
    pollingStatus: "Booking Pace Monitor",
    pollingDescription: "The system checks for new reservations {frequency} and recalculates prices for affected dates.",
    refreshComplete: "Prices refreshed.",
    refreshError: "Failed to refresh prices.",
    refreshSlow: "Still working — your property management system is taking longer than usual to respond.",
    refreshTimedOut: "Price generation timed out. Your property management system did not respond in time — check the connection and try again.",
    regeneratingPrices: "Regenerating prices with updated strategy…",
    lastPoll: "Last Update",
    nextPoll: "Next Update",
    status: "Status",
    monitoringActive: "Monitoring is active",
    monitoringPaused: "Monitoring is paused",
    lastChangedDates: "Last Changed Dates",
    // Saving replaces the property's whole strategy set, so two people in the
    // editor at once means one of them loses their work. The banner names who
    // is there and says what a save does — a warning that only says "careful"
    // gives the operator nothing to act on.
    concurrentEditors: "Someone else has this strategy open: {editors}. Whoever saves last replaces the other edits.",
    concurrentEditorUnknown: "another user",
    // ── The strategy list (`strategies/StrategyList.tsx`) ───
    // Versions share the strategy's name, so a version is told apart by its
    // number and by when it was saved.
    savedAt: "Saved {date}",
    versionsTitle: "Versions",
    noActiveStrategy: "No strategy is pricing your property. Activate one below.",
    otherStrategiesTitle: "Other strategies",
    otherStrategiesDescription: "Kept, but not pricing your property. Activating one recalculates your prices with it.",
    // One phrase per plural form, read through `pluralForm`.
    earlierVersionsOne: "{count} earlier version",
    earlierVersionsFew: "{count} earlier versions",
    earlierVersionsOther: "{count} earlier versions",
    // What each row's actions are called for a screen reader and in tests:
    // twenty buttons all named "Delete" are one button to a screen reader.
    activateVersion: "Activate version {version}",
    editVersion: "Edit version {version}",
    deleteVersion: "Delete version {version}",
    activateNamed: "Activate {name}",
    editNamed: "Edit {name}",
    deleteNamed: "Delete {name}",
    deleteVersionTitle: "Delete version {version}?",
    deleteVersionDescription: "Version {version} of “{name}” will be deleted, and you will no longer be able to switch back to it.",
    deleteStrategyTitle: "Delete this strategy?",
    deleteStrategyDescription: "“{name}” will be deleted.",
    deleteStrategyWithVersionsDescription: "“{name}” will be deleted with all {count} of its versions.",
  },

  // ── PMS Sync Status ─────────────────────────────────────
  pmsSync: {
    title: "Property Management Price Sync",
    description: "See how your draft prices are pushed to the Property Management system.",
    autoSyncEnabled: "Auto-sync is enabled",
    autoSyncDisabled: "Auto-sync is disabled",
    nextSync: "Next Update",
    // The cadence itself comes from the shared `syncCadence` table.
    runsEvery: "Runs {cadence}",
    lastUpdate: "Last Update",
    noUpdatesYet: "No updates sent to the Property Management system yet.",
    entriesSent: "{count} entries sent",
    status: "Status",
    completed: "Completed",
    failed: "Failed",
    // The last upload landed, short of what Optivo itself left unwritten. Said
    // of a send and of a restore alike: it claims nothing of what becomes of
    // a held night, which the Upload Log says under the upload's own row.
    heldBack: "Held back: {count}",
    configureAutoSync: "Configure Auto-Sync",
    viewUploadLogs: "View Upload Logs",
    enableAutoSync: "Enable auto-sync in the Connection Manager to automatically push prices to the Property Management system.",
  },

  // ── Strategy Parameters ─────────────────────────────────
  // ── Sidebar sync indicator ───────────────────────────────
  syncStatus: {
    state: {
      good: "In sync",
      manual: "Manual sending",
      syncing: "Syncing…",
      pending: "First sync soon",
    },
    issueShort: {
      lastRunFailed: "Last run failed",
      lastRunInterrupted: "Run interrupted",
      overdue: "Behind schedule",
      pmsUnreachable: "Cannot reach PMS",
      lastPushFailed: "Price send failed",
      lastPushHeldBack: "Part held back",
      pollerPaused: "Syncing paused",
    },
    issueLong: {
      lastRunFailed: "The last sync ran into a problem. Optivo will try again on the next run.",
      lastRunInterrupted: "The last sync did not finish — most likely an update restarted Optivo mid-run. The next run will complete it.",
      overdue: "The last check is older than the sync cadence allows. Optivo will catch up on the next run; if this keeps happening, contact support.",
      pmsUnreachable: "{provider} could not be reached with the saved credentials. Check the connection.",
      lastPushFailed: "The last price update did not reach {provider}. The upload log has the details.",
      lastPushHeldBack: "Optivo left part of the last send unwritten and tries it again with the next one. {provider} refused nothing. The upload log has the reason.",
      pollerPaused: "Background syncing is paused on the server, so bookings are not being read and prices are not being sent.",
    },
    // The same kind where the last upload put a price list back: a restore, or
    // a retry of one. What a held night means is the same of any upload. What
    // becomes of it is not: a restore commits no drafts, so nothing tries its
    // night again, and the send's "tries it again with the next one" promised
    // a retry nothing would make. Said only where the status says the upload
    // was a restore (`push.lastRestoresPriceList`). A kind not listed here is
    // explained by `issueLong` whatever the upload was.
    issueLongRestore: {
      lastPushHeldBack: "Optivo left part of the last restore unwritten. {provider} refused nothing. Optivo does not try a restore again, so the nights left unwritten keep the minimum stay they have now. To put the saved ones back, restore again later or set them in {provider}. The upload log has the reason.",
    },
    // An issue kind neither table above lists. The page and the server deploy
    // separately and a tab stays open for days, so a kind the server gains is
    // met by pages that predate it. `short` is a rail headline: 18 characters
    // at most.
    unknownIssue: {
      short: "Needs attention",
      long: "Optivo reports something this version of the page cannot describe. Reload the page to see what it is.",
    },
    // A verdict `state` does not list, or a status the indicator could not
    // draw. Either way the page cannot say how the sync is doing, so it claims
    // neither a fault nor that all is well. `short` is a rail headline (18
    // characters at most) and `reload` the line under it.
    unknownState: {
      short: "Status unknown",
      reload: "Reload the page",
      long: "Optivo reports a state this version of the page cannot describe. Reload the page to see it.",
    },
    summary: {
      good: "Bookings are being read and prices kept up to date. Nothing to do.",
      manual: "Bookings are being read, but prices are only sent when you send them.",
      syncing: "Reading bookings and updating prices.",
      pending: "Nothing has been read yet.",
    },
    flow: {
      inbound: "Bookings in",
      outbound: "Prices out",
      next: "Next run",
    },
    manualTag: "manual",
    notYet: "not yet",
    paused: "paused",
    prices: "{count} prices",
    heldBack: "{count} held back",
    actions: {
      checkConnection: "Check connection",
      uploadLog: "Upload log",
      syncSettings: "Auto-sync settings",
    },
    // Tonight in the rail, read off the status's `tonight`
    // (`components/pms/tonightReading.ts`). `pricing` is the indicator's third
    // line: what Optivo did with tonight's prices, a word for each pricing
    // action the server can answer with (`services/outlookApi.ts`), so a new
    // action needs its word in both files. Each is scoped to today, because
    // "Holding prices" alone under a hotel's name reads as the hotel holding
    // them, and keeps what the dashboard's sentence for that action names
    // (`dashboard.outlook.pricingStatus`): its verb, the lock for `locked`,
    // sold out for `nothingToSell`. `market` follows it on the days the
    // market is not ordinary, and has a word for those alone: a favourable or
    // a calm market is not said on the rail. `stale` stands in the line's
    // place over prices more than a day old. `title` heads the popover's
    // section, whose sentences are the dashboard's own but for `manual`.
    //
    // A pricing word and a market word share one line of the rail, 215px at
    // the 18px root, so a word is measured before it is lengthened. The
    // widest English pair, "Discounting today · Strong market", is 205px in
    // the default skin. Under Better Hotel's Poppins it is 229px, and four
    // English pairs there say the pricing word alone: the line drops a market
    // that does not fit whole, never cut. Every Czech pair fits in every skin.
    tonight: {
      title: "Today",
      pricing: {
        discounting: "Discounting today",
        holding: "Holding today",
        raising: "Raising today",
        adjusting: "Fine-tuning today",
        locked: "Locked today",
        nothingToSell: "Sold out today",
      },
      market: {
        strong: "Strong market",
        soft: "Soft market",
        weak: "Weak market",
      },
      stale: "Prices over a day old",
      // The popover's sentence where prices do not leave on their own. The
      // indicator's own: the dashboard's speaks of "the outlook", which the
      // popover has none of on screen.
      manual: "Prices are not sent on their own: this is Optivo's plan until you send it.",
    },
  },

  strategyParams: {
    title: "Pricing Parameters",
    expertMode: "Expert mode",
    expertModeHint: "Show the advanced sections too. Everything hidden already has a working default, so you can come back to it later.",
    expertSectionsHidden: "{count} advanced sections hidden",
    expertSectionReveal: "I'm an expert — show advanced settings",
    expertSectionHide: "Hide advanced settings",
    // ── Finding your way around the editor ─────────────────
    // A navigator beside one open section (`settings/StrategyEditorNav.tsx`).
    sectionNavLabel: "Strategy sections",
    summaryTitle: "Summary",
    summaryDescription: "What this strategy sets, section by section. Open a section from its row.",
    sectionPosition: "{position} of {count}",
    previousSection: "Previous",
    nextSection: "Next",
    previousSectionLabel: "Previous section",
    nextSectionLabel: "Next section",
    sectionPagerLabel: "Previous and next section",
    sectionSwitcherLabel: "Choose a section",
    // The fold toggle at the head of the navigator, as the app menu's.
    sectionNavCollapse: "Collapse section list",
    sectionNavExpand: "Expand section list",
    unsavedChangesMark: "Unsaved changes",
    // One phrase per plural form, read through `pluralForm`; flat because
    // this group is also read as a bag of strings.
    unsavedSectionsCountOne: "{count} section changed",
    unsavedSectionsCountFew: "{count} sections changed",
    unsavedSectionsCountOther: "{count} sections changed",
    unsavedSectionsHeading: "Not saved yet. Saving creates a new version.",
    savesImmediately: "Saved as you edit, for every version of this strategy.",
    savesSeparately: "Saved with its own button, for every version of this strategy.",
    setupFixBasePrices: "Set the base prices",
    setupMissingBasePrices: "Base prices are required before Optivo can calculate suggestions.",
    setupPartialBasePrices: "Some room types still need a base price.",
    setupSourceUser: "Customized",
    setupSourceAnalysis: "Analysis",
    setupSourceQuestionnaire: "Questionnaire",
    setupSourceTypology: "Typology preset",
    setupSourceSystem: "System default",
    setupSourceUnanswered: "Question unanswered",
    sectionQuestionError: "Could not apply that answer. Please try again.",
    // ── Section descriptions ───────────────────────────────
    // One line per collapsible section, saying what the operator can decide
    // inside it. Rendered under the title so the collapsed list reads as a
    // table of contents rather than a row of nouns.
    marketInfluenceSectionDesc: "Decide whether competitor rates and the weather forecast move your prices, and how hard each one pulls.",
    strategySectionDesc: "Pick the goal Optivo prices toward, and how strongly rates react to occupancy, booking pace and lead time.",
    bookingWindowSectionDesc: "How long before arrival Optivo holds each room type's base price, and from when it may discount to fill a night.",
    roomKindStructureSectionDesc: "Rename, reorder or hide the room types your PMS delivers, and set how many rooms and beds each one has.",
    roomKindOverridesSectionDesc: "Set the base, minimum and maximum price of every room type, and give a single type its own goal or price curve.",
    roomStructureSectionDesc: "Set what one, two or more guests pay in the same room, and what an extra bed adds to the night.",
    priceGranularitySectionDesc: "Choose the rounding step every suggested price lands on, so rates come out in whole, believable numbers.",
    distributionSectionDesc: "Set what a Friday, Saturday and Sunday night is worth next to an ordinary midweek night.",
    channelMappingSectionDesc: "Push a channel-specific price into its own Previo rate plan, adjusted per demand level.",
    nonRefundableSectionDesc: "Offer a discounted, non-cancellable rate on its own rate plan, deepest on the nights that are selling slowest.",
    gapFillingSectionDesc: "Discount the orphan nights stranded between two reservations, and set how wide a gap still qualifies.",
    lastMinuteSectionDesc: "Decide how prices behave in the final days before arrival, and how low they may go to fill the room.",
    seasonalitySectionDesc: "Mark each month as high, low or common season, and set what those months add to or take off the price of every night in them.",
    rateRestrictionsSectionDesc: "Set the minimum length of stay, the weekend and holiday rule, and when it is released before arrival.",
    holidaysSectionDesc: "Load public holidays for your countries and set a premium or discount for each date, including your own.",
    bridgeDaysSectionDesc: "Price the ordinary nights around a holiday break: the one caught inside it and the ones right before and after it.",
    marketInfluenceTitle: "Market influence",
    marketInfluenceDesc: "Let the local comp set move your nightly rate. Nights the market has never been captured for are priced without it, and say so in the simulator.",
    marketInfluenceEnabled: "Follow the local market",
    marketInfluenceStrength: "How closely to follow",
    marketInfluenceStrengthHint: "This setting scales competitor prices and nothing else — the weather has its own above. At 0% competitors are ignored. At 100% the night the market prices highest or lowest moves your rate by about 6%, and by about 9% where the competitors have also sold out. Whatever the weather, school holidays and competitors say together, a rate never moves by more than 15% — a bound that limits both the effect of bad competitor data and the price signalling risk of following a market you also compete in.",
    weatherInfluenceEnabled: "Let weather move rates",
    weatherInfluenceStrength: "How much weather counts",
    weatherInfluenceStrengthHint: "Weather works whether or not you track competitors, and this is the only setting that scales it. At 0% the forecast is ignored in pricing, but you are still shown when extreme weather is coming. At 100% a storm-hit night is discounted by up to about 7%.",
    weatherExtremesHint: "Heatwaves, hard frost, downpours, snowstorms, gales, thunderstorms and freezing rain are read as lowered demand, and they are what discounts a night noticeably. Ordinary weather only tilts a rate by a percent or two: up on a day warmer than is usual for its date, down on a colder or a wet one. The temperature is read against what is usual for the time of year, so an ordinary day leaves the price alone in January and in July alike, and 10 °C either way counts in full. The forecast moves prices for the next 10 days; what is forecast further out is only shown. To lower a weekend by more than that, add it under Holidays & Special Dates with a negative adjustment.",
    marketFollowEnabled: "Set my base price from the market",
    marketFollowPosition: "Position against the market",
    marketFollowHint: "On nights Optivo holds a market reading — the median of at least 5 priced properties ticked in the competitor set below — that median becomes the base price of your cheapest room type, moved by this figure: +5% sits five percent above the market, −5% below it. The median is what a competitor's cheapest room sells for, so your other room types keep their usual spacing above it.",
    marketFollowStandDownHint: "Weekend, holiday, season and booking-window adjustments stand down on those nights, because the market's price already carries them; your own occupancy, booking pace and last-minute rules still apply, inside your minimum and maximum prices. Nights without a reading are priced by your strategy alone.",
    marketFollowExample: "At {position}%, a night the market prices at {median} {currency} opens at {price} {currency} for your cheapest room type.",
    compsetFilterTitle: "Competitor set",
    compsetFilterDesc: "Everything a place search finds around your property is listed below. The ticked properties are your competitor set: market influence reads the median of their prices, and following the market sets your base price from it. Rules keep working as the market changes; tick or untick any property to override them.",
    compsetFilterUnratedNote: "Properties captured without a type, rating, review count or price are kept — missing data is not a reason to exclude them. Untick them individually if you want them out.",
    compsetFilterLoadError: "Could not load the competitor set.",
    compsetFilterSaveError: "Could not save the competitor set. Your last change was undone.",
    keyMetrics: "Key Metrics",
    occupancyRate: "Occupancy",
    bookingWindowTitle: "Booking Window",
    bookingWindowTypicalDaysLabel: "Typical booking window (days)",
    bookingWindowTypicalDaysHint: "Until this many days before arrival, Optivo keeps a night at least at its base price — moved by its weekday, season and holidays — however slowly it sells. Inside the window it may discount to fill it. So a longer window lets discounts start earlier, and a shorter one holds the price longer. Under Maximize Occupancy no price is held, and the window only times the price movement.",
    bookingWindowAnalysisHint: "Analysis: {days} days",
    bookingWindowReset: "Reset to analysis value",
    bookingWindowNoRoomKinds: "No room kinds available yet. Re-run onboarding to populate room kinds.",
    bookingWindowRoomKindColumn: "Room kind",
    bookingWindowTypicalColumn: "Typical",
    bookingWindowDaysSuffix: "days",
    bookingWindowSeasonReadout: "Booking window by season",
    bookingWindowSeasonHigh: "High season",
    bookingWindowSeasonLow: "Low season",
    bookingWindowSeasonHint: "Every date is in high or low season. The seasons are weeks, not months: high season is the weeks your booking history marks as busy, and it has nothing to do with the months set in Seasonality. Each season is measured the same way over its own nights, so the measured typical window lies between the two. A room type with too few bookings of its own shows the whole property's windows; one with too few in a season shows a dash there and uses its typical window.",
    leadTimeCurveTitle: "Price movement across the booking window",
    leadTimeCurveHint: "Where the price sits at three points of the booking window: while the stay is still far out, around the demand peak, and as arrival nears. A positive percentage raises the price, a negative one lowers it — each point is yours to set in either direction. The percentages apply globally; the timing of each phase adapts to each room kind's own booking window (Typical / High season / Low season).",
    leadTimeCurveStartPct: "Far-out adjustment",
    leadTimeCurveStartPctHint: "Applied while the stay is beyond the typical booking window — the starting point of the price curve.",
    leadTimeCurvePeak: "Peak-demand adjustment",
    leadTimeCurvePeakHint: "Applied around each room kind's booking window peak, where the curve reaches its furthest point.",
    leadTimeCurveArrival: "Near-arrival adjustment",
    leadTimeCurveArrivalHint: "Applied as the arrival date approaches within each room kind's booking window — where the curve ends.",
    leadTimeCurveReset: "Reset to default",
    leadTimeCurvePreviewTitle: "Curve preview",
    leadTimeCurvePreviewX: "Days before arrival",
    leadTimeCurvePreviewY: "Adjustment",
    leadTimeCurvePreviewBuildup: "Booking builds up",
    leadTimeCurvePreviewArrival: "Arrival",
    seasonality: "Seasonality",
    seasonClassificationHint: "Mark each month as High, Low, or Common. High and Low months apply their own price adjustment; Common months are neutral.",
    seasonFromAnalysisTag: "From analysis",
    highSeason: "High",
    lowSeason: "Low",
    commonSeason: "Common",
    highSeasonMultiplier: "High Season Adjustment",
    lowSeasonMultiplier: "Low Season Adjustment",
    seasonMonthAdjustment: "{month} adjustment",
    seasonResetToAnalysis: "Reset to analysis",
    // The year, drawn. Same three series as the day-of-week chart, so an
    // operator who has read one already knows how to read this one.
    seasonChartTitle: "Your year",
    seasonChartConfigured: "Your price",
    seasonChartHistory: "What you sold for",
    seasonChartPattern: "Your seasonal pattern",
    seasonChartAxisMoney: "{currency} per night",
    seasonChartAnchorNamed: "Priced from your {roomKind} base price: {price} {currency}",
    seasonChartAnchor: "Priced from your base price: {price} {currency}",
    seasonChartAnchorPick: "Priced from the base price of",
    seasonChartAnchorOption: "{roomKind} · {price} {currency}",
    distribution: "Day Adjustments",
    weekendPremium: "Day Adjustments",
    weekendDiscount: "Day Adjustments",
    mondayAdjustment: "Monday Adjustment",
    tuesdayAdjustment: "Tuesday Adjustment",
    wednesdayAdjustment: "Wednesday Adjustment",
    thursdayAdjustment: "Thursday Adjustment",
    fridayAdjustment: "Friday Adjustment",
    saturdayAdjustment: "Saturday Adjustment",
    sundayAdjustment: "Sunday Adjustment",
    dayAdjustmentsHint: "Use positive percentages for premiums and negative percentages for discounts. On special dates the day-of-week adjustment is skipped — only the special-day premium applies.",
    dayAdjustmentsAnalysisValue: "Analysis: {value}",
    dayAdjustmentsAnalysisNights: "{nights} nights",
    dayAdjustmentsAnalysisEvidence: "Derived from {nights} room nights across {months} months, each room type compared with itself at the same number of guests.",
    dayAdjustmentsAnalysisHolidays: "Stays over a public holiday or special date are left out ({nights} room nights): Holidays & Special Dates prices those nights.",
    dayAdjustmentsResetToAnalysis: "Reset to analysis",
    dayAdjustmentsSourceHistoric: "Your own history",
    dayAdjustmentsSourceBlended: "Your history blended with defaults",
    dayAdjustmentsSourceDefaults: "Optivo defaults",
    dayAdjustmentsStandardHint: "Set what each of these three nights is worth next to an ordinary midweek night. On a special date the special-date price replaces this adjustment, and a last-minute discount cancels it.",
    dayAdjustmentsAnchorNamed: "A Monday–Thursday night in your {roomKind}: {price} {currency}",
    dayAdjustmentsAnchor: "A Monday–Thursday night: {price} {currency}",
    dayAdjustmentsAnchorNote: "Monday to Thursday keeps this price. The three nights below are set against it.",
    dayAdjustmentsAnchorBaseNamed: "Your {roomKind} base price: {price} {currency}",
    dayAdjustmentsAnchorBase: "Your base price: {price} {currency}",
    dayAdjustmentsAnchorNoteAllDays: "Every night below is set against this price.",
    dayAdjustmentsAnchorExampleNamed: "Example: a Monday–Thursday night in your {roomKind} is {price} {currency}",
    dayAdjustmentsAnchorBaseExampleNamed: "Example: your {roomKind} base price is {price} {currency}",
    dayAdjustmentsAnchorExampleNote: "These adjustments apply to every room type, each against its own base price, which Monday to Thursday keeps. The three prices below are for this one; the others move by the same percentage.",
    dayAdjustmentsAnchorExampleNoteAllDays: "These adjustments apply to every room type, each against its own base price. The amounts below are for this one; the others move by the same percentage.",
    dayAdjustmentsMidweekHint: "Monday to Thursday open at 0 — your base price is already the midweek rate. Move one only when that weekday genuinely trades differently.",
    dayAdjustmentsChartTitle: "Your week",
    dayAdjustmentsChartConfigured: "Your price",
    dayAdjustmentsChartHistory: "What you sold for",
    dayAdjustmentsChartSuggested: "Your weekly pattern",
    dayAdjustmentsChartAxisMoney: "{currency} per night",
    dayAdjustmentsNightPrice: "{day} night price",
    dayAdjustmentsRaise: "Raise price: {day}",
    dayAdjustmentsLower: "Lower price: {day}",
    dayAdjustmentsDelta: "{money} {currency} · {percent}",
    dayAdjustmentsPercentValue: "{value}%",
    dayAdjustmentsSameAsWeekday: "Same as a midweek night",
    dayAdjustmentsHistoryMoney: "Sold for {sold} {currency} against {baseline} on an ordinary night in the same rooms",
    dayAdjustmentsAtLimit: "This is as far as a day-of-week adjustment goes. Use Seasonality or Special Dates for bigger swings.",
    dayAdjustmentsSimulatorNote: "Seasonality, demand and your price limits still apply — open the live price example to see the final rate.",
    dayAdjustmentsInheritedValue: "Property-wide: {value}",
    channelMappingTitle: "Channel → Rate Plan Mapping",
    channelMappingBasePlanUnaffected: "Base plan rates are unaffected.",
    channelMappingEnable: "Enable mapping",
    ratePlanLabel: "Rate plan",
    ratePlanPlaceholder: "Select rate plan…",
    channelAdjustmentByLevelLabel: "Adjustment per demand level",
    channelAdjustmentByLevelHint: "A demand level here is the night's overall demand against an ordinary night of this room type: the weekend, a holiday, the season, the week's history, the market, booking pace and how full the room type is, as Optivo weighed them when it last calculated the night's price. The colour on the Prices calendar is something else — it shows booking pace alone. No surcharge on ordinary nights and a higher one on the dates everyone wants makes booking direct pay off where it counts. A level left off follows the nearest level below it that has one, so this channel never ends up selling at your base price.",
    channelLevelDisabledHint: "Give this demand level its own adjustment",
    channelMappingNoChannels: "No channels configured. Re-run onboarding to detect channels from analysis.",
    channelMappingNoRatePlans: "No rate plans available from the PMS.",
    channelMappingIncomplete: "Choose a rate plan — this channel is switched on, but nothing is sent to it until it has one.",
    nonRefundableBasePlanTargetWarning: "This is the base rate plan. Prices calculated from it are sent after it, so writing them back onto it would overwrite what Optivo just sent — choose a different plan.",
    nonRefundableTitle: "Non-Refundable Pricing",
    nonRefundableRangeHint: "The discount runs as a range across the night's overall demand, read the same way as for the channel plans — deepest on the quietest nights, shrinking on the dates everyone wants.",
    nonRefundableEnable: "Enable non-refundable pricing",
    nonRefundableAdjustmentLabel: "Discount or premium",
    nonRefundableMaxOccupancy: "Maximum room type occupancy",
    nonRefundableMaxOccupancyHint: "Occupancy here is the room type's own, never the whole property's: the share of its rooms sold on the night, options and blocks not counted. A night above the ceiling gets no non-refundable rate at all.",
    nonRefundableMinDaysBeforeArrival: "Minimum days before arrival",
    nonRefundableMinDaysHint: "Neither does a night closer to arrival than the lead time.",
    nonRefundableNoRatePlans: "No rate plans available from the PMS.",
    nonRefundableNoRoomKinds: "No room kinds available yet. Re-run onboarding to populate room kinds.",
    nonRefundableRoomKindLabel: "Room kind",
    nonRefundableCopyToAll: "Copy to all",
    nonRefundableSharedRatePlanLabel: "Rate plan (shared across room kinds)",
    nonRefundableSharedRatePlanHint: "Pushes the non-refundable price to this single Previo rate plan for every enabled room kind.",
    nonRefundablePerRoomKindHeading: "Per room kind",
    nonRefundableIncomplete: "Choose a rate plan — non-refundable pricing is switched on, but nothing is sent until it has one.",
    nonRefundableAdjustmentUnitLabel: "Discount stated as",
    nonRefundableAdjustmentUnitPercent: "Percent",
    nonRefundableAdjustmentUnitAmount: "Amount",
    nonRefundableAdjustmentUnitHint: "A percentage follows the rate, so a suite gives up more than a single. A flat amount is the same money off every room type.",
    nonRefundableExcludedNightTypesLabel: "Never offer it on",
    nonRefundableExcludedNightTypesHint: "Nights that tend to sell themselves. A bridge day is the single ordinary night caught between two premium ones.",
    nonRefundableNightTypeWeekend: "Friday and Saturday nights",
    nonRefundableNightTypeHoliday: "Holidays and special dates",
    nonRefundableNightTypeBridgeDay: "Bridge days",
    nonRefundableAtMinimalDemand: "At minimal demand",
    nonRefundableAtTopDemand: "At TOP demand",
    nonRefundableDemandLadder: "What each demand level gets",
    nonRefundableNoAmount: "No amount set — Optivo would send this room type\u2019s base price to the non-refundable plan unchanged.",
    nonRefundableRoomKindColumn: "Room type",
    nonRefundableMaxOccupancyColumn: "Max room type occupancy",
    nonRefundableMinDaysColumn: "Min days",
    ratePlanNone: "No rate plan",
    ratePlanClaimedBy: "used by {holder}",
    ratePlanFreeHint: "A plan another setting already uses cannot be picked here. Set that setting to “No rate plan” to free it.",
    ratePlanConflictWarning: "{holders} also writes to this rate plan — only one of them is actually sent. Give one of them a plan of its own.",
    levelMinimal: "Minimal",
    levelLow: "Low",
    levelStandard: "Standard",
    levelElevated: "Elevated",
    levelHigh: "High",
    levelTop: "TOP",
    strategyPosture: "Strategy Posture",
    optimizationGoal: "Optimization Goal",
    revenueGoal: "Maximize Revenue",
    revenueGoalHint: "Higher rates when demand is strong, modest discounts in slow periods.",
    hybridGoal: "Optivo Recommended",
    hybridGoalHint: "Holds prices like Max Revenue before the median lead time, then switches to Max Occupancy as arrival approaches.",
    occupancyGoal: "Maximize Occupancy",
    occupancyGoalHint: "More aggressive discounting to fill rooms, smaller premiums for high demand.",
    roomKindStrategyOverridesTitle: "Prices by Room Type",
    roomKindStrategyOverridesHint: "Two kinds of setting live here, and they behave differently when left empty — each group states its own rule below.",
    roomKindStrategyOverridesEssentialHint: "Set the base, minimum and maximum price for each room type. Every rate Optivo calculates for a room type comes from these.",
    overrideUseDefault: "Use default",
    overrideGoal: "Optimization Goal",
    overrideBasePrice: "Base Price",
    overrideMinPrice: "Minimum Price",
    overrideMaxPrice: "Maximum Price",
    overrideFromAnalysisHint: "From analysis",
    overrideFromAnswersHint: "From your answers",
    overrideAnswersMismatchWarning: "The analysed base price differs by more than {pct} from the regular-day price you expect ({answer}). Check which is right.",
    overrideAnswersMismatchSummary: "The regular-day price you expect ({answer}) differs by more than {pct} from the analysed base price for: {roomKinds}. Check which is right before prices go live.",
    overrideEffectiveFromSet: "Optivo will use {value} — the price you set here.",
    overrideEffectiveFromAnalysis: "Optivo will use {value} — from this room type's own booking history.",
    overrideEffectiveFromBand: "Optivo will use {value} — {pct} of the base price.",
    overrideEffectiveFromAnswers: "Optivo will use {value} — the price you stated in your answers.",
    overrideReferenceLabel: "For reference",
    overrideBandReference: "{pct} of base price",
    overrideConsequenceFollowsAnalysis: "Nothing of your own is set here — type a price to use it instead.",
    overrideConsequenceFollowsBand: "It moves whenever the base price changes. Type a price to fix it here.",
    overrideConsequenceFixed: "Clear the field and Optivo would go back to {value}.",
    overrideConsequenceFixedNothing: "Clear the field and Optivo could not price this room type at all.",
    overridePriceBandSummary: "Optivo prices {roomKind} from {base}: the nightly rate stays between {min} and {max}.",
    overridePriceBandCaveat: "Extra guests can be priced above that range, and a last-minute minimum — if one is set — may go below {min}; no guest count is priced under the minimum that applies to the night.",
    overridePriceVatNote: "The stated price is incl. VAT for a double room (double use).",
    overrideMissingBasePriceWarning: "No base price for this room type, so Optivo will not calculate any prices for it. Enter one here.",
    overrideMissingBasePriceSummary: "No base price is set for: {roomKinds}. Optivo will not calculate any prices for these room types until you enter one.",
    roomKindOwnScopeTitle: "Prices — this room type only",
    roomKindOwnScopeHint: "Base Price is required — Optivo calculates every rate for this room type from it. No property-wide price stands behind these fields: leave it empty and Optivo uses the price derived from this room type's own history, and if there is none it cannot price this room type at all.",
    roomKindOwnScopeBadge: "Room type only",
    roomKindBasePriceRequiredBadge: "Required",
    roomKindPropertyScopeTitle: "Strategy — overrides property-wide settings",
    roomKindPropertyScopeHint: "Empty means the property-wide setting applies. Fill one in only when this room type should behave differently from the rest of the property.",
    roomKindInheritedBadge: "Property-wide",
    roomKindOverriddenBadge: "Overridden",
    roomKindOverrideReset: "Reset to property-wide value",
    roomKindLeadTimeCurveTitle: "Price movement",
    roomKindLeadTimeCurveHint: "These show the property-wide curve until you change one. Any edit gives this room type its own curve; reset puts the property-wide one back.",
    roomKindLeadTimeCurveStartPct: "Far-out adjustment",
    roomKindLeadTimeCurveStartPctHint: "Applied while the stay is beyond this room type's typical booking window.",
    roomKindDayAdjustmentsTitle: "Day of week",
    roomKindDayAdjustmentsHint: "Every night follows the property-wide week until you change it. A night you change belongs to this room type; the rest keep following, including when the property-wide week moves. Reset hands them all back.",
    overrideTierLabel: "Tier",
    tierFromBasePrice: "From Base Price",
    tierOverrideCount: "{count} room-kind override(s)",
    reactivity: "Price Reactivity",
    reactivityHint: "How big a price change Optivo may make when your occupancy and booking pace call for one. Every level reacts to the same signals — the difference is the size of the steps, up and down.",
    gentle: "Gentle",
    gentleHint: "Small steps. Prices stay close to your base price — mild premiums on strong dates, mild discounts on quiet ones. Guests see stable, predictable rates. The default, and the right fit for most properties.",
    moderate: "Moderate",
    moderateHint: "Noticeable moves. Prices follow demand visibly in both directions while staying anchored to your base price. For properties whose demand clearly swings and should show in the rate.",
    aggressive: "Aggressive",
    aggressiveHint: "Big moves. Strong premiums while dates fill up, deep discounts to rescue dates selling slowly. Captures the most from demand, but guests will see rates change a lot.",
    customLabel: "custom",
    // `occupancyScaleMax` caps the occupancy ratio, and the field states the
    // premium that cap allows on the base price (1.25 is +10 %), never the cap
    // itself — see `occupancy-premium.ts`.
    occupancyScaleMax: "Max Occupancy Premium",
    occupancyScaleMaxHint: "The most occupancy can add to a night's base price. A night earns more of it the further its occupancy runs above what Optivo aims for on that night; weekday, season and pace adjustments come on top.",
    paceLower: "Pace Floor",
    paceUpper: "Pace Ceiling",
    paceSensitivityHint: "How strongly pace deviations move the price.",
    paceFloorHint: "Minimum pace multiplier — how much the price drops when behind pace.",
    paceCeilingHint: "Maximum pace multiplier — how much the price rises when ahead of pace. It also caps how far above its base price a night opens before its first booking when its type usually sells early, such as a Saturday that sells out months ahead.",
    reactivityOccLabel: "occ.",
    reactivityPaceLabel: "pace",
    pickupInfluenceTitle: "Bookings gained",
    pickupInfluenceHint: "React to how fast a date is filling right now, not just how full it already is.",
    pickupInfluenceEnabled: "Let recent pickup move the price",
    pickupInfluenceStrength: "How closely to follow pickup",
    pickupInfluenceStrengthHint: "Higher follows recent booking speed more closely. The effect is capped at 5% of the calculated rate either way.",
    pickupWindowDays: "Measure pickup over",
    pickupWindowDaysHint: "How far back to look when asking how fast this date is filling. Shorter reacts sooner; longer is steadier.",
    pickupInfluenceGuardrail: "Pickup and pace share one limit: together they can never move a rate further than the pace floor and ceiling above already allow. Pickup stays inactive until a date has a full window of measured booking history.",
    minRoomsForRoomKindSignal: "Min. rooms for own signal",
    minRoomsForRoomKindSignalHint: "Room categories with fewer rooms than this threshold always use the whole-property signal — for occupancy, booking pace, pickup and the weekly demand signal — because a handful of rooms is too noisy to price on.",
    propertyPaceWeight: "Property pace weight",
    propertyPaceWeightHint: "Share of the booking-pace adjustment driven by the whole property (vs the selected room category). 60% means the property pace contributes 60% and the room-kind pace 40%. Room categories below the configured room threshold always use the whole-property pace.",
    propertyPaceWeightShort: "property",
    roomKindPaceWeightShort: "room kind",
    propertyDemandWeight: "Property demand weight",
    propertyDemandWeightHint: "Share of the weekly demand signal driven by the whole property (vs the selected room category). 60% means the property's week contributes 60% and the room category's own week 40%. Room categories below the room threshold always use the whole-property demand signal. In a price's explanation this is the “Weekly demand from history” row.",
    propertyDemandWeightShort: "property",
    roomKindDemandWeightShort: "room kind",
    propertyPickupWeight: "Property pickup weight",
    propertyPickupWeightHint: "Share of the pickup reading driven by the whole property (vs the selected room category). 60% means the property's pickup contributes 60% and the room category's own pickup 40%. Room categories below the room threshold always use the whole-property pickup.",
    propertyPickupWeightShort: "property",
    roomKindPickupWeightShort: "room kind",
    postureBiasTitle: "Changeover from protecting to filling",
    postureBiasDescription: "Optivo Recommended protects your rate while a date is further out than your typical booking window, then changes over to discounting it to fill. These three decide where that changeover falls, how sharply it lands, and whether a date's own booking pace may move it.",
    postureBias: "Changeover point",
    postureBiasHint: "50 changes over at your typical booking window; lower holds the rate through more of the window, higher starts filling sooner. At 0 it behaves exactly like Maximize Revenue, at 100 exactly like Maximize Occupancy.",
    postureBiasBalanced: "changes over at your typical booking window",
    postureBiasRateLeaning: "leans towards protecting your rate",
    postureBiasFillLeaning: "leans towards filling",
    postureBiasLikeRevenue: "same as Maximize Revenue",
    postureBiasLikeOccupancy: "same as Maximize Occupancy",
    hybridChangeoverDays: "Ease the changeover in over",
    hybridChangeoverDaysHint: "0 switches on the day. A wider window eases the rate from protecting to filling instead of stepping, so a guest checking twice does not see a jump.",
    hybridChangeoverDaysUnit: "days either side",
    hybridPacePull: "Let the date's own pace move it",
    hybridPacePullHint: "0 keeps the changeover on the calendar. Above it, a date already selling well keeps its rate past the changeover and a date selling badly starts filling before it.",
    simOccupancyDemand: "Occupancy demand",
    simPaceAdjust: "Pace adjustment (ahead)",
    simDaysUntilArrival: "Days until arrival",
    simOccupancy: "Occupancy",
    simRoomKindOccupancy: "Room kind occupancy",
    simRoomKindOccupancyHint: "Occupancy of the selected room category — drives near-arrival and pace adjustments for this room kind.",
    simPropertyOccupancy: "Property occupancy",
    simPropertyOccupancyHint: "Occupancy across the whole property — pulls every room type's price with it, in either direction.",
    simBookingSpeed: "Booking speed",
    simPaceBehind: "Behind",
    simPaceOnTrack: "On track",
    simPaceAhead: "Ahead",
    addRow: "Add Row",
    lastMinuteTitle: "Last-Minute Pricing",
    // No window here: the length depends on each room kind's mode, and a
    // subtitle has no room to say which one it means. "How & when it applies"
    // below states the demand-led window, and the mode legend the override's.
    lastMinuteHint: "When enabled, prices are gradually decreased as arrival nears to fill remaining rooms.",
    lastMinuteEnabled: "Enable Last-Minute Pricing",
    lastMinuteMinPrice: "Minimum Price Target",
    lastMinuteMinPriceHint: "The lowest price the system will reach. Defaults to the Minimal price from Key Metrics if left empty.",
    lastMinuteAdjustmentStepsLabel: "Number of Price Adjustments",
    // `{length}` is the property's opening when one is set, else the demand-led
    // 36 hours; `{cadence}` the time between two drops in it. Both arrive as
    // "1 h 30 min", which reads the same in every locale.
    lastMinuteAdjustmentStepsHint: "In the {length} window the price drops every {cadence} — the table above has every window's cadence.",
    // Shown beside it only where a room kind is set to always discount. That
    // mode runs a season-dependent window instead, so the cadence above is
    // several times too slow for it — the sentence an operator read before
    // counting 6 of 12 drops by mid-afternoon and reporting the engine broken.
    lastMinuteAdjustmentStepsOverrideHint: "Room kinds set to Always discount fit the same drops into a shorter, season-dependent window — {low} h in low season, {common} h in common, {high} h in high — so they fall much closer together there.",
    // The window table. Its two mode names are not here — the rows reuse
    // `lastMinuteUseDefault` / `lastMinuteRoomKindOn`, the strings the
    // per-room-kind select offers, so a row and the option it describes cannot
    // drift apart.
    lastMinuteWindowTableHeading: "When the price moves",
    lastMinuteWindowModeColumn: "Mode",
    lastMinuteWindowSeasonColumn: "Season",
    lastMinuteWindowOpensColumn: "Window opens",
    lastMinuteWindowLengthColumn: "Length",
    lastMinuteWindowCadenceColumn: "Price drops every",
    lastMinuteWindowSeasonAny: "Any season",
    lastMinuteWindowSeasonLow: "Low season",
    lastMinuteWindowSeasonHigh: "High season",
    // Deliberately not "common season": the engine classifies a night as high
    // or low and nothing else, so the 10-hour window is reached only where no
    // high-season week has been identified — before the analysis has run, or
    // where no week clears the demand threshold.
    lastMinuteWindowSeasonUnknown: "No high season identified yet",
    lastMinuteWindowOpensDayBefore: "{time}, day before",
    lastMinuteWindowOpensArrivalDay: "{time}, arrival day",
    lastMinuteWindowFootnote: "Every window ends at {time} on the arrival day, where the price reaches its last-minute minimum — so a shorter window starts later rather than finishing sooner. Times follow the property's own clock, and the season comes from your booking history, week by week.",
    // Two of the table's four columns are settings and two are not. Left
    // unsaid, the table reads as four dials of which three cannot be found —
    // so this names the two that exist, and says the windows have none. The
    // two names are interpolated from the controls' own labels rather than
    // written out here, or renaming a dial would strand the pointer.
    lastMinuteWindowSetWhere: "The opening is set below under {windowLabel} — Automatic keeps the windows in this table — the mode and any opening of its own per room kind under {overridesTitle}, and the cadence with {stepsLabel}. Only the {time} cutoff is Optivo's own.",
    // The property-wide opening. Its options are clock times built from the
    // two `lastMinuteWindowOpens…` templates above beside their length, so no
    // option carries a string of its own.
    lastMinuteWindowOpensLabel: "When the window opens",
    lastMinuteWindowOpensHint: "Automatic runs the windows in the table above. Pick a time to open every room kind's window then instead — a room kind can still set its own below.",
    lastMinuteWindowOpensAutomatic: "Automatic — by mode and season",
    // Said only where a window in use is cut finer than the fastest sync.
    lastMinuteCadenceOutrunsSync: "Prices are recalculated with each sync, every 30 minutes at best, so drops set closer together than that arrive two or more at a time.",
    lastMinuteHoldLabel: "Keep the discount when bookings pick up",
    lastMinuteHoldHint: "For room kinds discounting by demand. When bookings pick up inside the window, the discount stops deepening instead of being withdrawn: rising demand lifts the price only slightly, and discounting carries on if the pickup fades. Off, the discount is withdrawn as soon as demand is back on track.",
    // How deep Discount by demand goes, by the night's demand colour. The
    // three buttons are the calendar legend's own words, read from
    // `pricingCalendar.demand` by the field, so none are keyed here. It
    // replaced "When discounting starts", which asked the same question as a
    // share of normal pace.
    lastMinuteDemandStartLabel: "Discount by demand: when discounting starts",
    lastMinuteDemandStartHint: "Applies to room types set to Discount by demand. Inside the window, Optivo compares the bookings a room type usually still takes for the night before {time} on the arrival day — from its own booking history, scaled by how fast bookings have been arriving lately — with the rooms it still has free. While those bookings would fill the free rooms the price holds; as fewer are still expected it steps down, and a room still free at {time} reaches its minimum price. Early starts discounting while more bookings are still expected; Late waits until fewer are. A property without enough booking history is read on its demand colour instead.",
    lastMinutePacingLabel: "Discount Pacing",
    // The ramp is the same shape whatever the window's length, so naming a
    // length bought nothing here and was false for the override mode.
    lastMinutePacingHint: "Controls how the discount ramps across the window. Faster = larger discounts early. Slower = discounts concentrated near arrival.",
    lastMinuteSimHours: "Hours until cutoff",
    lastMinuteActive: "Last-minute adjustment active",
    lastMinutePreview: "Discount curve — drag bars to customise",
    lastMinuteResetCurve: "Reset to curve",
    lastMinutePerRoomKindTitle: "Per Room Kind Overrides",
    lastMinutePerRoomKindHint: "Set whether last-minute runs, when its window opens and the minimum price target for each room kind. The discount curve stays shared.",
    lastMinuteUseDefault: "Discount by demand",
    lastMinuteOverrideEnabled: "Override Enabled",
    lastMinuteOverrideMinPrice: "Override Minimum Price",
    lastMinuteRoomKindMinPriceLabel: "Minimum price target",
    lastMinuteRoomKindMinPriceHint: "The lowest price last-minute can reach for this room kind. Defaults to the derived Key Metrics minimal; enter a value to override it.",
    lastMinuteRoomKindColumn: "Room kind",
    lastMinuteRoomKindModeColumn: "Last-minute",
    lastMinuteRoomKindWindowColumn: "Window opens",
    lastMinuteRoomKindWindowFollows: "Same as property",
    lastMinuteRoomKindMinPriceColumn: "Minimum price",
    lastMinuteRoomKindMinPriceFollows: "An empty box follows the room kind's Key Metrics minimum — the grey figure standing in it.",
    lastMinuteRoomKindMinPriceReset: "Reset to derived",
    lastMinuteRoomKindMinPriceNoDerived: "No derived minimum — enter a floor to enable last-minute discounts.",
    lastMinuteHowHeading: "How & when it applies",
    // Both halves — the 36 hours and the demand gate — belong to the
    // demand-led mode, and the override obeys neither, so the sentence names
    // whose they are rather than stating them of the whole section.
    lastMinuteWhenHint: "Room kinds left on Discount by demand hold their price while the bookings they usually still take would fill their free rooms, and step down as fewer are still expected — every free room reaches its minimum by the cutoff; room kinds set to Always discount step down regardless of demand. The table below says when each of them moves.",
    lastMinuteGoalRevenue: "With a Max Revenue posture, the steps before the window discount sparingly. Inside it, last-minute works the same under every posture: room kinds discounting by demand hold while the bookings they usually still take would fill their free rooms, and reach their minimum by the cutoff.",
    lastMinuteGoalOccupancy: "With a Max Occupancy posture, the steps before the window discount readily, so last-minute often finds a night already low. Inside it, room kinds discounting by demand hold while the bookings they usually still take would fill their free rooms, and reach their minimum by the cutoff.",
    lastMinuteGoalHybrid: "With the Optivo Recommended posture, a night keeps its base price until it is inside its booking window. Inside the last-minute window, room kinds discounting by demand hold while the bookings they usually still take would fill their free rooms, and reach their minimum by the cutoff.",
    lastMinutePropertyDisabledNote: "Last-minute pricing is off for this strategy. It runs only for room kinds explicitly enabled below.",
    lastMinuteRoomKindOn: "Always discount (override)",
    lastMinuteRoomKindOff: "Never discount",
    lastMinuteModeExplainHeading: "What each choice means",
    lastMinuteModeExplainDefault: "Discount by demand — follows the property switch above: inside the window, holds the price while the bookings this room kind usually still takes would fill its free rooms, steps it down toward the minimum as fewer are still expected, and a room still free at the cutoff reaches the minimum. With that switch off, this room kind never discounts.",
    lastMinuteModeExplainOn: "Always discount (override) — always steps the price down toward the minimum as arrival nears, regardless of demand, inside its window — left automatic, a shorter one the season sets.",
    lastMinuteModeExplainOff: "Never discount — last-minute pricing never runs for this room kind.",
    lastMinuteNoRoomKinds: "No room kinds available yet.",
    losRestrictions: "Length of Stay Restrictions",
    defaultMinLos: "Default Min Length of Stay",
    defaultMinLosHint: "The minimum stay applied when nothing else raises it. Everything else releases back to this floor.",
    losNightsUnit: "nights",
    losAnalysisStayLength: "Analysis: median {median} nights · avg {avg}",
    losAnalysisOneNightShare: "{pct}% are 1-night stays",
    weekendHolidayLosEnabled: "Minimum stay on weekends and holidays",
    weekendHolidayLosEnabledHint: "Apply a 2-night minimum stay on Friday and Saturday arrivals. When public holidays extend the weekend, the whole break is covered instead — up to 4 nights, from the evening guests arrive through every night inside it, so it cannot be split by a one-night booking. The last day of a break is when guests leave, so it stays free, and ordinary weekdays are never restricted. Inside the release window below the minimum is always released; before it, it can be released early when demand is weak, unless you switch that off below. Where a stay runs into a night already sold out in its room type, that room type's minimum comes down to the stay that can still be booked, until a room frees up.",
    losReleaseWithinDays: "Always release within",
    losReleaseWithinDaysHint: "Inside this many days before arrival the minimum stay always drops back to your default, whatever demand says.",
    losReleaseWithinDaysOffHint: "Set to 0 to let demand alone decide when the minimum stay is released.",
    losReleaseWithinDaysUnit: "days",
    losReleaseWithinDaysNeverHint: "At 0, with “Release the minimum when demand is weak” off, the minimum stay holds until arrival, unless a night the stay needs sells out.",
    losWeakWeekRelease: "Release the minimum when demand is weak",
    losWeakWeekReleaseHint: "When demand for a weekend or holiday is weak, its minimum can drop back to your default before the release window, as when weekends at that time of year were less than 60% full last year. Switch this off if you plan your minimum stays ahead and send them to your PMS.",
    losWeakWeekReleaseOffHint: "Weak weeks keep the minimum too: only the release window above hands it back, or a night the stay needs selling out.",
    // In place of the hint above while Optivo reads the weekend's own bookings.
    losWeakWeekReleaseDemandHint: "Far from arrival the booking history decides: the minimum is released in a week it calls weak, but not while the weekend is already selling clearly faster than usual. From 30 days before arrival, once half of the bookings the weekend usually gets are normally in, the current bookings decide: the minimum is released when they show the weekend ending clearly below its usual occupancy, and held otherwise. A minimum released for a weak week comes back only once the weekend sells clearly faster than usual. “Always release within” always applies.",
    roomStructure: "Guest Count Pricing",
    roomKindStructureTitle: "Room Kind Structure",
    roomKindStructureHint: "Rename, renumber, or hide what comes from the PMS. Changes here affect the pricing calendar, analytics, drafts, and price uploads for this connection. Every visible kind is priced and pushed to the PMS — hide listings that are not sellable accommodation, such as parking, promo blocks, or reserves.",
    roomKindStructureColRoomKind: "Room Kind",
    roomKindStructureColDisplayName: "Display Name",
    roomKindStructureColRooms: "Rooms",
    roomKindStructureColBeds: "Beds",
    roomKindStructureColExtraBeds: "Extra",
    roomKindStructureColMaxOcc: "Max Occ.",
    roomKindStructureColVisible: "Visible",
    roomKindStructurePlaceholderName: "Use PMS name",
    roomKindStructureSave: "Save Structure",
    roomKindStructureSaving: "Saving…",
    roomKindStructureSaved: "Saved",
    roomKindStructureSaveError: "Save failed",
    roomKindStructureReset: "Reset",
    roomKindStructureEmpty: "No room kinds returned by the PMS yet.",
    roomKindStructureHiddenBadge: "Hidden",
    roomKindStructureAutoHiddenBadge: "Auto-hidden",
    roomKindStructurePmsValue: "PMS",
    roomKindStructurePricedBadge: "Priced & pushed",
    roomKindStructureNotPricedBadge: "Not priced",
    roomKindStructureColOrder: "Order",
    roomKindStructureMoveUp: "Move up",
    roomKindStructureMoveDown: "Move down",
    roomKindStructureOrderHint: "The order below is the row order in every calendar — reservations, price drafts, and the metric views.",
    roomCapacities: "Room Capacities",
    beds: "Beds",
    extraBeds: "Extra",
    maxOccupancy: "Max",
    roomCount: "Rooms",
    occupancyAdjustments: "Per-Person Occupancy Price Adjustments",
    occupancyLabel: "Occ. {n}",
    occupancyAdjustmentWarning: "Price for fewer guests must not exceed the price for more guests. Check highlighted occupancy levels.",
    guestStepsHint: "Two guests pay the base rate. These two steps set every other guest count, applied to each room type from its own base price — changing one rewrites the table below.",
    singleGuestStepLabel: "One guest instead of two",
    singleGuestStepHint: "What a single guest pays against the two-guest base rate, never under the minimum that applies to the night.",
    extraGuestStepLabel: "Each guest beyond two",
    extraGuestStepHint: "Added for every guest above two, on a real bed or a roll-away alike.",
    guestStepUnitAmount: "Amount",
    guestStepUnitSelect: "Unit for {field}",
    guestPricingRoomTypeColumn: "Room type",
    guestPricingGuestsHeading: "Nightly rate by number of guests",
    guestPricingBaseTag: "base",
    guestPricingBaseNote: "Two guests pay the room type's base rate, set in Prices by Room Type. Bed counts come from Room Kind Structure.",
    guestPricingSingleGuestNote: "A room type that sleeps only one guest is sold at its base rate — “{step}” does not change it.",
    guestPricingExtraBedNote: "A shaded price is a guest sleeping on an extra bed.",
    guestPricingNoRoomKinds: "No room kinds available yet. Re-run onboarding to populate room kinds.",
    priceGranularity: "Price Granularity",
    priceGranularityHint: "Rounding step for generated prices.",
    priceCharmEnding: "Charm ending",
    priceCharmEndingHint: "Trim the rounded price so it reads 990 rather than 1000. Only endings that fit inside the rounding step are offered.",
    priceCharmEndingOff: "Off",
    priceCharmEndingExample: "{before} becomes {after}",
    holidays: "Holidays & Special Dates",
    specialDatesHint: "Public holidays are pre-filled from the selected countries. Duplicate dates are merged automatically. You can adjust each entry individually or add custom dates. A special-day premium replaces the weekend premium rather than adding to it, and only where it is higher — a night whose own weekend premium, or its room type's, is higher keeps that one. A new entry opens at what a peak night is worth here: {premium}%. Each entry then stands on its own — changing your Friday or Saturday no longer moves it.",
    // Shared by the wizard's Special Dates step and this section: the list
    // says it, through `buildSpecialDatesLabels`, on both.
    specialDatesNightRule: "Pick the nights an entry prices in its calendar: a night belongs to the day guests arrive, as on the Prices page. A public holiday prices the night before it, since a guest who wakes up on the holiday arrived the evening before — so New Year's Day prices the night Dec 31 → Jan 1.",
    specialDatePricedNight: "night {from} → {to}",
    // A period: {count} is a `specialDateNightCount*` form, {from} its first
    // evening and {to} its last morning. Three flat keys rather than one plural
    // object, since this section is read as a bag of strings.
    specialDatePricedNights: "{count}: {from} → {to}",
    specialDateNightCountOne: "{count} night",
    specialDateNightCountFew: "{count} nights",
    specialDateNightCountOther: "{count} nights",
    // The calendar the nights are picked in.
    specialDatePickNights: "Pick nights",
    specialDateChangeNights: "Change nights",
    specialDatePickerHint: "Click a night. For a period, click its last night too.",
    specialDatePickerDone: "Done",
    // Under a row whose date the engine never matches, in place of its night.
    specialDateMissing: "Pick the nights in the calendar: until then this entry prices none.",
    specialDateUnreadable: "Optivo can't read this date, so this entry prices no night. Pick the nights in the calendar again.",
    specialDateNeedsYear: "A date without a year comes round every year: switch on Recurring, or pick the nights in the calendar.",
    specialDateYearOnRecurring: "A date with a year comes round once: switch off Recurring, or pick the nights in the calendar again.",
    // A row that moves with Easter: where its date would stand ("Easter +1",
    // "Easter −2", "Easter"), and in place of the date field.
    specialDateEasterPattern: "Easter {offset}",
    specialDateMovesWithEaster: "Its date moves with Easter, so this entry prices the feast in every year. Switch off Recurring to keep only its next date.",
    // Under an entry whose next night keeps a higher premium of its own: a
    // special date replaces the night's day premium only where it is higher.
    // {day} is the night's weekday; {roomTypes} is the groups joined by "; ".
    specialDateOutrankedRoomTypes: "{day}: these room types keep their own, higher premium that night — {roomTypes}.",
    specialDateOutrankedGroup: "{names} {premium}%",
    specialDateOutrankedProperty: "{day}: your own, higher premium of {premium}% applies that night instead.",
    // The same, of a period's nights on several weekdays: {day} names them all.
    specialDateOutrankedRoomTypesNights: "{day}: these room types keep their own, higher premium on those nights — {roomTypes}.",
    specialDateOutrankedPropertyNights: "{day}: your own, higher premium of {premium}% applies on those nights instead.",
    // A save the server refused for one of those dates, on either surface.
    specialDatesSaveRefused: "Nothing was saved: a special date has a date Optivo can't read. The rows marked in red under Holidays & Special Dates say which.",
    selectCountry: "Select country…",
    reloadHolidays: "Reload public holidays",
    holidaysActive: "active",
    loadingHolidays: "Loading…",
    bulkAdjust: "Bulk set",
    bulkAdjustHint: "Set the same adjustment on every active entry. A minus makes them all cheaper than an ordinary night.",
    apply: "Apply",
    collapseList: "Collapse list",
    expandList: "Show all entries",
    showMore: "Show more…",
    unnamed: "Unnamed",
    customDateName: "Name (e.g. Christmas Day)",
    specialDateAdjustment: "Price adjustment",
    premium: "Premium",
    discount: "Discount",
    recurring: "Recurring",
    noHolidays: "No special dates configured. Select a country to load public holidays, or add entries manually.",

    // Parameter hints (explanations)
    highSeasonMultiplierHint: "Moves the nightly rate in high-demand months. e.g. +15% means a 2 000 base becomes 2 300; a minus prices those months down.",
    lowSeasonMultiplierHint: "Moves the nightly rate in low-demand months. Usually a minus to attract bookings — a plus if quiet months are when your rooms are scarce.",
    weekendPremiumParamHint: "Use day-specific adjustments instead of a combined weekend premium.",

    // Price simulator labels
    simBase: "Base rate",
    simSeason: "Season adjustment",
    simHighSeason: "High season",
    simLowSeason: "Low season",
    simWeekend: "Day adjustment",
    simDayType: "Day",
    simWeekday: "Weekday",
    simMonday: "Monday",
    simTuesday: "Tuesday",
    simWednesday: "Wednesday",
    simThursday: "Thursday",
    simFriday: "Friday",
    simSaturday: "Saturday",
    simSunday: "Sunday",
    simDateType: "Date type",
    simDateTypeRegular: "Regular",
    simDateTypeHoliday: "Holiday",
    simDateTypeBridge: "Bridge day",
    simDateTypeShoulder: "Shoulder day",
    simDateTypeGapFill: "Gap fill",
    simLosRelaxed: "MinLOS relaxed",
    simMonth: "Month",
    simDynamicDemand: "Dynamic demand",
    simSpecialDayOverride: "Replaced by the special-day premium — special-day and weekend premiums are never combined.",
    simHoliday: "Holiday",
    simNights: "nights",
    simClamped: "Clamped to guardrails",
    simGuardrails: "Rate guardrails",
    simRounding: "Rounded to nearest",
    simFinal: "Final",
    simExampleTitle: "Live Price Example",
    simShowPreview: "Show Live Price Example",
    simHidePreview: "Hide Live Price Example",
    simRoomKind: "Room kind",
    simPreviewRoomCategory: "Preview room category",
    simPreviewRoomCategoryHint: "FYI: this category is defined by room kind assignment and can be changed there.",
    simPreviewRoomFallback: "Standard category was not available, so preview uses the closest configured room category.",

    // Gap filling
    gapFillingTitle: "Gap Filling",
    gapFillingHint: "Detects short gaps (orphan nights) between existing reservations, which rarely sell at the same price as the nights around them, and prices them on their own.",
    gapFillingEnabled: "Enable Gap Filling",
    gapFillingMaxGapDays: "Maximum Gap Size",
    gapFillingMaxGapDaysHint: "The largest gap to price on its own. One night means a true orphan sandwiched between two bookings.",
    gapFillingAdjustmentPct: "Price adjustment",
    gapFillingAdjustmentPctHint: "How the price moves for dates in a detected gap. Usually a discount — set a plus if a single night between two stays is scarce rather than unwanted. A discount stands down where the night does not need one: a date you priced yourself, a night already selling well ahead of target, or one last-minute pricing has taken over. A plus always applies.",
    gapFillingNight: "night",
    gapFillingNights: "nights",

    // Bridge-day premium and shoulder days (strategy editor labels)
    bridgeDaysTitle: "Bridge & Shoulder Days",
    bridgeDaysHint: "When a single non-premium night sits between two premium nights (holiday↔weekend, weekend↔holiday, or holiday↔holiday), price it as a premium night too.",
    bridgeDaysEnabled: "Enable Bridge-Day Premium",
    bridgeDaysPremiumPct: "Price adjustment",
    bridgeDaysPremiumPctHint: "How the price moves on a detected bridge night. Usually a premium — set a minus if the night nobody wants is the one in the middle.",
    bridgeDaysFollowsWeekend: "Follows your weekend rate of {weekend}%. A bridge night is priced as the premium night it sits between.",
    bridgeDaysOffWeekend: "This premium stands on its own. Your weekend rate is {weekend}%.",
  },

  // ── Pace Curve Chart ─────────────────────────────────────
  paceCurveChart: {
    title: "Pace → Price Curve",
    revenueHint: "Exponential: small discounts when behind, accelerating premiums when ahead.",
    occupancyHint: "Logarithmic: aggressive discounts when behind, plateauing premiums when ahead.",
    hybridHint: "Optivo Recommended: occupancy-style discounts when behind pace, revenue-style premiums when ahead.",
    xLabel: "Booking Pace",
    yLabel: "Price Adjustment",
    gentle: "Gentle",
    moderate: "Moderate",
    aggressive: "Aggressive",
    behindPace: "← Behind pace",
    onTrack: "On track",
    aheadOfPace: "Ahead of pace →",
    custom: "Custom",
  },

  // ── Auth Callback ───────────────────────────────────────
  auth: {
    error: "Authentication Error",
    fallback: "Authentication failed",
    tryAgain: "Try again",
    completing: "Completing sign in...",
  },

  // ── App / Dashboard ─────────────────────────────────────
  app: {
    checkingPms: "Checking Property Management connection...",
    pmsUnhealthy:
      "Property Management connection is unhealthy. Please verify your credentials.",
    pmsVerifyFailed:
      "Could not verify Property Management connection. Please check your credentials.",
    connectionStillUnhealthy: "Connection still unhealthy.",
    pageLoadFailedTitle: "This page could not be loaded",
    pageLoadFailedMessage:
      "A new version may have been released since this tab was opened. Reloading picks up the current one.",
    pageLoadFailedReload: "Reload",
    // Under the same offer where the whole app could not be drawn
    // (`AppUnavailable`). There is no rail left to ask Optivo from, and a
    // reload that does not help leaves no other way on, so the address is
    // named here.
    pageLoadFailedContact: "If reloading does not help, write to {email}.",
  },

  // ── User Management (admin-only) ────────────────────────
  userManagement: {
    searchPlaceholder: "E-mail or user ID",
    title: "User Management",
    description: "View and manage all registered users",
    loading: "Loading users...",
    retry: "Try again",
    loadError: "Failed to load users",
    noUsers: "No users found",
    email: "Email",
    role: "Role",
    status: "Status",
    joined: "Joined",
    actions: "Actions",
    roleAdmin: "Admin",
    roleUser: "User",
    statusActive: "Active",
    statusInactive: "Inactive",
    statusPending: "Pending Approval",
    approve: "Approve",
    makeAdmin: "Make Admin",
    removeAdmin: "Remove Admin",
    activate: "Activate",
    deactivate: "Deactivate",
    confirmDeactivate: "Deactivate this user? They will be logged out immediately and blocked from logging in.",
    confirmRemoveAdmin: "Remove admin role from this user?",
    toastRoleUpdated: "User role updated",
    toastRoleUpdateFailed: "Failed to update user role",
    toastStatusUpdated: "User status updated",
    toastStatusUpdateFailed: "Failed to update user status",
    cannotModifySelf: "You cannot modify your own account",
    userCount: "{count} users",
    // The number a user gave on the sign-up form, which support calls
    // before activating the account. The link's name says what it does.
    callPhone: "Call {phone}",
    // Temporary access — a sign-in an admin mints for a tester. It skips the
    // verification code and the approval by construction, and stops working
    // on its own when the days are up.
    createTemporary: "Create temporary access",
    temporaryTitle: "New temporary access",
    temporaryDescription:
      "A sign-in for testing that works straight away: no e-mail verification, no approval and no two-step verification. It stops working on its own when the time is up.",
    temporaryEmail: "E-mail (sign-in name)",
    temporaryEmailHint:
      "Does not need to be a real mailbox — nothing is sent to it. It is only the name to sign in with.",
    temporaryEmailInvalid: "Enter an e-mail address, such as tester@example.com.",
    temporaryPassword: "Password",
    temporaryPasswordHint: "At least 8 characters. Shown in the clear so you can hand it over.",
    temporaryPasswordTooShort: "The password must be at least 8 characters long.",
    generatePassword: "Generate",
    temporaryDuration: "Access for",
    // A whole phrase per option rather than a count in a frame — Czech
    // declines the noun with the number.
    accessDays: {
      d1: "1 day",
      d7: "7 days",
      d14: "14 days",
      d30: "30 days",
      d90: "90 days",
    },
    temporaryCreate: "Create access",
    temporaryCreating: "Creating...",
    temporaryCreatedTitle: "Temporary access created",
    temporaryCreatedHint: "Hand these details over now — the password is not shown again.",
    temporaryValidUntil: "Works until {date}",
    temporaryGrantHint:
      "To let this account see a property, open PMS Connections, choose the property and add this e-mail under Access.",
    copyCredentials: "Copy sign-in details",
    copied: "Sign-in details copied",
    copyFailed: "Could not copy — select the text and copy it by hand",
    done: "Done",
    toastTemporaryCreated: "Temporary access created",
    toastTemporaryCreateFailed: "Failed to create temporary access",
    statusExpired: "Expired",
    accessUntil: "Access until {date}",
    confirmActivateExpired:
      "This account's temporary access has ended. Activating it removes the time limit — it then stays active until you deactivate it.",
    // A reset link an admin generates and hands over. The self-service flow
    // e-mails the same link and so stops where the mailbox does.
    resetLink: "Password reset link",
    resetLinkDescription:
      "Pass this link on yourself — nothing has been e-mailed. It sets a new password once and then stops working.",
    resetLinkGenerating: "Generating the link...",
    resetLinkFor: "For {email}",
    resetLinkExpiresAt: "Works until {date}",
    resetLinkHint:
      "The link is shown only here. Generating another one replaces it, and the older link stops working.",
    resetLinkBlocked:
      "This account still will not be able to sign in — approve or activate it as well, or a new password gets them no further.",
    copyLink: "Copy link",
    linkCopied: "Link copied",
    toastResetLinkFailed: "Failed to generate a password reset link",
    // Deleting outright — the one action here that is not a switch. It is
    // what gives the e-mail back, so the person can register again.
    deleteUser: "Delete account",
    confirmDeleteUser:
      "Delete the account {email}? The sign-in stops working at once and the e-mail address is freed, so it can be registered again. What the account did stays in the activity log. This cannot be undone.",
    toastUserDeleted: "Account deleted",
    toastUserDeleteFailed: "Failed to delete the account",
    // The AI agents and their security tokens, off for everybody until an
    // administrator switches them on here for one person. Stopping them
    // keeps the person's tokens, which work again once they are back on.
    allowAiAgents: "Allow AI agents",
    stopAiAgents: "Stop AI agents",
    // Beside the role while they are on; nothing while they are off.
    aiAgentsBadge: "AI agents",
    confirmStopAiAgents:
      "Stop the AI agents of {email}? Their agents stop working at once. Their security tokens are kept, and work again if you allow the AI agents again.",
    toastAiAgentsAllowed: "AI agents allowed for {email}",
    toastAiAgentsStopped: "AI agents stopped for {email}",
    toastAiAgentsFailed: "Failed to change the AI agents",
  },

  // ── Admin Price Plans (admin-only) ─────────────────────
  adminPriceDrafts: {
    searchPlaceholder: "Property, account, or owner",
    title: "Price Plans",
    description: "Every stored price plan, across all properties",
    intro: "One plan is stored per property, covering the next year. Deleting one discards every prepared price it holds — including nights the hotel locked or edited by hand — and nothing in Optivo can bring them back.",
    uploadWarning: "A plan deleted while its property is uploading cannot be marked as sent afterwards. Check that no sync is running before you clear anything.",
    loading: "Loading price plans...",
    noDrafts: "No price plans are stored",
    retry: "Try again",
    property: "Property",
    account: "Account",
    range: "Range",
    prices: "Prices",
    sent: "Sent",
    locked: "Locked",
    updated: "Last updated",
    actions: "Actions",
    orphaned: "No property",
    orphanedHint: "No property is linked to this plan — no account can see or restore it",
    delete: "Delete",
    deleting: "Deleting...",
    confirmDeleteTitle: "Delete this property's price plan?",
    confirmDelete: "This removes all {count} prepared prices for {property}, locked and hand-edited ones included. Prices already sent to the property management system stay as they are, and a new plan is calculated on the property's next sync.",
    purgeOrphaned: "Clear plans with no property",
    confirmPurgeOrphanedTitle: "Delete plans that have no property?",
    confirmPurgeOrphaned: "These plans have no property linked, so nothing reaches them through Optivo and no hotel loses prices. Normally there are none — the button stays off unless drift has left some behind.",
    purgeAll: "Delete every plan",
    confirmPurgeAllTitle: "Delete every stored price plan?",
    confirmPurgeAll: "This clears the price plan of every property on every account and cannot be undone. Prices already sent to property management systems are unaffected; each property gets a new plan on its next sync.",
    toastDeleted: "Price plan deleted",
    toastDeleteFailed: "Failed to delete the price plan",
    toastPurged: "{count} price plans deleted",
    toastPurgeFailed: "Failed to delete the price plans",
    draftCount: "{count} plans",
    unknownProperty: "Property #{id}",
    unknownAccount: "—",
  },

  // ── Admin: Bank Transfers ──────────────────────────────
  adminWirePayments: {
    searchPlaceholder: "Variable symbol, account, or invoice",
    title: "Bank Transfers",
    description: "Transfers waiting to be matched against the company account",
    intro: "A card payment settles itself. A bank transfer lands on the company account, where Optivo cannot see it — find the variable symbol on the statement, check the amount matches, then confirm receipt here.",
    loading: "Loading bank transfers...",
    retry: "Try again",
    noneWaiting: "No transfers are waiting — nothing to check against the statement",
    noneAtAll: "No bank transfers have been requested yet",
    scopePending: "Waiting",
    scopeAll: "All",
    pendingCount: "{count} waiting",
    variableSymbol: "Variable symbol",
    account: "Account",
    payer: "Payer",
    amount: "Amount",
    period: "Covers",
    requested: "Requested",
    status: "Status",
    actions: "Actions",
    // What the amount covers. A payment under the price list of 5 Oct 2026
    // is explained by the properties it bills and their rooms, said together;
    // one opened before it by the rooms it was counted from.
    properties: { one: "{count} property", few: "{count} properties", other: "{count} properties" },
    rooms: "{count} rooms",
    // Market data comes with both products; the payment says so only where a
    // surcharge agreed on top was billed with it.
    marketDataIncluded: "incl. market-data surcharge",
    unknownAccount: "—",
    statusPending: "Waiting",
    statusPaid: "Received",
    statusCanceled: "Canceled",
    statusFailed: "Failed",
    approve: "Confirm receipt",
    reject: "Did not arrive",
    approving: "Confirming...",
    confirmApproveTitle: "Confirm this transfer arrived?",
    confirmApprove: "Check the company account for variable symbol {vs} first. Confirming marks {amount} from {account} as received, extends their paid period to {periodEnd}, and issues the invoice.",
    confirmRejectTitle: "Mark this transfer as not received?",
    confirmReject: "This cancels the {amount} payable for {account}, so they can start a new payment. Nothing is charged and no invoice is issued.",
    noteLabel: "Bank statement reference",
    notePlaceholder: "e.g. KB statement 27/08, transaction 4471",
    noteHint: "Optional. Stored with the decision so it can be traced back to the statement later.",
    settledBy: "Confirmed by {admin}",
    // A wire settles on a person's word, so the person deciding sees
    // what the payer agreed to. Null is unknown, not a refusal.
    termsAccepted: "Terms accepted {date}",
    termsNotRecorded: "Terms agreement not recorded",
    invoiceIssued: "Invoice {number}",
    toastApproved: "Transfer confirmed — the subscription is paid",
    toastApproveFailed: "Failed to confirm the transfer",
    toastRejected: "Transfer marked as not received",
    toastRejectFailed: "Failed to update the transfer",
    // Money received for a period another payment had already paid is not
    // applied: it is recorded as to be refunded, and the refund is made by
    // hand. This is the one list here that holds card payments as well.
    scopeRefund: "To refund",
    refundDueCount: "{count} to refund",
    noneToRefund: "No payment is waiting to be refunded",
    statusRefundDue: "To refund",
    statusRefunded: "Refunded",
    // A status this version of the console has no word for.
    statusOther: "Other",
    paidByTransfer: "Bank transfer",
    paidByCard: "Card payment",
    paidByCardRenewal: "Card, charged automatically",
    gatewayId: "Gateway payment {id}",
    // A transfer cancelled here — replaced by a newer payment, or marked as
    // not received — can still land on the account.
    approveCanceled: "Arrived after all",
    confirmApproveCanceledTitle: "Confirm this cancelled transfer arrived?",
    confirmApproveCanceled: "This transfer was cancelled: a newer payment replaced it, or it was marked as not received. Check the company account for variable symbol {vs} first. If nothing else has paid the period, confirming marks {amount} from {account} as received, extends their paid period to {periodEnd}, and issues the invoice. If the period is already paid, it is recorded as to be refunded instead.",
    markRefunded: "Record refund",
    markingRefunded: "Recording...",
    confirmRefundTitle: "Record that this payment was refunded?",
    confirmRefund: "Optivo sends no money itself. Refund {amount} to {account} first — in the payment gateway's admin for a card payment, from the company account for a bank transfer — then record it here. Variable symbol {vs}.",
    refundNoteLabel: "Refund reference",
    refundNotePlaceholder: "e.g. refunded in the gateway 06/10, or KB statement 06/10",
    refundNoteHint: "Optional. Stored with the record so the refund can be traced later.",
    refundDueHint: "Received for a period already paid. Nothing was applied.",
    refundedBy: "Refund recorded by {admin}",
    refundedOn: "Refund recorded {date}",
    toastRefundDue: "Recorded, not applied: the period was already paid, so this transfer is to be refunded",
    toastRefunded: "Refund recorded",
    toastRefundFailed: "Failed to record the refund",
  },


  // ── Admin — PMS connections ────────────────────────────
  // Shared by every admin list: the search box and the pager beneath it.
  adminList: {
    searchLabel: "Search",
    clearSearch: "Clear search",
    showing: "Showing {from}–{to} of {total}",
    pageOf: "{page} / {pages}",
    previousPage: "Previous page",
    nextPage: "Next page",
    noMatches: "Nothing matches “{search}”",
    clearToSeeAll: "Clear the search to see the whole list",
  },
  adminConnections: {
    searchPlaceholder: "Property, account, or person",
    title: "PMS Connections",
    description: "Every connected property, across all accounts",
    intro: "Support view over every PMS connection in Optivo. Change the operational settings a property got wrong, check whether its provider is reachable, and repair access when an account can no longer reach its own owner. Credentials are not shown or editable here.",
    loading: "Loading connections...",
    retry: "Try again",
    empty: "No PMS connections have been created yet",
    connectionCount: "{count} connections",
    // Table
    property: "Property",
    account: "Account",
    provider: "Provider",
    currency: "Currency",
    health: "Health",
    syncCadence: "Sync",
    lastSync: "Last sync",
    members: "People",
    createdBy: "Created by",
    actions: "Actions",
    unnamed: "Unnamed property",
    never: "Never",
    memberCount: "{count} people",
    notConfigured: "Not configured",
    // Nothing recorded the creator before this was added, and the backfill
    // only credited an account that had a single owner — so say so, rather
    // than leaving a blank cell that reads as a failed load.
    creatorUnknown: "Not recorded",
    creatorLeft: "No longer reaches this property",
    createdByLine: "Created by {name}",
    creatorBadge: "Creator",
    healthHealthy: "Healthy",
    healthUnhealthy: "Unhealthy",
    healthUnknown: "Unknown",
    // Settings dialog
    editSettings: "Settings",
    settingsTitle: "Settings for {name}",
    settingsDescription: "Operational settings only. This screen never shows or changes the property's PMS credentials.",
    nameLabel: "Property name",
    namePlaceholder: "e.g. Hotel Ostrov",
    currencyLabel: "Currency",
    currencyWarning: "Changing the currency re-prices this property: every stored price is converted to the new currency in the same step. This is not a label change.",
    // The options themselves come from the shared `syncCadence` table, so the
    // picker and every screen that merely reports the cadence say one thing.
    cadenceLabel: "Sync frequency",
    cadenceWarning: "How often Optivo reads this property's bookings and sends prices back. This is the only place it can be set — the property's own auto-sync dialog states it and offers no control.",
    // One of Optivo's own test properties. The mark decides one thing: the
    // welcome page's public count of priced rooms leaves the property out.
    testPropertyLabel: "Test property",
    testPropertyHint: "One of Optivo's own test properties, not a customer's. Its rooms are left out of the number of priced rooms on the public welcome page. Nothing else changes: it is synced, priced and billed as before.",
    testBadge: "test property",
    // The account's subscription. Under the price list of 5 Oct 2026 every
    // covered property pays the price of the product it is, and a price for
    // each of its rooms, and the customer chooses no plan, so the block
    // states the list — built from the same table as the public one — and
    // holds the two prices support agrees on top of it.
    subscriptionTitle: "Account subscription",
    subscriptionScope: "Applies to the whole account — every property on it.",
    subscriptionListTitle: "Price list, a month",
    // Beside a product's own price: what each room adds. {price} is the
    // list's price of a room.
    subscriptionPerRoom: "+ {price} per room",
    // Who OPTIVO Rentals is for. {types} are the wizard's own names for the
    // property types; the list sets no size limit.
    subscriptionRentalsFor: "{types}, of any size",
    subscriptionListRule: "Each covered property pays the price of what it is, and for each room Optivo prices in it; the customer chooses no plan. A property of another type, or with no type stated, pays the {hotels} price.",
    // "Up to" is a ceiling: support sets the cadence above for each property
    // and nothing enforces it, so there is no plan to hold it against.
    subscriptionIncluded: "Both include prices up to {count}× a day (the sync frequency above; nothing enforces it), {days} days ahead, and revenue studies in a quarter: {analyses}.",
    subscriptionNone: "No subscription yet — the customer has not opened Payments & billing.",
    individualPriceLabel: "Individual monthly price (CZK)",
    // What the list has no price for is agreed here: more revenue studies
    // than a quarter includes, or any other deal. Every account has a list
    // price, so nobody waits for this field any more.
    individualPriceHint: "Replaces the list price of every covered property — for more revenue studies a quarter than the list includes, or another deal. Empty = the price list.",
    // Market data comes with both products, so there is no market to classify
    // — only a surcharge for extended market data, billed as a line of its own.
    marketDataPriceLabel: "Market Data — monthly surcharge (CZK)",
    marketDataPriceHint: "Market data is in the price of both products. A surcharge agreed for extended market data is billed as its own line on top. Empty = none.",
    priceInvalid: "Enter an amount in CZK of at least 0, with at most two decimals — or leave the field empty.",
    save: "Save changes",
    saving: "Saving...",
    cancel: "Cancel",
    confirmCurrencyTitle: "Change currency from {from} to {to}?",
    confirmCurrency: "Every price stored for {name} will be converted to {to} in the same transaction. The property's operator will see their prices restated. This cannot be undone from here.",
    confirmCurrencyAction: "Convert prices",
    // Health
    checkHealth: "Check health",
    checking: "Checking...",
    // Force recalculation — the one action here that changes what a property
    // sells rather than who may see it, so it is confirmed and it says where
    // the numbers go next.
    recalculate: "Recalculate prices",
    recalculating: "Recalculating...",
    recalcConfirmTitle: "Recalculate prices for {name}?",
    recalcConfirm: "Every price for the year and a half ahead is worked out again from this property's active strategy. Prices its operator locked or typed by hand are kept. The new prices reach the PMS on the property's next auto-sync — this does not send them.",
    recalcConfirmAction: "Recalculate",
    toastRecalcStarted: "Recalculating prices for {name}",
    toastRecalcJoined: "A recalculation was already running for {name}",
    toastRecalcDone: "Prices recalculated for {name}",
    toastRecalcFailed: "Recalculation failed",
    toastRecalcNoStrategy: "{name} has no active strategy, so there is nothing to price with",
    // Re-running the property analysis. It reads a year of somebody else's API
    // and rewrites the figures a strategy is calibrated against, so it is
    // confirmed, and the confirmation says what it keeps as well as what it
    // restates.
    refreshAnalysis: "Re-run analysis",
    refreshingAnalysis: "Analysing...",
    analysisConfirmTitle: "Re-run the analysis for {name}?",
    analysisConfirm: "Optivo re-reads a year of this property's bookings from its PMS and measures them again — the typical booking window, the analysed price per room type, the weekday pattern. It takes a few minutes and makes a long run of requests against the provider. Figures the operator set themselves are kept; the rest are restated, and the property's prices follow them on its next run. Optivo already does this once a day on its own.",
    analysisConfirmAction: "Re-run analysis",
    toastAnalysisStarted: "Reading a year of bookings for {name} — this takes a few minutes",
    toastAnalysisJoined: "An analysis was already running for {name}",
    toastAnalysisDone: "Analysis refreshed for {name}",
    toastAnalysisDoneCount: "{count} reservations read",
    toastAnalysisFailed: "The analysis failed",
    // Access dialog
    manageAccess: "Access",
    accessTitle: "Who can reach {name}",
    accessDescription: "The people who can reach {name}: its owners, the members whose full access covers it, and anybody given read-only access to it.",
    // The blast radius has to be stated before the invite, not after: what the
    // access chosen covers, and on which of the account's properties.
    accessScopeWarning: "This account owns {count} properties. Choose the access, then which of them it covers.",
    accessScopeMemberThis: "Full access covers {name} alone: its prices, strategy and settings. The account's other properties are not included.",
    accessScopeMemberAll: "Full access covers all {count} properties of this account, and any it adds later.",
    accessScopeMemberSome: "Full access covers the {count} chosen properties: their prices, strategies and settings.",
    accessScopeViewerSome: "Read-only access covers the {count} chosen properties: their prices and analytics, never the strategy or the settings.",
    accessScopeFrontDeskSome: "Front-desk access covers the {count} chosen properties: their prices, reservations and why each price is what it is — never the analytics, the revenue, the strategy or the settings.",
    accessScopeSingle: "This account owns only this property. Full access also covers any property it adds later; read-only access covers this one alone.",
    // Full access on an account of one property is the whole account, standing.
    accessScopeSingleFull: "This account owns only this property. Full access covers it, and any property the account adds later.",
    roleOwner: "Owner",
    roleMember: "Member",
    makeOwner: "Make owner",
    makeMember: "Step down to member",
    removeMember: "Remove",
    addPlaceholder: "Email address of an existing user",
    add: "Grant access",
    adding: "Granting...",
    noMembers: "Nobody can reach this property.",
    userNotFound: "No Optivo user with that email. They have to sign in once before they can be given access.",
    confirmRemoveTitle: "Remove {email} from {account}?",
    // A server that does not tell properties apart takes the membership off
    // the whole account, and this says so.
    confirmRemove: "They lose full access to every property this account owns. Their own data is untouched, and they can be added again later.",
    confirmRemovePropertyTitle: "Remove {email} from {name}?",
    confirmRemoveOnlyThis: "They lose access to {name}, the only property of this account they reach. Their own data is untouched, and they can be given access again later.",
    confirmStepDownTitle: "Step {email} down to member?",
    confirmStepDown: "They keep access but can no longer invite people, change billing, or delete properties.",
    // Toasts
    toastSaved: "Settings saved",
    toastSaveFailed: "Failed to save the settings",
    toastHealthy: "Connection is healthy",
    toastUnhealthy: "Connection is unhealthy",
    toastHealthFailed: "Health check failed",
    toastMemberAdded: "Access granted",
    toastMemberAddFailed: "Failed to grant access",
    toastRoleUpdated: "Role updated",
    toastRoleUpdateFailed: "Failed to change the role",
    toastMemberRemoved: "Access removed",
    toastMemberRemoveFailed: "Failed to remove access",
    loadError: "Failed to load the connections",
    membersLoadError: "Failed to load the people who can reach this property",
    // Read-only access: a property's prices and analytics, never the
    // strategy or the settings.
    roleViewer: "Read-only",
    addRoleMember: "Full access",
    addRoleViewer: "Read-only (prices and analytics)",
    accessScopeViewer: "Read-only access covers {name} alone: its prices and analytics, never the strategy or the settings.",
    makeViewer: "Make read-only",
    makeFullAccess: "Give full access",
    confirmMakeViewerTitle: "Make {email} read-only on {name}?",
    // What happens on the account's other properties follows (`accessScope`).
    confirmMakeViewer: "They keep read-only access to this property — prices and analytics, no strategy.",
    confirmMakeViewerAction: "Make read-only",
    confirmFullAccessTitle: "Give {email} full access?",
    // A server that does not tell properties apart gives the whole account.
    confirmFullAccess: "They will be able to change the prices, strategies and settings of every property this account owns, and of any it adds later.",
    confirmFullAccessThis: "They will be able to change the prices, strategy and settings of {name}, and of any property the account adds later.",
    confirmFullAccessAction: "Give full access",
    confirmRemoveViewer: "They lose read-only access to {name}. Nothing else changes, and they can be given access again later.",
    // The front desk: one property's prices and reservations, and why each
    // price is what it is — never the analytics or what the property earns.
    roleFrontDesk: "Front desk",
    addRoleFrontDesk: "Front desk (prices and reservations, no analytics)",
    // Nothing is chosen for the admin: granting waits until one is.
    chooseAccess: "Choose the access to grant",
    accessScopeFrontDesk: "Front-desk access covers {name} alone: its prices, reservations and why each price is what it is — never the analytics, the revenue, the strategy or the settings.",
    makeFrontDesk: "Make front desk",
    makeAnalyticsViewer: "Allow analytics",
    confirmMakeFrontDeskTitle: "Give {email} front-desk access to {name}?",
    confirmMakeFrontDesk: "They keep front-desk access to this property — prices and reservations, no analytics or revenue.",
    confirmNarrowToFrontDesk: "They keep this property's reservations with their prices, and no longer see its analytics (occupancy, revenue and recent bookings), the other views of the Prices calendar, the price history or the upload log.",
    confirmMakeFrontDeskAction: "Make front desk",
    confirmWidenToAnalyticsTitle: "Let {email} see the analytics of {name}?",
    confirmWidenToAnalytics: "They will also see this property's analytics (occupancy, revenue and recent bookings) and the whole Prices page: every calendar view, the price history and the upload log.",
    confirmWidenToAnalyticsAction: "Allow analytics",
    // {kind} is the access their row names ("Read-only", "Front desk").
    toastAlreadyReadOnly: "This person already has access to this property: {kind}. Change it on their row.",
    // A server that does not know the front desk gives the analytics instead.
    toastFrontDeskNotGranted: "Front-desk access could not be given: the person now has read-only access with analytics. Remove it if that is not wanted.",
  },

  // ── Admin Features (admin-only) ────────────────────────
  // What is rolled out person by person, and the switch for each person. The
  // features themselves are the server's list: one this page has no entry
  // for under `features` is drawn by its key.
  adminFeatures: {
    searchPlaceholder: "E-mail or property",
    title: "Features",
    description: "What is rolled out person by person",
    intro: "A new feature is not given to every customer on the day it ships. Switch it on here for the people who are to have it, ourselves first. A switch belongs to the person: they have the feature on every property they run, and their colleagues on the same property do not until they are switched on too. A person nobody has decided for has it off.",
    loading: "Loading people...",
    retry: "Try again",
    empty: "Nobody has an account yet",
    loadError: "Failed to load the features",
    personCount: {
      one: "{count} person",
      few: "{count} people",
      other: "{count} people",
    },
    // Table
    person: "Person",
    properties: "Properties they run",
    // Under a person's address: who they are to Optivo, and an account that
    // cannot sign in, for which a switch changes nothing yet.
    administrator: "Optivo administrator",
    accountPending: "Account waiting for approval",
    accountDeactivated: "Account deactivated",
    accountExpired: "Temporary access has ended",
    // A state of an account this version of the page has no words for.
    accountUnknown: "Account status: {status}",
    // What a switch for the person reaches: the properties they run.
    noProperties: "Runs no property",
    unnamedProperty: "Unnamed property",
    // Which people the list shows.
    scopeAll: "Everybody",
    scopeEnabled: "With a feature on",
    noneEnabled: "Nobody has a feature switched on yet",
    // A feature's state for one person, beside its switch. The date is when
    // somebody last switched it; a person nobody has decided for shows none.
    on: "On",
    off: "Off",
    onSince: "On since {date}",
    offSince: "Off since {date}",
    // The switch's own name: the feature and the person it is for.
    switchLabel: "{feature} for {name}",
    // How far a rollout has got, across everybody and whatever is searched.
    enabledNowhere: "Not switched on for anybody",
    enabledCount: {
      one: "On for {count} person",
      few: "On for {count} people",
      other: "On for {count} people",
    },
    // Switching on is asked first: it changes what a customer's people are
    // given, on every property the person runs.
    confirmTitle: "Switch on {feature} for {name}?",
    confirmReach: "They run these properties, and get it on each of them:",
    confirmNoReach: "They run no property yet, so nothing changes for them until they do.",
    confirmAction: "Switch on",
    cancel: "Cancel",
    toastOn: "{feature} is on for {name}",
    toastOff: "{feature} is off for {name}",
    toastFailed: "The feature could not be switched",
    // A feature the server rolls out and this version of the page cannot describe.
    unknownFeature: "This version of the page has no description of this feature. Reload the page to see it.",
    // Something on the server keeps the feature from everybody, and this
    // version of the page has no words for what.
    holdUnknown: "This server does not offer it to anybody right now, so a person switched on does not get it yet.",
    features: {
      assistant: {
        name: "Chat with the Optivo assistant (AI)",
        // The column's heading: short, the row being narrow.
        short: "Assistant (AI)",
        description: "Offers the person a chat with the Optivo assistant (AI) in Ask Optivo, on every property they run. When they ask, the settings, prices and booking counts of the property they have open are sent to Anthropic to be processed. A person with it off sends nothing.",
        holds: {
          not_configured: "This server has no Anthropic API key, so nobody is offered the assistant yet. A person switched on gets it once the key is set.",
          switched_off: "The assistant is switched off for everybody on this server (ASSISTANT=off). A person switched on gets it once that is lifted.",
        },
      },
      mcp: {
        name: "AI agents and security tokens",
        // The column's heading: short, the row being narrow.
        short: "AI agents",
        description: "Shows the person the security tokens and the AI agents section of their account settings, so an AI agent can connect to Optivo with a token they make. A token reaches the properties they run and never does more than they can. Switching it off keeps their tokens, which work again once it is back on.",
        holds: {
          switched_off: "The MCP server is switched off for everybody on this server (MCP_SERVER=off). A person switched on can make tokens, and their agents connect once that is lifted.",
        },
      },
      lighthouse: {
        name: "Competitor prices from the property's own Lighthouse account",
        // The column's heading: short, the row being narrow.
        short: "Lighthouse",
        description: "Offers the person, on the competitor tracking card of every property they run, the choice to read competitor prices from the property's own Lighthouse account instead of Optivo's market data. They paste their Lighthouse API token and pick the hotel and competitor set. Switching it off keeps the link, which works again once it is back on.",
        holds: {
          switched_off: "Lighthouse is switched off for everybody on this server (LIGHTHOUSE=off). A person switched on sees the choice once that is lifted.",
        },
      },
    },
  },

  // ── Admin Activity Log (admin-only) ────────────────────
  adminActivity: {
    searchPlaceholder: "Person, action, or property",
    title: "Activity Log",
    description: "What every user did, across all accounts",
    intro: "Every change a signed-in user makes is recorded here as it happens — settings saved, prices sent, access granted, and the sign-ins around them — together with the answer the server gave. Reads are not recorded, and neither is anything a request carried: no passwords, no card details.",
    loading: "Loading activity...",
    retry: "Try again",
    empty: "Nothing has been recorded yet",
    entryCount: "{count} entries",
    // The window is the server's own number rather than a figure typed into
    // the copy — an env var can move it, and a note that lagged would be a
    // promise nobody checked.
    retentionNote: "Entries are deleted automatically after {days} days.",
    retentionNoteOne: "Entries are deleted automatically after 1 day.",
    loadError: "Failed to load the activity log",
    // Table
    when: "When",
    who: "Who",
    what: "What",
    outcome: "Outcome",
    // A person's name is the way to their own trail: clicking it puts the
    // address in the search box, which is the one filter every admin list
    // has.
    filterByUser: "Show only {name}",
    // The property has been deleted, or never had a name: the id is all
    // that is left to say, and it is said rather than left blank.
    propertyById: "Property #{id}",
    // An administrator opening a customer's property through the read-only
    // access they added themselves, said in words rather than left as a key.
    openedByStaff: "Opened read-only, as an admin",
    // An edit an AI agent made, named by the security token it used — the
    // one thing that tells two of a person's agents apart.
    viaMcpToken: "By an AI agent, with the token “{name}”",
    // A strategy save leaves the diff of what it replaced; the row folds it
    // behind its count. Three forms, because Czech declines the noun with
    // the number.
    changesNone: "No changes",
    changesOne: "1 change",
    changesFew: "{count} changes",
    changesMany: "{count} changes",
    changeCreated: "created",
    changeDeleted: "deleted",
    // Saving from the strategy editor mints a new version; the lines under it
    // compare it with the version it replaced, and this line says which.
    changeVersioned: "created as version {version}, compared with version {from}",
    changesTruncated: "+{count} more, not recorded",
    outcomeOk: "OK",
    outcomeRefused: "Refused",
    outcomeFailed: "Failed",
    // One word per action the recorder can produce, keyed in
    // `components/admin/activity-action-labels.ts`. An action with no word
    // here shows as its raw key.
    actions: {
      signedIn: "Signed in",
      signedOut: "Signed out",
      wireTransferSettledAdmin: "Bank transfer settled (admin)",
      wireTransferRejectedAdmin: "Bank transfer rejected (admin)",
      paymentRefundRecordedAdmin: "Payment refund recorded (admin)",
      connectionSettingsChangedAdmin: "Property name or currency changed (admin)",
      syncCadenceChangedAdmin: "Sync cadence changed (admin)",
      userFeatureSwitchedAdmin: "Feature switched on or off for a person (admin)",
      individualPricingChangedAdmin: "Individual prices changed (admin)",
      recalculationForcedAdmin: "Price recalculation forced (admin)",
      analysisStartedAdmin: "Property analysis re-run (admin)",
      accessGrantedAdmin: "Access granted (admin)",
      memberRoleChangedAdmin: "Member role changed (admin)",
      accessRemovedAdmin: "Access removed (admin)",
      pricePlanDeletedAdmin: "Price plan deleted (admin)",
      pricePlansPurgedAdmin: "Price plans purged (admin)",
      userRoleChangedAdmin: "User role changed (admin)",
      userFeatureChangedAdmin: "Switched a feature for a user (admin)",
      userActiveChangedAdmin: "User activated or deactivated (admin)",
      temporaryAccessCreatedAdmin: "Temporary access created (admin)",
      userDeletedAdmin: "Account deleted (admin)",
      passwordResetLinkIssuedAdmin: "Password reset link generated (admin)",
      strategiesSaved: "Strategies saved",
      autoSyncChanged: "Auto-sync settings changed",
      promoCodeRedeemed: "Promo code redeemed",
      subscriptionChanged: "Subscription settings changed",
      paymentOpened: "Payment opened",
      paymentRefreshed: "Payment status re-checked",
      billingDetailsChanged: "Billing details changed",
      recurringCancelled: "Automatic renewal cancelled",
      connectionSettingsSaved: "Property settings saved",
      distributionSettingsSaved: "Distribution settings saved",
      ratesWrittenToPms: "Rates written to the PMS",
      exchangeRatesRefreshed: "Exchange rates refreshed",
      exchangeRatesOverridden: "Exchange rates set by hand",
      exchangeRateOverridesCleared: "Hand-set exchange rates cleared",
      marketLocationSaved: "Market location saved",
      propertyFactsSaved: "Property type or price facts saved",
      compsetFilterSaved: "Competitor filter saved",
      compsetRefreshed: "Competitor rates refreshed",
      marketSignalsRefreshed: "Market signals refreshed",
      competitorPinned: "Competitor pinned",
      pinnedCompetitorChanged: "Pinned competitor changed",
      competitorUnpinned: "Competitor unpinned",
      competitorSearchRun: "Competitor search run",
      lighthouseLinked: "Lighthouse account linked",
      lighthouseUnlinked: "Lighthouse account unlinked",
      compsetSourceChanged: "Competitor price source changed",
      lighthouseRefreshed: "Competitor prices fetched from Lighthouse",
      allNotificationsRead: "All notifications marked read",
      notificationRead: "Notification marked read",
      aiSettingsSavedAdmin: "AI settings saved (admin)",
      aiModelPullStartedAdmin: "AI model download started (admin)",
      wizardStarted: "Setup wizard started",
      wizardReset: "Setup wizard reset",
      wizardStepSaved: "Setup wizard step saved",
      analysisLinked: "Analysis linked to the setup",
      wizardCompleted: "Setup wizard completed",
      connectionCreated: "Property connection created",
      connectionChanged: "Property connection changed",
      connectionDeleted: "Property connection deleted",
      activePropertySwitched: "Active property switched",
      pmsCredentialsTested: "PMS credentials tested",
      accessGranted: "Access granted",
      memberRoleChanged: "Member role changed",
      accessRemoved: "Access removed",
      pmsCredentialsSaved: "PMS credentials saved",
      priceListBackedUp: "Price list backed up",
      priceListRestoredFromFile: "Price list restored from a file",
      priceListBackupRestored: "Price list backup restored",
      pricePlanEntriesEdited: "Price plan entries edited",
      pricesBulkEdited: "Prices edited for several nights",
      priceAdjustmentSet: "Price adjustment set (without locking)",
      priceAdjustmentRemoved: "Price adjustment removed",
      apiTokenCreated: "Security token created",
      apiTokenRevoked: "Security token revoked",
      mcpSettingsChanged: "AI agent access changed",
      termsAccepted: "General terms accepted",
      // Recorded by the MCP server itself, not by a browser session.
      mcpPriceEditApplied: "Prices edited by an AI agent",
      mcpPriceAdjustmentSet: "Price adjustment set by an AI agent (without locking)",
      mcpPriceAdjustmentRemoved: "Price adjustment removed by an AI agent",
      priceGenerationStarted: "Price calculation started",
      priceGenerationStatusCleared: "Price calculation status cleared",
      bookingSyncTriggered: "Booking sync triggered",
      priceUploadStarted: "Price upload to the PMS started",
      priceUploadResumed: "Price upload resumed",
      priceUploadStatusCleared: "Price upload status cleared",
      analysisStarted: "Property analysis started",
      analysisCleared: "Property analysis cleared",
      bookingsClassified: "Booking classification run",
      roomKindStructureSaved: "Room kind structure saved",
      roomKindOverrideRemoved: "Room kind override removed",
      segmentChannelOverridesSaved: "Channel and segment overrides saved",
      strategyHelpRequested: "Strategy help requested",
      assistantHandedOver: "Assistant conversation handed to support",
      helpRequestCancelled: "Help request withdrawn",
      strategyProposalActivated: "Strategy proposal activated",
      strategyProposalDeclined: "Strategy proposal declined",
      strategyProposalSentAdmin: "Strategy proposal sent (admin)",
      strategyProposalResentAdmin: "Strategy proposal e-mailed again (admin)",
      helpRequestOpenedAdmin: "Help case opened for a customer (admin)",
      helpRequestClosedAdmin: "Help request closed (admin)",
      helpRequestRepliedAdmin: "Help request answered (admin)",
      supportReminderCreatedAdmin: "Support reminder set (admin)",
      supportReminderChangedAdmin: "Support reminder changed (admin)",
      supportReminderDoneAdmin: "Support reminder marked done (admin)",
      supportReminderReopenedAdmin: "Support reminder reopened (admin)",
      supportReminderDeletedAdmin: "Support reminder deleted (admin)",
      supportReminderCaseOpenedAdmin: "Case opened for a support reminder (admin)",
    },
  },

  // ── Admin: strategy help ───────────────────────────────
  // The support side of strategy help (`components/AdminStrategyHelp.tsx` and
  // `components/admin/StrategyProposalDialog.tsx`). The proposal's own words go
  // to the operator in their language; these are the console's.
  adminStrategyHelp: {
    title: "Strategy Help",
    description: "Questions and revenue study requests — sent from the product or opened by support for a customer — the answers and settings proposed, and the reminders of what support promised to come back to.",
    intro: "Answer a case with a letter from Optivo — on its own, or as a proposal: the settings the study's replay priced with, a summary for the review page, and the study's PDF and figures. The operator activates a proposal in one click; nothing changes before that. Close a case you answered some other way.",
    loading: "Loading requests…",
    retry: "Try again",
    searchPlaceholder: "Search case, property, e-mail or message…",
    scopeOpen: "Waiting",
    scopeAll: "All",
    openCount: "{count} waiting",
    noneWaiting: "Nothing is waiting for an answer.",
    noneAtAll: "No requests yet.",
    caseNumber: "Case",
    requested: "Requested",
    property: "Property",
    from: "From",
    request: "Request",
    status: "Status",
    actions: "Actions",
    originSupport: "Opened by support",
    // A follow-up study the operator confirmed when activating a proposal,
    // opened as a case of its own when it fell due.
    originFollowUp: "Follow-up study",
    // A study the monthly revenue review opened for a month that earned no
    // more than the same month a year earlier.
    originRevenueReview: "Monthly revenue review",
    followUpConfirmedFrom: "Confirmed when activating the proposal of {case}, {date}.",
    followUpConfirmedNoCase: "Confirmed when activating a proposal, {date}.",
    kindQuestion: "Question",
    kindStudy: "Revenue study",
    kindSetup: "Setup by Optivo",
    noMessage: "No message",
    languageCs: "Czech",
    languageEn: "English",
    answerIn: "Answer in {language}",
    requestCopies: "Copy: {emails}",
    statusOpen: "Waiting",
    statusClosed: "Closed",
    statusAnswered: "Answered",
    statusProposalPending: "Proposal sent",
    statusProposalUnsent: "Proposal stored, not e-mailed",
    statusProposalAccepted: "Activated",
    statusProposalDeclined: "Declined",
    statusProposalSuperseded: "Superseded",
    // Under a proposal's status: since when it waits, and where the follow-up
    // study it offered stands once it was activated.
    pendingSince: "Waiting to be activated since {date}",
    followUpDue: "Follow-up study due {date}",
    followUpOpenedCase: "Follow-up study opened: {case}",
    followUpOpened: "Follow-up study opened {date}",
    followUpCancelledAt: "Follow-up study cancelled {date}",
    followUpCancelledByNewerAt: "Follow-up study cancelled by a newer activation, {date}",
    followUpNotWanted: "Follow-up study not wanted",
    statusReplySent: "Answered by e-mail",
    statusReplyUnsent: "Answer stored, not e-mailed",
    answer: "Answer",
    sendProposal: "Send proposal",
    closeRequest: "Close",
    closeTitle: "Close this request?",
    closeBody: "Close it when you answered some other way — by e-mail or on the phone. Nothing changes for the operator.",
    closed: "Request closed",
    closeFailed: "The request could not be closed",
    copyLink: "Copy the proposal link",
    linkCopied: "Link copied",
    resend: "E-mail the proposal again",
    resent: "Proposal e-mailed",
    resendFailed: "The proposal was not e-mailed",
    resendReply: "E-mail the answer again",
    replyResent: "Answer e-mailed",
    replyResendFailed: "The answer was not e-mailed",
    // Asked before "Resend" mails a stored answer again to an address whose
    // account no longer reaches the property; the body is `recipientsWithoutAccess`.
    resendWithoutAccessTitle: "E-mail it again to an address without access?",
    resendAnyway: "E-mail it anyway",
    dialogTitle: "Propose settings",
    dialogDescription: "For {property}. The operator sees every setting before activating it.",
    answerTitle: "Answer the case",
    answerDescription: "For {property}. The letter goes from Optivo. With settings it is a proposal the operator reviews and activates in one click; without them, just the answer.",
    questionTitle: "The question this answers",
    questionAskedBy: "{email}, {date}",
    questionQuoted: "The e-mail says it back under the signature — who asked, when and in what words — so the letter does not have to repeat it.",
    questionHandedOver: "A conversation with the Optivo assistant (AI), handed over without a note. Read it in the e-mail support got about this case.",
    questionCreditedHandedOver: "Under the signature the e-mail says only who handed the conversation over and when, and quotes none of it. Say in the letter what you are answering.",
    questionFollowUp: "A follow-up study, confirmed when the proposal of {case} was activated.",
    questionFollowUpNoCase: "A follow-up study, confirmed when a proposal was activated.",
    questionCreditedFollowUp: "Under the signature the e-mail says in one line who confirmed it and when.",
    importTitle: "Load from the study",
    importHint: "Drop the case's folder or its deliver folder here, or choose its files — for an answer, reply.txt and the figures it shows. They fill the settings, the summary, the letter with its recipient and subject, the PDF and the figures, and a proposal is checked at once.",
    importChoose: "Choose files",
    importReading: "Reading…",
    importLoaded: "Loaded from the study",
    importPatch: "Settings",
    importSteps: "Steps",
    importSummary: "Summary",
    importLetter: "Letter",
    importStudy: "Study",
    importFigures: "Figures",
    importNothing: "None of these files belongs in a proposal.",
    importFailed: "The files could not be read.",
    importSeveral: "{files}: several files for one field, so none of them was loaded.",
    importFigureNotShown: "{file} is left out: the letter never shows it.",
    importAttachmentMismatch: "The letter names {named} as its attachment, but the PDF is {file}.",
    importUnused: "Not used: {files}.",
    importStepsInvalid: "{file}: the steps cannot go as they are — see under Steps.",
    importFollowUpInvalid: "{file} names a follow-up term of {named}, which cannot be offered (a term is {min} to {max} days): no term was chosen.",
    importCaseMismatch: "These files answer case {fileCase}, and this proposal answers {case}. Nothing was loaded: send them from {fileCase} in the list.",
    importCaseElsewhere: "These files answer case {fileCase}. Nothing was loaded: send them from {fileCase} in Strategy Help, so the answer stays with its case.",
    connectionLabel: "Property (connection id)",
    recipient: "Recipient",
    copies: "Copy to",
    copiesHint: "The colleagues the operator asked to copy, separated by commas. A letter's Kopie: line replaces them.",
    recipientsWithoutAccess: "No full access to this property: {emails}. In Optivo such an account cannot open this answer, yet the letter still reaches it by e-mail, attachments included. Go ahead only if that is intended.",
    language: "Language",
    patchLabel: "Settings — a JSON merge patch over the strategy's params",
    patchHint: "The study's replay PATCH file as it is: fractions rather than percent, null removes a key.",
    patchOptionalHint: "Optional — without settings only the letter goes. The study's replay PATCH file as it is: fractions rather than percent, null removes a key.",
    loadJson: "Load a JSON file",
    patchInvalidJson: "This is not valid JSON.",
    stepsLabel: "Steps — why each change is made and what it aims for",
    stepsHint: "The steps list of the study's kroky.json: each with a title, why, aim, and the settings it moves — or where the operator does it by hand. The operator reads them on the review page and again when activating.",
    stepsInvalidJson: "These steps are not valid JSON.",
    loadSteps: "Load kroky.json",
    stepsPreviewHint: "As the review page shows them:",
    followUpLabel: "Follow-up study",
    followUpNone: "None",
    followUpDays: { one: "{count} day", few: "{count} days", other: "{count} days" },
    followUpHint: "Offered to the operator when activating: they confirm it, and its case opens on its own when it falls due.",
    followUpFileDiffers: "The study's steps file names {days}: its letter and PDF say that term.",
    followUpFileNone: "The study's steps file offers no follow-up: its letter and PDF name none.",
    // Why the e-mail waits: a server from before the steps stores a proposal
    // without them, under a letter that promises them.
    stepsUnchecked: "Check the proposal before e-mailing it: the steps and the term go only to a server that reads them.",
    stepsNotRead: "This server does not read steps or a follow-up term yet: e-mailed now, the proposal would reach the operator without them. Clear both to e-mail it, or store it without e-mailing.",
    uncoveredKeys: "No step names these settings: {keys}.",
    // What is wrong with the steps; {step} counts from 1, {field} is the JSON field.
    stepsNotAList: "The steps are not a list.",
    stepsTooMany: "At most {max} steps.",
    stepNotAnObject: "Step {step} is not an object.",
    stepUnknownField: "Step {step}: {field} is not a field of a step (title, why, aim, settings, where).",
    stepTextInvalid: "Step {step}: {field} is missing or not text.",
    stepTextTooLong: "Step {step}: {field} is longer than {max} characters.",
    stepSettingsInvalid: "Step {step}: settings is a list of at most {max} paths, each up to {chars} characters.",
    stepSettingNotInPatch: "Step {step} names settings the proposal does not change, or a list item by its place or by a field inside it: {paths}.",
    stepWithoutSettingsOrWhere: "Step {step} names no setting, and no place (where) to do it by hand.",
    followUpDaysInvalid: "A follow-up term is {min} to {max} days.",
    stepProblemOther: "The steps cannot go as they are.",
    summaryLabel: "Summary on the review page",
    summaryHint: "Two or three sentences in the operator's language: what changes and why.",
    sendFromOptivo: "E-mail it from Optivo",
    sendFromOptivoHint: "Off: the proposal is stored and you get its link to send yourself.",
    subject: "Subject",
    message: "Message",
    messageHint: "Paste the study's email.txt or a reply's reply.txt — its Komu and Předmět lines fill the fields above, and a case number its subject ends with has to be this one. A line [obrázek 1: file.png] shows that figure; with settings, [tlačítko] places the button, which otherwise closes the message.",
    study: "Study (PDF)",
    figures: "Figures (PNG or JPEG)",
    removeFile: "Remove {name}",
    check: "Check",
    checking: "Checking…",
    send: "Send proposal",
    store: "Store proposal",
    sendReply: "Send the answer",
    sending: "Sending…",
    changesAgainst: "Changes against {strategy} (version {version})",
    noChanges: "The strategy already has every one of these settings.",
    noActiveStrategy: "The property has no active strategy.",
    replyCheck: "No settings: the letter goes as the answer, without a button.",
    unknownKeys: "Not a setting Optivo knows: {keys}. Check the spelling.",
    unreadableDates: "The engine cannot read these special dates: {patterns}.",
    problemEmpty: "The message is empty.",
    problemHeaders: "The message still starts with the Komu / Předmět lines.",
    problemPlaceholder: "The message still has a [DOPLNIT …] to fill in.",
    problemFigureMissing: "The message shows {file}, but no such figure is attached.",
    problemFigureUnplaced: "{file} is attached, but the message never shows it.",
    problemButtonWithoutSettings: "The message places a button with [tlačítko], but without settings there is nothing for it to open.",
    resultSent: "Proposal e-mailed to {email}.",
    resultSentCopies: "Proposal e-mailed to {email}, with a copy to {copies}.",
    resultStored: "Proposal stored. Send this link yourself:",
    resultStepsDropped: "This server stored the proposal without its steps and follow-up term: the operator sees neither.",
    resultEmailFailed: "Proposal stored, but the e-mail failed ({error}). E-mail it again from the list, or send this link yourself:",
    resultSkipped: "Proposal stored, but this server sends no e-mail. Send this link yourself:",
    replyResultSent: "Answer e-mailed to {email}.",
    replyResultSentCopies: "Answer e-mailed to {email}, with a copy to {copies}.",
    replyResultEmailFailed: "Answer stored, but the e-mail failed ({error}). E-mail it again from the list.",
    replyResultSkipped: "Answer stored, but this server sends no e-mail.",
    copy: "Copy",
    done: "Done",
    failed: "The proposal was not stored",
    replyFailed: "The answer was not sent",
    // A follow-up reminder support sets on a case: the row action, and the
    // badge that narrows the list to the cases with one fallen due.
    remind: "Set a reminder",
    remindersDue: "Reminders due: {count}",
    scopeDue: "Reminder due",
    noneDue: "No reminder is due.",
    // Reminders set on a property alone, before every reminder had a case.
    caselessTitle: "Reminders without a case ({count})",
    caselessIntro: "Each was set on a property alone. Open a case for it, so the answer — a letter or recommended settings — goes out through Optivo; the reminder moves onto the case.",
    // A case support opens on a customer's behalf
    // (`components/admin/SupportCaseDialog.tsx`); the customer sees nothing of
    // it until the answer reaches them.
    newCase: "New case",
    newCaseTitle: "Open a case for a customer",
    newCaseDescription: "A question or a revenue study the customer asked for by phone or by e-mail. It is answered here like any other case; the customer sees nothing of it until the answer reaches them.",
    newCasePropertyHint: "The property the case is about.",
    newCaseKindLabel: "What was asked for",
    newCaseMessageLabel: "What the customer asked",
    newCaseMessageHintQuestion: "In your words, for the queue. The answer does not quote it back.",
    newCaseMessageHintStudy: "Optional for a study: what the customer wants looked at. The answer does not quote it back.",
    newCaseRecipientLabel: "Answer goes to",
    newCaseRecipientHint: "Usually the person who asked. The property's people are offered as you type.",
    newCaseCopiesLabel: "Copy the answer to",
    newCaseCopiesHint: "Up to 5 addresses, separated by commas. Optional.",
    newCaseLanguageLabel: "Answer in",
    newCaseFollowUp: "Come back to it on a later day",
    newCaseFollowUpHint: "Sets a reminder on the case: on that day support@ gets one e-mail saying what was promised.",
    newCaseSave: "Open case",
    newCaseSaving: "Opening…",
    newCaseOpened: "{case} opened",
    newCaseOpenedWithFollowUp: "{case} opened, follow-up on {date}",
    newCaseFailed: "The case could not be opened.",
    // Opening a case for a reminder set on a property alone, from its row.
    newCaseForReminderTitle: "Open a case for this reminder",
    newCaseForReminderDescription: "So the answer — a letter or recommended settings — goes out through Optivo from the case. The reminder moves onto it, its day and promise unchanged.",
    newCaseForReminderFor: "{property}, reminder for {date}:",
    newCaseOpenedForReminder: "{case} opened; the reminder is on it",
    newCaseErrorProperty: "Pick the property.",
    newCaseErrorMessage: "Say what the customer asked.",
    newCaseErrorMessageTooLong: "The message can be at most 4,000 characters.",
    newCaseErrorRecipient: "Enter the e-mail address the answer goes to.",
    newCaseErrorCopies: "Each copy must be an e-mail address, and at most 5 of them.",
    // A property with no name, said by its id.
    propertyById: "Property #{id}",
  },

  // ── Admin: support reminders ───────────────────────────
  // What support promised a customer for a later day
  // (`components/AdminSupportReminders.tsx` and
  // `components/admin/SupportReminderDialog.tsx`). The note itself is written
  // in English, for the e-mail support@ gets on the day.
  adminSupportReminders: {
    // A property with no name, said by its id.
    propertyById: "Property #{id}",
    mailThread: "Mail thread {ref}",
    recordedBy: "{email}, {date}",
    stateOverdue: "Overdue",
    stateToday: "Today",
    stateUpcoming: "Upcoming",
    // The follow-ups listed in a case's row of the strategy-help list.
    listLabel: "Reminders",
    followUpOn: "Reminder",
    // Where the reminder's e-mail to support@ stands. {reason} is a reason in
    // words where this page has them (mailReason…), else as the server recorded it.
    mailSent: "Mailed {date}",
    mailSkipped: "Not sent: {reason}",
    mailSending: "Sending…",
    mailUnconfirmed: "Not confirmed, sent again with the next pass",
    mailUnconfirmedLast: "Not confirmed on the last attempt; not sent again",
    mailDoneUnconfirmed: "Not confirmed; not sent again, marked done",
    mailRetrying: "Failed ({reason}), retried hourly — attempt {attempts} of {max}",
    mailGaveUp: "Gave up after {attempts} attempts: {reason}",
    mailDue: "Due, not sent yet",
    mailUpcoming: "Will be sent on the due day",
    mailDoneUnsent: "Not sent: marked done before it went out",
    mailDoneFailed: "Failed ({reason}); not tried again, marked done",
    // A send record this version of the page has no words for.
    mailUnknown: "State not known to this version of the page; reload",
    mailReasonNoTransport: "mail is not set up on this server",
    mailReasonNotDelivered: "the mail service did not send it",
    mailReasonLeaseLapsed: "the last attempt was never confirmed",
    noteMore: "Show the whole note",
    noteLess: "Show less",
    markDone: "Done",
    edit: "Edit",
    delete: "Delete",
    deleteTitle: "Delete this reminder?",
    deleteBody: "It is removed for good, and nothing is e-mailed on its day.",
    markedDone: "Reminder marked done",
    deleted: "Reminder deleted",
    markDoneFailed: "The reminder was not marked done",
    deleteFailed: "The reminder was not deleted",
    createTitle: "New reminder",
    createDescription: "What support promised and for which day. On that day support@ gets one e-mail with it.",
    createForCase: "For {case} · {property}.",
    editTitle: "Edit the reminder",
    editDescription: "A new day e-mails it again on that day.",
    editFor: "About {subject}.",
    propertyLabel: "Property",
    propertyPlaceholder: "Choose a property",
    propertyLoading: "Loading properties…",
    propertyLoadFailed: "The properties could not be loaded.",
    propertyNotLoaded: "Not loaded",
    propertyRetry: "Load again",
    // Offered where the console holds more properties than one read lists.
    propertySearchLabel: "Find a property",
    propertySearchPlaceholder: "Part of its name or its account's",
    propertiesCapped: "Showing the first {shown} of {total} properties. Type part of a name to find another.",
    propertyNoMatch: "No property matches.",
    dueLabel: "Due day",
    dueHint: "Support's day, on Prague's calendar.",
    noteLabel: "What was promised",
    noteHint: "In English, as support@ will read it on the day.",
    noteCount: "{count} / {max} characters",
    mailRefLabel: "Mail thread",
    mailRefPlaceholder: "support:114",
    mailRefHint: "Optional: the mailbox and the message to answer in.",
    save: "Save",
    saving: "Saving…",
    created: "Reminder set for {date}",
    saved: "Reminder saved",
    failed: "The reminder was not saved",
    errorDueInvalid: "Choose the day it falls due.",
    errorDuePast: "That day has passed. Choose today or a later day.",
    errorNoteRequired: "Say what was promised.",
    errorNoteTooLong: "Keep it to 1000 characters.",
    errorMailRefInvalid: "A mail thread is a mailbox and a number, like support:114.",
    errorCaseNotFound: "No case has that number.",
    errorPropertyNotFound: "That property no longer exists.",
    errorCaseMismatch: "That case belongs to another property.",
    errorNotFound: "This reminder no longer exists.",
    errorDone: "This reminder is done. Reopen it to change it.",
    errorAlreadyDone: "This reminder was already done.",
    errorNotDone: "This reminder is still open.",
    errorHasCase: "This reminder is already on a case.",
    // On a reminder set on a property alone, before reminders always had a case.
    openCase: "Open a case",
  },

  // ── Account Deactivated ────────────────────────────────
  accountDeactivated: {
    title: "Account Deactivated",
    message: "Your account has been deactivated by an administrator. Please contact your administrator for assistance.",
    signOut: "Sign out",
    signInWithDifferentAccount: "Sign in with a different account",
  },

  // ── Temporary Access Ended ─────────────────────────────
  accountExpired: {
    title: "Temporary Access Ended",
    message: "The temporary access for this account has ended. Ask an administrator if you need it extended.",
    signInWithDifferentAccount: "Sign in with a different account",
  },

  // ── Account Pending Admin Approval ─────────────────────
  termsConsent: {
    title: "Optivo's general terms",
    intro: "To use Optivo, confirm the business you act for and accept the general terms. They bind a user who registered earlier from the day they are accepted here.",
    pendingActivation: "Once you accept, we still review and activate your account. You cannot use Optivo until then. We will let you know by e-mail as soon as it is active.",
    signedInAs: "Signed in as {email}",
    companyIdLabel: "Company ID (IČO) of the business you act for",
    companyIdHint: "Eight digits, as the commercial or trade register states it.",
    companyIdInvalid: "This company ID does not match its check digit. Check it, please.",
    companyIdPrefilled: "The company ID you stated last time is filled in.",
    business: "I declare that I conclude the contract in the course of my business.",
    terms: "I confirm that I know the general terms and their annexes, and I accept them.",
    documentsLabel: "Read them here (each opens in a new tab):",
    documentTerms: "General terms",
    documentSpecification: "Service specification (Annex 1)",
    documentDpa: "Data processing agreement (Annex 2)",
    documentPriceList: "Price list (Annex 3)",
    expressHeading: "I expressly accept these provisions of the general terms:",
    serviceNature: "the nature of the service: Optivo is a tool, not an adviser, and prices are mine to decide",
    pricePush: "sending prices to the reservation system",
    automatedTools: "the use of automated tools and artificial intelligence",
    anonymisedData: "the use of anonymised data",
    knowHowPenalty: "the protection of the service and its know-how, including the contractual penalty",
    liability: "compensation for harm and its limitation",
    articleLink: "Article {article}",
    allRequired: "To continue, confirm every item.",
    submit: "Accept and continue",
    submitting: "Saving…",
    signOut: "Sign out",
    versionChanged: "The general terms have changed in the meantime. Reload the page and read the current wording.",
    reload: "Reload",
    failed: "The acceptance could not be saved. Try again, please.",
  },
  accountPendingApproval: {
    title: "Waiting for Admin Approval",
    message: "Thanks for signing up! Your account is pending approval from an administrator. You will be able to sign in as soon as your account has been activated. We will let you know by e-mail.",
    signOut: "Sign out",
    signInWithDifferentAccount: "Sign in with a different account",
  },

  // ── Another property is open ───────────────────────────
  // The open property is shared by every tab, window and device of a user, and
  // a page reads it once. `title` and `description` are said when a settings
  // card's or the strategy's save is refused because the page was loaded for
  // another property than the one open now; the page may already be gone when
  // the answer comes (hence "in this tab"). `pageDescription` and `sendTitle`
  // say the same of the Prices page's changes and its send. The rest is the
  // notice the shell shows in place of any page once it finds another property
  // open (`components/pms/PropertyOpenedElsewhereNotice.tsx`).
  activeConnectionChanged: {
    title: "Nothing was saved",
    description:
      "The open property changed after this page loaded, in this tab or in another one. Reload the page to see the settings of the property that is open now.",
    pageDescription:
      "Another property has been opened since this page loaded, in this tab or in another one. Reload the page to see the property that is open now.",
    sendTitle: "Nothing was sent",
    recalculateTitle: "Prices were not recalculated",
    reload: "Reload page",
    openedTitle: "Another property is open now",
    openedDescription:
      "It was opened after this tab loaded, in another tab, window or device. This page is hidden so that nothing on it is read from or saved to the wrong property.",
    closedTitle: "No property is open now",
    closedDescription:
      "The property this tab shows is no longer open, and no other property has been opened. This page is hidden so that nothing on it is saved.",
    thisTab: "This tab shows",
    openNow: "Open now",
    noneOpen: "No property",
    unsavedKept: "Your unsaved changes are still here. Open {name} again to save them — reloading the page discards them.",
    unsavedKeptUnnamed:
      "Your unsaved changes are still here. Open this tab's property again to save them — reloading the page discards them.",
    switchBack: "Open {name} again",
    switchBackUnnamed: "Open this tab's property again",
    switchBackFailed:
      "The property could not be opened again. Reload the page to continue with the property that is open.",
  },

  // ── Drafts ──────────────────────────────────────────────
  drafts: {
    title: "Price plan",
    description: "Review the prices Optivo prepared, send them to Property Management, or clear them",
    loading: "Loading prices…",
    loadError: "Failed to load the price plan",
    // Prices are worked out on each sync and when the strategy changes;
    // this screen only re-reads what Optivo has already stored, so the
    // button says "data" and the line beside it says how old that is.
    refreshData: "Refresh data",
    refreshingData: "Refreshing data…",
    lastCalculated: "Prices last calculated {time}",
    lastCalculatedUnknown: "Last calculation not recorded",
    empty: "No price plan for these dates",
    emptyHint: "Optivo prepares prices on each sync and whenever the strategy changes. Check back after the next sync, or pick a different date range.",
    dateRange: "{from} \u2014 {to}",
    entries: "{count} prices",
    createdAt: "Created: {date}",
    updatedAt: "Updated: {date}",
    sendToPms: "Send to Property Management",
    sending: "Sending...",
    sendSuccess: "{count} prices sent to Property Management successfully",
    nothingToSend: "No prices to send — they may already be synced or filtered out",
    sendConfirm: "Send all {count} prices to Property Management? This will immediately update your rates.",
    ratePlan: "Rate Plan",
    ratePlanDefault: "Base rate plan (default)",
    ratePlanLoading: "Loading rate plans...",
    ratePlanError: "Failed to load rate plans",
    sendRangeLabel: "Date range to send",
    sendRange1: "Today only",
    sendRange3: "Next 3 days",
    sendRange7: "Next 7 days",
    sendRange14: "Next 14 days",
    sendRange30: "Next 30 days",
    sendRange60: "Next 60 days",
    sendRange90: "Next 90 days",
    // Named for what it sends: the entries the dialog is handed are the dates
    // on screen, never the plan's whole year.
    sendRangeAll: "All dates on screen",
    // ── What a send writes ──────────────────────────────────────────────────
    // Under the count: the first and the last night the send writes and how
    // many nights. `{nights}` is a `sendNightCount` form. A send carries only
    // what is unsent, so it can skip nights between its first and its last —
    // `sendNightsSparse` says so rather than naming a run.
    sendNightCount: { one: "{count} night", few: "{count} nights", other: "{count} nights" },
    sendNightsSingle: "{date}, {nights}",
    sendNightsSpan: "{from} – {to}, {nights}",
    sendNightsRange: "{from} – {to}",
    sendNightsSparse: "{nights} between {from} and {to}",
    // A price Optivo has no record of sending replaces whatever the PMS holds
    // for the night — the hotel's own price list, usually. "On record": the
    // record is the plan and the Upload Log, which is kept for a few weeks.
    sendFirstTime: {
      one: "{count} price goes to Property Management for the first time on record and replaces the price it holds now.",
      few: "{count} prices go to Property Management for the first time on record and replace the prices it holds now.",
      other: "{count} prices go to Property Management for the first time on record and replace the prices it holds now.",
    },
    // Said when one of them lies past the last night the auto-sync sends.
    // `{days}` is a `sendAutoSyncDays` form, `{date}` that last night.
    sendAutoSyncDays: { one: "{count} day", few: "{count} days", other: "{count} days" },
    sendAutoSyncReach: "Auto-sync sends prices only {days} ahead, to {date}.",
    // The dates on screen standing in for a default window that held nothing
    // to send: a range nobody picked. `{window}` is that window's own label,
    // `{range}` the first and the last night of the send.
    sendFallbackTitle: "You did not pick this range",
    sendFallbackWhy: "“{window}” holds nothing to send, so every date on screen is offered instead.",
    sendFallbackAck: "I want to send the prices for {range}.",
    // Beside a Send that waits for a tick: the warning it belongs to is at the
    // top of a dialog that scrolls, and Send is at its foot.
    sendHeldForTick: "Tick the confirmation above to send.",
    sendLongRangeWarningTitle: "This send covers more than 90 days",
    sendLongRangeWarning: "You are about to send {count} prices covering {days} days. Your property management system accepts these one batch at a time, so this takes about {minutes} minutes and cannot be paused. Auto-sync can keep up to a year and a half up to date on its own schedule — send that much by hand only when you really need to.",
    sendLongRangeAck: "I understand this upload will take about {minutes} minutes.",
    // ── Price-list currency ─────────────────────────────────────────────────
    // A PMS takes a pushed price as a number in its own currency, so a send
    // priced in another currency is stopped before anything goes out.
    currencyBlockedTitle: "Sending is blocked: the currencies do not match",
    currencyNothingSentTitle: "Nothing was sent: the currencies do not match",
    currencyMismatchWhole: "Optivo calculates this property's prices in {pricedIn}, but the price list in {pms} is kept in {listCurrency}. {pms} would read every price as {listCurrency}.",
    currencyMismatchPartial: "Optivo calculates this property's prices in {pricedIn}, but in {pms} the price list of {roomTypes} is kept in {listCurrency}. {pms} would read those prices as {listCurrency}.",
    currencyFixWhole: "Change the property currency in Optivo ({settingsPath}) to {listCurrency}, or the price list currency in {pms} to {pricedIn}, then send again.",
    currencyFixPartial: "Change the price list currency of those room types in {pms} to {pricedIn}, then send again.",
    currencyFixMixed: "Make the property currency in Optivo ({settingsPath}) and the price list currency in {pms} the same, then send again.",
    currencyUnchecked: "Optivo could not check which currency the price list in {pms} is kept in, so these prices would be sent without that check.",
    sendRoomKindsLabel: "Room types to send",
    sendRoomKindsAll: "All room types",
    sendRoomKindsSearch: "Search room types…",
    expand: "Show entries",
    collapse: "Hide entries",
    room: "Room",
    date: "Date",
    source: "Source",
    sourceManual: "Manual",
    sourceRegenerated: "Regenerated",
    occupancy: "Occupancy",
    prices: "Prices",
    los: "LOS",
    goToCalendar: "Go to Calendar",
    saving: "Saving...",
    saveError: "Failed to save changes",
    generateDrafts: "Generate Drafts",
    generating: "Generating…",
    generateSuccess: "Generated {count} price entries",
    generateError: "Failed to generate drafts",
    viewCalendar: "Calendar",
    viewReservations: "Reservations",
    viewMinStay: "Minimum stay",
    searchRoomTypes: "Search…",
    allRoomTypes: "All room types",
    allSources: "All statuses",
    filterSource: "Status",
    clearFilters: "Clear filters",
    noFilterResults: "No prices match the selected filters.",
    roomKinds: "room kinds",
    days: "days",
    uploadProgress: "Uploading prices… {completed} of {total}",
    uploadComplete: "{count} prices uploaded successfully",
    // The PMS recalculates the rate plans that hang off the base plan whenever
    // a base price changes. Optivo waits that out before writing its own
    // channel and non-refundable prices, or the PMS would overwrite them.
    settlingForPms: "Waiting for Property Management to finish updating its linked rate plans…",
    sendingDerivedPlans: "Sending channel and non-refundable rate plans…",
    derivedPlansPending: "{count} linked rate plans still to send",
    uploadCompleteOf: "{sent} of {requested} prices sent to Property Management",
    uploadSkippedSoldOut: "{count} skipped — those room-nights are sold out, so a new rate would change nothing.",
    uploadFinishedHintPartial: "Skipped prices stay in the draft and go out on the next send once a room frees up. The Upload Log has the per-price breakdown.",
    uploadShowSkipped: "Show skipped",
    uploadHideSkipped: "Hide skipped",
    roomKindRenamed: "Renamed in the PMS — Optivo now shows the current name.",
    manageRoomKinds: "Manage room kinds",
    uploadPartial: "{delivered} of {total} prices delivered to Property Management",
    uploadRefused: "Refused by Property Management: {count}. Sending them again changes nothing until the reason below is fixed there; until then they stay unsent in the draft.",
    // Not a refusal: Optivo left these unwritten itself, so nothing needs
    // fixing in the PMS. A minimum stay sent on its own to a PMS that keeps it
    // on the price is the case: a night whose price Optivo cannot vouch for.
    uploadHeld: "Held back by Optivo: {count}. Optivo did not write them, for the reason below, so Property Management refused nothing and nothing needs fixing there. They stay unsent in the draft, and a later send tries them again.",
    // The same two of a restore, or of a retry of one, where the last thing
    // each says of a send does not hold: a restore commits no drafts, so
    // nothing stays unsent in one and no later send tries the night again.
    // What a restore holds back is a minimum stay put back without a price,
    // as the Upload Log says under that restore's own row. What a PMS refuses
    // of one can be a price or a minimum stay.
    uploadRefusedRestore: "Refused by Property Management: {count}. Restoring them again changes nothing until the reason below is fixed there. Optivo does not try a restore again, so those nights keep what they have now. To put the saved values back, restore again once the reason is fixed, or set them in Property Management.",
    uploadHeldRestore: "Held back by Optivo: {count}. Optivo did not write them, for the reason below, so Property Management refused nothing and nothing needs fixing there. Optivo does not try a restore again, so those nights keep the minimum stay they have now. To put the saved ones back, restore again later or set them in Property Management.",
    uploadHeldHint: "The Upload Log names each night and why it was held back.",
    uploadUndelivered: "Not delivered — the connection failed: {count}. A retry can still deliver them.",
    uploadRetry: "Retry {count}",
    uploadMoreErrors: "{count} more errors",
    uploadMoreReasons: "{count} more",
    uploadFinishedHint: "Nothing left to send for these prices. The Upload Log has the per-price breakdown, including what each rate changed from.",
    uploadDryRunHint: "No rates were sent. The Upload Log records this dry run so you can review what would have changed.",
    uploadFailedHint: "The Upload Log has the full error detail for this attempt.",
    viewUploadLog: "Open Upload Log",
    queued: "Queued",
    queuedCount: "+{count} queued",
    uploadQueuedToast: "Upload queued — it will start when the current one finishes.",
    uploadBusyToast: "A scheduled sync is sending prices right now — try again in a minute.",
    uploadStartFailedToast: "The upload did not start: {error}",
    // ── Upload scope ────────────────────────────────────────────────────────
    // Prices and minimum stay are independent layers of an upload.
    sendPricesLabel: "Upload prices",
    sendPricesHint: "Write the nightly rates from this draft to your Property Management.",
    sendMinLosLabel: "Upload minimum stay",
    // What the switch does to the PMS, said whether or not anything else can
    // be: it replaces the minimum stays set there, which is why it is off.
    sendMinLosHint: "Write Optivo's minimum length of stay for each night, in place of the one your Property Management holds. Off by default, so that yours stay as they are.",
    sendScopeNothingSelected: "Select at least one of prices or minimum stay.",
    sendMinLosUnsupported: "This Property Management connection cannot receive minimum stay restrictions.",
    // Offers what Optivo has already sent as well, for after a change made in
    // the PMS itself, which Optivo cannot see.
    resendLabel: "Send again what was already sent",
    resendHint: "Also sends the nights Optivo has sent before, for when they were changed in your Property Management directly. Off, only what changed since the last send goes.",
    // A send never writes a room type with no room left that night.
    soldOutLeftOut: {
      one: "{count} sold-out room type night is left out: a send skips a room type with no room left that night.",
      few: "{count} sold-out room type nights are left out: a send skips a room type with no room left that night.",
      other: "{count} sold-out room type nights are left out: a send skips a room type with no room left that night.",
    },
    dryRunLabel: "Dry run",
    dryRunHint: "Validate and produce the upload log without sending rates to Property Management.",
    dryRunBadge: "Dry run",
    dryRunInProgress: "Dry run — validating {completed} of {total} (no rates sent)",
    dryRunComplete: "Dry run finished — {count} entries validated, no rates sent to Property Management",
    bookingHighlight: {
      title: "Booking · {roomKind}",
      nightsAffected: "night(s) affected",
      nightsHighlighted: "night(s) highlighted",
      price: "Price",
      occupancy: "Occupancy",
      explanationLoading: "Working out what this booking changed…",
      demandShift: "Demand moved from {demandBefore} to {demandAfter}.",
      demandSteady: "Demand stayed {demandAfter}.",
      explanationHighlight: "The {nights} night(s) of this stay are highlighted below — each cell shows the current draft price for that night.",
      explanationUnchanged: "Occupancy for these nights held at {occ}%, so the draft price is unchanged.",
      explanationUp: "Occupancy moved from {occBefore}% to {occAfter}% after this booking, and the pricing model raised the draft nightly rate.",
      explanationDown: "Occupancy moved from {occBefore}% to {occAfter}% after this booking, and the pricing model lowered the draft nightly rate.",
      explanationFlat: "Occupancy moved from {occBefore}% to {occAfter}% after this booking, but the draft nightly rate came out the same.",
      // The dot on each highlighted night, named for a screen reader.
      cellMark: "Booking impact",
    },
    lockPrice: "Lock price",
    unlockPrice: "Unlock price",
    // What the toast says once a lock or unlock has landed. The two above are
    // the button's instruction, and read back afterwards they tell the
    // operator to do what they have just done.
    priceLockedToast: "Price locked",
    priceUnlockedToast: "Price unlocked",
    // Unlocking hands the night back to the engine, which prices it before the
    // request answers — so the notice that counted the wait down becomes the
    // outcome in its place, and the outcome only claims a recalculation when
    // there was one.
    unlockRecalculatingToast: "Unlocking the price and recalculating…",
    priceUnlockedRecalculatedToast: "Price unlocked and recalculated",
    // The same for the detail panel's save, whose button is
    // `suggestionDetail.savePrices` ("Save & Lock prices").
    pricesSavedToast: "Prices saved and locked",
    lockConfirmTitle: "Lock this price?",
    // A price lock is the price's alone: the minimum stay keeps a lock of its
    // own, and both confirmations say so before anything moves.
    lockConfirmDesc: "Locked prices will not be overwritten by automatic price generation, and the locked price is the one that goes to your PMS. Optivo keeps calculating its own price for the night and shows it beside yours. All occupancy options for this date and room will be locked together. Only the price is locked: the minimum stay has a lock of its own, in the Minimum stay view.",
    unlockConfirmTitle: "Unlock this price?",
    unlockConfirmDesc: "Unlocking hands this price back to Optivo: the night is recalculated straight away, so you can see what the strategy makes of it. The new price reaches your Property Management System with the next sync — automatic or manual — even if the value hasn't changed. Only the price is unlocked: a locked minimum stay stays locked until you unlock it separately.",
    lockedBadge: "Locked",
    uploadedBadge: "Uploaded",
    // Several nights × room types in one edit, from the Prices page header
    // (`drafts/BulkPriceEditDialog.tsx`, `services/core/bulkPriceEdit.ts`).
    bulkEdit: {
      openButton: "Edit several nights",
      title: "Edit several nights at once",
      description: "Set and lock one price, raise or lower the prices, lock them as they are, or unlock them — for a run of nights and one or more room types.",
      // The same dialog opened on nights selected on the grid.
      titleSelection: "Edit prices",
      descriptionSelection: "Set or change the prices of the nights you selected, or lock or unlock them.",
      selectionLabel: "Selected nights",
      nightsLabel: "Nights",
      nightsHint: "Click the first night, then the last.",
      nightCount: { one: "{count} night", few: "{count} nights", other: "{count} nights" },
      roomTypeCount: { one: "{count} room type", few: "{count} room types", other: "{count} room types" },
      scope: "{nights} for {roomTypes}",
      tooManyNights: "One edit covers at most {max} nights.",
      roomTypesLabel: "Room types",
      actionLabel: "What to do",
      actionSetAndLock: "Set a price and lock it",
      actionSetAndLockHint: "Optivo stops recalculating these prices and sends yours instead.",
      actionChange: "Raise or lower the prices and lock them",
      actionChangeHint: "Each price moves from where it is now by the same amount or percentage, and Optivo stops recalculating it.",
      directionLabel: "Direction",
      raise: "Raise",
      lower: "Lower",
      changeLabel: "By how much",
      unitLabel: "Unit",
      changeAmountHint: "Every guest count moves by the same amount.",
      changePercentHint: "Every guest count moves by the same percentage, rounded the way your strategy rounds prices.",
      percentTooDeep: "A cut must stay below 100%.",
      actionLock: "Lock the current prices",
      actionLockHint: "The prices stay as they are now; Optivo goes on setting the minimum stay.",
      // A price lock is the price's alone, so locking both is a choice of its own.
      actionLockPriceAndMinStay: "Lock the current prices and minimum stays",
      actionLockPriceAndMinStayHint: "The prices and the minimum stays stay as they are now.",
      actionUnlock: "Unlock",
      actionUnlockHint: "Optivo prices these nights from your strategy again, straight away. A locked minimum stay stays locked.",
      priceLabel: "Price per night ({currency})",
      priceHint: "For the occupancy the calendar shows, usually two guests. The prices for other guest counts move by the same amount.",
      // Under the price field while its text is no number ("1 500", "1500 CZK").
      // The field reads a decimal comma or a point, and nothing is set from
      // the digits such text begins with.
      priceUnreadable: "Enter the price as a number alone, such as 1500: no spaces, sign or currency.",
      submitSetAndLock: "Set and lock",
      submitChange: "Change and lock",
      submitLock: "Lock",
      submitLockPriceAndMinStay: "Lock both",
      submitUnlock: "Unlock",
      submitting: "Saving…",
      doneSetAndLock: { one: "{count} price set and locked", few: "{count} prices set and locked", other: "{count} prices set and locked" },
      doneChange: { one: "{count} price changed and locked", few: "{count} prices changed and locked", other: "{count} prices changed and locked" },
      doneLock: { one: "{count} price locked", few: "{count} prices locked", other: "{count} prices locked" },
      doneLockPriceAndMinStay: { one: "{count} price and minimum stay locked", few: "{count} prices and minimum stays locked", other: "{count} prices and minimum stays locked" },
      doneUnlock: { one: "{count} price unlocked", few: "{count} prices unlocked", other: "{count} prices unlocked" },
      recalculatedNote: "Optivo has priced them again from your strategy.",
      notRecalculatedNote: "Optivo prices them again at the next sync.",
      skippedNote: "Skipped {count}: Optivo has no price for them yet.",
      tooLowNote: "Left as they were: {count} — the change would have taken them to zero or below.",
      // What the edit would do, stated before it is applied.
      previewPrices: "Prices: {before} → {after} {currency}",
      previewTooLow: "Left as they are: {count} — the change would take them to zero or below.",
      previewMissing: "No price to change: {count}.",
      alreadyLockedNote: "Already locked: {count}.",
      notLockedNote: "Not locked: {count}.",
      nothingChanged: "There was nothing to change on these nights.",
      errors: {
        INVALID_RANGE: "Pick the first and the last night again.",
        PAST_RANGE: "These nights have already passed.",
        RANGE_TOO_LONG: "That is more nights than one edit can cover.",
        NO_ROOM_KINDS: "Pick at least one room type.",
        INVALID_ACTION: "Pick what to do with the prices.",
        INVALID_PRICE: "Enter a price above zero.",
        INVALID_AMOUNT: "Enter an amount above zero.",
        INVALID_PERCENT: "Enter a percentage above zero; a cut must stay below 100% and a rise at most 300%.",
        INVALID_MIN_LOS: "Enter a whole number of nights from 1 to 14.",
        generic: "The prices could not be changed. Please try again.",
      },
    },
    // Cells selected on the grid and the actions that apply to them
    // (`drafts/PriceSelectionBar.tsx`). The same actions sit behind a right
    // click, as a shortcut.
    selection: {
      region: "Selected prices",
      summary: "{roomTypes} · {nights}",
      dateRange: "{from} – {to} ({nights})",
      menuButton: "Edit prices",
      detail: "Price detail",
      setPrice: "Set a new price…",
      shiftPrice: "Change by an amount…",
      scalePrice: "Change by a percentage…",
      // What the selection's two "change by" items read: both lock what they
      // write, and beside "Adjust without locking…" a label that left the
      // lock out would be the first one an operator after "lower by 300" met.
      shiftPriceAndLock: "Change by an amount and lock…",
      scalePriceAndLock: "Change by a percentage and lock…",
      lock: "Lock prices",
      lockPriceAndMinStay: "Lock prices and minimum stays",
      unlock: "Unlock prices…",
      adjust: "Adjust without locking…",
      removeAdjustment: "Remove adjustment…",
      chooseNights: "Pick nights on a calendar…",
      clear: "Clear the selection",
      extendHint: "Shift+click or drag to select more",
      // A phone has no Shift key and its finger scrolls the grid, so the
      // docked bar offers the stretch as a switch of its own.
      extendOnTap: "Select a range",
      extendOnTapHint: "Tap the night where the range ends.",
    },
    // ── Adjustment without locking ────────────────────────────────────────
    // A signed amount or percentage an operator lays over Optivo's price of a
    // night of a room type, which stands while Optivo goes on recalculating
    // the night (`drafts/priceAdjustment.ts`; read through
    // `priceAdjustmentCopy`). It is never called "manual", which is the word
    // for a price typed by hand. No sentence says when a waiting adjustment is
    // applied: whichever recalculation prices its night next settles it, and
    // nothing on the page knows when that is.
    adjustment: {
      title: "Adjust without locking",
      // The same adjustment as a choice of "Edit several nights", directly
      // under the raise or cut that locks: the two differ by the lock alone,
      // and the hint says what that means before the choice is made.
      bulkAction: "Raise or lower the prices without locking",
      bulkActionHint: "Nothing is locked: Optivo keeps recalculating these nights and applies your adjustment to each new price, until you remove it or the night passes.",
      raise: "Raise",
      lower: "Lower",
      size: "By how much",
      // Under the size field while its text is no size. The field reads a
      // decimal comma or a point and takes no sign: the direction is the
      // toggle's beside it.
      sizeUnreadable: "Enter a number without a sign, such as 150 or 7.5.",
      stands: "Nothing is locked. Optivo keeps recalculating these nights and applies your adjustment to each new price, within the room type's minimum and maximum price for that night. It stays until you remove it or the night passes.",
      amountHint: "An amount moves the price for every guest count by the same sum.",
      percentHint: "A percentage of the price Optivo calculates for each night, not of the base price.",
      // A raise shrinks by the last-minute curve's reach for the night, which
      // Optivo stores from the window's opening whether or not the curve has
      // moved its own price yet. A cut does not shrink.
      raiseLastMinute: "Inside the last-minute window a raise shrinks as far as the last-minute curve has reached for the night, even before the curve moves Optivo's own price, so a free room still falls to its last-minute minimum.",
      lowerLastMinute: "A cut stays in force to the end, and never takes the price under the room type's minimum price for the night.",
      scopeChange: "Change nights and room types…",
      scopeNights: "Nights",
      scopeRoomTypes: "Room types",
      previewLoading: "Working out the prices…",
      previewPrices: "Prices now {before} → with the adjustment {after} {currency}",
      // Under the prices line, where only part of a raise is in force.
      // `{share}`: "16%", "16–40%"; `{inForce}`: "+140 CZK" against Optivo's
      // price. Counted where the window reaches only some of the prices.
      previewLastMinuteShare: "Inside the last-minute window {share} of it applies today ({inForce}).",
      previewLastMinuteShareSome: {
        one: "For {count} price inside the last-minute window, {share} of it applies today ({inForce}).",
        few: "For {count} prices inside the last-minute window, {share} of it applies today ({inForce}).",
        other: "For {count} prices inside the last-minute window, {share} of it applies today ({inForce}).",
      },
      previewNone: "No price changes now.",
      previewUnavailable: "The prices could not be worked out right now. You can still save the adjustment.",
      heldRecalculating: "Prices are being recalculated. You can save once that has finished.",
      save: "Save adjustment",
      savedNoneMoved: "No price changed now.",
      nothingToRemove: "No adjustment is set on these prices.",
      removedNoneRestored: "No price changed now.",
      // Removing asks first and names what goes. `{nights}` is the formatted
      // date range the selection bar prints (`selection.dateRange`, one date
      // for one night), never a count, which Czech would have to decline.
      removeTitle: "Remove the adjustment?",
      removeOne: "{delta} on {nights}, {roomTypes}.",
      removeSeveral: "The adjustments on {nights}, {roomTypes}.",
      removeEffect: "These prices go back to what Optivo calculates. A locked price keeps the adjustment until it is unlocked.",
      removeConfirm: "Remove",
      // What a cell's mark is named, by what the adjustment is doing there.
      // `{delta}` is what was set ("+200 CZK", "−15%"), `{inForce}` the move
      // of the shown price in force now, as signed money.
      markApplied: "Adjustment without locking: {delta}",
      markAppliedInForce: "Adjustment without locking {delta}: {inForce} in force now",
      markIdle: "Adjustment {delta} is set and does not move this price right now",
      markIdleMinimum: "Adjustment {delta} lowers nothing right now: the price is at the room type's minimum price for this night",
      markIdleCeiling: "Adjustment {delta} raises nothing right now: the price is at this night's maximum price",
      markIdleRounding: "Adjustment {delta} does not move this price: it is smaller than the price rounding",
      markIdleLastMinute: "Adjustment {delta} raises nothing right now: too little of it is left in the last-minute window",
      markIdleTooLow: "Adjustment {delta} is not used: it would leave no price",
      markLockedWith: "Locked including the adjustment {delta}",
      markWaitingLocked: "Adjustment {delta} waits: this price is locked",
      markWaitingTyped: "Adjustment {delta} waits: this price was typed in by hand and has not been recalculated yet",
      markWaitingRecalculation: "Adjustment {delta} waits until Optivo recalculates this night",
      // A state this version of the page has no word for.
      markUnknown: "Adjustment {delta}: reload the page to see what it is doing",
      legend: "Adjusted without locking",
      legendIdle: "Adjustment set, not moving the price",
      legendWaiting: "Adjustment waiting",
      noticeShow: "Show",
      listTitle: "Adjustments without locking",
      listDescription: "Each stays until it is removed or its night passes.",
      listAdd: "Add an adjustment…",
      listRemove: "Remove…",
      listRemoveLabel: "Remove the adjustment {delta} ({nights})",
      listSetBy: "{who} · {when}",
      listSetByUnknown: "Not recorded",
      listLeftOut: "For a room type Optivo does not price right now",
      listLeftOutUnnamed: "A room type no longer in use",
      // Counted sentences, each read with `pluralForm`. The dialog's preview,
      // the toasts and the list's reasons share them.
      replaced: {
        one: "Replaces {count} adjustment already set.",
        few: "Replaces {count} adjustments already set.",
        other: "Replaces {count} adjustments already set.",
      },
      // Why "No price changes now." can stand beside an adjustment that is in
      // force: these prices carried this very adjustment before the save.
      unchanged: {
        one: "{count} price already carries this adjustment.",
        few: "{count} prices already carry this adjustment.",
        other: "{count} prices already carry this adjustment.",
      },
      heldMinimum: {
        one: "{count} price stops at the room type's minimum price for its night.",
        few: "{count} prices stop at the room type's minimum price for their night.",
        other: "{count} prices stop at the room type's minimum price for their night.",
      },
      heldCeiling: {
        one: "{count} price stops at its night's maximum price.",
        few: "{count} prices stop at their night's maximum price.",
        other: "{count} prices stop at their night's maximum price.",
      },
      heldRounding: {
        one: "{count} price does not move: the adjustment is smaller than the price rounding.",
        few: "{count} prices do not move: the adjustment is smaller than the price rounding.",
        other: "{count} prices do not move: the adjustment is smaller than the price rounding.",
      },
      heldLastMinute: {
        one: "{count} price has so little of the raise left in its last-minute window that it does not move.",
        few: "{count} prices have so little of the raise left in their last-minute window that they do not move.",
        other: "{count} prices have so little of the raise left in their last-minute window that they do not move.",
      },
      tooLow: {
        one: "{count} price would fall to zero and is left as it is.",
        few: "{count} prices would fall to zero and are left as they are.",
        other: "{count} prices would fall to zero and are left as they are.",
      },
      waitingLocked: {
        one: "{count} price is locked: the adjustment waits until it is unlocked.",
        few: "{count} prices are locked: the adjustment waits until they are unlocked.",
        other: "{count} prices are locked: the adjustment waits until they are unlocked.",
      },
      waitingTyped: {
        one: "{count} price was typed in by hand: the adjustment waits until Optivo recalculates it.",
        few: "{count} prices were typed in by hand: the adjustment waits until Optivo recalculates them.",
        other: "{count} prices were typed in by hand: the adjustment waits until Optivo recalculates them.",
      },
      waitingRecalculation: {
        one: "{count} price waits until Optivo recalculates its night; the adjustment is applied then.",
        few: "{count} prices wait until Optivo recalculates their nights; the adjustment is applied then.",
        other: "{count} prices wait until Optivo recalculates their nights; the adjustment is applied then.",
      },
      unpriced: {
        one: "{count} price is missing: the adjustment applies once Optivo prices it.",
        few: "{count} prices are missing: the adjustment applies once Optivo prices them.",
        other: "{count} prices are missing: the adjustment applies once Optivo prices them.",
      },
      saved: {
        one: "Adjustment saved for {count} price",
        few: "Adjustment saved for {count} prices",
        other: "Adjustment saved for {count} prices",
      },
      savedMoved: {
        one: "{count} price changed now.",
        few: "{count} prices changed now.",
        other: "{count} prices changed now.",
      },
      removed: {
        one: "Adjustment removed from {count} price",
        few: "Adjustment removed from {count} prices",
        other: "Adjustment removed from {count} prices",
      },
      removedRestored: {
        one: "{count} price is back on Optivo's calculation.",
        few: "{count} prices are back on Optivo's calculation.",
        other: "{count} prices are back on Optivo's calculation.",
      },
      removedKeptLocked: {
        one: "{count} price is locked and keeps the adjustment until it is unlocked.",
        few: "{count} prices are locked and keep the adjustment until they are unlocked.",
        other: "{count} prices are locked and keep the adjustment until they are unlocked.",
      },
      removeCount: {
        one: "{count} price",
        few: "{count} prices",
        other: "{count} prices",
      },
      notice: {
        one: "{count} night carries an adjustment without locking",
        few: "{count} nights carry an adjustment without locking",
        other: "{count} nights carry an adjustment without locking",
      },
      listIdle: {
        one: "{count} price is not moved by it right now.",
        few: "{count} prices are not moved by it right now.",
        other: "{count} prices are not moved by it right now.",
      },
      listLockedWith: {
        one: "{count} locked price includes it.",
        few: "{count} locked prices include it.",
        other: "{count} locked prices include it.",
      },
      // By the server's refusal code; `unavailable` is the page's own, for a
      // save that met an API without the feature. Strings only: a refusal is
      // looked up by its code, and a counted form here would not be one.
      errors: {
        INVALID_RANGE: "Pick the nights to adjust.",
        PAST_RANGE: "These nights have passed.",
        RANGE_TOO_LONG: "Pick at most 62 nights.",
        RANGE_TOO_FAR: "Optivo prices a year and a half ahead. Pick nights within it.",
        NO_ROOM_KINDS: "Pick at least one room type.",
        ROOM_KINDS_NOT_PRICED: "Optivo no longer prices the selected room types. Reload the page.",
        INVALID_UNIT: "Pick an amount or a percentage.",
        INVALID_AMOUNT: "Enter an amount above zero.",
        INVALID_PERCENT: "Enter a percentage above zero; a cut must stay below 100% and a raise at most 300%.",
        TOO_MANY_ADJUSTMENTS: "Too many adjustments are in force. Remove some first.",
        unavailable: "Adjustments are not available right now. Reload the page in a minute.",
        generic: "The adjustment could not be saved. Please try again.",
        removeFailed: "The adjustment could not be removed. Please try again.",
      },
    },
    // ── Minimum stay ──────────────────────────────────────────────────────
    // The minimum-stay view, the mark every grid puts in the corner of a night
    // that has one, and the edits a selection offers for it
    // (`drafts/minStay.ts`). The minimum stay has a lock of its own, apart
    // from the price's: setting it here holds the minimum stay and leaves the
    // price to Optivo.
    minStay: {
      // The corner mark is a glyph and a number, so it is named in words.
      // `{nights}` is a `bulkEdit.nightCount` form.
      markLabel: "Minimum stay {nights}",
      markRelaxed: "Minimum stay {nights}, relaxed from {from} because the week is weak",
      // `{night}` is the sold-out night, as "Sat, 10/10".
      markRelaxedSoldOut: "Minimum stay {nights}, relaxed from {from} because {night} is sold out",
      markRelaxedSoldOutBefore: "Minimum stay {nights}, relaxed from {from} because the night before, {night}, is sold out",
      markRelaxedBehind: "Minimum stay {nights}, relaxed from {from} because the weekend is selling slower than usual",
      // A reason this page has no words for: never the weak week.
      markRelaxedGeneric: "Minimum stay {nights}, relaxed from {from}",
      // A minimum stay set without locking, shortened by "Always release within".
      markRelaxedRelease: "Minimum stay {nights}, shortened from {from} inside “Always release within”",
      // `{label}` is the mark's name so far.
      markStated: "{label}, set without locking",
      // A cell of the minimum-stay view.
      relaxedShort: "from {from}",
      freeRoomsLabel: "{free} of {total} rooms free",
      soldOut: "Sold out: no room of this type is left",
      // The rooms line of a night with no room left, in place of "0/4".
      roomsFull: "full",
      roomsClosed: "closed",
      closedHere: "Closed: the free rooms of this type are closed in the PMS",
      // A sold-out night's card, beside whether it was sent: its minimum stay
      // turns nobody away tonight and waits for a room.
      soldOutRestrictsNobody: "It restricts nobody now: no room of this type is left. It applies to the next room that frees up.",
      noArrival: "No arrivals",
      noArrivalLabel: "No arrivals: the minimum stay reaches {night}, when this room type is sold out",
      earlierArrival: "It still sells to a guest arriving the night before, {night}.",
      // The view's lines on the cell's card.
      lockedOwn: "Locked: Optivo keeps the minimum stay you set.",
      lockedWithPrice: "Locked with the price: unlock the minimum stay to let Optivo set it again.",
      followsStrategy: "Set by Optivo from your strategy.",
      // A night's third state, between the strategy's and a lock. `{nights}`
      // is a `bulkEdit.nightCount` form.
      statedOwn: "Set to {nights} without locking: Optivo keeps this number, shortens it before a sold-out night and inside “Always release within”, and returns to it when a room frees up.",
      sent: "Property Management holds this minimum stay.",
      notSent: "Not sent to Property Management yet.",
      relaxed: "Relaxed from {from}: your booking history marks the week as weak.",
      // `{fill}` is a whole percent: how full the weekends around this one were last year.
      relaxedWeekendFill: "Relaxed from {from}: at this time of year last year, weekends were only {fill}% full.",
      relaxedSoldOut: "Relaxed from {from}: this room type is sold out on {night}, so a longer stay cannot start tonight. It goes back up if a room frees up that night.",
      relaxedSoldOutBefore: "Relaxed from {from}: this room type is sold out on {night}, the night before, so no earlier arrival can reach tonight and the minimum asks only for the rest of the weekend or holiday. It goes back up if a room frees up that night.",
      // What the weekend's own bookings said, where Optivo read them: the whole
      // property's rooms a night (one decimal), each count after its noun.
      // `{nights}` is a `bulkEdit.nightCount` form.
      relaxedWeakWeekBookings: "Shortened from {from}: the booking history calls the week weak, and the current bookings do not contradict it.",
      relaxedBehind: "Shortened from {from}: the weekend is selling slower than usual. Rooms sold a night across the property: {booked} of {rooms} now, usually {usual} at this point; about {projected} expected by arrival.",
      relaxedGeneric: "Shortened from {from}.",
      relaxedRelease: "Shortened from {from}: the night is inside “Always release within”.",
      heldAhead: "Kept at {nights}: the weekend is selling faster than usual, so it is not released even in a weak week. Rooms sold a night across the property: {booked} now, usually {usual} at this point.",
      heldAsUsual: "Kept at {nights}: history calls the week weak, but the bookings do not show the weekend ending clearly below its usual occupancy. Rooms sold a night across the property expected by arrival: about {projected} of {rooms}.",
      // Under the view: whether what it shows reaches the PMS at all.
      dayCount: { one: "{count} day", few: "{count} days", other: "{count} days" },
      sendingOn: "Automatic sync sends these minimum stays to {pms} for the next {days}.",
      sendingPricesOnly: "Automatic sync sends prices only, so these minimum stays do not reach {pms}. Turn on “Send minimum stay” in the sync settings, or send them by hand.",
      sendingOff: "Automatic sync is off: these minimum stays reach {pms} only when you send them with “Send minimum stay” ticked.",
      sendingUnsupported: "{pms} does not take minimum stays from Optivo, so these stay in Optivo.",
      syncSettings: "Sync settings",
      // The key under the view.
      legendLabel: "Minimum stay",
      legendNoArrival: "No arrivals possible",
      legendLocked: "Locked",
      legendStated: "Set without locking",
      legendRelaxed: "“from 3”: relaxed when demand is weak",
      legendRelaxedStated: "“from 3”: relaxed by weak demand or “Always release within”",
      legendRelaxedSoldOut: "“from 2”: relaxed for a sold-out night",
      legendRooms: "“2/4”: 2 of 4 rooms free · “full”: sold out · “closed”: closed in the PMS",
      legendMark: "Minimum stay in nights",
      // The selection's menu in this view.
      menuButton: "Edit minimum stay",
      setStated: "Set without locking…",
      set: "Set and lock…",
      lock: "Lock the minimum stay",
      unlock: "Unlock the minimum stay…",
      // The same action where a selected night is stated: nothing there is locked.
      handBack: "Hand back to the strategy…",
      // The edit, from the selection or from the page header in this view.
      title: "Minimum stay for several nights",
      description: "Set a minimum stay with or without a lock, lock it as it is, or hand it back to the strategy — for a run of nights and one or more room types. Prices do not change.",
      titleSelection: "Edit the minimum stay",
      descriptionSelection: "Set the minimum stay of the nights you selected, or lock or unlock it. Prices do not change.",
      actionStated: "Set without locking",
      actionStatedHint: "Optivo keeps this number, shortens it before a sold-out night and inside “Always release within”, and returns to it when a room frees up.",
      actionSet: "Set a minimum stay and lock it",
      actionSetHint: "Optivo keeps this minimum stay on these nights and goes on pricing them.",
      actionLock: "Lock the current minimum stay",
      actionLockHint: "The minimum stay stays as it is now.",
      actionUnlock: "Unlock",
      actionUnlockHint: "Optivo sets the minimum stay of these nights from your strategy again, straight away. This also hands back a minimum stay set without locking.",
      actionHandBack: "Hand back to the strategy",
      actionHandBackHint: "Optivo drops your number and sets the minimum stay of these nights from your strategy again, straight away.",
      nightsLabel: "Minimum stay (nights)",
      nightsHint: "1 night means no minimum stay.",
      statedNightsHint: "1 to {max} nights. 1 night means no minimum stay.",
      statedTooLong: "Without a lock a minimum stay runs to {max} nights. Set and lock a longer one.",
      // Under the field while what is typed is no whole number of nights:
      // text with its unit ("2 nights"), or part of a night ("1,5").
      nightsUnreadable: "Enter a whole number of nights, such as 2.",
      submitStated: "Set",
      submitSet: "Set and lock",
      submitLock: "Lock",
      submitUnlock: "Unlock",
      submitHandBack: "Hand back",
      preview: "Minimum stay (nights): {before} → {after}",
      // A service from before this action answers that it does not know it.
      statedUnavailable: "Setting without a lock is not available yet. Use “Set and lock”, or reload the page and try again.",
      // Worked out without the bookings, so what is asked for rather than what applies.
      previewStated: "Minimum stay (nights): {before} → {after}, shorter where a sold-out night or “Always release within” calls for it",
      doneStated: { one: "{count} minimum stay set without locking", few: "{count} minimum stays set without locking", other: "{count} minimum stays set without locking" },
      doneSet: { one: "{count} minimum stay set and locked", few: "{count} minimum stays set and locked", other: "{count} minimum stays set and locked" },
      doneLock: { one: "{count} minimum stay locked", few: "{count} minimum stays locked", other: "{count} minimum stays locked" },
      doneUnlock: { one: "{count} minimum stay handed back to the strategy", few: "{count} minimum stays handed back to the strategy", other: "{count} minimum stays handed back to the strategy" },
      // `{nights}` is the form from `bulkEdit.nightCount`: what Optivo set on the
      // nights handed back, where it set every one the same.
      setToNote: "Optivo set them to {nights}.",
      setToNoteOne: "Optivo set it to {nights}.",
      recalculatedNote: "Optivo has set them from your strategy again.",
      notRecalculatedNote: "Optivo sets them again at the next sync.",
      skippedNote: "Skipped {count}: Optivo has nothing planned for them yet.",
      // What applies now, where it is shorter than the number just set.
      statedLoweredNote: "For now it applies as {nights}: a sold-out night or “Always release within” shortens it. To hold the number, use “Set and lock”.",
      statedNotRecalculatedNote: "Optivo works out what applies at the next sync.",
      alreadyStatedNote: "Already set to this number: {count}.",
      alreadyFollowsNote: "Already set by the strategy: {count}.",
      // The price detail's length-of-stay section.
      detailLocked: "Locked",
      detailLockedWithPrice: "Locked with the price",
      detailUnlock: "Unlock",
      detailUnlockLabel: "Unlock minimum stay",
      detailStated: "Set without locking",
      // `{nights}` is a `bulkEdit.nightCount` form.
      detailStatedNights: "Set without locking: {nights}",
      detailStatedSaveLocks: "A number you change here is locked. To change it without a lock, use “Set without locking” in the Minimum stay view.",
      detailHandBack: "Back to the strategy",
      detailHandBackLabel: "Hand the minimum stay back to the strategy",
      // The word in a cell of the minimum-stay view, beside “Locked” and “Uploaded”.
      statedBadge: "Set",
      unlockingToast: "Unlocking the minimum stay…",
      unlockedToast: "Minimum stay unlocked",
      unlockedRecalculatedToast: "Minimum stay unlocked and set again",
      handingBackToast: "Handing the minimum stay back to the strategy…",
      handedBackToast: "Minimum stay handed back to the strategy",
      handedBackRecalculatedToast: "Minimum stay handed back and set from the strategy",
      savedToast: "Minimum stay saved and locked",
      savedBothToast: "Prices and minimum stay saved and locked",
      saveMinStay: "Save & lock minimum stay",
      saveBoth: "Save & lock",
    },
    deltaVsUploaded: "{delta} / {previous}",
    deltaNeverUploaded: "Not yet uploaded to Property Management",
    deltaVsLastUpload: "Compared with the last price uploaded to Property Management",
    deltaVsLastCalculation: "Compared with the previous calculated price",
    viewLabel: "Calendar view",
    calendarControls: "Calendar controls",
    densityCompact: "Compact view",
    densityCompactHint: "Narrower nights, so more of the range fits on screen.",
    // The calendar laid out for a phone (`drafts/phone/`): one bar above the
    // grid, one sheet for what to show, one bar below it for what to do.
    phoneLayout: {
      heading: "Prices",
      options: "View and filters",
      optionsShort: "Options",
      sectionView: "Show",
      sectionRoomTypes: "Room types",
      sectionPrices: "Prices",
      sectionNotices: "Notes",
      sectionLegend: "Legend",
      sectionMore: "More",
      calculatedAt: "Calculated {time}",
      send: "Send",
      needsAttention: "needs attention",
      selectNight: "Select every room type on {night}",
    },
    baselineLabel: "Compare with",
    baselineLastCalculation: "Last recalculation",
    baselineYesterday: "Yesterday",
    baselineWeek: "Last week",
    baselineMonth: "Last month",
    baselineYesterdayTitle: "Compared with the price 24 hours ago",
    baselineWeekTitle: "Compared with the price 7 days ago",
    baselineMonthTitle: "Compared with the price 30 days ago",
    baselineRecordedOn: "recorded {date}",
    baselineNoHistory: "No prices recorded that far back yet — this baseline fills in as prices are recalculated over time.",
    metricScaleLabel: "Cell metric scale",
    metricDelta: "Price change",
    metricOccupancy: "Occupancy",
    metricDemand: "Demand",
    metricMarket: "Market pressure",
    metricOccupancyShort: "occ",
    metricMarketShort: "mkt",
    metricOccupancyTitle: "This room type was {current}% booked · property {property}% against a {target}% target when this price was calculated",
    metricOccupancyPropertyTitle: "Property-wide occupancy was {current}% against a {target}% target when this price was calculated — occupancy for this room type appears once the price is calculated again",
    metricMarketTitle: "Market pressure was {pct}% when this price was calculated. Higher means weaker demand — occupancy below target and bookings behind pace — and, where last-minute pricing follows demand, a stronger push to discount close to arrival. 0% means the date was on or ahead of target.",
    metricUnavailable: "No pricing detail stored for this price — it is filled in the next time the price is calculated",
    soldOut: "Sold out",
    soldOutTitle: "Every room of this type was already booked for this night when the price was calculated",
    paceScopeProperty: "This room type has too few rooms for its own figures to mean much, so the verdict follows the whole property.",
    paceLegendLabel: "Pace",
    paceLegendAtTarget: "At target",
    paceLegendOnPace: "On pace or too early",
    paceLegendWatch: "Falling behind soon",
    paceLegendBehind: "Behind pace",
    paceBehindIn: "behind in {days} d",
    paceBehindToday: "tips today",
    paceScopePropertyAtTarget: "{pct}% at target",
    paceScopePropertyBehind: "{pct}% behind",
    paceScopePropertyIn: "{pct}% in {days} d",
    paceScopePropertyToday: "{pct}% today",
    paceBehindLabel: "behind pace",
    paceBehindSince: "It has been behind pace for {days} day(s).",
    paceUnavailable: "Could not load booking pace — the occupancy view is showing figures without a pace verdict.",
    paceAtTargetTitle: "{current}% booked — at or above the {target}% occupancy target. Nothing to chase.",
    paceOutlookTitle: "Bookings here typically land within ~{window} days of arrival, so ~{expected}% booked would be normal today against the {target}% target. At {current}% this date is fine for now — but if nothing new books, it falls behind pace in {days} days.",
    paceWatchTitle: "~{expected}% booked would be normal by now (~{window}-day booking window), and this date sits at {current}% against the {target}% target. Not critical yet, but it is drifting — {days} days from falling behind.",
    paceCloseTitle: "{current}% booked against the {target}% target — close enough that this date cannot fall materially behind pace.",
    paceBehindTitle: "Around {expected}% booked would be normal this far out (~{window}-day booking window), but this date sits at {current}% — behind pace on the {target}% target. Time to act.",
    metricLegendDemandLow: "Behind pace",
    metricLegendDemandHigh: "Ahead of pace",
    metricLegendMarketLow: "On track",
    metricLegendMarketHigh: "Under pressure",
    metricCompset: "Market rates",
    metricCompsetShort: "{pct}%",
    metricCompsetTitle: "The local comp set priced this night at the {pct}th percentile of its own captured range — 100% is the most expensive night captured, 0% the cheapest. Median {median} across {count} priced competitors. Blank nights have no capture.",
    metricLegendCompsetLow: "Market cheap",
    metricLegendCompsetHigh: "Market expensive",
  },

  // ── A price recalculation in progress ───────────────────
  // What the Prices page shows in place of its grids while the property's
  // prices are being recalculated, and after a recalculation that failed.
  priceRecalculation: {
    running: "Recalculating prices…",
    runningHint: "The calendar comes back as soon as the new prices are ready, usually within a few seconds. Sending and editing prices wait until then.",
    slow: "Still working — your property management system is taking longer than usual to respond.",
    progressLabel: "Recalculation progress",
    failed: "The price recalculation did not finish.",
    failedHint: "Some prices in the calendar may still be from before the change.",
    dismiss: "Dismiss",
  },

  // ── Price history ───────────────────────────────────────
  // One night's price over time, per room kind. Every recalculation that moved
  // the price is a step; the line holds each price until the next one.
  priceHistory: {
    tabTitle: "Price history",
    intro: "How the price of one night moved over time, one row per room type — the same price the calendar shows. Each step is a recalculation that changed it, or a price set by hand; the line holds each price until the next one. A room booked for the night is marked at the moment it was booked, and the step it sold on is drawn in a second colour. While a price is locked no recalculation moves it, and that stretch is shaded.",
    legendLabel: "Key",
    legendLocked: "Price locked",
    legendBookedStep: "Price a room sold at, for as long as it stood",
    lockedSince: "Locked since {date}",
    lockedSinceAtLatest: "Locked since {date} or earlier",
    lockSet: "Locked",
    lockLifted: "Unlocked",
    lockSeeded: "Already locked",
    lockedPoint: "The price was locked",
    locksUnavailable: "The locks for this night could not be read, so none are marked.",
    nightLabel: "Night",
    previousNight: "Previous night",
    nextNight: "Next night",
    tonight: "Tonight",
    periodLabel: "Period",
    periodDay: "24 h",
    periodWeek: "7 days",
    periodMonth: "30 days",
    periodAll: "All",
    loading: "Loading price history…",
    loadError: "Could not load the price history for this night.",
    empty: "No price recorded for this night yet",
    emptyHint: "Optivo records a price each time a recalculation moves it, and keeps a night's history until 30 days after the night has passed.",
    followsOccupancy: "Occupancy {occupancy}",
    changeCount: "Changes: {count}",
    changeSince: "since {date}",
    range: "Range {low} – {high}",
    noChange: "No change in this period",
    moveCalculated: "Recalculated",
    moveManual: "Set by hand",
    moveFirst: "First recorded",
    carriedIn: "Already in effect when the period began",
    standingNow: "Still in effect",
    standingPast: "Last price for this night",
    fromPrice: "from {price}",
    showChanges: "Show changes",
    hideChanges: "Hide changes",
    colWhen: "When",
    colPrice: "Price",
    colChange: "Change",
    colSource: "Source",
    chartLabel: "{roomKind} — price of the night of {night} over time",
    booked: "Booked",
    bookedSummary: "Booked: {rooms}",
    bookedEarlier: "+{count} earlier",
    bookedPrice: "Price when booked",
    bookedDayPrices: "Prices that stood that day",
    bookedDayOnly: "The PMS reports the day of this booking, not the time",
    bookingsUnavailable: "The bookings for this night could not be read, so none are marked.",
    statusOption: "Option",
    statusBlocked: "Block",
    stayTotal: "{total} for {nights}",
    soldPerNight: "room {price} a night",
    soldWholePerNight: "{price} a night",
    roomsTimes: "{count} ×",
    moreRooms: "+{count} more",
    rooms: { one: "{count} room", few: "{count} rooms", other: "{count} rooms" },
    guests: { one: "{count} guest", few: "{count} guests", other: "{count} guests" },
    nights: { one: "{count} night", few: "{count} nights", other: "{count} nights" },
  },

  // ── Upload Log ──────────────────────────────────────────
  uploadLog: {
    // ── Restore scope ───────────────────────────────────────────────────────
    restorePricesLabel: "Restore prices",
    restorePricesHint: "Put the saved nightly rates back into your Property Management.",
    restoreMinLosLabel: "Restore minimum stay",
    restoreMinLosHint: "Put the saved minimum length of stay back, without changing prices.",
    restoreMinLosMissing: "This price list has no minimum stay recorded, so it cannot be restored.",
    restoreMinLosUnsupported: "This Property Management connection cannot receive minimum stay restrictions.",
    restoreScopeNothingSelected: "Select at least one of prices or minimum stay.",
    // A restore puts back the layers its upload wrote, where its restore point
    // holds them. One layer is said, not switched, followed by why the other
    // is not put back; the title and the button name what will be sent.
    restoreOnlyPrices: "Only prices are put back.",
    restoreOnlyMinLos: "Only the minimum stay is put back.",
    restoreNothing: "There is nothing to put back.",
    restorePricesNotWritten: "That upload sent no prices, so prices are left as they are now.",
    restoreMinLosNotWritten: "That upload sent no minimum stay, so the minimum stay is left as it is now.",
    restorePricesNotHeld: "The price list saved before that upload holds no prices.",
    restoreMinLosNotHeld: "The price list saved before that upload holds no minimum stay.",
    restoreNothingHeld: "The price list saved before that upload holds neither prices nor a minimum stay.",
    restoreConfirmTitleMinLos: "Restore minimum stay to Property Management?",
    restoreConfirmTitleBoth: "Restore prices and minimum stay to Property Management?",
    restoreConfirmActionMinLos: "Restore minimum stay",
    restoreConfirmActionBoth: "Restore prices and minimum stay",
    // ── Derived rate plans ──────────────────────────────────────────────────
    derivedPlansTitle: "Linked rate plans",
    derivedPlansHint: "Prices calculated from the base plan and sent after it.",
    derivedPlanChannel: "Channel rate plan {id}",
    derivedPlanNonRefundable: "Non-refundable rate plan {id}",
    derivedPlanChannelNamed: "Channel rate plan {name}",
    derivedPlanNonRefundableNamed: "Non-refundable rate plan {name}",
    derivedPlanNights: "{count} nights",
    derivedStatusSent: "Sent",
    derivedStatusFailed: "Failed",
    derivedStatusSkipped: "Not sent",
    tabTitle: "Upload Log",
    title: "Price Upload History",
    loading: "Loading upload log...",
    empty: "No prices have been uploaded to Property Management yet.",
    date: "Date",
    status: "Status",
    entriesUploaded: "Entries",
    errors: "Errors",
    warnings: "Warnings",
    statusCompleted: "Completed",
    statusFailed: "Failed",
    // An upload short only of what Optivo itself left unwritten: stored as
    // failed, since not every night landed, and no failure.
    statusHeld: "Held back: {count}",
    detailTitle: "Upload details — {date}",
    expandDetails: "Expand to full screen",
    collapseDetails: "Collapse panel",
    errorsList: "Errors:",
    heldReasonsList: "Why Optivo held these back:",
    moreErrors: "+{count} more errors",
    warningsList: "Warnings:",
    moreWarnings: "+{count} more warnings",
    notSentSoldOut: "Sold out — no price sent",
    notDeliveredRefused: "Refused by Property Management — not delivered",
    notDeliveredHeld: "Held back by Optivo — not written",
    notDeliveredHeldHint: "A night Optivo held back was not refused by Property Management: Optivo did not write it, and nothing needs fixing there. It stays unsent in the draft, and a later send tries it again.",
    // The same under a restore's own row, where the send's last sentence does
    // not hold: a restore commits no drafts, so nothing sends the night later.
    // What a restore holds back is a minimum stay put back without a price.
    notDeliveredHeldHintRestore: "A night Optivo held back was not refused by Property Management: Optivo did not write it, and nothing needs fixing there. Optivo does not try a restore again, so the night keeps the minimum stay it has now. To put the saved one back, restore again later or set it in Property Management.",
    notDeliveredUndelivered: "Not delivered — the connection failed",
    notSentHint: "A night with no room left to sell is not sent to Property Management — a new price would have nothing to act on. It goes out again as soon as a room frees up.",
    uploadedRooms: "Room types uploaded:",
    roomEntries: "{count} entries",
    dateRange: "{from} — {to}",
    startedAt: "Started: {date}",
    pageInfo: "Page {page} of {totalPages} ({total} uploads)",
    occupancy: "Occ. {n}",
    // The minimum stay an upload wrote for the night, where it sent one.
    minStayColumn: "Min. stay",
    minStayNights: { one: "{count} night", few: "{count} nights", other: "{count} nights" },
    changeDelta: "{delta} vs {previous}",
    changeNew: "New",
    changeUnchanged: "No change",
    summaryLabel: "Price changes:",
    summaryIncreased: "{count} increased",
    summaryDecreased: "{count} decreased",
    summaryUnchanged: "{count} unchanged",
    summaryNew: "{count} new",
    filterDateFrom: "From date",
    filterDateTo: "To date",
    filterRoomKind: "Room type",
    filterAllRooms: "All room types",
    filterClear: "Clear filters",
    retentionNote: "History older than {days} days is deleted automatically.",
    retentionNoteOne: "History older than 1 day is deleted automatically.",
    dryRunBadge: "Dry run",
    restore: "Restore",
    restoreTitle: "Restore the whole price list that was in Property Management before this upload",
    restoreTitleCells: "Put back what this upload changed in Property Management",
    restorePointExpired: "Restore point expired",
    restorePointExpiredHint: "The price list saved before this upload is no longer stored, so it cannot be downloaded or restored from here.",
    restoredBadge: "Restore",
    downloadPriceList: "Download price list",
    downloadRangeHint: "Downloads the current Property Management price list for the next 90 days",
    downloadSuccess: "Price list downloaded",
    downloadError: "Failed to download the price list",
    downloadBackup: "Download backup",
    downloadBackupError: "Failed to download the backup",
    restoreConfirmTitle: "Restore prices to Property Management?",
    restoreConfirmBody: "This re-sends the whole price list saved before the upload on {date}: every room type on every night of its period, not only what that upload changed. Anything sent to that period since will be overwritten.",
    restoreConfirmBodyCells: "This puts back what Property Management held just before the upload on {date}, only for the nights and room types that upload wrote. Anything sent to them since will be overwritten.",
    // Said under either sentence above where the PMS answers a reading of its
    // price list from a copy it keeps. Each names the layers the restore puts
    // back and nothing else: prices, minimum stays, or both. The backup's
    // minimum stay is read from the same copy as its prices, so the same is
    // said of it. Nothing is said where the restore puts nothing back.
    restoreConfirmCachedRead: "Property Management answers Optivo from a copy of its price list that can be an hour old or more. A price changed shortly before this upload may be missing from the backup, and the restore then puts back the one before it.",
    restoreConfirmCachedReadMinLos: "Property Management answers Optivo from a copy of its price list that can be an hour old or more. A minimum stay changed shortly before this upload may be missing from the backup, and the restore then puts back the one before it.",
    restoreConfirmCachedReadBoth: "Property Management answers Optivo from a copy of its price list that can be an hour old or more. A price or a minimum stay changed shortly before this upload may be missing from the backup, and the restore then puts back the one before it.",
    restoreConfirmAction: "Restore prices",
    restoreStarted: "Restore started — re-sending to Property Management",
    restoreSuccess: "Price list restored to Property Management",
    restoreError: "Failed to restore the price list",
    // A restore or a file upload that landed, short of what Optivo itself
    // left unwritten. Nothing failed, so it is not the error toast.
    restoreHeld: "Optivo held back {count} of what it was sending, and Property Management refused nothing. The Upload Log names each night and why.",
    uploadPriceList: "Upload price list",
    uploadRangeHint: "Push a previously downloaded price list back to Property Management",
    uploadInvalidFile: "That file is not a valid price list",
    uploadEmptyFile: "That price list has no prices to upload",
    uploadTooLarge: "That file is too large to upload",
    uploadConfirmTitle: "Upload price list to Property Management?",
    // The sentence and the button name what the switches will send: prices,
    // minimum stays or both. `{prices}` and `{minStays}` are the counted forms
    // below. A file's cells do not all hold both, so each has its own count.
    uploadConfirmBody: "This sends {prices} from the file to Property Management, overwriting current prices for those dates. A backup is taken first so you can undo it.",
    uploadConfirmBodyMinLos: "This sends {minStays} from the file to Property Management, overwriting current minimum stays for those dates. A backup is taken first so you can undo it.",
    uploadConfirmBodyBoth: "This sends {prices} and {minStays} from the file to Property Management, overwriting current prices and minimum stays for those dates. A backup is taken first so you can undo it.",
    uploadConfirmBodyNothing: "As it stands, this sends nothing from the file.",
    uploadConfirmPrices: { one: "{count} price", few: "{count} prices", other: "{count} prices" },
    uploadConfirmMinStays: { one: "{count} minimum stay", few: "{count} minimum stays", other: "{count} minimum stays" },
    uploadConfirmAction: "Upload prices",
    uploadConfirmActionMinLos: "Upload minimum stay",
    uploadConfirmActionBoth: "Upload prices and minimum stay",
    // Under the price switch of a file that holds no prices, which is then
    // off and locked; and in place of "select at least one" where the PMS can
    // receive nothing the file holds.
    uploadPricesMissing: "This price list has no prices recorded, so there are none to restore.",
    uploadNothingToSend: "Nothing in this file can be sent.",
    uploadStarted: "Upload started",
    uploadSuccess: "Price list uploaded to Property Management",
    uploadError: "Failed to upload the price list",
    // ── What started an upload ──────────────────────────────────────────────
    startedBy: "Started by",
    nights: "Nights",
    triggerManual: "Manual send",
    triggerAutoSync: "Auto-sync",
    triggerScheduleSave: "Auto-sync saved",
    triggerRetry: "Retry",
    triggerRestore: "Restore",
    triggerNotRecorded: "Not recorded",
    triggerWithEmail: "{trigger} — {email}",
    triggerNotRecordedDetail: "What started this upload was not recorded",
    detailNights: "Nights: {nights}",
  },

  // ── Analysis ─────────────────────────────────────────────
  analysis: {
    title: "Property Analysis",
    description: "Insights from the last year of historic reservation data",
    metaSummary: "Based on {count} reservations from {from} to {to}",
    emptyTitle: "No analysis data yet",
    emptyDescription: "Run the initial analysis from Property Manager Settings after connecting your PMS.",

    // Settings card
    settingsTitle: "Property Analysis",
    settingsDescription: "Analyse your booking history to detect seasonality, demand patterns, and channel performance",
    completed: "Completed",
    failed: "Failed",
    fetchingProgress: "Fetching reservations… week {current} of {total}",
    computing: "Computing metrics…",
    completedSummary: "Analysis complete — {count} reservations analysed",
    // A property that has not opened reads no reservations: say where its
    // prices start from rather than claiming a measurement.
    completedNoHistory: "No booking history yet",
    completedNoHistorySummary:
      "Your PMS has no stays for Optivo to measure — usual for a property that has not opened yet. Optivo starts from the prices in your PMS price list, where it lists any, and from the settings you choose next, and learns from your own bookings as they come in.",
    lastRun: "Last run:",
    unknownError: "An unknown error occurred",
    noAnalysisYet: "No analysis has been run yet. Click below to analyse your booking history.",
    runAnalysis: "Run Analysis",
    reRun: "Re-run Analysis",
    viewResults: "View Results",
    toastStarted: "Analysis started",
    toastStartedDesc: "Fetching last year of reservation data — this takes a few minutes.",
    resultsLoadFailed: "Could not load the analysis results. Try running the analysis again.",
    toastFailed: "Failed to start analysis",

    // Seasonality section
    seasonalityTitle: "Seasonality",
    seasonalityDesc: "Monthly average nightly rates and room-night volumes across the last year",
    seasonalityEmpty: "Not enough booking history to detect seasonality yet.",
    peakMonth: "Peak month",
    offPeakMonth: "Off-peak month",
    month: "Month",
    avgPrice: "Avg Nightly Rate",
    reservations: "Room Nights",
    totalRevenue: "Total Revenue",

    // Weekday distribution section
    weekdayTitle: "Check-in Day Distribution",
    weekdayDesc: "Which days of the week receive the most guest check-ins",
    busiestDay: "Busiest day",
    quietestDay: "Quietest day",

    // Booking window section
    bookingWindowTitle: "Booking Window",
    bookingWindowDesc: "Lead time between reservation creation and guest arrival",
    avgLeadTime: "Average lead time",
    medianLeadTime: "Median lead time",
    days: "days",

    // Length of stay section
    lengthOfStayTitle: "Length of Stay",
    lengthOfStayDesc: "Distribution of stay durations across all reservations",
    avgNights: "Average stay",
    medianNights: "Median stay",
    nights: "nights",

    // Channel mix section
    channelMixTitle: "Booking Channels",
    channelMixDesc: "Breakdown of reservation sources and their average prices",
    topChannel: "Top channel",
    channel: "Channel",
    bookings: "Bookings",
    share: "Share",

    // Key metrics section
    keyMetricsTitle: "Key Performance Metrics",
    occupancyRate: "Occupancy",
    basePrice: "Base Price",
    minimalAcceptablePrice: "Minimal price",
    roomNightsSold: "Room-Nights Sold",
    uniqueRooms: "Unique Rooms",

    // Occupancy spread section
    occupancySpreadTitle: "Occupancy Price Spread",
    occupancySpreadDesc: "Per-room-kind price multipliers derived from last month's PMS rate data — shows the typical price difference between occupancy levels",
    occupancySpreadRoomKind: "Room Kind",
    occupancySpreadBasePrice: "Base Price (occ 2)",
    occupancySpreadLevel: "Occupancy",
    occupancySpreadMultiplier: "Multiplier",
    occupancySpreadDiff: "Avg Δ Price",
    occupancySpreadStep: "occ {from}→{to}",
    occupancySpreadNoData: "No occupancy rate data available — rates will be fetched during the next analysis run.",

    // Create strategy from analysis
    createStrategyTitle: "Create Strategy from Analysis",
    createStrategyDesc: "Generate a new pricing strategy with parameters automatically derived from this analysis. The new strategy will be set as active; existing strategies will be kept for reference.",
    createStrategyButton: "Create Strategy",
    createStrategySuccess: "Strategy created and activated",
    createStrategySuccessDesc: "New strategy \"{name}\" is now active. Go to Strategies to review and adjust.",
    createStrategyError: "Failed to create strategy",

    // Room kind price tiers section
    roomKindTiersTitle: "Room Kind Price Tiers",
    roomKindTiersDesc: "Room types classified into price tiers based on average nightly rates from individual (non-group) reservations",
    roomKindTiersNoData: "No room kind tier data available — run the analysis first.",
    roomKindTiersName: "Room Kind",
    roomKindTiersAvgPrice: "Avg Nightly Price",
    roomKindTiersCount: "Reservations",
    roomKindTiersTier: "Tier",
    roomKindTiersExcluded: "{count} group reservation(s) excluded from tier calculation.",
    tierBudget: "Budget",
    tierEconomy: "Economy",
    tierStandard: "Standard",
    tierHigh: "High",
    tierLuxury: "Luxury",

    // AI property classification
    aiInsightsTitle: "AI Property Insights",
    aiInsightsNote: "AI-generated based on your booking history",
    propertyTypes: {
      hotel: "Hotel",
      boutique_hotel: "Boutique Hotel",
      pension_bb: "Pension / B&B",
      apartment: "Apartment / Apart-hotel",
      resort: "Resort",
      hostel: "Hostel",
      villa_vacation_rental: "Villa / Vacation Rental",
      campsite: "Campsite",
    },
    propertyTypeDescriptions: {
      hotel: "Full-service or limited-service property with private rooms and broad guest mix; balanced weekday and weekend demand.",
      boutique_hotel: "Smaller design-focused property with stronger premium positioning and often higher average-price sensitivity.",
      pension_bb: "Smaller guesthouse or B&B with simpler service model and stronger seasonality around leisure stays.",
      apartment: "Apartment or apart-hotel units with longer average stays and demand driven by family and extended bookings.",
      resort: "Destination-led property with peak seasonal swings, weekend/holiday spikes, and higher event sensitivity.",
      hostel: "Shared or budget-focused accommodation with high price sensitivity and frequent short lead-time bookings.",
      villa_vacation_rental: "Standalone villa/rental inventory with volatile demand, longer stays, and strong holiday concentration.",
      campsite: "Campsite or glamping site with cabins, bungalows or pitches; strongly seasonal, with demand concentrated in summer and around holidays.",
    },
  },

  // ── PMS Connections ────────────────────────────────────
  connections: {
    title: "Property Manager Connections",
    addConnection: "Add Connection",
    newConnection: "New Property Manager Connection",
    connectionName: "Connection name (optional)",
    hotelId: "Hotel ID",
    login: "Login",
    password: "Password",
    noActiveConnection: "No connection selected",
    manage: "Manage Connections",
    // Where the rail's property switcher could not be drawn, the link to this
    // page stands in for it, and this says why it is there. `{page}` is this
    // page's own title, so the sentence names the screen as the screen does.
    switcherUnavailable:
      "Your properties could not be listed here. Switch between them on the {page} page, or reload the page.",
    empty: "No Property Manager connections yet. Add one to get started.",
    confirmDelete: "Are you sure you want to delete this connection? All associated data will be lost, and people whose access covers only this property lose access to the account.",
    clickToRename: "Click to rename",
    members: "Members",
    manageMembers: "Manage Members",
    invite: "Invite",
    inviteEmailPlaceholder: "Enter email to invite",
    inviteIdentifierPlaceholder: "Email or Logto user id",
    removeMember: "Remove member",
    makeOwner: "Make {user} an owner",
    makeMember: "Change {user} to a member",
    confirmStepDown: "Give up your own owner rights? Only another owner can give them back.",
    roleUpdated: "Member role updated",
    roleUpdateFailed: "Could not change the member role",
    userNotFound: "No user found with this email or user id",
    inviteSuccess: "User added successfully",
    delete: "Delete connection",
    testHealth: "Test connection",
    modify: "Edit connection settings",
    activate: "Set as Active",
    activeLabel: "Active",
    redoWizard: "Re-run Setup Wizard",
    healthStatusHealthy: "Healthy",
    healthStatusUnhealthy: "Unhealthy",
    previoApiBase: "Previo API Base",
    languageId: "Language ID",
    testAndConnect: "Test & Connect",
    cancel: "Cancel",
    connectionCreated: "Connection created successfully",
    connectionTestFailed: "Connection test failed",
    testing: "Testing…",
    backToConnections: "Back to Connections",
    // Access roles. Owner and full access come from the account: an owner
    // reaches every property it holds, full access every property or only
    // those it was given; read-only is a property's prices and analytics,
    // with nothing to change and none of the strategy behind them.
    roleOwner: "Owner",
    roleMember: "Full access",
    roleViewer: "Read-only",
    // An administrator who added themselves to the property read-only: they
    // see everything a member sees and change nothing.
    roleStaff: "Admin view, read-only",
    inviteRole: "Access",
    inviteRoleMember: "Full access — prices, strategy and settings",
    inviteRoleViewer: "Read-only — prices and analytics",
    makeViewer: "Make {user} read-only on this property",
    makeFullAccess: "Give {user} full access",
    // What happens on the account's other properties follows (`accessScope`),
    // then `confirmContinue`.
    confirmMakeViewer: "{user} will keep read-only access to this property.",
    // A server that does not tell properties apart gives the whole account.
    confirmMakeFullAccess: "{user} will be able to change the prices and settings of every property in this account, and of any it adds later. Continue?",
    confirmMakeFullAccessThis: "{user} will be able to change the prices and settings of this property, and of any property the account adds later. Continue?",
    confirmContinue: "Continue?",
    viewerInviteSuccess: "Read-only access granted",
    // The front desk: the hotel's receptionists, who read this property's
    // prices and reservations and why each price is what it is, and nothing
    // that states what it earns. A read-only grant of its own kind.
    roleFrontDesk: "Front desk",
    inviteRoleFrontDesk: "Front desk — prices and reservations, no analytics or revenue",
    // Nothing is chosen for the owner: the invite waits until one is.
    chooseAccess: "Choose what they may do on this property",
    makeFrontDesk: "Give {user} front-desk access to this property",
    makeAnalyticsViewer: "Let {user} also see this property's analytics",
    confirmMakeFrontDesk: "{user} will keep front-desk access to this property — its prices and reservations, no analytics or revenue.",
    confirmWidenToAnalytics: "{user} will also see this property's analytics (occupancy, revenue and recent bookings) and the whole Prices page: every calendar view, the price history and the upload log. Continue?",
    frontDeskInviteSuccess: "Front-desk access granted",
    // {kind} is the access their row names ("Read-only", "Front desk").
    alreadyReadOnly: "This user already has access to this property: {kind}. Change it on their row.",
    // A server that does not know the front desk gives the analytics instead.
    frontDeskNotGranted: "Front-desk access could not be given: the person now has read-only access with analytics. Remove it if that is not wanted.",
  },

  // ── Re-running the setup wizard ──────────────────────────
  // The confirmation in front of "Re-run Setup Wizard". A restart used to
  // clear settings the wizard never asks for again, so it says what stays and
  // what is asked again before anything is reset. The kept lines are the
  // server's list in words (`RESTART_KEPT_ASSUMPTION_KEYS`).
  restartWizard: {
    title: "Re-run the setup wizard for {name}?",
    intro: "The wizard starts again from its first step. If a strategy prices this property now, it keeps pricing it until you finish and the property's pages stay open. Where auto-sync is on, it keeps sending those prices.",
    keptHeading: "Kept as it is",
    keptDistribution: "Channel rate plans and the non-refundable rate, set in the strategy editor. The wizard does not ask about them.",
    keptLocation: "Property location and guest source markets",
    keptOpeningDate: "Opening date, if one was entered",
    keptCompset: "Whether competitor tracking is on",
    keptPropertyFacts: "The property type and what your prices include",
    keptShown: "The wizard shows the location, the opening date, the property type, what your prices include and competitor tracking as they are, for you to confirm or change.",
    askedHeading: "Asked again",
    askedPricing: "Prices by Room Type, Day Adjustments, Holidays & Special Dates and every other pricing setting. The wizard proposes them afresh from your booking analysis and Optivo's defaults. Changes made in the current strategy are not carried over.",
    finish: "Finishing saves a new strategy and puts it in use. A strategy it replaces stays under Strategies, where you can switch back to it.",
    confirm: "Re-run the wizard",
  },

  // ── The property and its prices ─────────────────────────
  // The property type and what its price-list price includes: asked on the
  // wizard's Property Identity step and edited on the Settings page. Only the
  // type changes anything (the tariff); the base-price answer is checked
  // against what Optivo counts as the base price.
  propertyFacts: {
    cardTitle: "Your property and its prices",
    cardDescription: "What kind of property this is, and what the price in your price list includes. The setup wizard asks the same.",
    propertyTypeLabel: "Property type",
    propertyTypeHint: "A guesthouse, an apartment, a vacation rental or a campsite is billed as OPTIVO Rentals, whatever its number of rooms; every other type as OPTIVO Hotels.",
    saved: "Saved",
    saveFailed: "Could not save the answer. Nothing was changed.",
    loadFailed: "Could not read what is stored. Reload the page to try again.",
    // The admin console states the answers and changes none.
    notStated: "Not stated",
    statedByOperator: "Stated by the operator in the setup wizard or in Settings, and changed only there.",
    groupTitle: "What your prices include",
    groupDescription: "These answers do not change Optivo's prices. They tell us how to read your numbers in analyses, and Optivo says below where it counts the base price differently than you do.",
    choose: "Choose…",
    vatLabel: "Prices in your price list are",
    vat: {
      gross: "Including VAT",
      net: "Excluding VAT",
      not_vat_payer: "We are not VAT registered",
    },
    breakfastLabel: "Breakfast",
    breakfast: {
      included: "Included in the price",
      separate: "Charged separately",
      not_offered: "We don't serve breakfast",
    },
    baseLabel: "The base price in your price list is",
    base: {
      double_room: "The room for two guests",
      single_use: "The room for one (single use)",
      per_person: "A price per person",
    },
    notices: {
      matches: "Optivo counts it the same way: its base price is the room for two guests.",
      singleUse: "Optivo's base price is the room for two guests, and it works out the price for one guest from it in Guest Count Pricing. Enter base prices in Prices by Room Type as the price for two. A room that sleeps one guest is the exception: its base price is its only price.",
      singleUseOnePrice: "Optivo's base price is the room for two guests, and {pms} takes one price per room and night from Optivo: the price for two. Enter base prices in Prices by Room Type as the price for two.",
      perPerson: "Optivo prices the whole room, for each number of guests separately, never per person. If your price list holds a price per person, write to us through Ask Optivo before Optivo sends prices, so we can check your PMS takes them as meant.",
      perPersonPerGuest: "Optivo sends {pms} a price for the whole room, for each number of guests separately, never a price per person. Write to us through Ask Optivo before Optivo sends prices, so we can check {pms} takes them as meant.",
      perPersonOnePrice: "{pms} takes one price per room and night from Optivo: the price for two guests, never a price per person. Change the price list in {pms} to one price for the whole room before Optivo sends prices to it.",
    },
  },

  // ── Read-only access ─────────────────────────────────────
  // Somebody granted one property to read: its prices and analytics, nothing
  // they could change, and none of the strategy behind the prices.
  readOnlyAccess: {
    badge: "Read-only",
    badgeHint: "You can see this property's prices and analytics. Its pricing is managed by its revenue manager.",
    // An administrator who added themselves to this property read-only: they
    // see everything a member sees, and nothing here can be changed.
    staffBadge: "Admin view, read-only",
    staffBadgeHint: "You see everything in this property, the strategy included, and nothing here can be changed. Its owners see you in its access list.",
    // The hotel's receptionists: read-only access to the prices and the
    // reservations, without the analytics.
    frontDeskBadge: "Front desk",
    frontDeskBadgeHint: "You can see this property's prices, its reservations and why each price is what it is. Its pricing is managed by its revenue manager.",
    priceDetailNote: "Read-only access — prices are set by the property's revenue manager.",
    explanationTitle: "What shaped this price",
    movedUp: "Raised",
    movedDown: "Lowered",
    noMoves: "No factor moved this price away from its starting point.",
    explanationNote: "Read-only access shows which factors moved the price. The settings behind them stay with the property's revenue manager.",
    marketSettingsNote: "Which competitors are compared, and where the market is measured, is set by the property's revenue manager.",
    marketNotConfigured: "Market tracking has not been set up for this property yet.",
  },

  // An administrator reviews a customer's property by adding themselves to it
  // read-only, from the PMS connections console: the grant a viewer holds,
  // which for an administrator shows everything a member sees and still lets
  // nothing change — so that nobody breaks an account by accident while
  // looking at it.
  adminReadOnly: {
    openProperty: "Open read-only",
    openPropertyTitle: "Open {name} read-only?",
    openPropertyDescription:
      "You will be added to its access list as read-only, where its owners can see you. As an admin you see everything in it, the strategy included, and nothing can be changed. Remove yourself under Access when you are done.",
    openPropertyConfirm: "Add me and open",
    openPropertyFailed: "The property could not be opened",
    openPropertyMember:
      "You already have full access to this property, so it opens with full access rather than read-only. Open it from the property switcher.",
    strategyBadge: "Read-only",
    strategyNote: "You are reviewing this strategy read-only. Nothing here can be changed or saved.",
    viewStrategy: "View strategy",
    viewButton: "View",
    viewVersion: "View version {version}",
    viewNamed: "View {name}",
  },

  // ── Auto-Sync Scheduling ─────────────────────────────────
  autoSync: {
    title: "Auto-Sync to PMS",
    description: "Automatically send draft prices to your PMS on the property's sync schedule.",
    enabled: "Enable auto-sync",
    disabled: "Auto-sync is disabled",
    configure: "Configure Auto-Sync",
    frequencyLabel: "Update frequency",
    // Stated, not offered — the picker for it lives in the admin console.
    frequencyHelp: "How often we check your PMS for new bookings and send updated prices back. Set by Optivo for the whole property — contact support to change it.",
    daysAheadLabel: "Days ahead to send",
    daysAhead1: "Today only",
    daysAhead3: "Next 3 days",
    daysAhead7: "Next 7 days",
    daysAhead14: "Next 14 days",
    daysAhead30: "Next 30 days",
    daysAhead60: "Next 60 days",
    daysAhead90: "Next 90 days",
    daysAhead180: "Next 180 days",
    daysAhead365: "Next 365 days",
    daysAhead548: "Next 548 days (1.5 years)",
    // Shown once the reach passes the nights repriced on every sync.
    farHorizonHint: "Nights more than {days} days out are recalculated once a day rather than on every sync. Each sync sends at most {days} nights, the nearest first, so a longer range reaches your PMS over several syncs.",
    roomKindsLabel: "Room types to sync",
    roomKindsAll: "All room types",
    roomKindsSelected: "{count} / {total} selected",
    saveSuccess: "Auto-sync settings saved",
    saveError: "Failed to save auto-sync settings",
    loadError: "Failed to load auto-sync settings",
    // The line under the switch says what the switch's position means. While
    // it is on: what a save does. `{cadence}` is the property's own sync
    // cadence as it reads inside a sentence ("every 2 hours", from
    // `syncCadence`). It replaced "No drafts available — auto-sync will
    // activate once pricing drafts are generated", which stood at the foot of
    // every switched-on schedule, price plan or not.
    sendsOnSave: "Once you save, Optivo sends your PMS what is new or has changed since the last send: straight away, and then {cadence}.",
    // In its place only where the server says Optivo holds no price plan for
    // the property the dialog names.
    noPricesYet: "Optivo has not calculated any prices for this property yet, so there is nothing to send. Once this is saved, auto-sync sends them on its own as soon as they are calculated.",
    // The switch is off and the saved schedule is still on: `disabled` above
    // would not be true until the save, so this stands under the switch.
    stillOnUntilSaved: "Auto-sync is still on. It stops sending once you save.",
    // Asked when the X, Escape or a click beside the dialog would drop what
    // was changed. The three sentences say what closing leaves in force.
    unsavedTitle: "Close without saving?",
    unsavedTurnedOn: "Auto-sync is switched on here, but that is not saved. If you close now it stays off, and Optivo sends nothing to your PMS on its own.",
    unsavedTurnedOff: "Auto-sync is switched off here, but that is not saved. If you close now it stays on, and Optivo goes on sending to your PMS.",
    unsavedChanged: "What you changed here is not saved. If you close now, auto-sync goes on with the settings saved before.",
    unsavedKeepEditing: "Back to the settings",
    unsavedDiscard: "Close without saving",
    // On the footer's rule while something of the form lies under the fold;
    // pressed, it scrolls the form on. True at every place in the scroll,
    // also where only the end of a notice is left: "More settings below"
    // would not be. Keep it short: it stands on a 320px phone's dialog.
    continuesBelow: "Continues below",
    // Under the minimum-stay switch while it is on. True of a schedule that
    // sends them already and of one about to: a later send writes a night's
    // minimum stay again whenever the plan's own moves.
    minLosChangeInOptivo: "While Optivo sends minimum stays, change them in Optivo. A change made in {pms} itself can be replaced by a later send.",
    statusEnabled: "Auto-sync enabled",
    statusDisabled: "Auto-sync disabled",
  },

  // ── Minimum stay kept per night by the PMS ─────────────
  // A Previo hotel chooses in Previo whether it keeps a minimum stay per room
  // type or one per night for every room type. Optivo plans one per room type,
  // so where the PMS keeps one per night a send levels each night.
  //
  // The notice states what Optivo does and asks nothing of the operator. Its
  // last sentence used to begin "To keep a minimum stay for each room type,
  // set {pms}…", and a hotel read it as a demand and did not save (OPT-55,
  // Oct 2026). So `body` opens by saying nothing needs to be changed, and
  // `option` says what the PMS can do: no sentence here tells anybody to set
  // anything. `nights` follows `body` in a send, its "that" being the
  // levelling `body` ends on.
  minStayScope: {
    title: "{pms} keeps one minimum stay per night",
    body: "Nothing needs to be changed in {pms}. At this property it keeps one minimum stay per night for all room types, so Optivo sends each night the highest minimum stay of its room types.",
    // "Applies to", as the Czech says, and not "changes": the levelling is
    // what applies to a night; what changes in the PMS is the send's doing.
    nights: {
      one: "In this send that applies to {count} night, where the room types ask for different minimum stays.",
      few: "In this send that applies to {count} nights, where the room types ask for different minimum stays.",
      other: "In this send that applies to {count} nights, where the room types ask for different minimum stays.",
    },
    lost: "What is lost is a shorter minimum stay for one room type alone, such as the one Optivo sets before a sold-out night.",
    // Only where keeping one per night is the hotel's own setting in the PMS.
    option: "{pms} can also keep a minimum stay for each room type. That is a setting on its side, and Optivo then sends each room type its own.",
    // Under the room types' own minimum stays in Rate Restrictions, where no
    // send is on screen: what a room type's own minimum becomes in the PMS.
    roomKindsBody: "When Optivo sends minimum stays, it sends each night the highest of its room types' minimum stays to all of them. A minimum stay set here for one room type therefore applies to the whole night in {pms} where it is the highest, and a lower one has no effect there.",
  },

  // Said beside the minimum-stay switch of the Send dialog and the auto-sync
  // dialog while prices are switched off, for a PMS that keeps the minimum
  // stay on the price and so takes one only beside a price
  // (`MinStayAloneNotice`). It names the PMS and nothing else as where a
  // price comes from.
  //
  // The three `restore…` sentences say the same of minimum stays put back
  // without prices, in the Upload Log's restore confirmation and its file
  // dialog. A restore has words of its own because the send's do not hold
  // there: its confirmation may have no price switch to be off, nothing sends
  // a night it leaves out later, and the prices that could go with the
  // minimum stays are the saved ones, named only where they are on offer.
  minStayAlone: {
    title: "{pms} writes a minimum stay only beside a price",
    body: "{pms} keeps the minimum stay on the price itself. With prices switched off, Optivo re-sends each night's current price beside its minimum stay, after checking it against the price Optivo last sent.",
    held: "A night whose price was changed in {pms} itself around the time Optivo last sent one is left for a later send, because nothing says which of the two prices is the newer. So is a night with no price. The Upload Log lists them as held back.",
    together: "Sent together with their prices, all nights are written at once.",
    restoreBody: "{pms} keeps the minimum stay on the price itself. So beside each minimum stay it puts back, Optivo re-sends the price the night has now, after checking it against the price Optivo last sent.",
    restoreHeld: "A night whose price was changed in {pms} itself around the time Optivo last sent one keeps the minimum stay it has now, because nothing says which of the two prices is the newer. So does a night with no price. The Upload Log lists them as held back.",
    restoreTogether: "A night put back together with its saved price is written whatever its price is now.",
  },

  // ── Onboarding Wizard ──────────────────────────────────
  notifications: {
    title: "Notifications",
    empty: "You're all caught up.",
    unread: "unread",
    markRead: "Mark as read",
    markAllRead: "Mark all read",
    preferencesTitle: "Notifications",
    preferencesDesc: "Choose what Optivo notifies you about.",
    // One switch per notification Optivo actually sends. The far-out demand
    // alert is the only one; the four before it were never sent.
    prefFarOutDemand: "Unusual demand further ahead",
    prefFarOutDemandHint:
      "When a night more than 30 days away gains at least 3 rooms within a week, or a fifth of your rooms if that is more. It appears in the bell on the dashboard and is e-mailed to the owners and members of this property.",
    saved: "Notification preferences saved.",
    saveError: "Could not save notification preferences.",
    types: {
      onboarding_complete: {
        title: "Your pricing strategy is live",
        body: "Optivo is now suggesting nightly prices for the weeks ahead.",
      },
      far_out_demand: {
        title: "Unusual demand further ahead",
        // {minDaysOut} and {windowDays} come with the alert.
        body: "These nights more than {minDaysOut} days away filled unusually fast in the last {windowDays} days:",
      },
    },
    // Under each night, or run of a group's nights: "+14 rooms, 15 of 15 taken".
    farOutDemandFigures: "{gained}, {taken} of {total} taken",
    farOutDemandRoomsGained: { one: "+{count} room", few: "+{count} rooms", other: "+{count} rooms" },
  },
  wizard: {
    title: "Property Setup Wizard",
    subtitle: "Let's set up your property for optimal pricing.",
    valueSources: {
      pms: "Imported from PMS",
      location: "From your property location",
      analysis: "Calculated from your booking history",
      priceList: "From your PMS price list",
      questionnaire: "Derived from questionnaire answers",
      typology: "Typology preset",
      default: "Default recommendation",
    },
    continue: "Continue",
    back: "Back",
    enabled: "Enabled",
    disabled: "Disabled",
    exitWizard: "Back to Dashboard",
    expertMode: "Expert mode",
    expertModeHint: "Walk through the advanced steps too. Everything skipped already has a working default, and the strategy editor can fine-tune it later.",
    expertStepsHidden: "{count} advanced steps skipped",
    expertSectionReveal: "I'm an expert — show advanced settings",
    expertSectionHide: "Hide advanced settings",

    // ── Educational onboarding (archetype questionnaire) ──
    education: {
      // Intro / welcome step
      introTitle: "Welcome to Optivo",
      introSubtitle: "Smart pricing that learns how you like to work.",
      introWhatTitle: "What is revenue management?",
      introWhatBody:
        "Revenue management means selling the right room at the right price for the right night. Instead of one fixed rate, your prices flex with demand — higher when rooms are scarce, lower when you need to fill them.",
      introHowTitle: "How Optivo works",
      introHowBody:
        "Optivo watches your bookings, occupancy and booking pace, then suggests a price for every night. You stay in charge of how much it does automatically — from gentle recommendations to full autopilot.",
      introExpectTitle: "What to expect",
      introExpectBody:
        "First we'll connect your property and read its booking history. Then you choose: switch Optivo on with its recommended settings, let our team set it up for you, or walk through a short wizard. You can change any setting later.",
      introGlossaryHint: "New to the jargon? Hover any underlined term for a plain-language explanation.",
      glossaryTermAvgPrice: "average price",
      introStart: "Get started",
      introTime: "Takes about 5 minutes",

      // Quiz chrome
      quizProgress: "Question {current} of {total}",
      quizMultiHint: "Choose one or more",
      quizSingleHint: "Choose one",
      quizNext: "Next",
      quizPrev: "Back",
      quizSelectPrompt: "Select at least one answer to continue.",
      quizSelectOnePrompt: "Select an answer to continue.",
      quizAffectsSettings: "Updates: {settings}",
      quizRefinesTypology: "Shapes the profile we'll recommend on the next screen; no pricing number changes directly.",
      quizCurrentProfile: "Current match: {profile}",
      quizPricesHint: "Enter all three prices",
      quizPricesPrompt: "Enter all three prices to continue.",
      quizPricesOrderWarning: "Check the order — the minimal price should be below the regular-day price, and the top-demand price above it.",

      // Question gates — asked immediately before the setting they configure
      gateSubtitle: "Your answers set the starting values on the next screen. You can change every number there.",
      gateConfigures: "Next: {section}",
      gateSkip: "Skip — use my typology defaults",
      gateSkipToRecommendation: "Skip \u2014 recommend a profile for me",
      gateFinish: "Continue to {section}",

      // Recap shown on the settings step the answers configured
      recapTitle: "Set from your answers",
      recapYouAnswered: "You answered",
      recapChangeAnswer: "Change answer",
      recapValuesSet: "Values set: {fields}",
      // Plain-language names for the settings a recap says an answer changed.
      // The recap is read by the operators who chose the guided profile, so it
      // must never print the engine's own field identifiers at them.
      recapFields: {
        occupancyScaleMax: "How much a full house lifts the price",
        paceSensitivity: "How much booking speed moves the price",
        occupancyDamping: "How steadily the price reacts",
        marketInfluenceEnabled: "Competitor prices",
        marketInfluenceStrength: "How much competitors move your price",
        minPricePct: "Lowest price allowed",
        maxPricePct: "Highest price allowed",
        priceGranularity: "Price rounding",
        weekendHolidayLosEnabled: "Minimum stay on weekends and holidays",
        defaultMinLos: "Minimum nights",
        losReleaseWithinDays: "When the minimum stay is lifted",
        lastMinutePacing: "How quickly last-minute prices move",
        lastMinuteAdjustmentSteps: "Number of last-minute price steps",
        gapFillingEnabled: "Filling short gaps between stays",
        gapFillingMaxGapDays: "Longest gap worth filling",
        gapFillingAdjustmentPct: "Gap-filling adjustment",
        bridgeDayPremiumMultiplier: "Bridge-day adjustment",
        fridayAdjustmentPct: "Friday",
        saturdayAdjustmentPct: "Saturday",
        sundayAdjustmentPct: "Sunday",
        premiumRoomControlIntent: "Closer control of premium rooms",
      },

      // Strategy editor — answer the section's typology question without the wizard
      sectionQuestionUnanswered: "Answer one question to tune this section.",
      sectionQuestionAnswer: "Answer question",
      sectionQuestionApply: "Apply answer",
      sectionQuestionCancel: "Cancel",

      // Questions
      quiz: {
        q33: {
          question: "What prices do you expect to sell a night for?",
          hint: "Optional. Optivo prices each room type from its own booking history — or, before it has any, from your PMS price list — and you can set a room type's prices yourself below. These step in only for a room type none of those covers. When stated, they also set how far a rate may travel from its base price. Give them for a standard double room, incl. VAT.",
          minLabel: "Minimal price",
          minHint: "The lowest price you would ever sell a night for.",
          regularLabel: "Regular-day price",
          regularHint: "Your usual price on an ordinary night.",
          topLabel: "Top-demand price",
          topHint: "The price you expect to sell for when demand peaks.",
        },
      },

      // Archetypes
      archetypes: {
        volume_chaser: {
          name: "Full Rooms First",
          tagline: "Better full at a fair price than empty.",
        },
        revpar_hunter: {
          name: "Best Price First",
          tagline: "Aim high on every night, automatically.",
        },
        set_and_forget: {
          name: "Run It For Me",
          tagline: "Switch it on and get on with your day.",
        },
        lost: {
          name: "Getting Started",
          tagline: "We'll guide you, step by step.",
        },
      },

      // Glossary terms (plain-language tooltips)
      glossary: {
        adr: "Average price — the average nightly price of the rooms you actually sold.",
        occupancy: "Occupancy — the share of your rooms that are booked for a night.",
        pace: "Pace — how far along a date's bookings are versus where they normally are this far out.",
    pickup: "Pickup — the net change in bookings for a date over a window (new bookings minus cancellations).",
        los: "LOS — length of stay. How many nights a booking covers. A minimum LOS blocks very short stays on busy dates.",
        rgi: "RGI — revenue generation index. How your revenue compares with nearby competitors (100 = on par).",
      },
    },

    // ── The setup choice (step `typology`) ──
    // Three ways on once the property is connected and analysed: switch
    // Optivo on, have Optivo's team set it up, or walk the simplified wizard.
    // A request's refusals are worded by `strategyHelp`, the request's own.
    setupChoice: {
      title: "How would you like to set up your prices?",
      subtitle: "Your property is connected and its booking history read. We recommend leaving the first setup to us — you can change anything later in the strategy editor.",
      switchOn: "Switch on and go",
      switchOnMeta: "Done in a minute",
      switchOnDescription: "Optivo sets up your strategy from your booking history and its recommended settings, and takes you straight to your dashboard. It sends prices to your PMS only while sending is switched on — which starts out off.",
      switchOnAction: "Switch on and finish",
      // A property with no stays and no prices in its PMS list: finishing would
      // switch on a strategy that prices nothing.
      switchOnNeedsPrices: "Optivo has nothing to price your room types from yet — there are no bookings to measure, and your PMS price list lists no prices. Continue to set a base price for each room type; you can finish from there.",
      optivo: "Let Optivo set it up",
      optivoMeta: "We reply within {hours} hours",
      optivoDescription: "Our team goes through your property's booking history with the help of AI and sets up your strategy for you. Nothing changes until then.",
      optivoRecommended: "Recommended",
      optivoIncludes: "What you'll get",
      optivoIncludesAnalysis: "An initial analysis of your bookings and prices, sent to you by e-mail",
      optivoIncludesStrategy: "A pricing strategy set up to fit your property",
      optivoIncludesNothingToFill: "Nothing to fill in — just send the request",
      optivoMessageLabel: "Anything we should know? (optional)",
      optivoMessagePlaceholder: "For example a phone number and a good time to call",
      optivoAction: "Ask Optivo to set it up",
      requestedTitle: "We've received your request",
      requestedBody: "You asked on {date}. We'll get back to you at {email} within {hours} hours — until then there's nothing you need to do.",
      requestedBodyAccount: "You asked on {date}. We'll get back to you by e-mail within {hours} hours — until then there's nothing you need to do.",
      requestedCancel: "Cancel the request",
      sentToast: "Request sent to Optivo",
      sentToastDescription: "We'll get back to you within {hours} hours.",
      cancelledToast: "Request cancelled",
      closedToast: "That request was already closed",
      errorCancel: "The request could not be cancelled. Try again in a moment.",
      // The two ways the operator sets up themselves, folded under one toggle
      // below Optivo's offer, or below the request once it is waiting.
      ownWays: "Prefer to set up your prices yourself?",
      ownWaysWaiting: "Don't want to wait? Set up your prices yourself",
      guide: "Simplified wizard",
      guideSteps: { one: "{count} short step", few: "{count} short steps", other: "{count} short steps" },
      guideDescription: "Go through just the essentials — prices, weekends and holidays — and leave the rest on working defaults you can fine-tune later in the strategy editor.",
      guideGoal: "What should Optivo aim for?",
      guideGoalRecommended: "Optivo recommended",
      guideGoalRecommendedHint: "Holds your rate while arrival is far off, and fills rooms as it nears.",
      guideAction: "Continue setup",
    },

    // Step labels
    introStep: "Welcome",
    pmsConnection: "PMS Connection",
    setupChoiceStep: "How to set up",
    questionnaireStep: "Your Style",
    doneForNow: "I'm done for now",
    doneForNowTitle: "Continue with preset defaults?",
    doneForNowDescription: "Optivo will keep your saved choices and create an active preset strategy. No rates will be sent to your PMS. You can continue with the remaining settings in the strategy editor.",
    doneForNowConfirm: "Use preset and continue",
    doneForNowSuccess: "Your preset strategy is ready to refine.",
    doneForNowError: "Failed to create the preset strategy. Please try again.",
    stepSaveError: "Could not save this step. Your answers are still on screen \u2014 try continuing again in a moment.",
    analysis: "Analysis",
    propertyIdentity: "Property Profile",
    keyMetrics: "Key Metrics",
    propertyStructure: "Property Structure",
    lastMinutePricing: "Last-Minute Pricing",
    gapFilling: "Gap Filling",
    bridgeDays: "Bridge & Shoulder Days",
    dayAdjustments: "Day Adjustments",
    seasonClassification: "Seasonality",
    holidays: "Holidays",
    rateRestrictions: "LOS Restrictions",
    distribution: "Distribution",
    strategyPosture: "Strategy Posture",
    marketInfluenceStep: "Market Influence",
    roomKindOverrides: "Per-Room-Kind Overrides",
    bookingWindowStep: "Booking Window",
    lastMinuteStep: "Last-Minute Pricing",
    priceGranularity: "Price Granularity",
    restrictions: "Restrictions",
    reviewActivate: "Review & Activate",

    // Wizard v3 — question gates and merged settings steps
    keyMetricsStep: "Key Metrics",
    specialDatesStep: "Holidays & Special Dates",
    keyMetricsQuestions: "About Your Prices",
    marketInfluenceQuestions: "About Your Competition",
    priceGranularityQuestions: "About Price Precision",
    rateRestrictionsQuestions: "About Stay Rules",
    lastMinuteQuestions: "About Last-Minute Sales",
    dayAdjustmentsQuestions: "About Your Week",

    roomKindOverridesTitle: "Prices by Room Type",
    roomKindOverridesDescription: "Two kinds of setting: prices belong to each room type on its own, while the strategy goal and the price movement across the booking window are property-wide settings a room type can take over.",
    roomKindOverridesEmpty: "No room types detected yet — finish earlier steps first.",
    occupancyRecommendation: "Recommendation from analysis:",

    // New step translations
    priceGranularityStepDescription: "Choose the rounding step for generated prices. Smaller steps allow finer price control.",
    priceGranularityHint: "Rounding step for generated prices.",
    priceCharmEnding: "Charm ending",
    priceCharmEndingHint: "Trim the rounded price so it reads 990 rather than 1000. Only endings that fit inside the rounding step are offered.",
    priceCharmEndingOff: "Off",
    priceCharmEndingExample: "{before} becomes {after}",
    granularityExample: "Example",
    restrictionsTitle: "Restrictions",
    restrictionsDescription: "Set guardrails to keep generated prices within acceptable bounds and configure length-of-stay limits.",
    losRestrictionsHint: "Control the minimum and maximum nights a guest can book.",
    losRestrictionsTitle: "Length of Stay Restrictions",
    losRestrictionsDescription: "Control the minimum and maximum nights a guest can book.",
    noDistributionData: "No distribution data available from the analysis. You can continue.",
    reviewAnalysisSummary: "Based on historical analysis of your reservation data.",
    reviewNoHistorySummary: "No booking history yet — prices start from your PMS price list and the settings above, and learn from your own bookings as they come in.",
    reviewEdit: "Edit",
    reviewRoomPricesTitle: "Prices by Room Type",
    reviewRoomTypeColumn: "Room type",
    reviewMissingBasePriceTitle: "Some room types have no price yet",
    reviewMissingBasePriceBody: "Optivo cannot calculate prices for: {roomKinds}. Add a base price now, or activate and add it later — the other room types are priced either way.",
    reviewSpecialDatesActive: "{count} active",
    reviewCapacityValue: "{beds} beds \u00B7 sleeps {max}",
    reviewNoCapacities: "No room capacities were imported from your PMS.",
    reviewPremiumTiers: "premium tiers",
    reviewDiscountTiers: "discount tiers",

    // Step 1 - PMS
    pmsTitle: "Connect Your PMS",
    pmsDescription: "Connect to your PMS to import rooms and reservations.",
    pmsConnected: "PMS connected successfully.",
    pmsAlreadyConfigured: "Your PMS connection is active.",

    // Step 2 - Analysis
    analysisTitle: "Analyze Your Property",
    analysisDescription: "We'll scan your historical reservations to derive demand patterns, occupancy and revenue metrics.",
    startAnalysis: "Start Analysis",
    analysisRunning: "Analyzing reservations…",
    analysisComplete: "Analysis complete.",
    analysisPrompt: "Click below to start analyzing your booking history. This may take a few minutes.",

    // Step 2.5 - Property Classification
    aiTitle: "Property Profile",
    aiDescription: "Confirm what your property is, where it is, and how its rooms are structured. Property type defaults to Hotel — optionally run AI classification, then confirm or change it manually.",
    aiAssistLabel: "Optional AI Assist",
    aiChecking: "Checking AI availability…",
    aiClassifying: "Classifying your property…",
    aiClassifyingHint: "AI is analyzing your reservation history to determine property type. This may take a moment.",
    aiCancel: "Cancel",
    aiClassified: "Classification Complete",
    aiOverride: "Confirm or change property type",
    aiRetry: "Retry Classification",
    aiStartClassify: "Classify with AI",
    aiCancelled: "Classification Cancelled",
    aiFailed: "Classification Failed",
    aiCancelledDescription: "You cancelled the AI classification. Select your property type manually below.",
    aiFailedDescription: "AI classification could not complete. Select your property type manually below.",
    aiAvailable: "AI Available",
    aiUnavailable: "AI Unavailable",
    aiAvailableTitle: "AI Engine Connected",
    aiAvailableDescription: "Your AI engine is running and will automatically classify your property type during the analysis step. No additional setup needed.",
    aiUnavailableDescription: "The AI engine is not reachable. Select your property type manually below.",
    aiSelectPropertyType: "Select Your Property Type",
    aiSelectPlaceholder: "Choose a property type…",
    // The second sentence is the price list's rule (5 Oct 2026): nobody
    // chooses a plan, a property is billed by what it is, and its size does
    // not decide. The types and the product names are held to the price list
    // by WizardStepPropertyIdentity.test.tsx.

    // Market location — drives external demand signals (holidays, weather)
    marketLocationTitle: "Property location",
    marketLocationHint: "Sets your timezone — pricing reads your property's clock to decide what counts as today and when last-minute discounting starts — and reads school holidays, public holidays and weather for your market.",
    marketLocationRequired: "Required — your property's timezone comes from here, and pricing reads its clock to decide what counts as today and when last-minute discounting starts.",
    // A property with no stays yet: the analysis measures it from the day it opens.
    openingDateLabel: "Opening date",
    openingDateHint:
      "For a property that has not opened yet, or opened within the last year: the first night it sells. Optivo measures its bookings from that day — the months before it opened do not count as months nobody booked — and treats it as new for its first year.",
    marketLocationSearchPlaceholder: "Search for a town or city…",
    marketLocationNoResults: "No places matched that search.",
    marketLocationSharedName: "Several places share this name. Choose yours by its district and region.",
    marketLocationClear: "Clear",
    marketLocationChange: "Change",
    marketLocationFeederTitle: "Guest source markets",
    marketLocationFeederHint: "Countries your guests travel from. Their school holidays often move your occupancy more than local ones — set the share each one accounts for.",
    marketLocationFeederWeight: "How much this market matters",
    marketLocationFeederCountry: "Source market {n}",
    marketLocationFeederAdd: "Add",
    marketLocationFeederRemove: "Remove: {country}",

    // Competitor tracking — included in the subscription since the price list
    // of 5 Oct 2026, and still opt-in. The note says what switching it on
    // includes (the market is read a limited number of times a month, more is
    // agreed individually) and never where the rates are read.
    compsetTitle: "Competitor rate tracking",
    compsetDescription: "Reads what nearby properties are charging and folds it into your market conditions, so pricing reacts to the market and not only to your own occupancy.",
    compsetIncludedNote: "Competitor tracking is included in your subscription and stays off until you switch it on. Optivo reads the prices around each property a limited number of times a month; a larger allowance is agreed individually. Holidays, school holidays and weather are read either way.",
    compsetLocationRequired: "Assign your property location above first — competitor tracking searches by place, so it cannot run without one.",
    compsetEnableLabel: "Enable competitor tracking",
    compsetOptOutHint: "You can turn this off at any time in Settings → Competitor tracking.",
    aiTypeDescriptionFallback: "This property profile influences recommended default pricing behavior.",
    aiLocationLabel: "Property Location",
    aiOptional: "optional",
    aiLocationPlaceholder: "e.g. Prague, Czech Republic",
    aiLocationHint: "Providing a location helps AI make a more accurate classification.",

    // Step 3 - Property
    propertyTitle: "Property Structure",
    propertyDescription: "Verify room types, tiers, and capacities. Adjust anything that doesn't look right.",
    roomKind: "Room Kind",
    beds: "Beds",
    extraBeds: "Extra Beds",
    maxOccupancy: "Max Occ.",
    roomCount: "Rooms",
    avgPrice: "Avg price",
    reservations: "reservations",
    noRoomKinds: "No room kinds detected. Run analysis first.",
    roomCapacities: "Room Capacities",
    occupancyAdjustments: "Per-Person Occupancy Pricing",
    occupancyLabel: "Occ. {n}",
    occupancyAdjustmentWarning: "Price for fewer guests must not exceed the price for more guests.",
    guestStepsHint: "Two guests pay the base rate. These two steps set every other guest count, applied to each room type from its own base price — changing one rewrites the table below.",
    singleGuestStepLabel: "One guest instead of two",
    singleGuestStepHint: "What a single guest pays against the two-guest base rate, never under the minimum that applies to the night.",
    extraGuestStepLabel: "Each guest beyond two",
    extraGuestStepHint: "Added for every guest above two, on a real bed or a roll-away alike.",
    guestStepUnitAmount: "Amount",
    guestStepUnitSelect: "Unit for {field}",
    guestPricingRoomTypeColumn: "Room type",
    guestPricingGuestsHeading: "Nightly rate by number of guests",
    guestPricingBaseTag: "base",
    guestPricingBaseNote: "Two guests pay the room type's base rate. Bed counts come from Room Kind Structure.",
    guestPricingSingleGuestNote: "A room type that sleeps only one guest is sold at its base rate — “{step}” does not change it.",
    guestPricingExtraBedNote: "A shaded price is a guest sleeping on an extra bed.",

    // Step 4 - Demand
    demandTitle: "Demand Patterns Review",
    demandDescription: "Review seasonality, booking behavior, and historical metrics. Adjust values that don't look right.",
    occupancyRate: "Occupancy",
    minimalAcceptablePrice: "Minimal price",
    analysisInsights: "Analysis Insights",
    avgLeadTime: "Avg Booking Lead Time",
    avgStay: "Avg Length of Stay",
    busiestDay: "Busiest Check-in Day",
    quietest: "Quietest",
    days: "days",
    nights: "nights",
    median: "Median",
    seasonality: "Season Classification",
    seasonalityStepDescription: "Classify months into high and low season and set the corresponding price adjustments.",
    seasonClassificationHint: "Mark each month as High, Low, or Common. High and Low months apply their own price adjustment; Common months are neutral.",
    seasonFromAnalysisTag: "From analysis",
    highSeason: "High",
    lowSeason: "Low",
    commonSeason: "Common",
    highSeasonMultiplier: "High Season Adjustment",
    lowSeasonMultiplier: "Low Season Adjustment",
    seasonMonthAdjustment: "{month} adjustment",
    seasonResetToAnalysis: "Reset to analysis",
    // The year, drawn. Same three series as the day-of-week chart, so an
    // operator who has read one already knows how to read this one.
    seasonChartTitle: "Your year",
    seasonChartConfigured: "Your price",
    seasonChartHistory: "What you sold for",
    seasonChartPattern: "Your seasonal pattern",
    seasonChartAxisMoney: "{currency} per night",
    seasonChartAnchorNamed: "Priced from your {roomKind} base price: {price} {currency}",
    seasonChartAnchor: "Priced from your base price: {price} {currency}",
    seasonChartAnchorPick: "Priced from the base price of",
    seasonChartAnchorOption: "{roomKind} · {price} {currency}",
    bookingWindow: "Booking Window Settings",
    shortWindowDays: "Short Window (days)",
    lastMinuteDiscount: "Last-Minute Discount",
    occupancyThreshold: "Occ. Threshold",
    // Step 4.5 - Holidays
    holidaysTitle: "Holiday & Special Date Pricing",
    holidaysDescription: "Configure pricing adjustments for public holidays and custom special dates.",
    holidayCountries: "Guest Countries",
    holidayCountriesHint: "Select one or more countries to combine their public holidays.",
    // The country picker's own prompt on an empty selection — it reads this
    // itself, on the strategy editor as well as here.
    holidayCountriesPlaceholder: "Select countries…",
    countrySelected: "country selected",
    countriesSelected: "countries selected",
    selectCountry: "Select country…",
    detectingCountry: "Auto-detecting from PMS…",
    countryNotReported: "Your PMS didn't report this property's country. Choose it below to load its public holidays.",
    reloadHolidays: "Reload public holidays",
    weekendHolidayLosEnabled: "Minimum stay on weekends and holidays",
    weekendHolidayLosEnabledHint: "Apply a 2-night minimum stay on Friday and Saturday arrivals. When public holidays extend the weekend, the whole break is covered instead — up to 4 nights, from the evening guests arrive through every night inside it, so it cannot be split by a one-night booking. The last day of a break is when guests leave, so it stays free, and ordinary weekdays are never restricted. Inside the release window below the minimum is always released; before it, it can be released early when demand is weak, unless you switch that off below. Where a stay runs into a night already sold out in its room type, that room type's minimum comes down to the stay that can still be booked, until a room frees up.",
    losReleaseWithinDays: "Always release within",
    losReleaseWithinDaysHint: "Inside this many days before arrival the minimum stay always drops back to your default, whatever demand says.",
    losReleaseWithinDaysOffHint: "Set to 0 to let demand alone decide when the minimum stay is released.",
    losReleaseWithinDaysUnit: "days",
    losReleaseWithinDaysNeverHint: "At 0, with “Release the minimum when demand is weak” off, the minimum stay holds until arrival, unless a night the stay needs sells out.",
    losWeakWeekRelease: "Release the minimum when demand is weak",
    losWeakWeekReleaseHint: "When demand for a weekend or holiday is weak, its minimum can drop back to your default before the release window, as when weekends at that time of year were less than 60% full last year. Switch this off if you plan your minimum stays ahead and send them to your PMS.",
    losWeakWeekReleaseOffHint: "Weak weeks keep the minimum too: only the release window above hands it back, or a night the stay needs selling out.",
    // In place of the hint above while Optivo reads the weekend's own bookings.
    losWeakWeekReleaseDemandHint: "Far from arrival the booking history decides: the minimum is released in a week it calls weak, but not while the weekend is already selling clearly faster than usual. From 30 days before arrival, once half of the bookings the weekend usually gets are normally in, the current bookings decide: the minimum is released when they show the weekend ending clearly below its usual occupancy, and held otherwise. A minimum released for a weak week comes back only once the weekend sells clearly faster than usual. “Always release within” always applies.",
    specialDates: "Special Dates",
    specialDatesHint: "Public holidays are pre-filled from the selected countries. Duplicate dates are merged automatically. You can adjust each entry individually or add custom dates. A special-day premium replaces the weekend premium rather than adding to it, and only where it is higher — a night whose own weekend premium, or its room type's, is higher keeps that one. Each entry starts at what a peak night is worth here: {premium}%. Retune any entry and it stops following.",

    // Step 5 - Booking Window
    bookingWindowStepTitle: "Booking Window",
    bookingWindowStepDescription: "How long before arrival Optivo holds each room type's base price, and from when it may discount to fill a night.",
    bookingWindowTypicalDaysLabel: "Typical booking window (days)",
    bookingWindowTypicalDaysHint: "Until this many days before arrival, Optivo keeps a night at least at its base price — moved by its weekday, season and holidays — however slowly it sells. Inside the window it may discount to fill it. So a longer window lets discounts start earlier, and a shorter one holds the price longer. Under Maximize Occupancy no price is held, and the window only times the price movement.",
    bookingWindowAnalysisHint: "Analysis: {days} days",
    bookingWindowReset: "Reset to analysis value",
    bookingWindowNoRoomKinds: "No room kinds available yet. Re-run onboarding to populate room kinds.",
    bookingWindowRoomKindColumn: "Room kind",
    bookingWindowTypicalColumn: "Typical",
    bookingWindowDaysSuffix: "days",
    bookingWindowSeasonReadout: "Booking window by season",
    bookingWindowSeasonHigh: "High season",
    bookingWindowSeasonLow: "Low season",
    bookingWindowSeasonHint: "Every date is in high or low season. The seasons are weeks, not months: high season is the weeks your booking history marks as busy, and it has nothing to do with the months set in Seasonality. Each season is measured the same way over its own nights, so the measured typical window lies between the two. A room type with too few bookings of its own shows the whole property's windows; one with too few in a season shows a dash there and uses its typical window.",
    leadTimeCurveTitle: "Price movement across the booking window",
    leadTimeCurveHint: "Where the price sits at three points of the booking window: while the stay is still far out, around the demand peak, and as arrival nears. A positive percentage raises the price, a negative one lowers it — each point is yours to set in either direction. The percentages apply globally; the timing of each phase adapts to each room kind's own booking window (Typical / High season / Low season).",
    leadTimeCurveStartPct: "Far-out adjustment",
    leadTimeCurveStartPctHint: "Applied while the stay is beyond the typical booking window — the starting point of the price curve.",
    leadTimeCurvePeak: "Peak-demand adjustment",
    leadTimeCurvePeakHint: "Applied around each room kind's booking window peak, where the curve reaches its furthest point.",
    leadTimeCurveArrival: "Near-arrival adjustment",
    leadTimeCurveArrivalHint: "Applied as the arrival date approaches within each room kind's booking window — where the curve ends.",
    leadTimeCurveReset: "Reset to default",
    leadTimeCurvePreviewTitle: "Curve preview",
    leadTimeCurvePreviewX: "Days before arrival",
    leadTimeCurvePreviewY: "Adjustment",
    leadTimeCurvePreviewBuildup: "Booking builds up",
    leadTimeCurvePreviewArrival: "Arrival",
    weekendPremium: "Day Adjustments",
    weekendPremiumHint: "Set separate Friday, Saturday, and Sunday adjustments.",
    weekendDiscount: "Day Adjustments",
    weekendDiscountHint: "Use positive percentages for premiums and negative percentages for discounts.",
    dayAdjustmentsTitle: "Day Adjustments",
    dayAdjustmentsHint: "Use positive percentages for premiums and negative percentages for discounts. On special dates the day-of-week adjustment is skipped — only the special-day premium applies.",
    dayAdjustmentsAnalysisValue: "Analysis: {value}",
    dayAdjustmentsAnalysisNights: "{nights} nights",
    dayAdjustmentsAnalysisEvidence: "Derived from {nights} room nights across {months} months, each room type compared with itself at the same number of guests.",
    dayAdjustmentsAnalysisHolidays: "Stays over a public holiday or special date are left out ({nights} room nights): Holidays & Special Dates prices those nights.",
    dayAdjustmentsResetToAnalysis: "Reset to analysis",
    dayAdjustmentsSourceHistoric: "Your own history",
    dayAdjustmentsSourceBlended: "Your history blended with defaults",
    dayAdjustmentsSourceDefaults: "Optivo defaults",
    dayAdjustmentsStandardHint: "Set what each of these three nights is worth next to an ordinary midweek night. On a special date the special-date price replaces this adjustment, and a last-minute discount cancels it.",
    dayAdjustmentsAnchorNamed: "A Monday–Thursday night in your {roomKind}: {price} {currency}",
    dayAdjustmentsAnchor: "A Monday–Thursday night: {price} {currency}",
    dayAdjustmentsAnchorNote: "Monday to Thursday keeps this price. The three nights below are set against it.",
    dayAdjustmentsAnchorBaseNamed: "Your {roomKind} base price: {price} {currency}",
    dayAdjustmentsAnchorBase: "Your base price: {price} {currency}",
    dayAdjustmentsAnchorNoteAllDays: "Every night below is set against this price.",
    dayAdjustmentsAnchorExampleNamed: "Example: a Monday–Thursday night in your {roomKind} is {price} {currency}",
    dayAdjustmentsAnchorBaseExampleNamed: "Example: your {roomKind} base price is {price} {currency}",
    dayAdjustmentsAnchorExampleNote: "These adjustments apply to every room type, each against its own base price, which Monday to Thursday keeps. The three prices below are for this one; the others move by the same percentage.",
    dayAdjustmentsAnchorExampleNoteAllDays: "These adjustments apply to every room type, each against its own base price. The amounts below are for this one; the others move by the same percentage.",
    dayAdjustmentsMidweekHint: "Monday to Thursday open at 0 — your base price is already the midweek rate. Move one only when that weekday genuinely trades differently.",
    dayAdjustmentsChartTitle: "Your week",
    dayAdjustmentsChartConfigured: "Your price",
    dayAdjustmentsChartHistory: "What you sold for",
    dayAdjustmentsChartSuggested: "Your weekly pattern",
    dayAdjustmentsChartAxisMoney: "{currency} per night",
    dayAdjustmentsNightPrice: "{day} night price",
    dayAdjustmentsRaise: "Raise price: {day}",
    dayAdjustmentsLower: "Lower price: {day}",
    dayAdjustmentsDelta: "{money} {currency} · {percent}",
    dayAdjustmentsPercentValue: "{value}%",
    dayAdjustmentsSameAsWeekday: "Same as a midweek night",
    dayAdjustmentsHistoryMoney: "Sold for {sold} {currency} against {baseline} on an ordinary night in the same rooms",
    dayAdjustmentsAtLimit: "This is as far as a day-of-week adjustment goes. Use Seasonality or Special Dates for bigger swings.",
    dayAdjustmentsSimulatorNote: "Seasonality, demand and your price limits still apply — open the live price example to see the final rate.",
    dayAdjustmentsInheritedValue: "Property-wide: {value}",
    mondayAdjustment: "Monday Adjustment",
    tuesdayAdjustment: "Tuesday Adjustment",
    wednesdayAdjustment: "Wednesday Adjustment",
    thursdayAdjustment: "Thursday Adjustment",
    fridayAdjustment: "Friday Adjustment",
    saturdayAdjustment: "Saturday Adjustment",
    sundayAdjustment: "Sunday Adjustment",

    // Step 5.6 - Last-Minute Pricing (now its own wizard step)
    lastMinuteStepTitle: "Last-Minute Pricing",
    lastMinuteStepDescription: "Configure automatic price reductions as arrival nears to fill unsold rooms.",
    lastMinuteTitle: "Last-Minute Pricing",
    lastMinuteEnabled: "Enable Last-Minute Pricing",
    lastMinuteMinPrice: "Minimum Price Target",
    lastMinuteMinPriceHint: "The lowest price the last-minute algorithm will reach. Leave empty to use the Minimal price from Key Metrics.",
    lastMinuteAdjustmentStepsLabel: "Number of Price Adjustments",
    lastMinuteStepsUnit: "steps",
    lastMinutePacingLabel: "Discount Pacing",
    lastMinutePreview: "Discount curve — drag bars to customise",
    lastMinuteResetCurve: "Reset to curve",
    lastMinutePerRoomKindTitle: "Per Room Kind Overrides",
    lastMinutePerRoomKindHint: "Override the property-wide enabled state for individual room kinds. The minimum price is copied from the room-kind overrides step and the discount curve stays shared.",
    lastMinuteUseDefault: "Discount by demand",
    lastMinuteOverrideEnabled: "Override Enabled",
    lastMinuteOverrideMinPrice: "Override Minimum Price",
    lastMinuteRoomKindMinPriceLabel: "Minimum Price",
    lastMinuteRoomKindMinPriceHint: "Copied from the room-kind overrides step. Edit it there to change this value.",
    gapFillingTitle: "Gap Filling",
    gapFillingHint: "Detect short orphan-night gaps between reservations and price them on their own to improve sell-through.",
    gapFillingEnabled: "Enable Gap Filling",
    gapFillingMaxGapDays: "Maximum Gap Size",
    gapFillingMaxGapDaysHint: "The largest gap that should still get its own adjustment.",
    gapFillingAdjustmentPct: "Price adjustment",
    gapFillingAdjustmentPctHint: "How the price moves for dates inside a detected booking gap. Usually a discount — set a plus if a single night between two stays is scarce rather than unwanted. A discount stands down where the night does not need one: a date you priced yourself, a night already selling well ahead of target, or one last-minute pricing has taken over. A plus always applies.",
    gapFillingNight: "night",
    gapFillingNights: "nights",
    gapFillingStepTitle: "Gap Filling",
    gapFillingStepDescription: "Detect short orphan-night gaps between reservations and price them on their own to improve sell-through.",

    // Step 5.6b - Bridge-day premium (wizard step strings)
    bridgeDaysStepTitle: "Bridge & Shoulder Days",
    bridgeDaysStepDescription: "Price the ordinary nights around a holiday break: the one caught inside it and the ones right before and after it.",
    bridgeDaysTitle: "Bridge Days",
    bridgeDaysHint: "When a single non-premium night sits between two premium nights (holiday↔weekend, weekend↔holiday, or holiday↔holiday), price it as a premium night too.",
    bridgeDaysEnabled: "Enable Bridge-Day Premium",
    bridgeDaysPremiumPct: "Price adjustment",
    bridgeDaysPremiumPctHint: "How the price moves on a detected bridge night. Usually a premium — set a minus if the night nobody wants is the one in the middle.",
    bridgeDaysFollowsWeekend: "Follows your weekend rate of {weekend}%. A bridge night is priced as the premium night it sits between.",
    bridgeDaysOffWeekend: "Set by hand. Your weekend rate is {weekend}%.",

    // Step 5.7 - Day Adjustments
    dayAdjustmentsStepTitle: "Day Adjustments",
    dayAdjustmentsStepDescription: "Set what each night of the week is worth against your base price. Friday, Saturday and Sunday are the ones most properties change; expert mode opens the rest of the week.",

    // Step 6 - Strategy (Pacing)
    strategyTitle: "Strategy Posture",
    strategyDescription: "Choose your optimization goal and set pricing guardrails.",
    pacingStrategyTitle: "Booking Pacing Strategy",
    pacingStrategyDescription: "Your strategy tracks booking pace against historical benchmarks and adjusts prices automatically.",
    howPacingWorks: "How Pacing Works",
    aheadOfPace: "Ahead of Pace",
    aheadOfPaceHint: "Bookings exceed expectations — raise rates to capture more revenue.",
    onTrack: "On Track",
    onTrackHint: "Bookings match expectations — hold current rates.",
    behindPace: "Behind Pace",
    behindPaceHint: "Bookings trail expectations — lower rates to stimulate demand.",
    kpiBaselines: "KPI Baselines (from Analysis)",
    optimizationGoal: "Optimization Goal",
    revenueGoal: "Maximize Revenue",
    revenueGoalHint: "Higher rates when demand is strong, modest discounts in slow periods.",
    hybridGoal: "Optivo Recommended",
    hybridGoalHint: "Holds prices like Max Revenue before the median lead time, then switches to Max Occupancy as arrival approaches.",
    occupancyGoal: "Maximize Occupancy",
    occupancyGoalHint: "More aggressive discounting to fill rooms, smaller premiums for high demand.",
    roomKindStrategyOverridesTitle: "Prices by Room Type",
    roomKindStrategyOverridesHint: "Two kinds of setting live here, and they behave differently when left empty — each group states its own rule below.",
    roomKindStrategyOverridesEssentialHint: "Set the base, minimum and maximum price for each room type. Every rate Optivo calculates for a room type comes from these.",
    overrideUseDefault: "Use default",
    overrideGoal: "Optimization Goal",
    overrideBasePrice: "Base Price",
    overrideMinPrice: "Minimum Price",
    overrideMaxPrice: "Maximum Price",
    overrideFromAnalysisHint: "From analysis",
    overrideMinPriceFromBase: "Suggested from your pricing profile: {pct} of base price. Type your own to override.",
    overrideMaxPriceFromBase: "Suggested from your pricing profile: {pct} of base price. Type your own to override.",
    overridePriceVatNote: "The stated price is incl. VAT for a double room (double use).",
    overrideMissingBasePriceWarning: "No base price for this room type, so Optivo will not calculate any prices for it. Enter one here.",
    overrideMissingBasePriceSummary: "No base price is set for: {roomKinds}. Optivo will not calculate any prices for these room types until you enter one.",
    roomKindOwnScopeTitle: "Prices — this room type only",
    roomKindOwnScopeHint: "Base Price is required — Optivo calculates every rate for this room type from it. No property-wide price stands behind these fields: leave it empty and Optivo uses the price derived from this room type's own history, and if there is none it cannot price this room type at all.",
    roomKindOwnScopeBadge: "Room type only",
    roomKindBasePriceRequiredBadge: "Required",
    roomKindPropertyScopeTitle: "Strategy — overrides property-wide settings",
    roomKindPropertyScopeHint: "Empty means the property-wide setting applies. Fill one in only when this room type should behave differently from the rest of the property.",
    roomKindInheritedBadge: "Property-wide",
    roomKindOverriddenBadge: "Overridden",
    roomKindOverrideReset: "Reset to property-wide value",
    overrideBasePriceNoPropertyFallback: "Empty — Optivo uses the price derived from this room type's own history. There is no property-wide base price to fall back on.",
    roomKindLeadTimeCurveTitle: "Price movement",
    roomKindLeadTimeCurveHint: "These show the property-wide curve until you change one. Any edit gives this room type its own curve; reset puts the property-wide one back.",
    roomKindLeadTimeCurveStartPct: "Far-out adjustment",
    roomKindLeadTimeCurveStartPctHint: "Applied while the stay is beyond this room type's typical booking window.",
    roomKindDayAdjustmentsTitle: "Day of week",
    roomKindDayAdjustmentsHint: "Every night follows the property-wide week until you change it. A night you change belongs to this room type; the rest keep following, including when the property-wide week moves. Reset hands them all back.",
    overrideTierLabel: "Tier",
    tierFromBasePrice: "From Base Price",
    tierOverrideCount: "{count} room-kind override(s)",
    reactivity: "Price Reactivity",
    reactivityHint: "How big a price change Optivo may make when your occupancy and booking pace call for one. Every level reacts to the same signals — the difference is the size of the steps, up and down.",
    gentle: "Gentle",
    gentleHint: "Small steps. Prices stay close to your base price — mild premiums on strong dates, mild discounts on quiet ones. Guests see stable, predictable rates. The default, and the right fit for most properties.",
    moderate: "Moderate",
    moderateHint: "Noticeable moves. Prices follow demand visibly in both directions while staying anchored to your base price. For properties whose demand clearly swings and should show in the rate.",
    aggressive: "Aggressive",
    aggressiveHint: "Big moves. Strong premiums while dates fill up, deep discounts to rescue dates selling slowly. Captures the most from demand, but guests will see rates change a lot.",
    reactivityUpTo: "up to",
    reactivityOccLabel: "occ.",
    reactivityPaceLabel: "pace",
    pickupInfluenceTitle: "Bookings gained",
    pickupInfluenceHint: "React to how fast a date is filling right now, not just how full it already is.",
    pickupInfluenceEnabled: "Let recent pickup move the price",
    pickupInfluenceStrength: "How closely to follow pickup",
    pickupInfluenceStrengthHint: "Higher follows recent booking speed more closely. The effect is capped at 5% of the calculated rate either way.",
    pickupWindowDays: "Measure pickup over",
    pickupWindowDaysHint: "How far back to look when asking how fast this date is filling. Shorter reacts sooner; longer is steadier.",
    pickupInfluenceGuardrail: "Pickup and pace share one limit: together they can never move a rate further than the pace floor and ceiling above already allow. Pickup stays inactive until a date has a full window of measured booking history.",
    minRoomsForRoomKindSignal: "Min. rooms for own signal",
    minRoomsForRoomKindSignalHint: "Room categories with fewer rooms than this threshold always use the whole-property signal — for occupancy, booking pace, pickup and the weekly demand signal — because a handful of rooms is too noisy to price on.",
    propertyPaceWeight: "Property pace weight",
    propertyPaceWeightHint: "Share of the booking-pace adjustment driven by the whole property (vs the selected room category). 60% means the property pace contributes 60% and the room-kind pace 40%. Room categories below the configured room threshold always use the whole-property pace.",
    propertyPaceWeightShort: "property",
    roomKindPaceWeightShort: "room kind",
    propertyDemandWeight: "Property demand weight",
    propertyDemandWeightHint: "Share of the weekly demand signal driven by the whole property (vs the selected room category). 60% means the property's week contributes 60% and the room category's own week 40%. Room categories below the room threshold always use the whole-property demand signal. In a price's explanation this is the “Weekly demand from history” row.",
    propertyDemandWeightShort: "property",
    roomKindDemandWeightShort: "room kind",
    propertyPickupWeight: "Property pickup weight",
    propertyPickupWeightHint: "Share of the pickup reading driven by the whole property (vs the selected room category). 60% means the property's pickup contributes 60% and the room category's own pickup 40%. Room categories below the room threshold always use the whole-property pickup.",
    propertyPickupWeightShort: "property",
    roomKindPickupWeightShort: "room kind",
    postureBiasTitle: "Changeover from protecting to filling",
    postureBiasDescription: "Optivo Recommended protects your rate while a date is further out than your typical booking window, then changes over to discounting it to fill. These three decide where that changeover falls, how sharply it lands, and whether a date's own booking pace may move it.",
    postureBias: "Changeover point",
    postureBiasHint: "50 changes over at your typical booking window; lower holds the rate through more of the window, higher starts filling sooner. At 0 it behaves exactly like Maximize Revenue, at 100 exactly like Maximize Occupancy.",
    postureBiasBalanced: "changes over at your typical booking window",
    postureBiasRateLeaning: "leans towards protecting your rate",
    postureBiasFillLeaning: "leans towards filling",
    postureBiasLikeRevenue: "same as Maximize Revenue",
    postureBiasLikeOccupancy: "same as Maximize Occupancy",
    hybridChangeoverDays: "Ease the changeover in over",
    hybridChangeoverDaysHint: "0 switches on the day. A wider window eases the rate from protecting to filling instead of stepping, so a guest checking twice does not see a jump.",
    hybridChangeoverDaysUnit: "days either side",
    hybridPacePull: "Let the date's own pace move it",
    hybridPacePullHint: "0 keeps the changeover on the calendar. Above it, a date already selling well keeps its rate past the changeover and a date selling badly starts filling before it.",
    simOccupancyDemand: "Occupancy demand",
    simPaceAdjust: "Pace adjustment (ahead)",
    simDaysUntilArrival: "Days until arrival",
    simOccupancy: "Occupancy",
    simRoomKindOccupancy: "Room kind occupancy",
    simRoomKindOccupancyHint: "Occupancy of the selected room category — drives near-arrival and pace adjustments for this room kind.",
    simPropertyOccupancy: "Property occupancy",
    simPropertyOccupancyHint: "Occupancy across the whole property — pulls every room type's price with it, in either direction.",
    simBookingSpeed: "Booking speed",
    simPaceBehind: "Behind",
    simPaceOnTrack: "On track",
    simPaceAhead: "Ahead",
    customizeValues: "Customize values",
    customLabel: "custom",
    paceLower: "Pace Floor",
    paceUpper: "Pace Ceiling",
    paceFloorHint: "Minimum pace multiplier — how much the price drops when behind pace.",
    paceCeilingHint: "Maximum pace multiplier — how much the price rises when ahead of pace. It also caps how far above its base price a night opens before its first booking when its type usually sells early, such as a Saturday that sells out months ahead.",
    losRestrictions: "Length of Stay Restrictions",
    defaultMinLos: "Default Min LOS",
    defaultMinLosHint: "The minimum stay applied when nothing else raises it. Everything else releases back to this floor.",
    losNightsUnit: "nights",
    losAnalysisStayLength: "Analysis: median {median} nights · avg {avg}",
    losAnalysisOneNightShare: "{pct}% are 1-night stays",

    // Parameter hints (wizard)
    highSeasonMultiplierHint: "Moves the nightly rate in busy months. e.g. +15% means a 2 000 base becomes 2 300; a minus prices those months down.",
    lowSeasonMultiplierHint: "Moves the nightly rate in quiet months. Usually a minus to attract bookings — a plus if quiet months are when your rooms are scarce.",
    weekendPremiumParamHint: "Day-specific adjustments let you charge differently on Friday, Saturday, and Sunday.",
    simDayType: "Day",
    simWeekday: "Weekday",
    simMonday: "Monday",
    simTuesday: "Tuesday",
    simWednesday: "Wednesday",
    simThursday: "Thursday",
    simFriday: "Friday",
    simSaturday: "Saturday",
    simSunday: "Sunday",
    simDateType: "Date type",
    simDateTypeRegular: "Regular",
    simDateTypeHoliday: "Holiday",
    simDateTypeBridge: "Bridge day",
    simDateTypeShoulder: "Shoulder day",
    simDateTypeGapFill: "Gap fill",
    simMonth: "Month",
    simDynamicDemand: "Dynamic demand",
    simSpecialDayOverride: "Replaced by the special-day premium — special-day and weekend premiums are never combined.",

    // Price simulator labels
    simBase: "Base rate",
    simSeason: "Season adjustment",
    simHighSeason: "High season",
    simLowSeason: "Low season",
    simRoomKind: "Room kind",
    simShowPreview: "Show Live Price Example",
    simHidePreview: "Hide Live Price Example",
    // Breakdown rows — the panel falls back to hard-coded English without these.
    simExampleTitle: "Live Price Example",
    simFinal: "Final price",
    simClamped: "Clamped to guardrails",
    simNights: "nights",
    simLosRelaxed: "MinLOS relaxed",
    lastMinuteSimHours: "Hours until cutoff",
    lastMinuteActive: "Last-minute adjustment active",

    // Step 7 - Review
    reviewTitle: "Review & Activate",
    reviewDescription: "Verify your choices then activate your pricing strategy.",
    bookingBehavior: "Booking Behavior",
    strategyPostureLabel: "Strategy Posture",
    analysisSummary: "Analysis Summary",
    analysisSummaryDetail: "Based on the analysed reservations, a strategy will be created with these parameters.",
    finalized: "Setup complete! Your pricing strategy has been created.",
    finalizeError: "Failed to finalize onboarding. Please try again.",
    finalizing: "Creating strategy…",
    activateStrategy: "Activate Strategy",
    reviewReassuranceTitle: "Nothing is sent to your PMS yet",
    reviewReassuranceNoAutoPush: "No prices are sent to your PMS automatically — activating only creates your pricing plan inside Optivo.",
    reviewReassuranceReviewFirst: "You can review every recommendation in the price calendar first, night by night.",
    reviewReassuranceExplicitConfirm: "Rates reach your PMS only after you explicitly confirm and send them.",
    reviewReassurancePreviewMode: "Optivo stays in preview mode until you enable sending in the Connection Manager.",
  },

  // ── Recent Bookings ─────────────────────────────────────
  recentBookings: {
    title: "Recent Bookings",
    subtitle: "Reservations by booking date",
    emptyTitle: "No Bookings Found",
    emptyDescription: "No reservations were created in this period. Try selecting a different date range.",
    channel: "Channel",
    roomType: "Room Type",
    stayDates: "Stay Dates",
    bookedOn: "Booked On",
    pricePaid: "Price Paid",
    roomPerNight: "Room / night",
    priceImpact: "Suggested Price",
    priceImpactTooltip: "Suggested nightly rate after this booking. The delta combines the room-type occupancy effect, the property-wide occupancy effect and the booking-pace adjustment — the same pipeline that produces draft prices.",
    occupancyChange: "Occupancy",
    occupancyTooltip: "Room-type and property occupancy on the first night of the stay, before and after this booking. Expand the row to see every night.",
    hotelWideDemand: "Hotel-wide demand",
    hotelWideOccupancy: "overall occ.",
    propertyOccupancyUplift: "Property occupancy uplift",
    propertyOccupancy: "Property occ.",
    roomKindOccupancy: "Room-type occ.",
    paceEffect: "Pace effect",
    paceRatioLabel: "pace",
    paceAheadOfExpected: "ahead of expected pace",
    paceBehindExpected: "behind expected pace",
    paceOnExpected: "on expected pace",
    perNightBreakdown: "Per-night breakdown",
    expandNights: "Show all nights",
    collapseNights: "Hide nights",
    nightColumn: "Night",
    occupancyEffectLabel: "Occupancy effect",
    night: "night",
    nights: "nights",
    searchChannelPlaceholder: "Search channel…",
    searchRoomTypePlaceholder: "Filter room types…",
    noRoomTypes: "No room types",
    priceFrom: "From",
    priceTo: "To",
    clearFilter: "Clear",
    clearAllFilters: "Clear all",
    rowsPerPage: "Rows",
    showing: "Showing",
    of: "of",
    period: "Period",
    refresh: "Refresh",
    viewInCalendar: "View in Calendar",
    viewInDrafts: "View in Drafts",
    quickRanges: "Quick Ranges",
    today: "Today",
    last3Days: "Last 3 Days",
    lastWeek: "Last Week",
    last2Weeks: "Last 2 Weeks",
    last3Weeks: "Last 3 Weeks",
    canceled: "Canceled",
    canceledTooltip: "This reservation was canceled. The price impact shows how much capacity was freed and rates adjusted downward.",
    notReportedWithCommission: "Not reported · with commission",
  },

  // ── Forecast ───────────────────────────────────────────
  forecast: {
    title: "Revenue Forecast",
    totalRevenue: "Total Revenue",
    totalRoomNights: "Room Nights Sold",
    totalReservations: "Reservations",
    revenueByRoomKind: "Revenue by Room Type",
    roomType: "Room Type",
    revenue: "Revenue",
    lastYearRevenue: "Last Year",
    change: "Change",
    comparison: "Comparison",
    roomNights: "Room Nights",
    avgRate: "Avg Rate",
    total: "Total",
    refresh: "Refresh",
    upToToday: "Up to today",
    fullMonth: "Full month",
    days: "days",
    comparing: "Comparing",
    vs: "vs",
    fullMonthWarning: "",
    incompleteLastYear: "",
    emptyTitle: "No Revenue Data",
    emptyDescription: "Revenue data will appear here once reservations are recorded in the system.",
    months: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"] as const,
  },

  // ── Demand & Occupancy Forecast ────────────────────────
  demandForecast: {
    title: "Demand & Occupancy Forecast",
    subtitle: "Forward-looking 365-day forecast of demand and occupancy.",
    emptyState: "No room inventory available — connect your PMS to load the forecast.",
    noBaselineWarning: "Not enough historical reservation data yet — forecast is based on current bookings only. Accuracy will improve as more history accumulates.",
    horizon: {
      "30": "30 days",
      "90": "90 days",
      "180": "180 days",
      "365": "365 days",
    },
    summary: {
      next7d: "Next 7 days",
      next30d: "Next 30 days",
      next90d: "Next 90 days",
      next365d: "Next 365 days",
      avgOccupancy: "Avg forecast occupancy",
      rooms: "rooms",
    },
    charts: {
      forecastTitle: "Forecast occupancy",
      forecastHint: "Projected occupancy combines current on-the-books bookings with the historical pickup curve. Unconstrained demand removes the capacity ceiling.",
      forecastSeries: "Forecast %",
      unconstrainedSeries: "Unconstrained demand %",
      historicalAvgSeries: "Historical avg %",
      capacity: "100% capacity",
      bookingCurveTitle: "Historical booking pace",
      bookingCurveHint: "Typical share of final bookings already on the books at each lead time. Steeper curve = more last-minute bookings.",
      bookingCurveSeries: "On the books",
      daysBeforeStay: "days before stay",
      otbTitle: "Current on-the-books",
      otbHint: "Room nights currently booked for each future stay date.",
      otbSeries: "Room nights",
    },
    demandIndex: {
      title: "Detected Demand Index",
      hint: "Weekly market pressure blending historical average price with how full each week actually gets, so high-demand seasons are caught even when they were historically underpriced.",
      propertySeries: "Property-wide",
      roomKindSeries: "Room kind",
      typicalBand: "Typical",
      premiumBand: "Premium",
      discountBand: "Discount",
      selectRoomKind: "Select room kind",
      noRoomKind: "Property-wide only",
      fallback: "Using property-wide demand index for {roomKind} until there is enough room-kind evidence.",
      empty: "Run property analysis to detect weekly demand patterns.",
      week: "Week",
      adr: "Avg. Price",
      reservations: "Reservations",
      action: "Action",
      premium: "Premium",
      discount: "Discount",
      noAction: "No action",
      source: "Source",
      occupancyIndexSeries: "Occupancy demand",
      occupancyRate: "Occupancy",
    },
    insights: {
      topDemand: "Highest-demand dates",
      topDemandHint: "Candidates for rate increases.",
      promotionCandidates: "Promotion candidates",
      promotionCandidatesHint: "Lowest-demand dates in the next 90 days — consider discounts or marketing pushes.",
      empty: "No data available.",
    },
    table: {
      dailyTitle: "Daily forecast (next 90 days)",
      dailyHint: "Day-by-day breakdown of on-the-books, expected pickup, and forecast occupancy.",
      date: "Date",
      daysOut: "Days out",
      otb: "OTB",
      expectedPickup: "Pickup",
      forecast: "Forecast",
      unconstrained: "Unconstrained",
      historicalAvg: "Hist. avg",
      confidence: "Confidence",
    },
    confidence: {
      low: "Low",
      medium: "Medium",
      high: "High",
    },
  },

  // ── Performance ────────────────────────────────────────
  performance: {
    title: "Performance",
    subtitle: "Bookings, prices and occupancy over time.",
    emptyState: "No bookings in the selected period yet.",
    rangePresets: {
      "30_30": "\u00b130 days",
      "30_90": "-30 / +90",
      "90_90": "\u00b190 days",
      "180_180": "\u00b1180 days",
    },
    summary: {
      totalBookings: "Bookings created",
      totalBookingsHint: "In the selected period",
      totalNights: "Room nights",
      totalNightsHint: "Sold across the period",
      avgOccupancy: "Avg occupancy",
      rooms: "rooms",
      avgPrice: "Avg nightly price",
      avgPriceHint: "Across booked nights",
      totalRevenue: "Total revenue",
      totalRevenueHint: "Stay-date revenue",
    },
    charts: {
      occupancyPriceTitle: "Occupancy & average price",
      occupancyPriceHint: "By stay date. Left axis is occupancy %, right axis is avg nightly price ({currency}).",
      occupancySeries: "Occupancy %",
      avgPriceSeries: "Avg price",
      bookingsTitle: "Bookings created per day",
      bookingsHint: "Number of new reservations entered into the PMS each day.",
      bookingsSeries: "Bookings",
      today: "Today",
    },
    // The month-by-month card under the daily charts (`performance/MonthlyComparisonCard`).
    monthly: {
      title: "Month by month against last year",
      hint: "Completed months against the same months a year earlier. Both years are counted against today's rooms.",
      periodLabel: "Period",
      monthsOptions: { "3": "3 months", "6": "6 months", "12": "12 months" },
      metricLabel: "Show",
      metrics: { revenue: "Revenue", revpar: "RevPAR", occupancy: "Occupancy", adr: "ADR" },
      metricHints: {
        revenue: "Stay-date revenue of the month",
        revpar: "Revenue per available room night",
        occupancy: "Room nights sold of those available",
        adr: "Average price of a sold room night",
      },
      thisYear: "This year",
      lastYear: "Last year",
      lastYearValue: "Last year {value}",
      summaryHint: "Over the months both years cover",
      month: "Month",
      pointsUnit: "pp",
      refresh: "Read the months again",
      noLastYear: "Last year's month is not in the stored history, so it is not compared.",
      empty: "No completed month holds a booking yet.",
    },
  },

  // ── Segment & Channel Yield Report ────────────────────
  segmentChannel: {
    title: "Channels & Segments",
    subtitle: "Net revenue and length of stay by booking source.",
    emptyState: "No reservations in the selected period yet.",
    rangePresets: {
      "30": "Last 30 days",
      "90": "Last 90 days",
      "180": "Last 180 days",
      "365": "Last 365 days",
    },
    summary: {
      netRevenue: "Net revenue",
      commissionDeducted: "commissions deducted",
      directShare: "Direct share",
      directShareHint: "Of net revenue (commission-free)",
      avgLos: "Avg length of stay",
      avgLosHint: "Nights per booking",
    },
    actionHint: {
      title: "Direct demand looks strong",
      description:
        "Direct bookings already drive {direct} of net revenue. Consider closing or restricting high-commission channels ({channels}) on peak nights to protect margin.",
    },
    table: {
      title: "Channel breakdown",
      hint: "Sorted by net revenue. Commissions are estimates based on industry-standard rates.",
      hintWithReported:
        "Sorted by net revenue. Commissions come from your PMS where it reports them, otherwise from industry-standard rates.",
      channel: "Channel / segment",
      bookings: "Bookings",
      roomNights: "Room nights",
      avgLos: "LOS",
      revenue: "Gross revenue",
      commission: "Est. commission",
      commissionWithReported: "Commission",
      netRevenue: "Net revenue",
      share: "Net share",
      directLabel: "Direct (no commission)",
      estCommission: "Est. commission",
      reportedCommission: "Commission per PMS",
      channelNotReported: "Channel not reported",
      totalsRow: "Total",
    },
    disclaimer:
      "Commission rates are estimates: Booking.com 15%, Expedia 18%, Airbnb 14%, Agoda 17%, other OTAs 12%, direct 0%. Replace with your negotiated rates when available.",
    editor: {
      openButton: "Edit commissions",
      title: "Channel commission overrides",
      description:
        "Replace the built-in defaults with your negotiated rates. Leave blank to keep the default. Stored per PMS connection.",
      defaultLabel: "Default",
      overrideBadge: "override active",
      resetRow: "Reset to default",
      resetAll: "Reset all",
      save: "Save overrides",
      saving: "Saving…",
      savedToast: "Commission overrides saved.",
      invalidValue: "{label}: enter a percentage between 0 and 100.",
    },
  },

  // ── Market Conditions ─────────────────────────────────
  marketConditions: {
    title: "Market Conditions",
    subtitle: "What is driving demand around you, and what nearby properties are asking.",
    rangePresets: {
      "30": "Next 30 days",
      "90": "Next 90 days",
      "180": "Next 180 days",
    },
    context: {
      market: "Market",
      captured: "Captured {when}",
      neverCaptured: "Not captured yet",
      stale: "Out of date",
      mixedCurrency:
        "This market also has captures in {currencies}. Rates are never converted, so only {currency} is shown.",
      noCurrencyMatch:
        "This market has only been captured in {currencies}, but you report in {currency}. Capture again to see rates in your own currency.",
    },
    actions: {
      refreshSignals: "Refresh signals",
      // The two refreshes are told apart by what they spend: a property has
      // a limited number of competitor searches a month, and holidays and
      // weather use none. Neither is a charge (price list of 5 Oct 2026).
      refreshSignalsHint: "Holidays and weather. Uses no searches.",
      refreshCompset: "Capture competitors",
      refreshCompsetHint: "Uses one search per night captured, from the monthly allowance.",
      refreshing: "Capturing…",
      // A property reading its own Lighthouse account spends no search.
      fetchLighthouse: "Fetch from Lighthouse",
      fetchLighthouseHint: "Reads your competitor set from Lighthouse now, at most once an hour.",
    },
    summary: {
      demandIndex: "Avg demand",
      demandIndexHint: "0.50 is an ordinary night",
      supply: "Competitors",
      supplyHint: "Median listings per night",
      medianRate: "Median rate",
      medianRateHint: "Across captured nights",
      spread: "Spread",
      spreadHint: "p75 − p25 against the median",
      captures: "Captures",
      capturesHint: "Runs covering this range",
    },
    weatherExtremes: {
      label: "Extreme weather",
      lowersDemand: "Read as lowered demand",
      watch: "Watch",
      severe: "Severe",
      kinds: {
        heat: "Heatwave",
        cold: "Hard frost",
        downpour: "Downpour",
        snowstorm: "Snowstorm",
        gale: "Gale",
        thunderstorm: "Thunderstorm",
        freezingRain: "Freezing rain",
      },
      readings: {
        heat: "Feels like {value}°C",
        cold: "Feels like {value}°C",
        downpour: "{value} mm of rain",
        snowstorm: "{value} cm of snow",
        gale: "Gusts to {value} km/h",
        thunderstorm: "Thunderstorm forecast",
        freezingRain: "Freezing rain forecast",
      },
    },
    demandChart: {
      title: "Demand outlook",
      hint: "One score per night from holidays, weather, your special dates and competitor rates.",
      series: "Demand index",
      highBand: "Stronger than usual",
      typicalBand: "Ordinary",
      lowBand: "Weaker than usual",
      drivers: "Driven by",
      noDrivers: "Nothing notable for this night.",
      empty: "No signals captured yet. Refresh to pull holidays and weather.",
      metrics: {
        "holiday.school": "School holidays",
        "special_date.multiplier": "Your special dates",
        "weather.temp_max_c": "Temperature",
        "weather.temp_min_c": "Overnight low",
        "weather.apparent_max_c": "Feels like (day)",
        "weather.apparent_min_c": "Feels like (night)",
        "weather.precipitation_mm": "Rain",
        "weather.snowfall_cm": "Snow",
        "weather.wind_gust_kmh": "Wind gusts",
        "weather.extreme_severity": "Extreme weather",
        "compset.rate_percentile": "Competitor rates",
        "compset.supply_count": "Competitor supply",
        "compset.median_rate": "Median competitor rate",
        "compset.market_demand": "Market demand",
      },
    },
    // The forecast demand of the property's submarket, read with its own
    // competitor set. Shown by night and not yet priced; the source is never
    // named here.
    marketDemand: {
      title: "Market demand by night",
      night: "Night",
      level: "Market demand",
      note: "Read with your competitor set. Shown for now; it does not move prices yet.",
    },
    bandChart: {
      title: "Competitor rates by night",
      hint: "Median with the middle half of the market shaded. Lowest available room, two adults.",
      medianSeries: "Median",
      bandSeries: "Middle half",
      minSeries: "Cheapest",
      maxSeries: "Dearest",
      supply: "Listings",
      empty: "No competitor rates captured for this range yet.",
    },
    heatStrip: {
      title: "Nights at a glance",
      hint: "Shaded by demand. Pick a night with a capture to see who is in the market.",
      legendLow: "Quiet",
      legendHigh: "Busy",
      noData: "Nothing to show for this range yet.",
      supply: "listings",
      today: "Today",
    },
    table: {
      title: "Who is in the market",
      hint: "Lowest listed nightly rate for two adults, {date}.",
    },
    states: {
      noLocationTitle: "No property location assigned",
      noLocationBody:
        "Market signals are tied to where the property actually is. Set it in the onboarding Property Profile step and holidays, weather and competitors follow.",
      noLocationAction: "Go to setup",
      noCaptureTitle: "No competitor rates captured yet",
      noCaptureBody:
        "Competitor prices are read a limited number of times a month: each night captured uses one search. The nightly job will start filling this in, or capture now.",
      noCaptureAction: "Capture now",
      noCaptureUnavailable:
        "The server has no competitor data source configured, so capture is unavailable.",
    },
    toast: {
      signalsRefreshed: "Market signals refreshed.",
      compsetRefreshed: "Captured {dates} nights using {searches} searches.",
      nothingDue: "Every night in range is already current — nothing was spent.",
      budgetExhausted: "Your competitor search allowance is spent. Capture resumes once it renews; a larger allowance is agreed individually.",
      captureFailed: "Competitor capture failed. Try again later.",
      notConfigured: "No competitor data source is configured on the server.",
      failed: "Could not refresh market data.",
      lighthouseFetched: "Competitor prices fetched from Lighthouse.",
      lighthouseBudget: "Lighthouse has been read as often as it can be today. It is read again tomorrow.",
      lighthouseRunning: "Lighthouse is being read already.",
      lighthouseRecent: "Lighthouse was read less than an hour ago. Try again later.",
      lighthouseNotLinked: "No Lighthouse account is linked to this property.",
      lighthouseOff: "Lighthouse is switched off on this server.",
    },
  },

  pms: {
    // The PMS a connection is for, asked before any field on the form: every
    // field belongs to one PMS (`components/pms/PmsProviderPicker.tsx`). A
    // stored connection states its PMS instead, since no save changes it.
    providerChoice: {
      question: "Which reservation system (PMS) do you use?",
      description: "Pick yours, and the form below asks for exactly what Optivo needs to connect to it.",
      options: {
        previo: "API key (or login e-mail and password) and hotel ID from Previo",
        better_hotel: "API key from Better Hotel",
      },
      label: "Reservation system",
      fixedHint: "A connection stays with the system it was set up for. To connect a property in another system, add a new connection.",
    },
    providers: {
      previo: {
        displayName: "Previo",
        fields: {
          authMode: {
            label: "Authentication method",
            placeholder: "Select a method",
            help: "API key is recommended. Choose login + password only for older Previo accounts that don't have an API key yet.",
            options: {
              apiKey: "API key",
              password: "Login + password",
            },
          },
          apiKey: {
            label: "API key",
            placeholder: "e.g. 3hNNDdGVFNaRDdmSDNvd",
            help: "Paste the API key issued by Previo. Sent as an authorization header and stored as a secret.",
          },
          login: {
            label: "Login email",
            placeholder: "you@hotel.com",
            help: "The email address you use to sign in to Previo.",
          },
          password: {
            label: "Password",
            placeholder: "Enter your Previo password",
            help: "Stored encrypted on Optivo and only used to call the Previo API on your behalf.",
          },
          hotId: {
            label: "Hotel ID",
            placeholder: "e.g. 12345",
            help: "The numeric identifier Previo assigned to your property.",
          },
          langId: {
            label: "Language ID",
            placeholder: "1",
            help: "Language code for Previo API responses. Leave the default (1) unless you need a specific locale.",
          },
        },
      },
      better_hotel: {
        displayName: "Better Hotel",
        fields: {
          apiKey: {
            label: "API key",
            placeholder: "Paste your Better Hotel API key",
            help: "Get it yourself in Better Hotel: in its connected apps („Připojené aplikace“), add the OptivoRMS app from the revenue managers and copy its key. Treated as a secret.",
          },
        },
      },
    },
    nonRefundableHintTemplate:
      "Pushes the non-refundable price to this single {providerName} rate plan for every enabled room kind.",
    noRatePlansHintTemplate:
      "No rate plans available from the PMS. Ensure your {providerName} connection is healthy.",
  },

  // ── Ollama AI Settings ───────────────────────────────────
  ollamaSettings: {
    title: "Ollama AI Settings",
    description: "Configure the Ollama AI server connection and manage local models.",
    configTitle: "Connection",
    configDescription: "Set the URL of your Ollama server and verify connectivity.",
    ollamaApiBase: "Ollama API base URL",
    helper: "Leave empty to use the default (http://localhost:11434).",
    healthChecking: "Checking...",
    healthCheckButton: "Save & test connection",
    saveButton: "Save",
    healthStatusOk: "Connected.",
    healthStatusError: "Unreachable.",
    toastSaved: "Settings saved",
    toastSavedDesc: "Ollama connection settings have been saved.",
    toastSaveFailed: "Save failed",
    healthSuccess: "Connection successful",
    healthSuccessDesc: "Ollama is reachable and responding.",
    healthFailed: "Connection failed",
    modelPullStarted: "Model download started",
    modelPullComplete: "Model download complete",
    modelPullFailed: "Model download failed",
    modelTitle: "Model",
    modelDescription: "Select the AI model Optivo will use for pricing suggestions.",
    modelLabel: "Active model",
    modelPlaceholder: "Search models…",
    localModelsGroup: "Local models",
    cloudModelsGroup: "Cloud / API models",
    badgeCloud: "Cloud",
    badgeAvailable: "Downloaded",
    badgeNotDownloaded: "Not downloaded",
    badgeSelected: "Active",
    pullButton: "Download",
    pullError: "Download error",
    pullProgress: "Downloading…",
    downloadedTitle: "Downloaded models",
    downloadedDescription: "All models currently available on your Ollama server.",
    loadingModels: "Loading models…",
    noModelsDownloaded: "No models downloaded yet.",
    refreshModels: "Refresh",
    notesTitle: "Notes",
    notesDescription: "Important information about Ollama configuration.",
    accessLabel: "Access:",
    notesAccess: "The Ollama server must be reachable from the Optivo service, not just from your browser.",
    serverLabel: "Server:",
    notesServer: "Optivo connects to Ollama server-side. Ensure the API base URL is accessible from the server environment.",
  },

  // Account settings — the page behind the rail's account door, one section
  // per entry of `components/account/account-sections.ts`. `sections` is keyed
  // by the registry's own keys, so a section added there without words here
  // fails the build.
  account: {
    title: "Account settings",
    description: "Payments, security tokens and AI agents for your account.",
    doorLabel: "Account settings",
    navLabel: "Account settings sections",
    sections: {
      billing: {
        title: "Payments & billing",
        description: "Your Optivo subscription, monthly price and payments.",
      },
      tokens: {
        title: "Security tokens",
        description: "Tokens your AI agents and scripts use to work with your properties.",
      },
      mcp: {
        title: "AI agents (MCP)",
        description: "Connect an AI agent, such as Claude, to Optivo through its MCP server.",
      },
    },
    // Said in place of the security tokens or the AI agents section when the
    // server refuses them for the switch an administrator turned off while the
    // page was open (`FEATURE_NOT_ENABLED`). "{email}" is the support address,
    // drawn as a link.
    featureNotEnabled: {
      title: "AI agents are not switched on for your account",
      body: "Optivo switches them on for each person. Write to {email} and we will switch them on for you. Your security tokens are kept, and work again once they are on.",
    },
    // The security tokens section's body. The page draws its heading and
    // one-line description from `sections.tokens`; everything under them is
    // here. A token is the person's own and never the open property's.
    tokens: {
      intro: "Create one token for each agent or script, so each can be revoked on its own. A token reaches only the properties you run, and nothing it does is sent to your PMS by itself.",
      listTitle: "Your tokens",
      // "3 of 20": the live tokens against the most an account may hold.
      count: "{count} of {limit}",
      create: "Create token",
      // Said beside the button it holds back, rather than in a toast after a
      // click. Counted forms, since the limit is the server's to state.
      limitReached: {
        one: "You have {count} token, the most an account may hold. Revoke it to create another.",
        few: "You have {count} tokens, the most an account may hold. Revoke one to create another.",
        other: "You have {count} tokens, the most an account may hold. Revoke one to create another.",
      },
      empty: "You have no security tokens yet. Create one for each AI agent or script you connect.",
      loadFailed: "Your security tokens could not be loaded.",
      retry: "Try again",
      columns: {
        name: "Name",
        access: "Access",
        properties: "Properties",
        lastUsed: "Last used",
        expires: "Expires",
      },
      access: {
        read: "Read only",
        write: "Read and edit prices",
      },
      allProperties: "All properties you run",
      // A property the token names whose name was never set.
      propertyById: "Property #{id}",
      // Properties the token names that the person no longer runs: deleted,
      // or an account they left. Counted, since their names are no longer
      // theirs to read.
      propertiesUnavailable: {
        one: "{count} property no longer available",
        few: "{count} properties no longer available",
        other: "{count} properties no longer available",
      },
      neverUsed: "Never used",
      expired: "Expired",
      // The retention sweep deletes an expired token {days} days after it expired.
      expiredHint: "Deleted on {date}, {days} days after it expired.",
      // {relative} is a distance in words, from the browser: "in 3 days", "tomorrow".
      expiresSoon: "Expires {relative}",
      revokeLabel: "Revoke {name}",
      revoke: "Revoke",
      revokeTitle: "Revoke “{name}”?",
      revokeBody: "Any agent or script using it stops working at once. This cannot be undone.",
      revokeConfirm: "Revoke token",
      revoked: "Token “{name}” revoked.",
      revokeFailed: "The token could not be revoked. Try again.",

      // Creating one
      createTitle: "Create a security token",
      createDescription: "You will see the token once, right after it is created.",
      nameLabel: "Name",
      nameHint: "Say what uses it, such as “Claude on my laptop”, so you know which one to revoke.",
      nameRequired: "Name the token.",
      nameTooLong: "Keep the name to {max} characters.",
      accessLabel: "What it may do",
      accessReadHint: "Reads prices, bookings, analytics, the strategy and the sync status. Changes nothing.",
      accessWriteHint: "Can also set, shift and lock prices and minimum stays. It never sends anything to your PMS — the property's auto-sync does.",
      accessInvalid: "Choose what the token may do.",
      propertiesLabel: "Properties",
      propertiesAll: "All properties you run, including ones you add later",
      propertiesSome: "Only these properties",
      propertiesNone: "You run no property yet. The token reaches one as soon as you connect a property or join one.",
      propertiesRequired: "Choose at least one property.",
      propertiesUnknown: "One of the chosen properties is no longer yours to run. Choose again.",
      expiryLabel: "Expires after",
      // One whole phrase per life on offer: Czech declines the noun with the number.
      expiryDays: {
        d7: "7 days",
        d30: "30 days",
        d90: "90 days",
        d180: "180 days",
        d365: "1 year",
      },
      // A life a newer server offers that this build has no phrase for.
      expiryDaysOther: { one: "{count} day", few: "{count} days", other: "{count} days" },
      expiryOn: "It stops working on {date}.",
      expiryInvalid: "Choose how long the token works.",
      submit: "Create token",
      creating: "Creating…",
      createFailed: "The token could not be created. Try again.",

      // Shown once, right after it is created
      createdTitle: "Token “{name}” created",
      createdDescription: "Copy it now — Optivo shows it only this once. Store it like a password.",
      secretLabel: "Your new security token",
      copy: "Copy token",
      copied: "Token copied.",
      copyFailed: "The token could not be copied. Select it and copy it by hand.",
      connectAgent: "Connect an AI agent",
      connectAgentHint: "Opens the AI agent settings in a new tab, so the token stays here until you have copied it.",
      done: "Done",
    },
    // The AI agents section's body: the switch that lets agents connect with
    // the person's tokens, where they connect, and what they may do there.
    mcp: {
      loadFailed: "The AI agent settings could not be loaded.",
      retry: "Try again",
      unavailableTitle: "The MCP server is switched off",
      unavailableBody: "Optivo has switched its MCP server off for now, so no agent can connect. Your setting and your tokens are kept for when it is back.",
      accessTitle: "Access",
      enabledLabel: "Allow AI agents to connect",
      enabledHint: "Off refuses every agent using your tokens, without revoking them.",
      enabledOn: "AI agents can connect with your tokens.",
      enabledOff: "AI agents can no longer connect with your tokens.",
      saveFailed: "The setting could not be saved. Try again.",
      endpointLabel: "Server address",
      copyEndpoint: "Copy address",
      endpointCopied: "Address copied.",
      copyFailed: "It could not be copied. Select the text and copy it by hand.",
      // {version} is the newest protocol version the server speaks, a date.
      transport: "Streamable HTTP · protocol {version}",
      // Followed by the header itself, as code: `Authorization: Bearer <token>`.
      authHint: "An agent signs in with a security token, sent with every request as",
      createToken: "Create a security token",
      connectTitle: "Connect your agent",
      // {placeholder} is the snippets' own placeholder, shown as code.
      connectDescription: "Replace {placeholder} with one of your security tokens, and keep the file where only you can read it.",
      // A product name is a product name in every language; the tab carries it as is.
      clients: {
        claudeCode: "Claude Code",
        claudeDesktop: "Claude Desktop",
        vscode: "VS Code",
        other: "Cursor / other",
      },
      clientHints: {
        claudeCode: "Run this in a terminal.",
        claudeDesktop: "Add this to claude_desktop_config.json and restart Claude Desktop. It needs Node.js, for npx.",
        vscode: "Add this to .vscode/mcp.json (or your user mcp.json) — VS Code reads its servers under \"servers\".",
        other: "For Cursor, or another client that connects over HTTP and reads an mcp.json with headers under \"mcpServers\".",
      },
      copySnippet: "Copy",
      copySnippetLabel: "Copy the configuration for {client}",
      snippetCopied: "Copied.",
      toolsTitle: "What agents can do",
      toolsDescription: "The tools Optivo's MCP server offers, described as an agent reads them. A read-only token is shown only the tools that read.",
      toolRead: "Reads",
      toolWrite: "Edits prices",
      limitsTitle: "What agents never do",
      limitScope: "An agent reaches only the properties its token covers, and only while you run them.",
      limitNever: "It never sends prices to your PMS, changes a strategy, sees PMS credentials, or touches billing or members.",
      limitLocked: "A price an agent sets is locked, and goes out with the property's next auto-sync.",
      limitLog: "Optivo records every edit an agent makes, with the token it made it with.",
    },
  },

  // Billing — subscription, trial, promocode, wire QR + GoPay payments
  billing: {
    loadFailed: "Could not load billing.",

    // Subscription status card
    statusTitle: "Subscription",
    statusTrialing: "Free trial",
    statusActive: "Active",
    statusUnpaid: "Payment required",
    trialEndsOn: "Trial ends {date}",
    trialDaysLeft: "{count} days left",
    paidUntil: "Paid until {date}",
    unpaidHint: "Your trial has ended. Pay the first month below to keep pricing running.",
    currencyLabel: "Billing currency",
    currencyFixedHint: "Optivo invoices in Czech koruna.",

    // Promo code
    promoTitle: "Promo code",
    promoHint: "Have a promo code? It extends your free trial by one month.",
    promoPlaceholder: "Enter code",
    promoApply: "Apply",
    promoAppliedToast: "Promo code applied — trial extended by a month.",
    promoAppliedBadge: "Promo code applied",
    promoFailed: "Could not apply the promo code.",

    // Billing term
    intervalMonthly: "Monthly",
    intervalYearly: "Yearly",
    intervalSaved: "Billing period updated",
    intervalSaveFailed: "Could not change the billing period",
    priceTotalYear: "Total per year",
    // Under a year's total. A year is twelve months at the monthly price, so
    // the line says the month costs what it costs paid monthly.
    priceMonthlyEquivalent: "{amount} a month, the same as paying monthly",

    // Properties covered — which of the tenant's PMS connections are paid for.
    // Each covered property is billed the monthly price of the product it is
    // and a price for each of its rooms (5 Oct 2026), so a row states the
    // product, the rooms and what a month comes to.
    propertiesTitle: "Properties covered",
    // The …Price sentences follow their hint only where the list prices the
    // quote: not under an individual price, nor over the API before the list.
    propertiesHint: "Choose which of your properties this subscription pays for.",
    propertiesHintPrice: "Each covered property is billed the monthly price of its product, and for each of its rooms.",
    propertiesAllHint: "All your properties are covered, including any you add later.",
    propertiesAllHintPrice: "Each one adds the monthly price of its product and of its rooms.",
    propertiesSomeHint: "{count} of {total} properties are covered. A property you add later will not be covered until you tick it here.",
    // Properties are connected and none is ticked: the ones the subscription
    // paid for were deleted, and its stored selection names nothing that
    // still exists. The way on is a tick, so the list is shown even for one.
    propertiesNoneHint: "None of your properties is covered, so nothing is billed. Tick the ones this subscription should pay for.",
    // The rooms Optivo prices in a property: each is charged for. A rental's
    // unit counts as a room, as the price list says.
    propertiesRooms: { one: "{count} room", few: "{count} rooms", other: "{count} rooms" },
    propertiesMonthlyPrice: "{price} / month",
    propertiesUnnamed: "Property {id}",
    propertiesExcluded: "Not billed",
    // A covered property with no room priced is not billed either.
    propertiesNoRooms: "No rooms set up for pricing",
    propertiesAtLeastOne: "Keep at least one property covered.",
    propertiesSaved: "Properties covered updated",
    propertiesSaveFailed: "Could not update the properties covered",

    // Price breakdown — one row per billed property: the list price of the
    // product it is and of its rooms, then what support agreed and the total.
    // No discount.
    priceTitle: "Monthly price",
    // As the price list names its products. The keys are the engine's: `pro`
    // is what OPTIVO Hotels was stored as while it was a plan a subscription
    // chose. Since 5 Oct 2026 the product follows the property.
    productNames: {
        pro: "OPTIVO Hotels",
        rentals: "OPTIVO Rentals",
    },
    // The one row drawn over an API that predates this list: its quote is
    // worked out another way, so the page states its amount and no list price.
    priceSubscription: "Optivo subscription",
    // One subscription, one payment, every property — stated where the money
    // is. Only ever said of two properties or more.
    priceCoverageTitle: "Covered by this payment",
    priceCoverageMulti: {
        one: "One payment covers your property.",
        few: "One payment covers all {count} properties.",
        other: "One payment covers all {count} properties.",
    },
    priceCoverageSome: "This payment covers {count} of your {total} properties.",
    // A single property: "can I pay for several at once?", answered before
    // the second exists. At list price the next one adds the price of its own
    // product and of its rooms; under an individual price the amount is what
    // was agreed.
    priceCoverageSingle:
        "One subscription covers every property on your account — connect another and it joins this payment.",
    priceCoverageSingleList:
        "One subscription covers every property on your account — connect another and it joins this payment at the price of its own product and rooms.",
    // A covered property that is not on the bill, and why.
    priceNotBilled: "Not billed: {properties} (no rooms set up for pricing).",
    // Nothing is billed: the card shows the price list as the offer rather
    // than a total of zero.
    priceNoneCovered: "No property is covered yet, so nothing is billed. Each property you connect is billed the monthly price of its product, and for each of its rooms.",
    // Properties are connected and none is ticked: said as a tick to make,
    // never as a property to connect.
    priceNoneTicked: "None of your properties is covered, so nothing is billed. Tick one under “Properties covered” and it is billed the monthly price of its product, and for each of its rooms.",
    priceNoRoomsPriced: "No covered property has rooms set up for pricing yet, so nothing is billed.",
    // Market data is in the price of both products, so it is a row only where
    // support agreed a surcharge on top.
    priceMarketData: "OPTIVO Market Data",
    priceMarketDataAgreed: "A surcharge agreed with Optivo",
    priceTotal: "Total per month",
    // A covered property whose PMS could not be read counts no room, and a
    // property with no room priced is not billed: it is off the quote whole.
    roomsIncomplete: "One of your properties could not be read from its PMS, so it is missing from this price. A payment can be made once it can be read.",
    // Support's price, replacing the list price of every covered property.
    priceIndividual: "Individual price agreed with Optivo",

    // The price list in the page's own words, under the quote. Every figure
    // is the API's (`billing.products`); the page types none, so it cannot
    // state a price the checkout does not charge.
    priceListTitle: "Price list",
    priceListUnit: "a month: per property, plus per room",
    // Under a product's own price: what each of its rooms adds. {price} is
    // the API's price of a room.
    priceListPerRoom: "+ {price} per room",
    // Who each product is for, by its family. {types} are the property types'
    // own names, as the setup wizard offers them, and {rentals} the product
    // the hotel one is told apart from: it stands first in the list, so
    // "every other property" would be said before any had been named. The
    // list sets no size limit, so neither sentence states one.
    priceListAudience: {
        hotels: "Every property not of a type {rentals} is for, and one whose type has not been stated yet.",
        rentals: "A property of the type {types}, whatever its number of rooms.",
    },
    priceListIncludedLabel: "Included in each:",
    // The market-data line is said only while the API says every product
    // includes it. Where the data is read is never named.
    priceListIncluded: {
        cadence: "prices updated up to {count}× a day, {days} days ahead",
        marketData: "market data (competitor prices) and weather",
        analytics: "forecast and analytics",
        ratePlans: "linked rate plans where your PMS has them",
        users: "unlimited users",
    },
    // The revenue studies a quarter includes: the one asked for through Ask
    // Optivo. The form is chosen by the API's count.
    priceListAnalyses: {
        one: "one revenue study with recommended settings a quarter",
        few: "{count} revenue studies with recommended settings a quarter",
        other: "{count} revenue studies with recommended settings a quarter",
    },
    priceListByAgreement: {
        one: "More than one revenue study a quarter, or extended market data, is agreed individually — write to {email}.",
        few: "More than {count} revenue studies a quarter, or extended market data, is agreed individually — write to {email}.",
        other: "More than {count} revenue studies a quarter, or extended market data, is agreed individually — write to {email}.",
    },
    priceListTypeHint: "A property's product follows its type alone, and each room Optivo prices in it is charged for. The type is chosen in the setup wizard, on the Property Profile step.",
    priceListLink: "Full price list",

    // Payment
    payTitle: "Payment",
    // The term decides what a payment buys and how often a card is charged,
    // so the sentences that name it come in a monthly and a yearly form.
    payHint: "Pay the next month of your subscription.",
    payHintYear: "Pay the next year of your subscription.",
    payGopay: "Pay with GoPay",
    payGopayUnavailable: "GoPay is not configured yet — pay by bank transfer.",
    // The legal documents, listed on the billing page because that is where
    // the conditions they set actually govern something.
    legalTitle: "Conditions of service",
    legalHint: "The documents your subscription runs under. Each opens in a new tab.",
    // No property is covered, so a payment would buy nothing and the server
    // refuses it. Said as a sentence rather than a disabled button.
    payNothingToBill: "Connect a property before subscribing — the price is per property and per room, so there is nothing to pay for yet. Your trial keeps running in the meantime.",
    // Properties are connected and none is ticked: the server refuses that
    // payment as it refuses one with no property at all.
    payNoneTicked: "None of your properties is covered by the subscription, so there is nothing to pay for yet. Tick one under “Properties covered” above.",
    // Properties are covered but none has a room priced: the quote is zero,
    // and the server refuses that payment too.
    payNoRoomsPriced: "There is nothing to pay yet — no covered property has rooms set up for pricing.",
    // A covered property could not be read: it is missing from the price or
    // priced as the wrong product, and the server opens no list-priced payment
    // until it answers. Reading again is the retry, not a payment attempt.
    payRoomsUnread:
        "Your property could not be read from its PMS, so the price cannot be confirmed yet. A payment can be started once it answers.",
    payRoomsUnreadSeveral:
        "One of the covered properties could not be read from its PMS, so the price cannot be confirmed yet. A payment can be started once it answers — or leave that property out above, the one shown without a price, and pay for the others.",
    payCheckAgain: "Check again",
    // Support agreed a price of zero: nothing is owed, and the server refuses
    // a payment of nothing.
    payNothingOwed: "Nothing is owed under the price agreed with Optivo, so there is no payment to make.",
    payAcceptedCards: "We accept Visa and Mastercard, secured by 3-D Secure. Card details are entered on the GoPay gateway and never reach Optivo.",

    // Recurring payments — the mandate must be disclosed before it is given
    recurringOptIn: "Renew automatically every month",
    recurringOptInYear: "Renew automatically every year",
    recurringDisclosureMonthly: "Paying now also authorises us to charge this card once a month, on the day each paid period ends.",
    recurringDisclosureYearly: "Paying now also authorises us to charge this card once a year, on the day each paid period ends.",
    // What moves the amount under the price list of 5 Oct 2026: the
    // properties covered, the number of their rooms, a service agreed
    // individually, the list itself. A variable-amount mandate, in the words
    // of /recurring-terms.
    recurringDisclosureVariable: "The amount follows the properties your subscription covers and the number of their rooms: it changes only when they, an individually agreed service or the price list change. The current amount is always shown here before it is charged.",
    recurringDisclosureCancel: "You can cancel automatic renewal at any time on this page. Cancelling is immediate and free.",
    recurringTermsLink: "Recurring payment terms",
    recurringActive: "Automatic renewal is on.",
    // Restated after the mandate is authorised: GoPay wants the customer told
    // the parameters during the payment and again once it has finished. Kept
    // term-neutral because a yearly subscription renews yearly, not monthly.
    recurringActiveFrequency:
        "This card is charged automatically on the day each paid period ends, for the period that follows.",
    recurringSince: "Card authorised {date}",
    recurringLastCharge: "Last charged {date}",
    recurringCancel: "Cancel automatic renewal",
    recurringCanceled: "Automatic renewal cancelled",
    recurringCancelFailed: "Could not cancel automatic renewal",
    // Stands before the note a renewal pass left. A note is not always a
    // failure — where the gateway did not answer for a charge, whether it
    // went through is not known — so these words claim no outcome.
    recurringFailedNotice: "About the last automatic payment:",
    // A renewal is charged once (/recurring-terms, article 5): when that
    // charge takes nothing the page says when, what became of it, and that
    // the card is not tried again. One sentence per kind the API names;
    // `Other` stands for a kind a later API names, with what it recorded.
    recurringFailedDeclined: "The automatic payment on {date} did not go through: the card was declined.",
    recurringFailedTimedOut:
        "The automatic payment on {date} did not go through: it expired at the payment gateway before it was completed.",
    recurringFailedRefused: "The automatic payment on {date} did not go through: the payment gateway refused it.",
    recurringFailedOther: "The automatic payment on {date} did not go through.",
    recurringNoRetry: "We will not try the card again by ourselves.",
    recurringPayAnotherWay:
        "Pay for this period below, by card or by bank transfer. Automatic renewal then carries on from the period after it.",
    payTermsNotice: "By paying you agree to the",
    termsLink: "Terms and Conditions",
    payWire: "Pay by bank transfer",
    // A wire has no gateway to take the agreement, so it is taken here.
    wireTermsAccept: "I have read and agree to the Terms and Conditions",
    wireTermsHint:
        "A bank transfer is issued only once you agree. Card payments take this agreement at the gateway instead.",
    wireCovers: "Covers",
    paymentCreateFailed: "Could not create the payment.",
    // An earlier payment of the subscription turned out paid as this one was
    // asked for, so none was opened.
    paymentAlreadySettled: "The payment gateway has just confirmed an earlier payment for this subscription, so no new payment was opened.",
    // A covered property that cannot be read is missing from the quote, or
    // priced as the wrong product, so the server opens no list-priced payment
    // until it answers.
    paymentRoomsIncomplete:
        "One of your covered properties could not be read from its PMS, so the price of your subscription is not known yet. Try again once it can be read.",
    gopayRedirecting: "Redirecting to GoPay…",

    // Wire transfer details
    wireTitle: "Bank transfer",
    wireHint: "Scan the QR code with your banking app, or use the details below.",
    wireRecipient: "Recipient",
    wireAccount: "Account",
    wireIban: "IBAN",
    wireBic: "BIC/SWIFT",
    wireVariableSymbol: "Variable symbol",
    wireAmount: "Amount",
    wirePending: "The subscription activates once the transfer arrives — usually the next business day.",

    // VAT
    vatRow: "VAT ({rate} %)",
    vatReverseCharge: "VAT reverse charge — tax payable by the customer.",
    vatOutsideScope: "Outside the scope of Czech VAT.",

    // Billing details printed on invoices
    detailsTitle: "Billing details",
    detailsHint: "Company details printed on your invoices.",
    custName: "Company / name",
    custStreet: "Street",
    custCity: "City",
    custZip: "ZIP",
    custCountry: "Country code",
    custIco: "Company ID (IČO)",
    custDic: "VAT ID (DIČ)",
    detailsSave: "Save details",
    detailsSaved: "Billing details saved",
    detailsSaveFailed: "Could not save billing details",

    // Invoices
    invoicesTitle: "Invoices",
    invoicesEmpty: "No invoices yet — one is issued for every settled payment.",
    invoiceNumberCol: "Number",
    invoiceDateCol: "Issued",
    invoiceTotalCol: "Total",
    invoiceView: "View",
    invoicesExport: "Export CSV",

    // Payment history
    historyTitle: "Payments",
    historyEmpty: "No payments yet.",
    historyDate: "Created",
    historyPeriod: "Period",
    historyMethod: "Method",
    historyAmount: "Amount",
    historyStatus: "Status",
    methodWire: "Bank transfer",
    methodGopay: "GoPay",
    paymentStatusPending: "Pending",
    paymentStatusPaid: "Paid",
    paymentStatusCanceled: "Canceled",
    paymentStatusFailed: "Failed",
    // Received for a period another payment had already paid: nothing was
    // applied, and the money goes back.
    paymentStatusRefundDue: "To be refunded",
    paymentStatusRefunded: "Refunded",
    // A status this version of the page has no word for.
    paymentStatusOther: "Other",
  },


  // ── Cookie / storage disclosure ─────────────────────────
  cookieNotice: {
    regionLabel: "Browser storage notice",
    title: "Optivo stores only what it needs to work",
    body:
      "A session cookie keeps you signed in, and your theme, language and display preferences are saved in this browser. There is no advertising or third-party tracking, so there is nothing here to switch off.",
    privacyLink: "Read the privacy notice",
    acknowledge: "Got it",
  },
  // The notice over a document of the upcoming legal set (`components/legal/
  // upcoming/`): counsel's draft, read at a preview address beside the
  // document in force. The documents' own text is not here: it is legal text,
  // kept with the documents.
  legalOpenPoints: {
    note: "Some values in this document are still to be set ({count}); they are marked in the text.",
  },

  // ── Holidays: source markets the holiday list leaves out ─
  // Read by `SourceMarketHolidaysNotice` itself, on both the wizard step and
  // the strategy editor, rather than through either surface's label bag.
  holidaySourceMarkets: {
    missingOne: "This list is missing holidays from your source market: {countries}.",
    missingMany: "This list is missing holidays from your source markets: {countries}.",
    add: "Add holidays",
  },

} as const;

// Convert all literal string types to `string` so other locales can provide different values
type DeepStringify<T> = {
  [K in keyof T]: T[K] extends readonly string[] ? readonly string[]
  : T[K] extends string ? string
  : DeepStringify<T[K]>;
};

export type Translations = DeepStringify<typeof en>;
